"use client";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/lib/amount.ts
var amountFormats = /* @__PURE__ */ new Map();
function getAmountFormat(currency) {
  let format = amountFormats.get(currency);
  if (!format) {
    let formatter;
    try {
      formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency
      });
    } catch {
      formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    format = {
      formatter,
      fractionDigits: formatter.resolvedOptions().maximumFractionDigits ?? 2,
      symbol: formatter.formatToParts(0).find((part) => part.type === "currency")?.value ?? ""
    };
    amountFormats.set(currency, format);
  }
  return format;
}
function toAmountDraft(value, fractionDigits) {
  return value == null ? "" : (value / 10 ** fractionDigits).toFixed(fractionDigits);
}
function parseAmountDraft(draft, fractionDigits) {
  const amount = Number(draft);
  return draft === "" || !Number.isFinite(amount) ? void 0 : Math.round(amount * 10 ** fractionDigits);
}
function formatAmount(value, currency) {
  const { formatter, fractionDigits } = getAmountFormat(currency);
  return formatter.format(value / 10 ** fractionDigits);
}

// src/hooks/use-mobile.ts
import * as React from "react";
var MOBILE_BREAKPOINT = 1024;
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(void 0);
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return !!isMobile;
}

// src/components/ui/animated-check-icon.tsx
import * as React2 from "react";
import { jsx } from "react/jsx-runtime";
function AnimatedCheckIcon({ checked }) {
  const hasMounted = React2.useRef(false);
  const [animation, setAnimation] = React2.useState("none");
  React2.useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    setAnimation(checked ? "draw" : "reverse");
  }, [checked]);
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className: cn(
        "size-4 text-primary",
        animation === "draw" && "animate-check-draw",
        animation === "reverse" && "animate-check-draw-reverse"
      ),
      viewBox: "0 0 20 20",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      style: {
        strokeDasharray: 1,
        strokeDashoffset: animation === "none" ? checked ? 0 : 1 : void 0
      },
      children: /* @__PURE__ */ jsx("path", { d: "M14.75 6.5L8.25 13.5L5.25 10.3636", pathLength: "1" })
    }
  );
}

// src/components/ui/circle-questionmark-icon.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function CircleQuestionmarkIcon({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      viewBox: "0 0 20 20",
      fill: "currentColor",
      "aria-hidden": "true",
      className: cn("size-4 shrink-0", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx2(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M10 3.25C6.27208 3.25 3.25 6.27208 3.25 10C3.25 13.7279 6.27208 16.75 10 16.75C13.7279 16.75 16.75 13.7279 16.75 10C16.75 6.27208 13.7279 3.25 10 3.25ZM1.75 10C1.75 5.44365 5.44365 1.75 10 1.75C14.5563 1.75 18.25 5.44365 18.25 10C18.25 14.5563 14.5563 18.25 10 18.25C5.44365 18.25 1.75 14.5563 1.75 10ZM9.97804 5.91605C10.6677 5.88714 11.3423 6.12711 11.859 6.58621C12.3849 7.05336 12.7046 7.7095 12.7485 8.41149C12.7495 8.42703 12.75 8.44259 12.75 8.45816C12.7501 8.90202 12.6315 9.33784 12.4064 9.72042C12.1814 10.103 11.8581 10.4184 11.4701 10.634L10.3642 11.2482C10.002 11.4493 9.54545 11.3188 9.34434 10.9566C9.14323 10.5945 9.27374 10.1379 9.63586 9.93684L10.7416 9.3227C10.7416 9.32269 10.7416 9.32272 10.7416 9.3227C10.8957 9.23709 11.0242 9.11179 11.1135 8.95986C11.199 8.81451 11.2459 8.64981 11.2497 8.48148C11.225 8.18369 11.0866 7.90644 10.8628 7.70757C10.633 7.50344 10.3318 7.39845 10.025 7.41551C10.0008 7.41685 9.97658 7.41703 9.95239 7.41603C9.67227 7.40446 9.39689 7.49107 9.17382 7.66091C8.95074 7.83074 8.79398 8.07314 8.73058 8.34625C8.63692 8.74973 8.23391 9.0009 7.83042 8.90724C7.42694 8.81358 7.17577 8.41057 7.26943 8.00708C7.41185 7.39354 7.76403 6.84898 8.26517 6.46744C8.75669 6.09322 9.36124 5.89886 9.97804 5.91605Z"
          }
        ),
        /* @__PURE__ */ jsx2("path", { d: "M10.875 13.25C10.875 13.7332 10.4832 14.125 10 14.125C9.51675 14.125 9.125 13.7332 9.125 13.25C9.125 12.7668 9.51675 12.375 10 12.375C10.4832 12.375 10.875 12.7668 10.875 13.25Z" })
      ]
    }
  );
}

// src/components/ui/flags/flag-icon.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
function FlagIcon({ size = 24, className, children, ...props }) {
  return /* @__PURE__ */ jsx3(
    "svg",
    {
      "data-slot": "flag-icon",
      viewBox: "0 0 24 24",
      width: size,
      height: size,
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      className: cn("block shrink-0", className),
      ...props,
      children
    }
  );
}

// src/components/ui/flags/assets/AD.tsx
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var FlagAD = (props) => /* @__PURE__ */ jsxs2(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs2("g", { clipPath: "url(#cfAD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx4(
      "path",
      {
        d: "M6.78262 22.8093C8.36025 23.5721 10.1301 24 12 24C13.8699 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072C15.6397 0.427875 13.8699 0 12 0C10.1301 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx4(
      "path",
      {
        d: "M17.2174 22.8089C21.2313 20.868 24 16.7575 24 12C24 7.24252 21.2313 3.13205 17.2174 1.19114V22.8089Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx4(
      "path",
      {
        d: "M6.78263 22.8089V1.19114C2.76867 3.13205 0 7.24247 0 12C0 16.7575 2.76867 20.868 6.78263 22.8089Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs2("g", { children: [
      /* @__PURE__ */ jsx4(
        "path",
        {
          d: "M12 16.1739C12 14.259 12 12 12 12H15.1305V13.5652C15.1305 13.8371 14.6087 14.8335 13.3209 15.6522C12.8332 15.9623 12.3283 16.062 12 16.1739Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx4("path", { d: "M8.86954 8.86954H12V12H8.86954V8.86954Z", fill: "#D80027" })
    ] }),
    /* @__PURE__ */ jsx4(
      "path",
      {
        d: "M13.5652 7.82611C13.5652 7.24978 13.098 6.78263 12.5217 6.78263C12.3314 6.78263 12.1536 6.83438 12 6.92334C11.8464 6.83438 11.6686 6.78263 11.4783 6.78263C10.902 6.78263 10.4348 7.24978 10.4348 7.82611H7.82611V13.0434C7.82611 14.9858 9.36858 16.109 10.5782 16.6913C10.4877 16.8459 10.4348 17.0253 10.4348 17.2174C10.4348 17.7937 10.902 18.2609 11.4783 18.2609C11.6686 18.2609 11.8464 18.2092 12 18.1202C12.1536 18.2092 12.3314 18.2609 12.5217 18.2609C13.098 18.2609 13.5652 17.7937 13.5652 17.2174C13.5652 17.0253 13.5123 16.8459 13.4218 16.6913C14.6314 16.109 16.1739 14.9858 16.1739 13.0434V7.82611H13.5652ZM14.6087 13.0434C14.6087 13.3154 14.6087 14.1428 13.3209 14.9615C12.8332 15.2716 12.3283 15.4727 12 15.5845C11.6717 15.4727 11.1668 15.2716 10.6791 14.9615C9.39131 14.1428 9.39131 13.3154 9.39131 13.0434V9.39131H14.6087V13.0434Z",
        fill: "#FF9811"
      }
    )
  ] }),
  /* @__PURE__ */ jsx4("defs", { children: /* @__PURE__ */ jsx4("clipPath", { id: "cfAD_clip0_0_1", children: /* @__PURE__ */ jsx4("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AE.tsx
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var FlagAE = (props) => /* @__PURE__ */ jsxs3(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs3("g", { clipPath: "url(#cfAE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx5(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx5(
      "path",
      {
        d: "M6.78259 16.1739L7.82608 23.2537C9.12601 23.7361 10.5321 24 12 24C17.1595 24 21.5581 20.7435 23.2535 16.1739H6.78259Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx5(
      "path",
      {
        d: "M6.78259 7.82611L7.82608 0.746297C9.12601 0.263906 10.5321 0 12 0C17.1595 0 21.5581 3.2565 23.2535 7.82611H6.78259Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx5(
      "path",
      {
        d: "M0 12C0 17.1596 3.25655 21.5581 7.82611 23.2536V0.746399C3.25655 2.44191 0 6.84043 0 12Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx5("defs", { children: /* @__PURE__ */ jsx5("clipPath", { id: "cfAE_clip0_0_1", children: /* @__PURE__ */ jsx5("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AF.tsx
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var FlagAF = (props) => /* @__PURE__ */ jsxs4(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs4("g", { clipPath: "url(#cfAF_clip0_0_1)", children: [
    /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M17.2174 1.19072C15.6398 0.427875 13.8698 0 12 0C10.1302 0 8.36025 0.427875 6.78263 1.19072L5.73914 12L6.78263 22.8093C8.36025 23.5721 10.1302 24 12 24C13.8698 24 15.6398 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M6.78263 1.19114C2.76872 3.13209 0 7.24252 0 12C0 16.7575 2.76872 20.8679 6.78263 22.8089V1.19114Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx6(
      "path",
      {
        d: "M17.2174 1.19114V22.8089C21.2313 20.8679 24 16.7575 24 12C24 7.24252 21.2313 3.13209 17.2174 1.19114Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsxs4("g", { children: [
      /* @__PURE__ */ jsx6(
        "path",
        {
          d: "M12 7.82611C9.69478 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611ZM12 14.6087C10.5593 14.6087 9.39131 13.4407 9.39131 12C9.39131 10.5593 10.5593 9.39131 12 9.39131C13.4407 9.39131 14.6087 10.5593 14.6087 12C14.6087 13.4407 13.4408 14.6087 12 14.6087Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx6(
        "path",
        {
          d: "M12 10.4348C11.4237 10.4348 10.9565 10.902 10.9565 11.4783V13.0435H13.0435V11.4783C13.0435 10.902 12.5763 10.4348 12 10.4348Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx6("defs", { children: /* @__PURE__ */ jsx6("clipPath", { id: "cfAF_clip0_0_1", children: /* @__PURE__ */ jsx6("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AG.tsx
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var FlagAG = (props) => /* @__PURE__ */ jsxs5(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs5("g", { clipPath: "url(#cfAG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M0 12C0 12.822 0.082875 13.6245 0.240328 14.4L12 15.1305L23.7597 14.4C23.9172 13.6245 24 12.822 24 12C24 11.178 23.9171 10.3755 23.7597 9.6L12 8.86954L0.240328 9.6C0.082875 10.3755 0 11.178 0 12H0Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M23.7597 14.4H0.240326C1.35215 19.8773 6.19458 24 12 24C17.8054 24 22.6478 19.8773 23.7597 14.4Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M0.240326 9.6H23.7598C22.6478 4.12266 17.8055 0 12 0C6.19448 0 1.35215 4.12266 0.240326 9.6Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx7(
      "path",
      {
        d: "M17.2174 9.6H6.78265L8.91453 8.59716L7.77926 6.53255L10.0942 6.97528L10.3876 4.63673L12 6.35676L13.6125 4.63673L13.9058 6.97528L16.2208 6.53255L15.0856 8.59725L17.2174 9.6Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs5("g", { children: [
      /* @__PURE__ */ jsx7(
        "path",
        {
          d: "M0 12C0 18.6274 5.37262 24 12 24L2.39981 4.79958C0.893062 6.80531 0 9.29827 0 12Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx7(
        "path",
        {
          d: "M12 24C18.6274 24 24 18.6274 24 12C24 9.29827 23.1069 6.80531 21.6002 4.79958L12 24Z",
          fill: "#A2001D"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx7("defs", { children: /* @__PURE__ */ jsx7("clipPath", { id: "cfAG_clip0_0_1", children: /* @__PURE__ */ jsx7("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AI.tsx
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var FlagAI = (props) => /* @__PURE__ */ jsxs6(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs6("g", { clipPath: "url(#cfAI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs6("g", { children: [
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs6("g", { children: [
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M6.071 1.5652C4.19417 2.63391 2.63384 4.19419 1.56519 6.07102V12H4.69564V4.69575V4.69566H12C12 3.70833 12 2.76773 12 1.5652H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M12 10.5243L7.73646 6.26091H6.26083V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M20.294 13.7566C20.639 13.2818 20.8695 12.7021 20.8695 12V6.78263C20.4335 7.11019 19.8916 7.30434 19.3043 7.30434C18.4509 7.30434 17.6933 6.89452 17.2174 6.26095C16.7414 6.89452 15.9837 7.30434 15.1304 7.30434C14.543 7.30434 14.0012 7.11014 13.5652 6.78272V12C13.5652 12.7021 13.7957 13.2818 14.1407 13.7566H20.294Z",
        fill: "#F3F3F3"
      }
    ),
    /* @__PURE__ */ jsxs6("g", { children: [
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M19.2087 11.0321C19.3748 10.6268 19.504 10.0344 19.504 9.73964C19.504 9.26597 18.8865 8.88202 18.8865 8.88202C18.8865 8.88202 18.2689 9.26597 18.2689 9.73964C18.2689 10.0345 18.3982 10.6268 18.5642 11.0321L18.2077 11.837C18.4172 11.9222 18.6463 11.9695 18.8865 11.9695C19.1267 11.9695 19.3557 11.9221 19.5653 11.837L19.2087 11.0321Z",
          fill: "#FF9811"
        }
      ),
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M16.795 8.42587C16.3611 8.48466 15.7835 8.66911 15.5282 8.81648C15.118 9.05334 15.0942 9.78005 15.0942 9.78005C15.0942 9.78005 15.7356 10.1228 16.1457 9.88603C16.4011 9.73861 16.8494 9.33061 17.1172 8.98411L17.9926 8.89036C17.9616 8.66639 17.888 8.44434 17.768 8.23636C17.6478 8.02837 17.4924 7.85362 17.3138 7.71473L16.795 8.42587Z",
          fill: "#FF9811"
        }
      ),
      /* @__PURE__ */ jsx8(
        "path",
        {
          d: "M15.7449 11.8193C16.0127 12.1657 16.4612 12.5736 16.7166 12.7211C17.1268 12.9579 17.7681 12.6152 17.7681 12.6152C17.7681 12.6152 17.7441 11.8884 17.334 11.6515C17.0786 11.5041 16.5011 11.3198 16.0671 11.2612L15.5483 10.5499C15.3698 10.6888 15.2143 10.8636 15.0942 11.0714C14.9742 11.2795 14.9007 11.5014 14.8695 11.7255L15.7449 11.8193Z",
          fill: "#FF9811"
        }
      )
    ] }),
    /* @__PURE__ */ jsx8(
      "path",
      {
        d: "M14.0117 13.5653C14.9861 15.1289 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 19.4487 15.1289 20.4232 13.5653H14.0117Z",
        fill: "#338AF3"
      }
    )
  ] }),
  /* @__PURE__ */ jsx8("defs", { children: /* @__PURE__ */ jsx8("clipPath", { id: "cfAI_clip0_0_1", children: /* @__PURE__ */ jsx8("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AL.tsx
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var FlagAL = (props) => /* @__PURE__ */ jsxs7(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs7("g", { clipPath: "url(#cfAL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx9(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx9(
      "path",
      {
        d: "M18.7826 8.90667H14.4361C14.7044 8.6257 14.8695 8.24531 14.8695 7.82611C14.8695 6.96169 14.1688 6.26091 13.3043 6.26091C12.7598 6.26091 12.2804 6.53916 12 6.96108C11.7196 6.53916 11.2402 6.26091 10.6957 6.26091C9.83123 6.26091 9.13045 6.96169 9.13045 7.82611C9.13045 8.24531 9.29559 8.62575 9.56391 8.90667H5.21742C5.21742 10.0593 6.22134 10.9936 7.37386 10.9936H7.3043C7.3043 12.1463 8.23866 13.0806 9.39131 13.0806C9.39131 13.4539 9.4897 13.8039 9.66145 14.1069L7.93036 15.838L9.25852 17.1661L11.1425 15.2821C11.2163 15.3092 11.2927 15.3309 11.3715 15.3457L10.233 17.916L12 19.8261L13.767 17.916L12.6285 15.3456C12.7074 15.3308 12.7837 15.3091 12.8575 15.282L14.7415 17.166L16.0696 15.8379L14.3385 14.1068C14.5103 13.8038 14.6087 13.4538 14.6087 13.0805C15.7613 13.0805 16.6957 12.1462 16.6957 10.9935H16.6261C17.7787 10.9935 18.7826 10.0593 18.7826 8.90667Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx9("defs", { children: /* @__PURE__ */ jsx9("clipPath", { id: "cfAL_clip0_0_1", children: /* @__PURE__ */ jsx9("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AM.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var FlagAM = (props) => /* @__PURE__ */ jsxs8(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs8("g", { clipPath: "url(#cfAM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx10(
      "path",
      {
        d: "M24 12C24 10.5322 23.736 9.12605 23.2536 7.82611L12 7.30434L0.746391 7.82606C0.264047 9.12605 0 10.5322 0 12C0 13.4678 0.264047 14.874 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.874 24 13.4678 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx10(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746391C2.44191 20.7435 6.84042 24 12 24Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx10(
      "path",
      {
        d: "M0.746391 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0C6.84042 0 2.44191 3.2565 0.746391 7.82611Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx10("defs", { children: /* @__PURE__ */ jsx10("clipPath", { id: "cfAM_clip0_0_1", children: /* @__PURE__ */ jsx10("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ANG.tsx
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var FlagANG = (props) => /* @__PURE__ */ jsxs9(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs9("g", { clipPath: "url(#cfANG_clip0_2_8605)", children: [
    /* @__PURE__ */ jsx11(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx11(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0V0Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx11(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx11("defs", { children: /* @__PURE__ */ jsx11("clipPath", { id: "cfANG_clip0_2_8605", children: /* @__PURE__ */ jsx11("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AO.tsx
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var FlagAO = (props) => /* @__PURE__ */ jsxs10(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs10("g", { clipPath: "url(#cfAO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx12(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12C23.4783 12 12 13.5652 12 13.5652L0 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx12(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs10("g", { children: [
      /* @__PURE__ */ jsx12(
        "path",
        {
          d: "M10.3546 9.54431L11.3719 10.2826L10.9842 11.4783L12.0008 10.7388L13.0181 11.4771L12.629 10.2817L13.6454 9.54234L12.3885 9.54309L11.9993 8.34783L11.6116 9.54352L10.3546 9.54431Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx12(
        "path",
        {
          d: "M15 6.80386C14.0544 6.25791 13.0205 6.00023 12 6.00202V7.56689C12.7543 7.56553 13.5184 7.75584 14.2174 8.15944C16.3351 9.38212 17.0633 12.0998 15.8406 14.2175C14.6179 16.3352 11.9003 17.0634 9.78257 15.8407C9.1656 15.4845 8.66756 15.0008 8.30043 14.4427L6.99374 15.3054C7.49048 16.0605 8.16529 16.7143 8.99999 17.1961C11.8652 18.8504 15.5419 17.8652 17.1961 15C18.8503 12.1348 17.8652 8.45808 15 6.80386Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx12(
        "path",
        {
          d: "M8.54014 10.9565C8.1253 11.7149 8.40383 12.666 9.16217 13.0809L14.2457 15.8587C13.9 16.4906 14.0836 17.2598 14.7155 17.6055L16.0888 18.3567C16.7207 18.7024 17.5134 18.4703 17.8591 17.8383L18.6103 16.4652L8.54014 10.9565Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx12("defs", { children: /* @__PURE__ */ jsx12("clipPath", { id: "cfAO_clip0_0_1", children: /* @__PURE__ */ jsx12("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AR.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var FlagAR = (props) => /* @__PURE__ */ jsxs11(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs11("g", { clipPath: "url(#cfAR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx13(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs11("g", { children: [
      /* @__PURE__ */ jsx13(
        "path",
        {
          d: "M12 0C7.24247 0 3.13172 2.76867 1.19072 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx13(
        "path",
        {
          d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2174H1.19072C3.13172 21.2313 7.24247 24 12 24Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx13(
      "path",
      {
        d: "M15.5866 12L14.1211 12.6894L14.9015 14.1087L13.3101 13.8043L13.1085 15.4119L12 14.2295L10.8915 15.4119L10.6899 13.8043L9.09849 14.1087L9.87886 12.6893L8.41336 12L9.87891 11.3106L9.09849 9.89133L10.6898 10.1957L10.8915 8.58811L12 9.77049L13.1085 8.58811L13.3101 10.1957L14.9016 9.89133L14.1211 11.3107L15.5866 12Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx13("defs", { children: /* @__PURE__ */ jsx13("clipPath", { id: "cfAR_clip0_0_1", children: /* @__PURE__ */ jsx13("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/Artsakh.tsx
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var FlagArtsakh = (props) => /* @__PURE__ */ jsxs12(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs12("g", { clipPath: "url(#cfArtsakh_clip0_20_36)", children: [
    /* @__PURE__ */ jsx14(
      "path",
      {
        d: "M24 12C24 10.5321 23.736 9.12602 23.2536 7.82609L12 7.30432L0.746391 7.82604C0.264 9.12602 0 10.5321 0 12C0 13.4678 0.264 14.8739 0.746391 16.1739L12 16.6956L23.2536 16.1739C23.736 14.8739 24 13.4678 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx14(
      "path",
      {
        d: "M23.3145 16C21.7105 20.537 17.4578 23.8205 12.4131 23.9912C12.2759 23.9954 12.1382 24 12 24C11.8614 24 11.7234 23.9954 11.5859 23.9912C6.54164 23.8201 2.28943 20.5367 0.685547 16H23.3145Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx14(
      "path",
      {
        d: "M12 0C17.2246 0 21.6669 3.33968 23.3145 8H0.685547C2.33313 3.33968 6.77544 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx14(
      "path",
      {
        d: "M22.5 1V5.5H18.5V7.5H15.5V11H12.5V13H15.5V16.5H18.5V19.5H21.5V24H18.5V22.5H15.5V19.5H12.5V16H9.5V13.5H7V10.5H9.5V8H12.5V4.5H15.5V2.5H19.5V1H22.5Z",
        fill: "white"
      }
    )
  ] }),
  /* @__PURE__ */ jsx14("defs", { children: /* @__PURE__ */ jsx14("clipPath", { id: "cfArtsakh_clip0_20_36", children: /* @__PURE__ */ jsx14("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AS.tsx
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
var FlagAS = (props) => /* @__PURE__ */ jsxs13(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs13("g", { clipPath: "url(#cfAS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx15(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs13("g", { children: [
      /* @__PURE__ */ jsx15(
        "path",
        {
          d: "M17.8217 1.50525C16.0974 0.54675 14.1127 0 12 0C5.37309 0 0.00084375 5.37178 0 11.9986L8.34783 7.82611L17.8217 1.50525Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx15(
        "path",
        {
          d: "M0 12.0014C0.00084375 18.6282 5.37309 24 12 24C14.1127 24 16.0974 23.4533 17.8217 22.4947L8.34783 16.1739L0 12.0014Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx15(
      "path",
      {
        d: "M18.8217 2.12728C18.4994 1.9042 18.1654 1.69673 17.8213 1.50544L0 11.9986V12C0 12 0 12.001 0 12.0014L17.8213 22.4945C18.1655 22.3032 18.4994 22.0958 18.8218 21.8727L2.05416 12L18.8217 2.12728Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx15(
      "path",
      {
        d: "M20.8695 11.5525H19.4401C19.8118 11.1062 19.7889 10.4423 19.3701 10.0237C19.8137 9.58022 19.8137 8.86111 19.3701 8.41753L19.3434 8.44434C19.7869 8.00086 19.8137 7.25494 19.3701 6.81145L12.9456 13.236C13.3892 13.6795 14.0957 13.6742 14.5392 13.2307L14.6628 13.1177L17.7391 12.838V14.1611H18.7826V12.7432L20.3478 12.6009L20.8695 11.5525Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx15(
      "path",
      {
        d: "M13.0439 14.6087L12 14.087L13.0439 13.5652H19.8264V14.6087H13.0439Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx15("defs", { children: /* @__PURE__ */ jsx15("clipPath", { id: "cfAS_clip0_0_1", children: /* @__PURE__ */ jsx15("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AT.tsx
import { jsx as jsx16, jsxs as jsxs14 } from "react/jsx-runtime";
var FlagAT = (props) => /* @__PURE__ */ jsxs14(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs14("g", { clipPath: "url(#cfAT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx16(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.874 24 13.4678 24 12C24 10.5322 23.736 9.12614 23.2536 7.82611L12 6.78262L0.746391 7.82611C0.264047 9.12614 0 10.5322 0 12C0 13.4678 0.264047 14.874 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs14("g", { children: [
      /* @__PURE__ */ jsx16(
        "path",
        {
          d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx16(
        "path",
        {
          d: "M12 0C6.84037 0 2.4419 3.2565 0.746338 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx16("defs", { children: /* @__PURE__ */ jsx16("clipPath", { id: "cfAT_clip0_0_1", children: /* @__PURE__ */ jsx16("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AU.tsx
import { jsx as jsx17, jsxs as jsxs15 } from "react/jsx-runtime";
var FlagAU = (props) => /* @__PURE__ */ jsxs15(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs15("g", { clipPath: "url(#cfAU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx17(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0028 12 0.00135937 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs15("g", { children: [
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M12 0C11.9993 0 11.9986 4.6875e-05 11.998 4.6875e-05L12 0Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M11.9679 12H12C12 11.9891 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M12 6.2609C12 4.14942 12 2.76581 12 4.57764e-05H11.998C5.3715 0.0011239 0 5.37323 0 12H6.26086V8.47443L9.78642 12H11.9679C11.9787 11.9893 11.9894 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78651L8.47444 6.2609H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs15("g", { children: [
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M6.07101 1.5652C4.19419 2.6339 2.6339 4.19419 1.5652 6.07101V12H4.69565V4.6957V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M12 10.5243L7.73658 6.26095H6.26091C6.26091 6.26091 6.26091 6.26095 6.26091 6.26095L12 12H12C12 12 12 10.9826 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs15("g", { children: [
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M7.23726 14.087L7.89585 15.4641L9.38306 15.1203L8.71706 16.4939L9.91302 17.4423L8.42395 17.7779L8.42812 19.3043L7.23726 18.3494L6.04645 19.3043L6.05062 17.7779L4.56149 17.4423L5.75751 16.4939L5.09141 15.1203L6.57871 15.4641L7.23726 14.087Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M17.9664 16.6957L18.2957 17.3842L19.0394 17.2123L18.7063 17.8991L19.3043 18.3733L18.5598 18.5411L18.5618 19.3043L17.9664 18.8268L17.371 19.3043L17.3731 18.5411L16.6286 18.3733L17.2266 17.8991L16.8936 17.2123L17.6371 17.3842L17.9664 16.6957Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M14.9031 9.39131L15.2324 10.0799L15.976 9.90797L15.643 10.5947L16.241 11.069L15.4965 11.2368L15.4985 12L14.9031 11.5225L14.3077 12L14.3097 11.2368L13.5652 11.069L14.1632 10.5947L13.8302 9.90797L14.5738 10.0799L14.9031 9.39131Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M17.9664 5.21738L18.2957 5.90597L19.0394 5.73408L18.7064 6.42085L19.3043 6.89503L18.5598 7.06289L18.5618 7.82611L17.9664 7.3486L17.371 7.82611L17.3731 7.06289L16.6286 6.89503L17.2265 6.42085L16.8936 5.73408L17.6371 5.90597L17.9664 5.21738Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M20.6422 8.34783L20.9715 9.03642L21.7151 8.86449L21.3821 9.55125L21.9801 10.0255L21.2355 10.1933L21.2376 10.9565L20.6422 10.479L20.0468 10.9565L20.0489 10.1933L19.3043 10.0255L19.9023 9.55125L19.5693 8.86449L20.3129 9.03642L20.6422 8.34783Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx17(
        "path",
        {
          d: "M18.7289 12L18.9879 12.7972H19.8261L19.148 13.2899L19.407 14.087L18.7289 13.5943L18.0508 14.087L18.3098 13.2899L17.6317 12.7972H18.4699L18.7289 12Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx17("defs", { children: /* @__PURE__ */ jsx17("clipPath", { id: "cfAU_clip0_0_1", children: /* @__PURE__ */ jsx17("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AW.tsx
import { jsx as jsx18, jsxs as jsxs16 } from "react/jsx-runtime";
var FlagAW = (props) => /* @__PURE__ */ jsxs16(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs16("g", { clipPath: "url(#cfAW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx18(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs16("g", { children: [
      /* @__PURE__ */ jsx18(
        "path",
        {
          d: "M12 24C15.6371 24 18.896 22.3817 21.0966 19.8261H2.90339C5.10403 22.3817 8.36292 24 12 24Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx18(
        "path",
        {
          d: "M24 12C24 5.37262 18.6274 0 12 0C5.37262 0 0 5.37262 0 12C0 13.0831 0.144375 14.1323 0.413391 15.1305H23.5867C23.8556 14.1323 24 13.0831 24 12Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx18(
        "path",
        {
          d: "M0.953857 16.6957C1.18519 17.2392 1.45561 17.762 1.76128 18.2609H22.2387C22.5444 17.7619 22.8148 17.2392 23.0462 16.6957H0.953857Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx18(
      "path",
      {
        d: "M5.49923 7.56858L3.15473 6.53386L5.49923 5.49914L6.5339 3.15469L7.56858 5.49914L9.91303 6.53386L7.56858 7.56858L6.5339 9.91303L5.49923 7.56858Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx18(
      "path",
      {
        d: "M6.53391 4.44689L7.17286 5.89481L8.62083 6.53386L7.17286 7.17286L6.53391 8.62078L5.89486 7.17286L4.44694 6.53386L5.89486 5.89481L6.53391 4.44689Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx18("defs", { children: /* @__PURE__ */ jsx18("clipPath", { id: "cfAW_clip0_0_1", children: /* @__PURE__ */ jsx18("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AX.tsx
import { jsx as jsx19, jsxs as jsxs17 } from "react/jsx-runtime";
var FlagAX = (props) => /* @__PURE__ */ jsxs17(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs17("g", { clipPath: "url(#cfAX_clip0_0_1)", children: [
    /* @__PURE__ */ jsx19(
      "path",
      {
        d: "M23.5866 15.1305C23.8556 14.1323 24 13.0831 24 12C24 10.9169 23.8556 9.8677 23.5866 8.86955L10.9565 0.0456562C8.61178 0.247687 6.46008 1.12378 4.69566 2.47945L0.413391 8.86955C0.144375 9.8677 0 10.9169 0 12C0 13.0768 0.142641 14.1202 0.408656 15.1131L4.69561 21.5205C6.46003 22.8762 8.61173 23.7524 10.9565 23.9543L23.5866 15.1305Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs17("g", { children: [
      /* @__PURE__ */ jsx19(
        "path",
        {
          d: "M0.413391 15.1305C1.1077 17.7067 2.63794 19.9395 4.69566 21.5205V15.1305H0.413391Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx19(
        "path",
        {
          d: "M10.9565 23.9543C11.3005 23.984 11.6483 24 12 24C17.5443 24 22.2096 20.2396 23.5866 15.1305H10.9565V23.9543Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx19(
        "path",
        {
          d: "M23.5866 8.86955C22.2096 3.76036 17.5443 0 12 0C11.6483 0 11.3005 0.0160312 10.9565 0.0456562V8.86955H23.5866Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx19(
        "path",
        {
          d: "M4.69566 2.47945C2.63794 4.06055 1.1077 6.29334 0.413391 8.86955H4.69566V2.47945Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx19(
      "path",
      {
        d: "M23.8984 10.4348H9.39136H9.39131V0.285234C8.28234 0.531141 7.23098 0.929719 6.26086 1.45908V10.4347H0.101578C0.0347813 10.9471 0 11.4695 0 12C0 12.5305 0.0347813 13.0529 0.101578 13.5652H6.26081H6.26086V22.5409C7.23098 23.0702 8.28234 23.4689 9.39131 23.7147V13.5653V13.5653H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9471 23.8984 10.4348Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx19("defs", { children: /* @__PURE__ */ jsx19("clipPath", { id: "cfAX_clip0_0_1", children: /* @__PURE__ */ jsx19("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/AZ.tsx
import { jsx as jsx20, jsxs as jsxs18 } from "react/jsx-runtime";
var FlagAZ = (props) => /* @__PURE__ */ jsxs18(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs18("g", { clipPath: "url(#cfAZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx20(
      "path",
      {
        d: "M24 12C24 10.5322 23.736 9.12605 23.2536 7.82611L12 7.30434L0.746391 7.82606C0.264047 9.12605 0 10.5322 0 12C0 13.4678 0.264047 14.874 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.874 24 13.4678 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx20(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx20(
      "path",
      {
        d: "M0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs18("g", { children: [
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M12.2609 15.3913C10.3879 15.3913 8.86954 13.8729 8.86954 12C8.86954 10.1271 10.3879 8.60869 12.2609 8.60869C12.8448 8.60869 13.3943 8.75635 13.8741 9.01632C13.1214 8.28033 12.0922 7.82607 10.9565 7.82607C8.6513 7.82607 6.78262 9.69478 6.78262 12C6.78262 14.3051 8.65134 16.1738 10.9565 16.1738C12.0923 16.1738 13.1215 15.7196 13.8741 14.9836C13.3943 15.2437 12.8448 15.3913 12.2609 15.3913Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx20(
        "path",
        {
          d: "M14.8696 9.65218L15.3188 10.9155L16.5297 10.3398L15.9541 11.5508L17.2174 12L15.9541 12.4493L16.5297 13.6602L15.3188 13.0845L14.8696 14.3478L14.4204 13.0845L13.2094 13.6602L13.7851 12.4493L12.5217 12L13.7851 11.5508L13.2094 10.3398L14.4204 10.9155L14.8696 9.65218Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx20("defs", { children: /* @__PURE__ */ jsx20("clipPath", { id: "cfAZ_clip0_0_1", children: /* @__PURE__ */ jsx20("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BA.tsx
import { jsx as jsx21, jsxs as jsxs19 } from "react/jsx-runtime";
var FlagBA = (props) => /* @__PURE__ */ jsxs19(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs19("g", { clipPath: "url(#cfBA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx21(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx21(
      "path",
      {
        d: "M12 0C7.74517 0 4.00783 2.21452 1.87708 5.5537L18.1502 22.3054C21.6537 20.21 24 16.3792 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs19("g", { children: [
      /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M24 12C24 8.88647 22.8142 6.05015 20.8696 3.91743V20.0826C22.8142 17.9498 24 15.1135 24 12Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M14.9901 22.5878L16.2471 22.587L16.6348 21.3913L17.024 22.5865L17.6544 22.5861C18.041 22.3792 18.4149 22.1518 18.7747 21.9051L2.0948 5.2252C1.81992 5.62626 1.56923 6.04504 1.34381 6.4791L1.49203 6.93435L2.74898 6.93356L1.73255 7.67301L2.1217 8.86827L1.10433 8.13009L0.497531 8.57146C0.174047 9.65784 0 10.8086 0 12C0 18.6274 5.37262 24 12 24C13.4012 24 14.7461 23.7594 15.9961 23.3179L14.9901 22.5878ZM2.67652 11.4783L3.06417 10.2826L2.04689 9.54431L3.30384 9.54351L3.69155 8.34777L4.0807 9.54304L5.33766 9.54224L4.32117 10.2817L4.71033 11.477L3.69295 10.7388L2.67652 11.4783ZM5.26519 14.087L5.65284 12.8913L4.63556 12.153L5.89252 12.1522L6.28022 10.9565L6.66937 12.1517L7.92633 12.1509L6.90989 12.8904L7.29905 14.0856L6.28167 13.3475L5.26519 14.087ZM7.85381 16.6956L8.24147 15.5L7.22419 14.7617L8.48114 14.7609L8.86884 13.5651L9.258 14.7604L10.515 14.7596L9.49851 15.4991L9.88767 16.6943L8.8703 15.9561L7.85381 16.6956ZM11.4589 18.5649L10.4424 19.3043L10.8301 18.1086L9.81281 17.3704L11.0698 17.3696L11.4575 16.1738L11.8467 17.3691L13.1036 17.3683L12.0872 18.1078L12.4763 19.303L11.4589 18.5649ZM13.0311 21.913L13.4188 20.7173L12.4015 19.9791L13.6584 19.9783L14.0461 18.7825L14.4353 19.9778L15.6922 19.977L14.6758 20.7164L15.065 21.9117L14.0476 21.1734L13.0311 21.913Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx21("defs", { children: /* @__PURE__ */ jsx21("clipPath", { id: "cfBA_clip0_0_1", children: /* @__PURE__ */ jsx21("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BB.tsx
import { jsx as jsx22, jsxs as jsxs20 } from "react/jsx-runtime";
var FlagBB = (props) => /* @__PURE__ */ jsxs20(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs20("g", { clipPath: "url(#cfBB_clip0_0_1)", children: [
    /* @__PURE__ */ jsx22(
      "path",
      {
        d: "M17.2174 1.19072C15.6397 0.427875 13.8698 0 12 0C10.1302 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093C8.36025 23.5721 10.1302 24 12 24C13.8698 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs20("g", { children: [
      /* @__PURE__ */ jsx22(
        "path",
        {
          d: "M6.78263 1.19114C2.76872 3.13205 0 7.24247 0 12C0 16.7575 2.76872 20.868 6.78263 22.8089V1.19114Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx22(
        "path",
        {
          d: "M17.2174 1.19114V22.8089C21.2313 20.868 24 16.7575 24 12C24 7.24251 21.2313 3.13205 17.2174 1.19114Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx22(
      "path",
      {
        d: "M15.6522 7.30434L16.3522 7.65431L15.6522 7.30434L14.9521 6.95437C14.91 7.03879 13.9959 8.89847 13.8466 11.7391H12.7826V7.30434L12 6.26086L11.2174 7.30434V11.7391H10.1534C10.004 8.89847 9.09005 7.03879 9.04781 6.95437L7.64784 7.65436C7.6575 7.67358 8.60873 9.60975 8.60873 12.5218V13.3044H11.2174V17.7392H12.7826V13.3044H15.3913V12.5217C15.3913 11.0162 15.6518 9.77475 15.8704 8.9984C16.1086 8.15184 16.3507 7.65745 16.353 7.65253L15.6522 7.30434Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx22("defs", { children: /* @__PURE__ */ jsx22("clipPath", { id: "cfBB_clip0_0_1", children: /* @__PURE__ */ jsx22("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BD.tsx
import { jsx as jsx23, jsxs as jsxs21 } from "react/jsx-runtime";
var FlagBD = (props) => /* @__PURE__ */ jsxs21(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs21("g", { clipPath: "url(#cfBD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx23(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx23(
      "path",
      {
        d: "M9.39132 17.2174C12.2728 17.2174 14.6087 14.8815 14.6087 12C14.6087 9.11852 12.2728 6.78262 9.39132 6.78262C6.50985 6.78262 4.17395 9.11852 4.17395 12C4.17395 14.8815 6.50985 17.2174 9.39132 17.2174Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx23("defs", { children: /* @__PURE__ */ jsx23("clipPath", { id: "cfBD_clip0_0_1", children: /* @__PURE__ */ jsx23("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BE.tsx
import { jsx as jsx24, jsxs as jsxs22 } from "react/jsx-runtime";
var FlagBE = (props) => /* @__PURE__ */ jsxs22(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs22("g", { clipPath: "url(#cfBE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx24(
      "path",
      {
        d: "M16.1739 0.746391C14.8739 0.264047 13.4678 0 12 0C10.5322 0 9.12609 0.264047 7.82611 0.746391L6.78262 12L7.82611 23.2536C9.12609 23.736 10.5322 24 12 24C13.4678 24 14.8739 23.736 16.1739 23.2536L17.2174 12L16.1739 0.746391Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx24(
      "path",
      {
        d: "M24 12C24 6.84047 20.7435 2.44191 16.1739 0.746437V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx24(
      "path",
      {
        d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2537V0.746437C3.2565 2.44191 0 6.84047 0 12Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx24("defs", { children: /* @__PURE__ */ jsx24("clipPath", { id: "cfBE_clip0_0_1", children: /* @__PURE__ */ jsx24("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BF.tsx
import { jsx as jsx25, jsxs as jsxs23 } from "react/jsx-runtime";
var FlagBF = (props) => /* @__PURE__ */ jsxs23(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs23("g", { clipPath: "url(#cfBF_clip0_0_1)", children: [
    /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12C23.4783 12 12 13.5652 12 13.5652L0 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx25(
      "path",
      {
        d: "M12 7.82611L12.9064 10.616H15.8401L13.4668 12.3404L14.3732 15.1305L12 13.4062L9.62663 15.1305L10.5332 12.3404L8.15982 10.616H11.0934L12 7.82611Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx25("defs", { children: /* @__PURE__ */ jsx25("clipPath", { id: "cfBF_clip0_0_1", children: /* @__PURE__ */ jsx25("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BG.tsx
import { jsx as jsx26, jsxs as jsxs24 } from "react/jsx-runtime";
var FlagBG = (props) => /* @__PURE__ */ jsxs24(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs24("g", { clipPath: "url(#cfBG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx26(
      "path",
      {
        d: "M24 12C24 10.5322 23.736 9.12605 23.2536 7.82612L12 7.30435L0.746391 7.82607C0.264047 9.12605 0 10.5322 0 12C0 13.4679 0.264047 14.874 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.874 24 13.4679 24 12Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx26(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx26(
      "path",
      {
        d: "M0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx26("defs", { children: /* @__PURE__ */ jsx26("clipPath", { id: "cfBG_clip0_0_1", children: /* @__PURE__ */ jsx26("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BH.tsx
import { jsx as jsx27, jsxs as jsxs25 } from "react/jsx-runtime";
var FlagBH = (props) => /* @__PURE__ */ jsxs25(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs25("g", { clipPath: "url(#cfBH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx27(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx27(
      "path",
      {
        d: "M12 0C9.59174 0 7.34962 0.709922 5.4703 1.93116L8.55651 3.9997L4.69565 5.99967L8.55651 7.99964L4.69565 9.99961L8.55651 11.9993L4.69565 13.9993L8.55651 15.9992L4.69565 17.9993L8.55651 19.9993L5.46993 22.0685C7.34934 23.29 9.5916 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0V0Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx27("defs", { children: /* @__PURE__ */ jsx27("clipPath", { id: "cfBH_clip0_0_1", children: /* @__PURE__ */ jsx27("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BI.tsx
import { jsx as jsx28, jsxs as jsxs26 } from "react/jsx-runtime";
var FlagBI = (props) => /* @__PURE__ */ jsxs26(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs26("g", { clipPath: "url(#cfBI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx28(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs26("g", { children: [
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M19.3067 2.47979C15.0105 -0.82612 8.98949 -0.82612 4.6933 2.47974L12 9.78643L19.3067 2.47979Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M11.9999 14.2136L4.6933 21.5202C8.98949 24.8261 15.0105 24.8261 19.3067 21.5202L12 14.2135L11.9999 14.2136Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs26("g", { children: [
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M9.78644 12L2.4798 4.69331C-0.82611 8.9895 -0.82611 15.0105 2.4798 19.3067L9.78644 12Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M14.2136 12L21.5202 19.3067C24.8261 15.0105 24.8261 8.9895 21.5202 4.69331L14.2136 12Z",
          fill: "#6DA544"
        }
      )
    ] }),
    /* @__PURE__ */ jsx28(
      "path",
      {
        d: "M12 17.2174C14.8815 17.2174 17.2174 14.8815 17.2174 12C17.2174 9.11852 14.8815 6.78262 12 6.78262C9.11855 6.78262 6.78265 9.11852 6.78265 12C6.78265 14.8815 9.11855 17.2174 12 17.2174Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs26("g", { children: [
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M12 8.34782L12.4518 9.13045H13.3555L12.9037 9.91303L13.3555 10.6957H12.4518L12 11.4783L11.5482 10.6957H10.6445L11.0964 9.91303L10.6445 9.13045H11.5482L12 8.34782Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M9.70337 12L10.1552 12.7826H11.0589L10.607 13.5652L11.0589 14.3478H10.1552L9.70337 15.1305L9.2515 14.3478H8.34784L8.79967 13.5652L8.34784 12.7826H9.2515L9.70337 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx28(
        "path",
        {
          d: "M14.2967 12L14.7485 12.7826H15.6522L15.2003 13.5652L15.6522 14.3478H14.7485L14.2967 15.1305L13.8448 14.3478H12.9412L13.393 13.5652L12.9412 12.7826H13.8448L14.2967 12Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx28("defs", { children: /* @__PURE__ */ jsx28("clipPath", { id: "cfBI_clip0_0_1", children: /* @__PURE__ */ jsx28("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BJ.tsx
import { jsx as jsx29, jsxs as jsxs27 } from "react/jsx-runtime";
var FlagBJ = (props) => /* @__PURE__ */ jsxs27(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs27("g", { clipPath: "url(#cfBJ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx29(
      "path",
      {
        d: "M7.82611 10.9565L8.8696 23.5866C9.8677 23.8556 10.9169 24 12 24C18.6274 24 24 18.6274 24 12L7.82611 10.9565Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx29(
      "path",
      {
        d: "M7.82611 12L8.86955 0.413391C9.8677 0.144375 10.9169 0 12 0C18.6274 0 24 5.37262 24 12H7.82611Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx29(
      "path",
      {
        d: "M0 12C0 17.5443 3.76036 22.2096 8.86955 23.5866V0.413391C3.76036 1.79039 0 6.45567 0 12Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx29("defs", { children: /* @__PURE__ */ jsx29("clipPath", { id: "cfBJ_clip0_0_1", children: /* @__PURE__ */ jsx29("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BL.tsx
import { jsx as jsx30, jsxs as jsxs28 } from "react/jsx-runtime";
var FlagBL = (props) => /* @__PURE__ */ jsxs28(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs28("g", { clipPath: "url(#cfBL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs28("g", { children: [
      /* @__PURE__ */ jsx30(
        "path",
        {
          d: "M21.4261 9.65218H17.4782C17.4782 8.93182 16.8943 8.34784 16.1739 8.34784L15.1304 10.4348C15.1304 10.4348 16.2435 13.5652 16.2087 13.5652H17.513C18.2334 13.5652 18.8173 12.9813 18.8173 12.2609C19.5378 12.2609 20.1217 11.677 20.1217 10.9566H20.0782C20.7986 10.9565 21.4261 10.3726 21.4261 9.65218Z",
          fill: "#ACABB1"
        }
      ),
      /* @__PURE__ */ jsx30(
        "path",
        {
          d: "M2.57391 9.65218H6.52173C6.52173 8.93182 7.10571 8.34784 7.82607 8.34784L8.86956 10.4348C8.86956 10.4348 7.75649 13.5652 7.7913 13.5652H6.48697C5.76656 13.5652 5.18263 12.9813 5.18263 12.2609C4.46223 12.2609 3.8783 11.677 3.8783 10.9566H3.92174C3.20133 10.9565 2.57391 10.3726 2.57391 9.65218Z",
          fill: "#ACABB1"
        }
      )
    ] }),
    /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M16.1739 16.6956V17.2173H7.82607V16.6956H5.73912V18.7826H7.82607V19.3043H16.1739V18.7826H18.2608V16.6956H16.1739Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M7.82608 8.34781V13.5651C7.82608 16.7601 12 17.7391 12 17.7391C12 17.7391 16.1739 16.7601 16.1739 13.5651V8.34781L12 7.82608L7.82608 8.34781Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M7.82613 10.4349H16.1739V13.5654H7.82613V10.4349Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx30(
      "path",
      {
        d: "M14.0869 5.73911V6.52173L13.5652 6.78261L13.0435 6.26084V4.69562H10.9565V6.26084L10.4348 6.78261L9.91303 6.52173V5.73911H7.82608V8.34779H16.1739V5.73911H14.0869Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx30("defs", { children: /* @__PURE__ */ jsx30("clipPath", { id: "cfBL_clip0_0_1", children: /* @__PURE__ */ jsx30("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BM.tsx
import { jsx as jsx31, jsxs as jsxs29 } from "react/jsx-runtime";
var FlagBM = (props) => /* @__PURE__ */ jsxs29(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs29("g", { clipPath: "url(#cfBM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs29("g", { children: [
      /* @__PURE__ */ jsx31("path", { d: "M12 5.73914V9.78647L7.82611 5.73914H12Z", fill: "#0052B4" }),
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M6.26085 12H9.78637L6.26085 7.30434L5.73914 10.9565L6.26085 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs29("g", { children: [
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs29("g", { children: [
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M6.07103 1.5652C4.1942 2.6339 2.63387 4.19419 1.56522 6.07101V12.5217H4.69567V4.69575V4.69565H12.5217C12.5217 3.70833 12.5217 2.76773 12.5217 1.5652H6.07103Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx31(
        "path",
        {
          d: "M12.4771 11.0013L7.73649 6.26086H6.26086V6.26095L12.4769 12.477H12.477C12.4771 12.477 12.4771 11.4595 12.4771 11.0013Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M13.5652 6.26086V11.9999C13.5652 14.7956 20.8696 14.7956 20.8696 11.9999V6.26086H13.5652Z",
        fill: "#F3F3F3"
      }
    ),
    /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M13.5652 12V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8696 14.7956 20.8696 12V12H13.5652Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M17.2174 9.70435L15.5217 10.4348V12L17.2174 13.0435L18.913 12V10.4348L17.2174 9.70435Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx31(
      "path",
      {
        d: "M15.5217 8.86955H18.913V10.4348H15.5217V8.86955Z",
        fill: "#338AF3"
      }
    )
  ] }),
  /* @__PURE__ */ jsx31("defs", { children: /* @__PURE__ */ jsx31("clipPath", { id: "cfBM_clip0_0_1", children: /* @__PURE__ */ jsx31("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BN.tsx
import { jsx as jsx32, jsxs as jsxs30 } from "react/jsx-runtime";
var FlagBN = (props) => /* @__PURE__ */ jsxs30(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs30("g", { clipPath: "url(#cfBN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx32(
      "path",
      {
        d: "M12.0001 23.9983C18.6265 23.9983 23.9983 18.6265 23.9983 12.0001C23.9983 5.37363 18.6265 0.00183105 12.0001 0.00183105C5.37366 0.00183105 0.00186157 5.37363 0.00186157 12.0001C0.00186157 18.6265 5.37366 23.9983 12.0001 23.9983Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx32(
      "path",
      {
        d: "M1.60925 6.00089C1.06769 6.93878 0.668213 7.91933 0.402191 8.91812L20.4678 20.5031C21.1998 19.7733 21.8493 18.937 22.3907 17.9991L1.60925 6.00089Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs30("g", { children: [
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M17.9991 1.6092C13.1983 -1.16253 7.27879 -0.238742 3.53214 3.4969L23.5978 15.0818C24.9597 9.9694 22.7999 4.38098 17.9991 1.6092Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M6.00091 22.3908C10.8017 25.1625 16.7213 24.2387 20.4679 20.5031L0.402184 8.91811C-0.959611 14.0306 1.20006 19.6191 6.00091 22.3908Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs30("g", { children: [
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M15.3908 10.6958C15.3908 12.5685 13.8727 14.0867 12 14.0867C10.1273 14.0867 8.60919 12.5685 8.60919 10.6958C8.60919 10.112 8.7568 9.56252 9.01678 9.08284C8.28097 9.83534 7.82669 10.8644 7.82669 12C7.82669 14.3048 9.69512 16.1733 12 16.1733C14.3049 16.1733 16.1733 14.3049 16.1733 12C16.1733 10.8644 15.7191 9.83534 14.9832 9.08284C15.2432 9.56262 15.3908 10.112 15.3908 10.6958Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M11.2176 6.78343H12.7826V14.6084H11.2176V6.78343Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M14.6083 8.34839H9.39172C9.39172 8.70854 9.70546 9.00048 10.0656 9.00048H10.0438C10.0438 9.36063 10.3358 9.65257 10.6959 9.65257C10.6959 10.0127 10.9879 10.3047 11.348 10.3047H12.6521C13.0123 10.3047 13.3042 10.0127 13.3042 9.65257C13.6644 9.65257 13.9563 9.36063 13.9563 9.00048H13.9346C14.2946 9.00043 14.6083 8.70858 14.6083 8.34839Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx32("path", { d: "M8.34845 15.13H15.6517V16.695H8.34845V15.13Z", fill: "#D80027" }),
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M13.5651 15.6517H16.695V17.2167H13.5651V15.6517Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx32(
        "path",
        {
          d: "M7.30515 15.6517H10.4351V17.2167H7.30515V15.6517Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx32("defs", { children: /* @__PURE__ */ jsx32("clipPath", { id: "cfBN_clip0_0_1", children: /* @__PURE__ */ jsx32("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BO.tsx
import { jsx as jsx33, jsxs as jsxs31 } from "react/jsx-runtime";
var FlagBO = (props) => /* @__PURE__ */ jsxs31(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs31("g", { clipPath: "url(#cfBO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx33(
      "path",
      {
        d: "M24 12C24 10.5322 23.736 9.12605 23.2536 7.82611L12 7.30434L0.746391 7.82606C0.264 9.12605 0 10.5322 0 12C0 13.4678 0.264 14.874 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.874 24 13.4678 24 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx33(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx33(
      "path",
      {
        d: "M0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx33("defs", { children: /* @__PURE__ */ jsx33("clipPath", { id: "cfBO_clip0_0_1", children: /* @__PURE__ */ jsx33("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BR.tsx
import { jsx as jsx34, jsxs as jsxs32 } from "react/jsx-runtime";
var FlagBR = (props) => /* @__PURE__ */ jsxs32(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs32("g", { clipPath: "url(#cfBR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx34(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx34(
      "path",
      {
        d: "M12 4.69566L21.913 12L12 19.3043L2.08697 12L12 4.69566Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx34(
      "path",
      {
        d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs32("g", { children: [
      /* @__PURE__ */ jsx34(
        "path",
        {
          d: "M9.91303 11.7391C9.18722 11.7391 8.48677 11.8495 7.82742 12.0544C7.85663 14.3344 9.71306 16.1739 12 16.1739C13.4141 16.1739 14.6631 15.47 15.4181 14.3942C14.1263 12.7771 12.1388 11.7391 9.91303 11.7391Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx34(
        "path",
        {
          d: "M16.0965 12.7997C16.1468 12.5408 16.1739 12.2736 16.1739 12C16.1739 9.69478 14.3052 7.82611 12 7.82611C10.28 7.82611 8.80345 8.86678 8.16445 10.3526C8.7293 10.2356 9.31411 10.1739 9.91312 10.1739C12.3374 10.1739 14.5305 11.1816 16.0965 12.7997Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx34("defs", { children: /* @__PURE__ */ jsx34("clipPath", { id: "cfBR_clip0_0_1", children: /* @__PURE__ */ jsx34("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BS.tsx
import { jsx as jsx35, jsxs as jsxs33 } from "react/jsx-runtime";
var FlagBS = (props) => /* @__PURE__ */ jsxs33(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs33("g", { clipPath: "url(#cfBS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx35(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs33("g", { children: [
      /* @__PURE__ */ jsx35(
        "path",
        {
          d: "M7.30434 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0C8.68622 0 5.68654 1.34339 3.51511 3.51511L7.30434 7.82611Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx35(
        "path",
        {
          d: "M7.30434 16.1739H23.2536C21.5581 20.7435 17.1595 24 12 24C8.68622 24 5.68654 22.6566 3.51511 20.4849L7.30434 16.1739Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx35(
      "path",
      {
        d: "M3.51468 3.51469C-1.1716 8.20097 -1.1716 15.799 3.51468 20.4853C5.45123 18.5488 7.31371 16.6863 12 12L3.51468 3.51469Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx35("defs", { children: /* @__PURE__ */ jsx35("clipPath", { id: "cfBS_clip0_0_1", children: /* @__PURE__ */ jsx35("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BT.tsx
import { jsx as jsx36, jsxs as jsxs34 } from "react/jsx-runtime";
var FlagBT = (props) => /* @__PURE__ */ jsxs34(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs34("g", { clipPath: "url(#cfBT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M20.4852 20.4853C15.7989 25.1716 8.20098 25.1716 3.51465 20.4853C-1.17163 15.799 -1.17163 8.20102 3.51465 3.5147C3.51634 3.5147 14.2986 0.328742 18.9849 5.01502C23.6713 9.7013 20.4885 20.482 20.4852 20.4853Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M20.4853 20.4853C25.1716 15.799 25.1716 8.20101 20.4853 3.51468C15.799 -1.17165 8.20104 -1.1716 3.51471 3.51468L20.4853 20.4853Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M15.1367 15.1379C14.6438 15.3315 14.071 15.3109 13.5741 15.0321C12.6961 14.5393 12.3827 13.424 12.8756 12.5459L12.726 12.462C14.2293 12.0995 15.3415 10.7349 15.322 9.12469C15.3099 8.12358 14.8626 7.22794 14.1629 6.6151L13.0673 7.73757C13.4815 8.06766 13.75 8.574 13.7569 9.14368C13.7691 10.1506 12.9599 10.9796 11.9531 10.9918L11.9552 11.1632C10.8896 10.0426 9.15184 9.76168 7.76711 10.5837C6.9062 11.0948 6.35429 11.9299 6.17331 12.8423L7.69318 13.23C7.77198 12.7062 8.07615 12.2205 8.56604 11.9296C9.43187 11.4157 10.5544 11.702 11.0684 12.5678L11.216 12.4802C10.7783 13.9633 11.4038 15.6087 12.8081 16.397C13.6812 16.887 14.6804 16.9474 15.5611 16.6479L15.1367 15.1379ZM11.9657 12.035L11.9656 12.0351L11.9657 12.035Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx36(
      "path",
      {
        d: "M16.4431 8.12724L15.595 11.154L13.0501 10.6841L11.412 7.30744L6.98669 8.8778L6.6377 7.89436L5.47989 7.75172L6.35228 10.2103L9.3977 9.4314L10.2632 11.8703L8.1579 14.9772L11.7306 18.0243L11.0534 18.8183L11.5088 19.8924L13.2018 17.9075L11.0046 15.6595L12.684 13.6906L16.4273 13.9604L17.2799 9.34271L18.3061 9.53222L19.0087 8.60082L16.4431 8.12724ZM11.9991 12.0817V12.0818V12.0817Z",
        fill: "#FFEACF"
      }
    )
  ] }),
  /* @__PURE__ */ jsx36("defs", { children: /* @__PURE__ */ jsx36("clipPath", { id: "cfBT_clip0_0_1", children: /* @__PURE__ */ jsx36("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BW.tsx
import { jsx as jsx37, jsxs as jsxs35 } from "react/jsx-runtime";
var FlagBW = (props) => /* @__PURE__ */ jsxs35(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs35("g", { clipPath: "url(#cfBW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx37(
      "path",
      {
        d: "M0.566673 8.34778C0.199079 9.49946 8.16988e-07 10.7263 4.7692e-05 12C8.16988e-07 13.2735 0.199079 14.5004 0.56672 15.6521L12 16.1739L23.4333 15.6521C23.8009 14.5004 24 13.2735 24 12C24 10.7264 23.8009 9.49946 23.4333 8.34778L12 7.82607L0.566673 8.34778Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx37(
      "path",
      {
        d: "M24 12C24 11.2881 23.9377 10.5908 23.8187 9.91298H0.181313C0.0623437 10.5908 0 11.288 0 11.9999C0 12.7119 0.0623906 13.4091 0.181313 14.0869H23.8187C23.9376 13.4091 24 12.7118 24 12Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs35("g", { children: [
      /* @__PURE__ */ jsx37(
        "path",
        {
          d: "M12 24C17.3539 24 21.8881 20.4936 23.4333 15.6521H0.566719C2.11195 20.4935 6.64622 23.9999 12 24Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx37(
        "path",
        {
          d: "M12 4.57782e-05C6.64622 4.57782e-05 2.112 3.50644 0.566671 8.34778L23.4332 8.34783C21.8881 3.50644 17.3538 -4.79718e-05 12 4.57782e-05Z",
          fill: "#338AF3"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx37("defs", { children: /* @__PURE__ */ jsx37("clipPath", { id: "cfBW_clip0_0_1", children: /* @__PURE__ */ jsx37("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BY.tsx
import { jsx as jsx38, jsxs as jsxs36 } from "react/jsx-runtime";
var FlagBY = (props) => /* @__PURE__ */ jsxs36(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs36("g", { clipPath: "url(#cfBY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx38(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FCFCFC"
      }
    ),
    /* @__PURE__ */ jsxs36("g", { children: [
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M4.95652 11.1304L3.65218 8.76403L4.95652 6.43477L6.26086 8.76403L4.95652 11.1304Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M2.34783 11.1304L1.04349 8.76403L2.34783 6.43477L3.65217 8.76403L2.34783 11.1304Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M4.95652 17.5652L3.65218 15.1988L4.95652 12.8696L6.26086 15.1988L4.95652 17.5652Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M2.34783 17.5652L1.04349 15.1988L2.34783 12.8696L3.65217 15.1988L2.34783 17.5652Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M6.26086 2.32931L5.89158 1.66988C5.22924 2.06231 4.608 2.51667 4.03561 3.02499L4.95652 4.69566L6.26086 2.32931Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx38(
        "path",
        {
          d: "M4.95652 19.3044L4.02586 20.9664C4.59774 21.4753 5.21855 21.9304 5.88061 22.3236L6.26091 21.6337L4.95652 19.3044Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx38(
      "path",
      {
        d: "M7.30434 15.1304V23.0459C8.74664 23.6598 10.3335 24 12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739L7.30434 15.1304Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx38(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.8739 24 13.4678 24 12C24 5.37263 18.6274 0 12 0C10.3336 0 8.74664 0.339984 7.30434 0.953813V16.1739H23.2536V16.1739Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx38("defs", { children: /* @__PURE__ */ jsx38("clipPath", { id: "cfBY_clip0_0_1", children: /* @__PURE__ */ jsx38("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/BZ.tsx
import { jsx as jsx39, jsxs as jsxs37 } from "react/jsx-runtime";
var FlagBZ = (props) => /* @__PURE__ */ jsxs37(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs37("g", { clipPath: "url(#cfBZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M24 12C24 7.88151 21.9249 4.24809 18.7635 2.08697L12 1.5652L5.23655 2.08692C2.07516 4.24809 0 7.88156 0 12C0 16.1186 2.07516 19.7519 5.23655 21.913L12 22.4348L18.7635 21.9131C21.9249 19.7519 24 16.1186 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs37("g", { children: [
      /* @__PURE__ */ jsx39(
        "path",
        {
          d: "M18.7634 2.08697C16.8376 0.770437 14.5089 0 12 0C9.4911 0 7.1624 0.770437 5.23654 2.08697H18.7634Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx39(
        "path",
        {
          d: "M5.23654 21.913C7.16235 23.2296 9.4911 24 12 24C14.5089 24 16.8376 23.2296 18.7634 21.913H5.23654Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M12 17.7391C15.1696 17.7391 17.7391 15.1696 17.7391 12C17.7391 8.83036 15.1696 6.26086 12 6.26086C8.83037 6.26086 6.26086 8.83036 6.26086 12C6.26086 15.1696 8.83037 17.7391 12 17.7391Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M12 16.6957C14.5933 16.6957 16.6957 14.5933 16.6957 12C16.6957 9.40666 14.5933 7.30434 12 7.30434C9.40667 7.30434 7.30435 9.40666 7.30435 12C7.30435 14.5933 9.40667 16.6957 12 16.6957Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M12 15.1305C13.7289 15.1305 15.1305 13.7289 15.1305 12C15.1305 10.2711 13.7289 8.86955 12 8.86955C10.2711 8.86955 8.86955 10.2711 8.86955 12C8.86955 13.7289 10.2711 15.1305 12 15.1305Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx39(
      "path",
      {
        d: "M12 11.2174L10.3044 12V13.3043L12 14.3478L13.6957 13.3043V12L12 11.2174Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx39("path", { d: "M10.3044 10.4348H13.6957V12H10.3044V10.4348Z", fill: "#FFDA44" })
  ] }),
  /* @__PURE__ */ jsx39("defs", { children: /* @__PURE__ */ jsx39("clipPath", { id: "cfBZ_clip0_0_1", children: /* @__PURE__ */ jsx39("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CA.tsx
import { jsx as jsx40, jsxs as jsxs38 } from "react/jsx-runtime";
var FlagCA = (props) => /* @__PURE__ */ jsxs38(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs38("g", { clipPath: "url(#cfCA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx40(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs38("g", { children: [
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M24 12C24 7.24247 21.2313 3.13172 17.2174 1.19072V22.8092C21.2313 20.8683 24 16.7575 24 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M0 12C0 16.7575 2.76867 20.8683 6.78263 22.8093V1.19072C2.76867 3.13172 0 7.24247 0 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx40(
        "path",
        {
          d: "M14.087 13.5652L16.1739 12.5217L15.1305 12V10.9565L13.0435 12L14.087 9.91303H13.0435L12 8.34782L10.9565 9.91303H9.91303L10.9565 12L8.86955 10.9565V12L7.82611 12.5217L9.91303 13.5652L9.39131 14.6087H11.4783V16.1739H12.5217V14.6087H14.6087L14.087 13.5652Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx40("defs", { children: /* @__PURE__ */ jsx40("clipPath", { id: "cfCA_clip0_0_1", children: /* @__PURE__ */ jsx40("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CC.tsx
import { jsx as jsx41, jsxs as jsxs39 } from "react/jsx-runtime";
var FlagCC = (props) => /* @__PURE__ */ jsxs39(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs39("g", { clipPath: "url(#cfCC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx41(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs39("g", { children: [
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M18.4211 17.2174L18.7504 17.9059L19.494 17.7341L19.161 18.4208L19.759 18.895L19.0145 19.0628L19.0166 19.8261L18.4211 19.3486L17.8257 19.8261L17.8278 19.0628L17.0833 18.895L17.6812 18.4208L17.3482 17.7341L18.0918 17.9059L18.4211 17.2174Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M15.3578 9.91304L15.6871 10.6016L16.4307 10.4297L16.0977 11.1165L16.6957 11.5907L15.9511 11.7585L15.9532 12.5217L15.3578 12.0443L14.7624 12.5217L14.7644 11.7585L14.0199 11.5907L14.6179 11.1165L14.2848 10.4297L15.0285 10.6016L15.3578 9.91304Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M18.4211 5.73915L18.7504 6.4277L19.494 6.25576L19.1611 6.94257L19.759 7.41681L19.0145 7.58457L19.0165 8.34784L18.4211 7.87032L17.8257 8.34784L17.8278 7.58457L17.0833 7.41681L17.6813 6.94257L17.3483 6.25576L18.0918 6.4277L18.4211 5.73915Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M21.0969 8.86955L21.4262 9.5581L22.1699 9.38621L21.8368 10.073L22.4348 10.5472L21.6903 10.715L21.6923 11.4783L21.0969 11.0008L20.5015 11.4783L20.5035 10.715L19.759 10.5472L20.357 10.073L20.024 9.38621L20.7676 9.5581L21.0969 8.86955Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M19.1835 12.5217L19.4426 13.3189H20.2808L19.6026 13.8116L19.8617 14.6087L19.1835 14.1161L18.5055 14.6087L18.7645 13.8116L18.0864 13.3189H18.9245L19.1835 12.5217Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M13.3043 15.3913C11.4313 15.3913 9.91297 13.8729 9.91297 12C9.91297 10.1271 11.4313 8.60869 13.3043 8.60869C13.8883 8.60869 14.4378 8.75639 14.9175 9.01632C14.165 8.28028 13.1358 7.82607 11.9999 7.82607C9.69477 7.82607 7.82605 9.69478 7.82605 12C7.82605 14.3051 9.69477 16.1738 11.9999 16.1738C13.1358 16.1738 14.165 15.7196 14.9175 14.9836C14.4378 15.2436 13.8883 15.3913 13.3043 15.3913Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M7.54622 4.69566C7.18529 4.07204 6.51146 3.65218 5.73915 3.65218C4.96683 3.65218 4.29301 4.07204 3.93207 4.69566H7.54622Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx41(
        "path",
        {
          d: "M7.55316 4.7077L5.73914 6.52172L3.92513 4.7077C3.75174 5.01197 3.65222 5.36386 3.65222 5.73914C3.65222 6.89175 4.58658 7.82611 5.73919 7.82611C6.8918 7.82611 7.82616 6.8918 7.82616 5.73914C7.82611 5.36386 7.72655 5.01197 7.55316 4.7077Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx41(
      "path",
      {
        d: "M5.21735 6V7.76034C5.38408 7.80323 5.55892 7.82606 5.73906 7.82606C5.91921 7.82606 6.09405 7.80319 6.26078 7.76034V6H5.21735Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx41("defs", { children: /* @__PURE__ */ jsx41("clipPath", { id: "cfCC_clip0_0_1", children: /* @__PURE__ */ jsx41("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CD.tsx
import { jsx as jsx42, jsxs as jsxs40 } from "react/jsx-runtime";
var FlagCD = (props) => /* @__PURE__ */ jsxs40(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs40("g", { clipPath: "url(#cfCD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx42(
      "path",
      {
        d: "M22.6675 6.49763C22.1131 5.4233 21.3863 4.41493 20.4857 3.51436C19.5851 2.61375 18.5767 1.88696 17.5024 1.33252L9.04853 9.04843L1.33258 17.5023C1.88706 18.5766 2.61386 19.585 3.51442 20.4856C4.41499 21.3862 5.42341 22.113 6.49769 22.6674L14.9515 14.9515L22.6675 6.49763Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx42(
      "path",
      {
        d: "M3.51443 20.4856C4.01782 20.989 4.55496 21.438 5.11835 21.8333L21.8332 5.11832C21.438 4.55493 20.9891 4.01779 20.4857 3.5144C19.9822 3.01101 19.4451 2.56209 18.8817 2.16684L2.16687 18.8817C2.56207 19.445 3.011 19.9822 3.51443 20.4856Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs40("g", { children: [
      /* @__PURE__ */ jsx42(
        "path",
        {
          d: "M3.51429 3.5144C-0.271664 7.30035 -0.99846 12.9862 1.33245 17.5024L17.5023 1.3326C12.986 -0.998256 7.3002 -0.271365 3.51429 3.5144Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx42(
        "path",
        {
          d: "M20.4857 20.4857C24.2716 16.6998 24.9983 11.0139 22.6676 6.49774L6.49774 22.6675C11.0139 24.9984 16.6998 24.2716 20.4857 20.4857Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx42(
      "path",
      {
        d: "M6.39498 3.65179L7.04251 5.64478H9.13801L7.44273 6.87647L8.09026 8.8694L6.39498 7.63767L4.69961 8.8694L5.34723 6.87647L3.65186 5.64478H5.74736L6.39498 3.65179Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx42("defs", { children: /* @__PURE__ */ jsx42("clipPath", { id: "cfCD_clip0_0_1", children: /* @__PURE__ */ jsx42("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CF.tsx
import { jsx as jsx43, jsxs as jsxs41 } from "react/jsx-runtime";
var FlagCF = (props) => /* @__PURE__ */ jsxs41(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs41("g", { clipPath: "url(#cfCF_clip0_0_1)", children: [
    /* @__PURE__ */ jsx43(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs41("g", { children: [
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M22.5409 6.26086C20.9903 3.41888 18.3239 1.27345 15.1305 0.412735L14.6087 6.26086H22.5409Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M1.45908 6.26086H9.39126L8.86954 0.412735C5.67609 1.27345 3.00975 3.41892 1.45908 6.26086Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs41("g", { children: [
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M14.6087 12V17.7391H22.5409C23.4711 16.0342 24 14.0789 24 12H14.6087Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M9.39131 12H0C0 14.0789 0.528891 16.0342 1.45908 17.7391H9.39127V12H9.39131Z",
          fill: "#6DA544"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs41("g", { children: [
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M1.45908 17.7391C3.00975 20.5811 5.67609 22.7265 8.86954 23.5873L9.39126 18.4728L1.45908 17.7391Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M15.1305 23.5873C18.3239 22.7265 20.9903 20.5811 22.5409 17.7391L14.6087 18.4727L15.1305 23.5873Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M14.6087 18.4727L22.5409 17.7391H14.6087V18.4727Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx43(
        "path",
        {
          d: "M9.39131 17.7391H1.45908L9.39131 18.4727V17.7391Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx43(
      "path",
      {
        d: "M12 0C10.9169 0 9.86771 0.144375 8.86955 0.413391V23.5867C9.86771 23.8556 10.9169 24 12 24C13.0831 24 14.1323 23.8556 15.1305 23.5866V0.413391C14.1323 0.144375 13.0831 0 12 0V0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx43(
      "path",
      {
        d: "M6.4546 2.60869L6.77832 3.60511H7.82611L6.97852 4.22095L7.30228 5.21738L6.4546 4.60158L5.60696 5.21738L5.93082 4.22095L5.08322 3.60511H6.13092L6.4546 2.60869Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx43("defs", { children: /* @__PURE__ */ jsx43("clipPath", { id: "cfCF_clip0_0_1", children: /* @__PURE__ */ jsx43("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CG.tsx
import { jsx as jsx44, jsxs as jsxs42 } from "react/jsx-runtime";
var FlagCG = (props) => /* @__PURE__ */ jsxs42(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs42("g", { clipPath: "url(#cfCG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx44(
      "path",
      {
        d: "M6.50486 22.6708L15.3204 15.3205L22.6708 6.50494C21.5279 4.29019 19.7159 2.47712 17.502 1.33286L8.67948 8.67961L1.33282 17.502C2.47699 19.716 4.29007 21.528 6.50486 22.6708Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx44(
      "path",
      {
        d: "M20.4856 20.4857C24.2715 16.6997 24.9983 11.0139 22.6674 6.49771L6.49765 22.6675C11.0139 24.9983 16.6997 24.2715 20.4856 20.4857Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx44(
      "path",
      {
        d: "M3.51415 3.51424C-0.27175 7.30014 -0.998499 12.986 1.33226 17.5022L17.5021 1.3324C12.9859 -0.998457 7.30005 -0.271615 3.51415 3.51424Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx44("defs", { children: /* @__PURE__ */ jsx44("clipPath", { id: "cfCG_clip0_0_1", children: /* @__PURE__ */ jsx44("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CH.tsx
import { jsx as jsx45, jsxs as jsxs43 } from "react/jsx-runtime";
var FlagCH = (props) => /* @__PURE__ */ jsxs43(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs43("g", { clipPath: "url(#cfCH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx45(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx45(
      "path",
      {
        d: "M18.2609 9.91307H14.087V5.73914H9.91306V9.91307H5.73917V14.087H9.91306V18.2609H14.087V14.087H18.2609V9.91307Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx45("defs", { children: /* @__PURE__ */ jsx45("clipPath", { id: "cfCH_clip0_0_1", children: /* @__PURE__ */ jsx45("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CI.tsx
import { jsx as jsx46, jsxs as jsxs44 } from "react/jsx-runtime";
var FlagCI = (props) => /* @__PURE__ */ jsxs44(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs44("g", { clipPath: "url(#cfCI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx46(
      "path",
      {
        d: "M12 24C13.4678 24 14.8739 23.736 16.1739 23.2536L16.6956 12L16.1739 0.746391C14.8739 0.264047 13.4678 0 12 0C10.5321 0 9.12602 0.264047 7.82609 0.746391L7.30432 12L7.82604 23.2536C9.12602 23.736 10.5321 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx46(
      "path",
      {
        d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746399C3.2565 2.44191 0 6.84043 0 12Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx46(
      "path",
      {
        d: "M16.1739 0.746399V23.2537C20.7435 21.5581 24 17.1596 24 12C24 6.84043 20.7435 2.44191 16.1739 0.746399Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx46("defs", { children: /* @__PURE__ */ jsx46("clipPath", { id: "cfCI_clip0_0_1", children: /* @__PURE__ */ jsx46("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CK.tsx
import { jsx as jsx47, jsxs as jsxs45 } from "react/jsx-runtime";
var FlagCK = (props) => /* @__PURE__ */ jsxs45(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs45("g", { clipPath: "url(#cfCK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx47(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs45("g", { children: [
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs45("g", { children: [
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M6.07101 1.56522C4.19419 2.63392 2.63386 4.1942 1.5652 6.07103V12H4.69565V4.69576V4.69567H12C12 3.70834 12 2.76775 12 1.56522H6.07101Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M12 10.5243L7.73648 6.26093H6.26086V6.26102L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs45("g", { children: [
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M16.1739 12L16.396 12.6832H17.1143L16.5332 13.1055L16.7552 13.7888L16.1739 13.3665L15.5927 13.7888L15.8147 13.1055L15.2335 12.6832H15.9519L16.1739 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M13.2225 13.2226L13.8626 13.5487L14.3706 13.0407L14.2582 13.7503L14.8984 14.0764L14.1888 14.1887L14.0764 14.8984L13.7502 14.2583L13.0407 14.3707L13.5487 13.8627L13.2225 13.2226Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M12 16.1739L12.6832 15.9519L12.6833 15.2336L13.1055 15.8147L13.7888 15.5926L13.3665 16.1739L13.7888 16.7552L13.1055 16.5332L12.6833 17.1143V16.396L12 16.1739Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M13.2225 19.1253L13.5486 18.4852L13.0407 17.9772L13.7503 18.0896L14.0764 17.4495L14.1888 18.1591L14.8984 18.2714L14.2582 18.5976L14.3706 19.3072L13.8626 18.7991L13.2225 19.1253Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M16.1739 20.3478L15.9519 19.6645H15.2335L15.8147 19.2423L15.5927 18.559L16.1739 18.9813L16.7552 18.559L16.5332 19.2423L17.1143 19.6645H16.396L16.1739 20.3478Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M19.1253 19.1253L18.4852 18.7992L17.9772 19.3072L18.0896 18.5976L17.4494 18.2715L18.159 18.1591L18.2714 17.4495L18.5976 18.0896L19.3071 17.9772L18.7991 18.4852L19.1253 19.1253Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M20.3478 16.1739L19.6645 16.396V17.1143L19.2423 16.5331L18.559 16.7552L18.9813 16.1739L18.559 15.5926L19.2423 15.8147L19.6645 15.2336V15.9519L20.3478 16.1739Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx47(
        "path",
        {
          d: "M19.1253 13.2226L18.7992 13.8627L19.3071 14.3706L18.5976 14.2582L18.2714 14.8984L18.159 14.1887L17.4494 14.0764L18.0896 13.7503L17.9772 13.0407L18.4852 13.5486L19.1253 13.2226Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx47("defs", { children: /* @__PURE__ */ jsx47("clipPath", { id: "cfCK_clip0_0_1", children: /* @__PURE__ */ jsx47("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CL.tsx
import { jsx as jsx48, jsxs as jsxs46 } from "react/jsx-runtime";
var FlagCL = (props) => /* @__PURE__ */ jsxs46(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs46("g", { clipPath: "url(#cfCL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 5.37263 12 12 12 12C12 12 21.0825 12 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0V12C12 12 4.17389 12 0 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx48(
      "path",
      {
        d: "M7.14325 4.17389L7.92029 6.56536H10.4348L8.40053 8.04333L9.17753 10.4348L7.14325 8.95678L5.10897 10.4348L5.88597 8.04333L3.85168 6.56536H6.3662L7.14325 4.17389Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx48("defs", { children: /* @__PURE__ */ jsx48("clipPath", { id: "cfCL_clip0_0_1", children: /* @__PURE__ */ jsx48("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CM.tsx
import { jsx as jsx49, jsxs as jsxs47 } from "react/jsx-runtime";
var FlagCM = (props) => /* @__PURE__ */ jsxs47(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs47("g", { clipPath: "url(#cfCM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx49(
      "path",
      {
        d: "M17.2174 1.19072C15.6397 0.427875 13.8698 0 12 0C10.1302 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093C8.36025 23.5721 10.1302 24 12 24C13.8698 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx49(
      "path",
      {
        d: "M11.9999 7.82611L13.0358 11.0145H16.3887L13.6763 12.9853L14.7123 16.1739L11.9999 14.2032L9.28747 16.1739L10.3236 12.9853L7.61127 11.0145H10.9639L11.9999 7.82611Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx49(
      "path",
      {
        d: "M6.78263 1.19115C2.76872 3.13205 0 7.24248 0 12C0 16.7575 2.76872 20.868 6.78263 22.8089V1.19115Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx49(
      "path",
      {
        d: "M17.2173 1.19115V22.8089C21.2313 20.868 24 16.7575 24 12C24 7.24252 21.2313 3.13205 17.2173 1.19115Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx49("defs", { children: /* @__PURE__ */ jsx49("clipPath", { id: "cfCM_clip0_0_1", children: /* @__PURE__ */ jsx49("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CN.tsx
import { jsx as jsx50, jsxs as jsxs48 } from "react/jsx-runtime";
var FlagCN = (props) => /* @__PURE__ */ jsxs48(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs48("g", { clipPath: "url(#cfCN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx50(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs48("g", { children: [
      /* @__PURE__ */ jsx50(
        "path",
        {
          d: "M6.56719 7.30313L7.60312 10.4906H10.9547L8.24531 12.4641L9.28125 15.6516L6.56719 13.6828L3.85313 15.6516L4.89375 12.4641L2.17969 10.4906H5.53125L6.56719 7.30313Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx50(
        "path",
        {
          d: "M14.2266 18.5859L13.4344 17.6109L12.2625 18.0656L12.9422 17.0109L12.15 16.0313L13.3641 16.3547L14.0485 15.3L14.1141 16.5563L15.3328 16.8797L14.1563 17.3297L14.2266 18.5859Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx50(
        "path",
        {
          d: "M15.8016 15.7266L16.1766 14.5266L15.15 13.8L16.4063 13.7813L16.7766 12.5813L17.1844 13.7719L18.4406 13.7578L17.4328 14.5078L17.836 15.6984L16.8094 14.9719L15.8016 15.7266Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx50(
        "path",
        {
          d: "M17.925 8.80782L17.3719 9.93751L18.2719 10.8141L17.0297 10.6359L16.4765 11.7609L16.2609 10.5234L15.014 10.3453L16.1297 9.75938L15.914 8.5172L16.814 9.39376L17.925 8.80782Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx50(
        "path",
        {
          d: "M14.2594 5.38594L14.1656 6.6375L15.3328 7.11094L14.1094 7.41094L14.0203 8.66719L13.3594 7.59844L12.1359 7.89844L12.9469 6.9375L12.2812 5.87344L13.4484 6.34688L14.2594 5.38594Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx50("defs", { children: /* @__PURE__ */ jsx50("clipPath", { id: "cfCN_clip0_0_1", children: /* @__PURE__ */ jsx50("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CO.tsx
import { jsx as jsx51, jsxs as jsxs49 } from "react/jsx-runtime";
var FlagCO = (props) => /* @__PURE__ */ jsxs49(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs49("g", { clipPath: "url(#cfCO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx51(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12L12 13.0435L0 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx51(
      "path",
      {
        d: "M1.60575 18C3.68067 21.5867 7.55836 24 12 24C16.4416 24 20.3193 21.5867 22.3942 18L12 17.2174L1.60575 18Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx51(
      "path",
      {
        d: "M22.3942 18C23.4153 16.235 24 14.1858 24 12H0C0 14.1858 0.584719 16.235 1.60575 18H22.3942Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx51("defs", { children: /* @__PURE__ */ jsx51("clipPath", { id: "cfCO_clip0_0_1", children: /* @__PURE__ */ jsx51("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CR.tsx
import { jsx as jsx52, jsxs as jsxs50 } from "react/jsx-runtime";
var FlagCR = (props) => /* @__PURE__ */ jsxs50(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs50("g", { clipPath: "url(#cfCR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx52(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx52(
      "path",
      {
        d: "M23.2536 7.82611H0.746391C0.264047 9.1261 0 10.5322 0 12C0 13.4678 0.264047 14.8739 0.746391 16.1739H23.2537C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.1261 23.2536 7.82611Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs50("g", { children: [
      /* @__PURE__ */ jsx52(
        "path",
        {
          d: "M12 0C8.36294 0 5.10405 1.61831 2.90341 4.17389H21.0966C18.896 1.61831 15.6371 0 12 0Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx52(
        "path",
        {
          d: "M21.0966 19.8261H2.90341C5.10405 22.3817 8.36294 24 12 24C15.6371 24 18.896 22.3817 21.0966 19.8261Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx52("defs", { children: /* @__PURE__ */ jsx52("clipPath", { id: "cfCR_clip0_0_1", children: /* @__PURE__ */ jsx52("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CU.tsx
import { jsx as jsx53, jsxs as jsxs51 } from "react/jsx-runtime";
var FlagCU = (props) => /* @__PURE__ */ jsxs51(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs51("g", { clipPath: "url(#cfCU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx53(
      "path",
      {
        d: "M4.6875e-05 2.08698H0V21.913H4.6875e-05V12.0001V2.08698Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx53(
      "path",
      {
        d: "M12 24C18.6273 24 23.9999 18.6274 23.9999 12C23.9999 5.37262 18.6273 6.10352e-05 12 6.10352e-05C5.37256 6.10352e-05 0 5.37262 0 12C0 18.6274 5.37256 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs51("g", { children: [
      /* @__PURE__ */ jsx53(
        "path",
        {
          d: "M12.0001 9.15527e-05C8.07418 9.15527e-05 4.58883 1.88554 2.39954 4.80009H21.6005C19.4113 1.8855 15.9259 9.15527e-05 12.0001 9.15527e-05Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx53(
        "path",
        {
          d: "M12.0001 24C15.9259 24 19.4113 22.1145 21.6006 19.2H2.39954C4.58879 22.1145 8.07418 24 12.0001 24Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx53(
        "path",
        {
          d: "M6.10352e-05 12C6.10352e-05 12.822 0.082936 13.6246 0.240389 14.4H23.7598C23.9172 13.6246 24.0001 12.822 24.0001 12C24.0001 11.1781 23.9172 10.3755 23.7598 9.60004H0.240389C0.082936 10.3755 6.10352e-05 11.1781 6.10352e-05 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx53(
      "path",
      {
        d: "M3.51474 3.51477C-1.1715 8.201 -1.1715 15.799 3.51474 20.4853C5.45128 18.5488 7.31372 16.6863 12 12.0001L3.51474 3.51477Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx53(
      "path",
      {
        d: "M4.85676 8.86963L5.63367 11.261H8.14832L6.11409 12.739L6.891 15.1304L4.85676 13.6524L2.82243 15.1304L3.59953 12.739L1.56525 11.261H4.07972L4.85676 8.86963Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx53("defs", { children: /* @__PURE__ */ jsx53("clipPath", { id: "cfCU_clip0_0_1", children: /* @__PURE__ */ jsx53("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CV.tsx
import { jsx as jsx54, jsxs as jsxs52 } from "react/jsx-runtime";
var FlagCV = (props) => /* @__PURE__ */ jsxs52(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs52("g", { clipPath: "url(#cfCV_clip0_0_1)", children: [
    /* @__PURE__ */ jsx54(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs52("g", { children: [
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M12 24C15.6371 24 18.896 22.3817 21.0966 19.8261H2.90339C5.10403 22.3817 8.36292 24 12 24Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M24 12C24 5.37262 18.6274 0 12 0C5.37262 0 0 5.37262 0 12C0 13.0831 0.144375 14.1323 0.413391 15.1305H23.5867C23.8556 14.1323 24 13.0831 24 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx54(
      "path",
      {
        d: "M0.953812 16.6956C1.18514 17.2392 1.45556 17.762 1.76133 18.2609H22.2388C22.5444 17.7619 22.8149 17.2392 23.0463 16.6956H0.953812Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs52("g", { children: [
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M8.57766 12.7826L8.77191 13.3805H9.40055L8.892 13.75L9.08625 14.3478L8.57766 13.9783L8.06906 14.3478L8.26336 13.75L7.75481 13.3805H8.38345L8.57766 12.7826Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M8.57766 20.6087L8.77191 21.2066H9.40055L8.892 21.576L9.08625 22.1739L8.57766 21.8044L8.06906 22.1739L8.26336 21.576L7.75481 21.2066H8.38345L8.57766 20.6087Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M4.93472 17.962L5.12897 18.5598H5.75761L5.24907 18.9292L5.44331 19.5272L4.93472 19.1577L4.42613 19.5272L4.62047 18.9292L4.11188 18.5598H4.74047L4.93472 17.962Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M6.3262 13.5652L6.52045 14.1631H7.14905L6.64055 14.5326L6.8348 15.1305L6.3262 14.7609L5.81761 15.1305L6.01195 14.5326L5.50331 14.1631H6.13195L6.3262 13.5652Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M4.11188 16.0273L4.74038 16.0273L4.93467 15.4293L5.12897 16.0273L5.75761 16.0272L5.24902 16.3967L5.44331 16.9946L4.93467 16.6251L4.42613 16.9945L4.62038 16.3967L4.11188 16.0273Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M5.81766 21.2771L6.01191 20.6793L5.50327 20.3099H6.13191L6.3262 19.712L6.5205 20.3098H7.14909L6.6405 20.6793L6.83475 21.2772L6.32616 20.9076L5.81766 21.2771Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M12.2192 17.962L12.025 18.5598H11.3964L11.9049 18.9292L11.7106 19.5272L12.2192 19.1577L12.7278 19.5272L12.5335 18.9292L13.0421 18.5598H12.4135L12.2192 17.962Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M10.8278 13.5652L10.6336 14.1631H10.0049L10.5134 14.5326L10.3192 15.1305L10.8278 14.7609L11.3364 15.1305L11.1421 14.5326L11.6506 14.1631H11.022L10.8278 13.5652Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M13.0421 16.0273L12.4136 16.0273L12.2193 15.4293L12.025 16.0273L11.3964 16.0272L11.9049 16.3967L11.7106 16.9946L12.2193 16.6251L12.7278 16.9945L12.5336 16.3967L13.0421 16.0273Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx54(
        "path",
        {
          d: "M11.3363 21.2771L11.1421 20.6793L11.6507 20.3099H11.0221L10.8278 19.712L10.6335 20.3098H10.0049L10.5135 20.6793L10.3192 21.2772L10.8278 20.9076L11.3363 21.2771Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx54("defs", { children: /* @__PURE__ */ jsx54("clipPath", { id: "cfCV_clip0_0_1", children: /* @__PURE__ */ jsx54("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CY.tsx
import { jsx as jsx55, jsxs as jsxs53 } from "react/jsx-runtime";
var FlagCY = (props) => /* @__PURE__ */ jsxs53(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs53("g", { clipPath: "url(#cfCY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx55(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FCFCFC"
      }
    ),
    /* @__PURE__ */ jsx55(
      "path",
      {
        d: "M18.7826 10.4348C18.4174 10.4348 17.9652 10.4348 17.2174 10.4348C17.2174 13.3163 14.8815 15.6522 12 15.6522C9.1185 15.6522 6.78267 13.3163 6.78267 10.4348C6.2783 10.4348 5.72175 10.4348 5.21747 10.4348C5.21747 13.5386 7.30228 16.1555 10.1481 16.9614C9.83606 17.5541 9.88359 18.3003 10.3293 18.8565C10.9147 18.3873 11.5207 17.9016 12.0348 17.4897C12.549 17.9017 13.155 18.3874 13.7404 18.8565C14.1905 18.2948 14.2348 17.5391 13.9124 16.9436C16.727 16.1179 18.7826 13.5165 18.7826 10.4348Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx55(
      "path",
      {
        d: "M7.82611 9.91304C7.82611 9.91304 7.82611 12.5217 10.4348 12.5217L10.9565 13.0434H12C12 13.0434 12.5217 11.4782 13.5652 11.4782C13.5652 11.4782 13.5652 10.4348 14.6087 10.4348C15.6522 10.4348 16.1739 10.4348 16.1739 10.4348C16.1739 10.4348 15.6522 8.34779 18.2609 6.78258L17.2174 6.26086C17.2174 6.26086 13.5652 8.86955 10.9565 8.34783V9.39132H9.91303L9.39131 8.8696L7.82611 9.91304Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx55("defs", { children: /* @__PURE__ */ jsx55("clipPath", { id: "cfCY_clip0_0_1", children: /* @__PURE__ */ jsx55("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/CZ.tsx
import { jsx as jsx56, jsxs as jsxs54 } from "react/jsx-runtime";
var FlagCZ = (props) => /* @__PURE__ */ jsxs54(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs54("g", { clipPath: "url(#cfCZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx56(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx56(
      "path",
      {
        d: "M10.9565 12C10.9565 12 3.52174 20.487 3.51471 20.4853C5.68624 22.6568 8.68629 24 12 24C18.6274 24 24 18.6274 24 12H10.9565Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx56(
      "path",
      {
        d: "M3.51471 3.51468C-1.17157 8.20096 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51468Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx56("defs", { children: /* @__PURE__ */ jsx56("clipPath", { id: "cfCZ_clip0_0_1", children: /* @__PURE__ */ jsx56("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DE.tsx
import { jsx as jsx57, jsxs as jsxs55 } from "react/jsx-runtime";
var FlagDE = (props) => /* @__PURE__ */ jsxs55(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs55("g", { clipPath: "url(#cfDE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx57(
      "path",
      {
        d: "M0.746391 16.1739C2.44191 20.7435 6.84042 24 12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739L12 15.1305L0.746391 16.1739Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx57(
      "path",
      {
        d: "M12 0C6.84042 0 2.44191 3.2565 0.746391 7.82611L12 8.86955L23.2536 7.82606C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx57(
      "path",
      {
        d: "M0.746391 7.82611C0.264047 9.1261 0 10.5322 0 12C0 13.4678 0.264047 14.8739 0.746391 16.1739H23.2537C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.1261 23.2536 7.82611H0.746391Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx57("defs", { children: /* @__PURE__ */ jsx57("clipPath", { id: "cfDE_clip0_0_1", children: /* @__PURE__ */ jsx57("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DJ.tsx
import { jsx as jsx58, jsxs as jsxs56 } from "react/jsx-runtime";
var FlagDJ = (props) => /* @__PURE__ */ jsxs56(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs56("g", { clipPath: "url(#cfDJ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx58(
      "path",
      {
        d: "M10.9565 13.0435C10.9565 13.0435 3.52172 3.51305 3.51469 3.51473C5.68622 1.34316 8.68626 0 12 0C18.6274 0 24 5.37262 24 12L10.9565 13.0435Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx58(
      "path",
      {
        d: "M10.9565 12C10.9565 12 3.52172 20.487 3.51469 20.4853C5.68622 22.6568 8.68626 24 12 24C18.6274 24 24 18.6274 24 12H10.9565Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx58(
      "path",
      {
        d: "M3.51469 3.51468C-1.17159 8.20096 -1.17159 15.799 3.51469 20.4853C5.45123 18.5488 7.31372 16.6863 12 12L3.51469 3.51468Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx58(
      "path",
      {
        d: "M4.85672 8.86954L5.63367 11.2609H8.14833L6.11404 12.739L6.89095 15.1304L4.85672 13.6524L2.82239 15.1304L3.59948 12.739L1.5652 11.2609H4.07967L4.85672 8.86954Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx58("defs", { children: /* @__PURE__ */ jsx58("clipPath", { id: "cfDJ_clip0_0_1", children: /* @__PURE__ */ jsx58("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DK.tsx
import { jsx as jsx59, jsxs as jsxs57 } from "react/jsx-runtime";
var FlagDK = (props) => /* @__PURE__ */ jsxs57(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs57("g", { clipPath: "url(#cfDK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx59(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs57("g", { children: [
      /* @__PURE__ */ jsx59(
        "path",
        {
          d: "M9.39136 10.4348H23.8984C23.1314 4.54697 18.0969 0 12 0C11.104 0 10.2313 0.0990469 9.39131 0.285234V10.4348H9.39136Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx59(
        "path",
        {
          d: "M6.26086 10.4347V1.45908C2.96587 3.25692 0.607031 6.55462 0.101578 10.4348H6.26086V10.4347Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx59(
        "path",
        {
          d: "M6.26081 13.5652H0.101578C0.607031 17.4454 2.96587 20.7431 6.26086 22.5409L6.26081 13.5652Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx59(
        "path",
        {
          d: "M9.39131 13.5653V23.7148C10.2313 23.901 11.104 24 12 24C18.0969 24 23.1314 19.453 23.8984 13.5652H9.39131V13.5653Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx59("defs", { children: /* @__PURE__ */ jsx59("clipPath", { id: "cfDK_clip0_0_1", children: /* @__PURE__ */ jsx59("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DM.tsx
import { jsx as jsx60, jsxs as jsxs58 } from "react/jsx-runtime";
var FlagDM = (props) => /* @__PURE__ */ jsxs58(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs58("g", { clipPath: "url(#cfDM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs58("g", { children: [
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M14.3478 23.7697C19.0917 22.8286 22.8286 19.0917 23.7697 14.3478H14.3478V23.7697Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M0.230293 14.3478C1.17131 19.0917 4.90828 22.8286 9.65217 23.7697V14.3478H0.230293Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M23.7697 9.65216C22.8286 4.90832 19.0917 1.17135 14.3478 0.230286V9.65216H23.7697Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M9.65217 0.230286C4.90828 1.17135 1.17131 4.90832 0.230293 9.65216H9.65217V0.230286Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs58("g", { children: [
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M11.2174 14.3478H9.65218V23.7697C10.163 23.871 10.6855 23.9396 11.2174 23.9739V14.3478Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M11.2174 9.65219V0.026123C10.6855 0.0603418 10.1629 0.129014 9.65218 0.230311V9.65219H11.2174Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M12.7826 0.0261094C12.5238 0.009375 12.263 0 12 0C11.737 0 11.4762 0.009375 11.2174 0.0261094V9.65222H12.7826V0.0261094H12.7826Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M11.2174 23.9739C11.4762 23.9906 11.737 24 12 24C12.263 24 12.5238 23.9906 12.7826 23.9739V14.3478H11.2174V23.9739H11.2174Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs58("g", { children: [
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M9.65217 11.2174V9.65216H0.230295C0.128952 10.163 0.0603265 10.6855 0.0261078 11.2174H9.65217Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M14.3478 11.2174H23.9739C23.9397 10.6855 23.871 10.163 23.7697 9.65216H14.3479V11.2174H14.3478Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M14.3478 12.7826H23.9739C23.9906 12.5238 24 12.263 24 12C24 11.737 23.9906 11.4762 23.9739 11.2174H14.3478V12.7826Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M0.0261094 11.2174C0.00942187 11.4762 0 11.737 0 12C0 12.263 0.00942187 12.5238 0.0261094 12.7826H9.65222V11.2174H0.0261094V11.2174Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx60(
      "path",
      {
        d: "M12 17.7391C15.1696 17.7391 17.7391 15.1696 17.7391 12C17.7391 8.83037 15.1696 6.26086 12 6.26086C8.83036 6.26086 6.26086 8.83037 6.26086 12C6.26086 15.1696 8.83036 17.7391 12 17.7391Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs58("g", { children: [
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M13.3075 12.6403C12.8806 11.7866 12.3258 10.9213 12.3258 10.9213C12.3258 10.9213 12.3396 10.5273 12.3396 10.2925C12.3396 9.93891 12.0529 9.65222 11.6992 9.65222C11.3642 9.65222 11.0897 9.90961 11.0617 10.2373C10.7983 10.2545 10.5899 10.473 10.5899 10.7408C10.5899 10.9355 10.7002 11.1042 10.8616 11.1885C10.9418 11.0008 11.099 10.8543 11.2933 10.7875C11.325 10.8134 11.359 10.8366 11.3954 10.8562L11.4774 10.8955C11.4774 10.8955 11.1732 11.7529 11.1732 12.2135C11.1732 13.5083 12.027 13.9077 12.027 13.9077V13.921L11.6001 14.3479H12.4538V13.4941L12.8807 13.921C12.8845 13.913 13.6893 13.404 13.3075 12.6403Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M12 7.30435L12.1942 7.9022H12.8228L12.3143 8.27171L12.5086 8.86955L12 8.50009L11.4914 8.86955L11.6857 8.27171L11.1772 7.9022H11.8057L12 7.30435Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M9.24 8.20116L9.7485 8.57067L10.2571 8.20111L10.0628 8.79905L10.5714 9.16847L9.9428 9.16852L9.74855 9.76636L9.55425 9.16852L8.92566 9.16847L9.43421 8.79895L9.24 8.20116Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M7.53417 10.549H8.16272L8.35702 9.95114L8.55127 10.549L9.17991 10.5489L8.67136 10.9185L8.86566 11.5163L8.35702 11.1468L7.84847 11.5163L8.04267 10.9185L7.53417 10.549Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M7.53417 13.451L8.04267 13.0816L7.84842 12.4837L8.35702 12.8532L8.86561 12.4837L8.67136 13.0815L9.17995 13.4511L8.55127 13.4511L8.35702 14.0489L8.16272 13.451H7.53417Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M9.24 15.7989L9.43421 15.2011L8.92561 14.8316H9.55425L9.74855 14.2337L9.9428 14.8315H10.5714L10.0628 15.2011L10.2571 15.7989L9.7485 15.4294L9.24 15.7989Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M12 16.6957L11.8057 16.0979H11.1772L11.6857 15.7284L11.4914 15.1305L12 15.4999L12.5085 15.1305L12.3143 15.7284L12.8228 16.0979L12.1942 16.0979L12 16.6957Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M14.76 15.7989L14.2515 15.4294L13.7429 15.799L13.9372 15.2011L13.4286 14.8316L14.0572 14.8315L14.2514 14.2337L14.4457 14.8316H15.0743L14.5658 15.2011L14.76 15.7989Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M16.4658 13.451H15.8373L15.643 14.0489L15.4487 13.4511H14.8201L15.3286 13.0816L15.1343 12.4838L15.643 12.8532L16.1515 12.4838L15.9573 13.0816L16.4658 13.451Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M16.4658 10.549L15.9573 10.9185L16.1515 11.5163L15.643 11.1468L15.1344 11.5164L15.3286 10.9185L14.8201 10.549L15.4487 10.549L15.6429 9.9512L15.8373 10.549H16.4658Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx60(
        "path",
        {
          d: "M14.76 8.20117L14.5658 8.79897L15.0743 9.16848H14.4457L14.2515 9.76638L14.0572 9.16853L13.4286 9.16858L13.9372 8.79897L13.7429 8.20122L14.2515 8.57064L14.76 8.20117Z",
          fill: "#496E2D"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx60("defs", { children: /* @__PURE__ */ jsx60("clipPath", { id: "cfDM_clip0_0_1", children: /* @__PURE__ */ jsx60("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DO.tsx
import { jsx as jsx61, jsxs as jsxs59 } from "react/jsx-runtime";
var FlagDO = (props) => /* @__PURE__ */ jsxs59(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs59("g", { clipPath: "url(#cfDO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M0.413406 15.1305C1.52144 19.2415 4.75848 22.4786 8.86961 23.5867V15.1305H0.413406Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M8.86956 0.413391C4.75848 1.52138 1.52139 4.75847 0.413406 8.86955H8.86961V0.413391H8.86956Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M23.5866 8.86955C22.4786 4.75847 19.2415 1.52138 15.1304 0.413391V8.86959H23.5866V8.86955Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M15.1304 23.5866C19.2415 22.4786 22.4786 19.2415 23.5867 15.1304H15.1304V23.5866Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M15.1305 12C15.1305 13.7289 13.7289 15.1304 12 15.1304C10.2711 15.1304 8.86955 13.7289 8.86955 12C8.86955 10.2711 12 8.86954 12 8.86954C12 8.86954 15.1305 10.2711 15.1305 12Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M8.86955 12C8.86955 10.2711 10.2711 8.86954 12 8.86954C13.7289 8.86954 15.1305 10.2711 15.1305 12",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx61(
      "path",
      {
        d: "M10.2391 10.4348V12.3913C10.2391 13.3638 11.0275 14.1522 12 14.1522C12.9725 14.1522 13.7608 13.3638 13.7608 12.3913V10.4348H10.2391Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx61("defs", { children: /* @__PURE__ */ jsx61("clipPath", { id: "cfDO_clip0_0_1", children: /* @__PURE__ */ jsx61("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/DZ.tsx
import { jsx as jsx62, jsxs as jsxs60 } from "react/jsx-runtime";
var FlagDZ = (props) => /* @__PURE__ */ jsxs60(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs60("g", { clipPath: "url(#cfDZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx62(
      "path",
      {
        d: "M12 0C18.6274 0 24 5.37262 24 12C24 18.6274 18.6274 24 12 24C12 23.4783 10.4348 12 10.4348 12L12 0Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx62(
      "path",
      {
        d: "M12 24C5.37262 24 0 18.6274 0 12C0 5.37262 5.37262 0 12 0",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsxs60("g", { children: [
      /* @__PURE__ */ jsx62(
        "path",
        {
          d: "M14.5783 9.69825L13.5937 11.055L11.9991 10.5379L12.9853 11.8935L12.0008 13.2503L13.5948 12.7314L14.5809 14.087L14.5799 12.4107L16.1739 11.8917L14.5794 11.3746L14.5783 9.69825Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx62(
        "path",
        {
          d: "M12.9955 15.3913C11.1225 15.3913 9.60416 13.8729 9.60416 12C9.60416 10.1271 11.1225 8.60869 12.9955 8.60869C13.5794 8.60869 14.129 8.75634 14.6087 9.01631C13.8561 8.28033 12.8269 7.82606 11.6911 7.82606C9.38591 7.82606 7.51724 9.69478 7.51724 12C7.51724 14.3051 9.38596 16.1738 11.6911 16.1738C12.8269 16.1738 13.8561 15.7196 14.6087 14.9836C14.129 15.2437 13.5794 15.3913 12.9955 15.3913Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx62("defs", { children: /* @__PURE__ */ jsx62("clipPath", { id: "cfDZ_clip0_0_1", children: /* @__PURE__ */ jsx62("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/EC.tsx
import { jsx as jsx63, jsxs as jsxs61 } from "react/jsx-runtime";
var FlagEC = (props) => /* @__PURE__ */ jsxs61(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs61("g", { clipPath: "url(#cfEC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12L12 13.0435L0 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M1.60577 18C3.6807 21.5867 7.55838 24 12 24C16.4417 24 20.3193 21.5867 22.3943 18L12 17.2174L1.60577 18Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M22.3942 18C23.4153 16.235 24 14.1858 24 12H0C0 14.1858 0.584719 16.235 1.60575 18H22.3942Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M12 14.6087C10.5615 14.6087 9.3913 13.4384 9.3913 12V10.4348C9.3913 8.99634 10.5616 7.82611 12 7.82611C13.4384 7.82611 14.6087 8.99639 14.6087 10.4348V12C14.6087 13.4385 13.4384 14.6087 12 14.6087Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx63(
      "path",
      {
        d: "M16.1739 5.73913H13.0434C13.0434 5.16285 12.5762 4.69565 11.9999 4.69565C11.4236 4.69565 10.9564 5.16285 10.9564 5.73913H7.82599C7.82599 6.31546 8.32797 6.78262 8.90421 6.78262H8.86947C8.86947 7.35894 9.33663 7.8261 9.91296 7.8261C9.91296 8.40243 10.3801 8.86959 10.9564 8.86959H13.0434C13.6197 8.86959 14.0869 8.40243 14.0869 7.8261C14.6632 7.8261 15.1304 7.35894 15.1304 6.78262H15.0956C15.6719 6.78262 16.1739 6.31541 16.1739 5.73913Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx63("defs", { children: /* @__PURE__ */ jsx63("clipPath", { id: "cfEC_clip0_0_1", children: /* @__PURE__ */ jsx63("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/EE.tsx
import { jsx as jsx64, jsxs as jsxs62 } from "react/jsx-runtime";
var FlagEE = (props) => /* @__PURE__ */ jsxs62(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs62("g", { clipPath: "url(#cfEE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M0 12C0 13.4679 0.264047 14.8739 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.8739 24 13.4679 24 12C24 10.5322 23.736 9.1261 23.2536 7.82612L12 7.30435L0.746391 7.82607C0.264047 9.1261 0 10.5322 0 12H0Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M12 0C6.84042 0 2.44191 3.2565 0.746391 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M23.2536 16.1739H0.746391C2.44191 20.7435 6.84042 24 12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx64("defs", { children: /* @__PURE__ */ jsx64("clipPath", { id: "cfEE_clip0_0_1", children: /* @__PURE__ */ jsx64("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/EG.tsx
import { jsx as jsx65, jsxs as jsxs63 } from "react/jsx-runtime";
var FlagEG = (props) => /* @__PURE__ */ jsxs63(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs63("g", { clipPath: "url(#cfEG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx65(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx65(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx65(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx65(
      "path",
      {
        d: "M16.1739 10.6956H13.0434C13.0434 10.1194 12.5762 9.65216 11.9999 9.65216C11.4236 9.65216 10.9564 10.1194 10.9564 10.6956H7.82599C7.82599 11.272 8.32797 11.7391 8.90425 11.7391H8.86947C8.86947 12.3155 9.33663 12.7826 9.91296 12.7826C9.91296 13.3589 10.3801 13.8261 10.9564 13.8261H13.0434C13.6197 13.8261 14.0869 13.3589 14.0869 12.7826C14.6632 12.7826 15.1304 12.3155 15.1304 11.7391H15.0956C15.6719 11.7391 16.1739 11.272 16.1739 10.6956Z",
        fill: "#FF9811"
      }
    )
  ] }),
  /* @__PURE__ */ jsx65("defs", { children: /* @__PURE__ */ jsx65("clipPath", { id: "cfEG_clip0_0_1", children: /* @__PURE__ */ jsx65("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/EHP.tsx
import { jsx as jsx66, jsxs as jsxs64 } from "react/jsx-runtime";
var FlagEHP = (props) => /* @__PURE__ */ jsxs64(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M1.19072 6.78262C0.427875 8.36025 0 10.1301 0 12C0 13.8699 0.427875 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174C23.5721 15.6397 24 13.8699 24 12C24 10.1301 23.5721 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M1.19113 17.2174C3.13204 21.2313 7.24251 24 12 24C16.7575 24 20.8679 21.2313 22.8088 17.2174H1.19113Z",
      fill: "#496E2D"
    }
  ),
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M1.19113 6.78263H22.8088C20.8679 2.76867 16.7575 0 12 0C7.24246 0 3.13204 2.76867 1.19113 6.78263Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M3.51465 3.51468C-1.17163 8.20096 -1.17163 15.799 3.51465 20.4853C5.4512 18.5488 7.31368 16.6863 12 12L3.51465 3.51468Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M15.1304 12C15.1304 10.2516 16.3592 8.79106 18 8.43275C17.7478 8.37767 17.4861 8.34781 17.2174 8.34781C15.2003 8.34781 13.5652 9.9829 13.5652 12C13.5652 14.0171 15.2003 15.6522 17.2174 15.6522C17.4861 15.6522 17.7477 15.6223 18 15.5672C16.3592 15.2089 15.1304 13.7483 15.1304 12Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx66(
    "path",
    {
      d: "M18.1534 9.65216L18.6715 11.2466H20.3478L18.9916 12.2318L19.5097 13.8261L18.1534 12.8408L16.7973 13.8261L17.3154 12.2318L15.9591 11.2466H17.6354L18.1534 9.65216Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx66("defs", { children: /* @__PURE__ */ jsx66("clipPath", { id: "cfEHP_clip0_2_8841", children: /* @__PURE__ */ jsx66("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ER.tsx
import { jsx as jsx67, jsxs as jsxs65 } from "react/jsx-runtime";
var FlagER = (props) => /* @__PURE__ */ jsxs65(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs65("g", { clipPath: "url(#cfER_clip0_0_1)", children: [
    /* @__PURE__ */ jsx67(
      "path",
      {
        d: "M10.9565 10.9565C10.9565 10.9565 3.52174 20.487 3.51471 20.4853C5.68624 22.6568 8.68629 24 12 24C18.6274 24 24 18.6274 24 12L10.9565 10.9565Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx67(
      "path",
      {
        d: "M10.9565 12C10.9565 12 3.52174 3.51305 3.51471 3.51473C5.68624 1.34316 8.68629 0 12 0C18.6274 0 24 5.37262 24 12H10.9565Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx67(
      "path",
      {
        d: "M3.51471 3.51468C-1.17157 8.20096 -1.17157 15.799 3.51471 20.4853C3.51438 20.4872 24 12 24 12L3.51471 3.51468Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx67(
      "path",
      {
        d: "M6.26087 7.04349C3.95935 7.04349 2.08698 8.91591 2.08698 11.2174V12.7826C2.08698 15.0841 3.9594 16.9565 6.26087 16.9565C8.56233 16.9565 10.4348 15.0841 10.4348 12.7826V11.2174C10.4348 8.91591 8.56238 7.04349 6.26087 7.04349ZM8.86955 12.7826C8.86955 13.9485 8.10071 14.938 7.04344 15.2712V13.5653L8.15082 12.4579L7.04405 11.3511L7.04344 10.9572V10.4348H5.47829V12L4.37166 13.1066L5.47829 14.2132V15.2713C4.42102 14.938 3.65218 13.9485 3.65218 12.7827V11.2175C3.65218 9.77902 4.82246 8.60878 6.26087 8.60878C7.69927 8.60878 8.86955 9.77907 8.86955 11.2175V12.7826Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx67("defs", { children: /* @__PURE__ */ jsx67("clipPath", { id: "cfER_clip0_0_1", children: /* @__PURE__ */ jsx67("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ES.tsx
import { jsx as jsx68, jsxs as jsxs66 } from "react/jsx-runtime";
var FlagES = (props) => /* @__PURE__ */ jsxs66(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs66("g", { clipPath: "url(#cfES_clip0_0_1)", children: [
    /* @__PURE__ */ jsx68(
      "path",
      {
        d: "M0 12C0 13.4679 0.264047 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739C23.736 14.8739 24 13.4679 24 12C24 10.5322 23.736 9.12612 23.2536 7.82614L12 6.78265L0.746391 7.82614C0.264047 9.12612 0 10.5322 0 12H0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs66("g", { children: [
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M23.2537 7.82611C21.5582 3.25655 17.1596 0 12.0001 0C6.84049 0 2.44198 3.25655 0.74646 7.82611H23.2537Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx68(
        "path",
        {
          d: "M0.74646 16.1739C2.44198 20.7435 6.84049 24 12.0001 24C17.1596 24 21.5582 20.7435 23.2537 16.1739H0.74646Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx68("defs", { children: /* @__PURE__ */ jsx68("clipPath", { id: "cfES_clip0_0_1", children: /* @__PURE__ */ jsx68("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ET.tsx
import { jsx as jsx69, jsxs as jsxs67 } from "react/jsx-runtime";
var FlagET = (props) => /* @__PURE__ */ jsxs67(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs67("g", { clipPath: "url(#cfET_clip0_0_1)", children: [
    /* @__PURE__ */ jsx69(
      "path",
      {
        d: "M0 12C0 13.4679 0.264047 14.874 0.746437 16.174L12 17.2174L23.2536 16.174C23.736 14.874 24 13.4679 24 12C24 10.5523 23.7435 9.16444 23.2737 7.87945L12 6.78262L0.726281 7.8795C0.256453 9.16444 0 10.5523 0 12H0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx69(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746391C2.44191 20.7435 6.84042 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx69(
      "path",
      {
        d: "M12 0C6.84042 0 2.44191 3.2565 0.746391 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx69(
      "path",
      {
        d: "M12 17.7391C15.1696 17.7391 17.7391 15.1696 17.7391 12C17.7391 8.83037 15.1696 6.26086 12 6.26086C8.83036 6.26086 6.26086 8.83037 6.26086 12C6.26086 15.1696 8.83036 17.7391 12 17.7391Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs67("g", { children: [
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M11.9988 7.55582L13.0348 10.7443H16.3928L13.6751 12.715L14.7193 15.913L11.9988 13.933L9.28247 15.9094L10.3224 12.715L7.6072 10.7443H10.9627L11.9988 7.55582Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx69(
        "path",
        {
          d: "M16.1308 12.7936L12.8441 11.7256L14.8754 8.92997L14.0312 8.31656L11.9999 11.1124L9.96876 8.31661L9.12455 8.92992L11.1557 11.7258L7.86919 12.7935L8.19159 13.786L11.4783 12.7182V16.1739H12.5217V12.7181L15.8083 13.786L16.1308 12.7936Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx69("defs", { children: /* @__PURE__ */ jsx69("clipPath", { id: "cfET_clip0_0_1", children: /* @__PURE__ */ jsx69("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/EUR.tsx
import { jsx as jsx70, jsxs as jsxs68 } from "react/jsx-runtime";
var FlagEUR = (props) => /* @__PURE__ */ jsxs68(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs68("g", { clipPath: "url(#cfEUR_clip0_1_5745)", children: [
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M12.0006 4.69568L12.3892 5.89141H13.6464L12.6292 6.6304L13.0178 7.82613L12.0006 7.0871L10.9835 7.82613L11.372 6.6304L10.3549 5.89141H11.6121L12.0006 4.69568Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M6.83523 6.83509L7.95554 7.40584L8.84453 6.51691L8.64779 7.75863L9.76806 8.32942L8.52629 8.52611L8.32961 9.76792L7.75881 8.64766L6.51709 8.84439L7.40607 7.95541L6.83523 6.83509Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M4.69629 12L5.89202 11.6115V10.3542L6.63096 11.3714L7.82674 10.9829L7.08766 12L7.82674 13.0171L6.63096 12.6286L5.89202 13.6458V12.3885L4.69629 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M6.83523 17.165L7.40603 16.0447L6.51709 15.1557L7.75886 15.3525L8.32956 14.2322L8.52629 15.474L9.76801 15.6707L8.64789 16.2415L8.84453 17.4832L7.95554 16.5942L6.83523 17.165Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M12.0006 19.3044L11.612 18.1086H10.3549L11.372 17.3697L10.9835 16.174L12.0006 16.9129L13.0178 16.174L12.6292 17.3697L13.6464 18.1086H12.3891L12.0006 19.3044Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M17.1654 17.165L16.0451 16.5942L15.1561 17.4832L15.3528 16.2414L14.2327 15.6707L15.4744 15.474L15.6711 14.2322L16.2418 15.3525L17.4835 15.1557L16.5946 16.0448L17.1654 17.165Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M19.3048 12L18.1091 12.3885V13.6458L17.3701 12.6286L16.1744 13.0171L16.9134 12L16.1744 10.9829L17.3701 11.3714L18.1091 10.3542V11.6115L19.3048 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx70(
      "path",
      {
        d: "M17.1654 6.83505L16.5946 7.95536L17.4836 8.84434L16.2418 8.64756L15.6711 9.76783L15.4744 8.52606L14.2327 8.32933L15.3528 7.75858L15.1561 6.51691L16.0452 7.40584L17.1654 6.83505Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx70("defs", { children: /* @__PURE__ */ jsx70("clipPath", { id: "cfEUR_clip0_1_5745", children: /* @__PURE__ */ jsx70("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FI.tsx
import { jsx as jsx71, jsxs as jsxs69 } from "react/jsx-runtime";
var FlagFI = (props) => /* @__PURE__ */ jsxs69(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs69("g", { clipPath: "url(#cfFI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx71(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx71(
      "path",
      {
        d: "M23.8984 10.4348H9.39136H9.39131V0.285248C8.28234 0.531154 7.23098 0.929732 6.26086 1.45909V10.4347V10.4348H0.101578C0.0347813 10.9472 0 11.4695 0 12C0 12.5305 0.0347813 13.0529 0.101578 13.5652H6.26081H6.26086V22.5409C7.23098 23.0702 8.28234 23.4689 9.39131 23.7147V13.5654V13.5653H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9472 23.8984 10.4348Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx71("defs", { children: /* @__PURE__ */ jsx71("clipPath", { id: "cfFI_clip0_0_1", children: /* @__PURE__ */ jsx71("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FJ.tsx
import { jsx as jsx72, jsxs as jsxs70 } from "react/jsx-runtime";
var FlagFJ = (props) => /* @__PURE__ */ jsxs70(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs70("g", { clipPath: "url(#cfFJ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx72(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs70("g", { children: [
      /* @__PURE__ */ jsx72("path", { d: "M12 5.73914V9.78646L7.82611 5.73914H12Z", fill: "#0052B4" }),
      /* @__PURE__ */ jsx72(
        "path",
        {
          d: "M6.26085 12H9.78642L6.26085 7.30432L5.73914 10.9565L6.26085 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs70("g", { children: [
      /* @__PURE__ */ jsx72(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx72(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.3715 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78642 12H11.968C11.9787 11.9893 11.9894 11.9786 12.0001 11.9679C12.0001 11.1594 12.0001 10.4381 12.0001 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs70("g", { children: [
      /* @__PURE__ */ jsx72(
        "path",
        {
          d: "M6.071 1.56519C4.19417 2.63389 2.63389 4.19417 1.56519 6.071V12H4.69564V4.69573V4.69564H12C12 3.70831 12 2.76772 12 1.56519H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx72(
        "path",
        {
          d: "M12 10.5243L7.73654 6.26089H6.26086V6.26099L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx72(
      "path",
      {
        d: "M13.5652 7.82614V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V7.82614L17.2174 6.78265L13.5652 7.82614Z",
        fill: "#F3F3F3"
      }
    ),
    /* @__PURE__ */ jsx72(
      "path",
      {
        d: "M20.8695 7.82613V6.26093H13.5652V7.82613H16.6955V10.9566H13.5652V12V12.0001L16.6955 12.0002V15.4922C17.0105 15.6036 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 17.4242 15.6037 17.739 15.4922V12.0001H20.8695V12V10.9566H17.739V7.82608H20.8695V7.82613Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx72("defs", { children: /* @__PURE__ */ jsx72("clipPath", { id: "cfFJ_clip0_0_1", children: /* @__PURE__ */ jsx72("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FK.tsx
import { jsx as jsx73, jsxs as jsxs71 } from "react/jsx-runtime";
var FlagFK = (props) => /* @__PURE__ */ jsxs71(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs71("g", { clipPath: "url(#cfFK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx73(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs71("g", { children: [
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.3715 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78642 12H11.9679L12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs71("g", { children: [
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M6.071 1.56522C4.19417 2.63392 2.63389 4.1942 1.56519 6.07103V12H4.69564V4.69576V4.69567H12C12 3.70834 12 2.76775 12 1.56522H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M12 10.5243L7.73657 6.26093H6.26089V6.26102L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx73(
      "path",
      {
        d: "M19.3043 14.087V14.6087H15.1304V14.087H13.5652V16.1739H14.6087V16.6957H19.8261V16.1739H20.8695V14.087H19.3043Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx73(
      "path",
      {
        d: "M13.5652 6.26086V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V6.26086H13.5652Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs71("g", { children: [
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M17.2172 10.5417C16.3042 10.5417 16.3042 11.3765 15.3912 11.3765C14.4782 11.3765 14.4782 10.5417 13.5652 10.5417V12.0025C14.4782 12.0025 14.4782 12.8373 15.3912 12.8373C16.3042 12.8373 16.3042 12.0025 17.2172 12.0025C18.1302 12.0025 18.1302 12.8373 19.0434 12.8373C19.9565 12.8373 19.9564 12.0025 20.8696 12.0025V10.5417C19.9565 10.5417 19.9565 11.3765 19.0434 11.3765C18.1302 11.3765 18.1302 10.5417 17.2172 10.5417Z",
          fill: "#F3F3F3"
        }
      ),
      /* @__PURE__ */ jsx73(
        "path",
        {
          d: "M17.2172 7.61737C16.3042 7.61737 16.3042 8.45217 15.3912 8.45217C14.4782 8.45217 14.4782 7.61737 13.5652 7.61737V9.07823C14.4782 9.07823 14.4782 9.91303 15.3912 9.91303C16.3042 9.91303 16.3042 9.07823 17.2172 9.07823C18.1302 9.07823 18.1302 9.91303 19.0434 9.91303C19.9565 9.91303 19.9564 9.07823 20.8696 9.07823V7.61737C19.9565 7.61737 19.9565 8.45217 19.0434 8.45217C18.1302 8.45217 18.1302 7.61737 17.2172 7.61737Z",
          fill: "#F3F3F3"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx73("defs", { children: /* @__PURE__ */ jsx73("clipPath", { id: "cfFK_clip0_0_1", children: /* @__PURE__ */ jsx73("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FM.tsx
import { jsx as jsx74, jsxs as jsxs72 } from "react/jsx-runtime";
var FlagFM = (props) => /* @__PURE__ */ jsxs72(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs72("g", { clipPath: "url(#cfFM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx74(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs72("g", { children: [
      /* @__PURE__ */ jsx74(
        "path",
        {
          d: "M12 5.21738L12.518 6.81169H14.1944L12.8382 7.79705L13.3562 9.39131L12 8.406L10.6438 9.39131L11.1619 7.79705L9.80566 6.81169H11.4821L12 5.21738Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx74(
        "path",
        {
          d: "M5.21741 12.0001L6.81172 11.482V9.8056L7.79708 11.1619L9.39135 10.6439L8.40603 12.0001L9.39135 13.3562L7.79708 12.8381L6.81172 14.1944V12.518L5.21741 12.0001Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx74(
        "path",
        {
          d: "M12.0001 18.7826L11.4821 17.1883H9.80566L11.1619 16.203L10.6439 14.6087L12.0001 15.594L13.3563 14.6087L12.8382 16.203L14.1944 17.1883H12.5181L12.0001 18.7826Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx74(
        "path",
        {
          d: "M18.7826 12L17.1883 12.518V14.1944L16.203 12.8381L14.6087 13.3561L15.594 12L14.6087 10.6438L16.203 11.1619L17.1883 9.8056V11.482L18.7826 12Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx74("defs", { children: /* @__PURE__ */ jsx74("clipPath", { id: "cfFM_clip0_0_1", children: /* @__PURE__ */ jsx74("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FO.tsx
import { jsx as jsx75, jsxs as jsxs73 } from "react/jsx-runtime";
var FlagFO = (props) => /* @__PURE__ */ jsxs73(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs73("g", { clipPath: "url(#cfFO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx75(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx75(
      "path",
      {
        d: "M23.4783 12L23.8982 10.4348C23.8285 9.90131 23.7238 9.37875 23.5866 8.86959H10.9565V0.0456543C10.424 0.091498 9.90157 0.172545 9.39129 0.285701L7.30432 1.5652L6.26084 1.45987C5.7117 1.7595 5.18853 2.10075 4.69564 2.47945V8.86954H0.413371C0.276167 9.3787 0.171542 9.90126 0.101792 10.4347L0.521699 12L0.101746 13.5652C0.171496 14.0987 0.276121 14.6212 0.413324 15.1304H4.69559V21.5205C5.18848 21.8992 5.7117 22.2405 6.26079 22.5401L7.826 22.4347L9.3912 23.7142C9.90153 23.8274 10.4239 23.9084 10.9564 23.9543V15.1305H23.5865C23.7237 14.6213 23.8283 14.0987 23.8981 13.5652L23.4783 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx75(
      "path",
      {
        d: "M23.8984 10.4348H9.39136H9.39131V0.285248C8.28234 0.531154 7.23098 0.929732 6.26086 1.45909V10.4348H0.101578C0.034875 10.9472 0 11.4695 0 12C0 12.5305 0.034875 13.0529 0.101578 13.5652H6.26081H6.26086V22.5409C7.23098 23.0702 8.28234 23.4689 9.39131 23.7147V13.5654V13.5653H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9472 23.8984 10.4348Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx75("defs", { children: /* @__PURE__ */ jsx75("clipPath", { id: "cfFO_clip0_0_1", children: /* @__PURE__ */ jsx75("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/FR.tsx
import { jsx as jsx76, jsxs as jsxs74 } from "react/jsx-runtime";
var FlagFR = (props) => /* @__PURE__ */ jsxs74(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs74("g", { clipPath: "url(#cfFR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx76(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx76(
      "path",
      {
        d: "M24 12C24 6.84043 20.7435 2.44191 16.1739 0.746399V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx76(
      "path",
      {
        d: "M0 12C0 17.1596 3.25655 21.5581 7.82611 23.2536V0.746399C3.25655 2.44191 0 6.84043 0 12Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx76("defs", { children: /* @__PURE__ */ jsx76("clipPath", { id: "cfFR_clip0_0_1", children: /* @__PURE__ */ jsx76("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GA.tsx
import { jsx as jsx77, jsxs as jsxs75 } from "react/jsx-runtime";
var FlagGA = (props) => /* @__PURE__ */ jsxs75(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs75("g", { clipPath: "url(#cfGA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx77(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.874 24 13.4679 24 12C24 10.5322 23.736 9.12617 23.2536 7.82614L12 6.78265L0.746391 7.82614C0.264047 9.12617 0 10.5322 0 12C0 13.4679 0.264047 14.874 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx77(
      "path",
      {
        d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx77(
      "path",
      {
        d: "M12 0C6.84037 0 2.4419 3.2565 0.746338 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx77("defs", { children: /* @__PURE__ */ jsx77("clipPath", { id: "cfGA_clip0_0_1", children: /* @__PURE__ */ jsx77("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GB.tsx
import { jsx as jsx78, jsxs as jsxs76 } from "react/jsx-runtime";
var FlagGB = (props) => /* @__PURE__ */ jsxs76(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs76("g", { clipPath: "url(#cfGB_clip0_0_1)", children: [
    /* @__PURE__ */ jsx78(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs76("g", { children: [
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M2.48059 4.69415C1.53799 5.92054 0.82722 7.33406 0.413361 8.86964H6.65608L2.48059 4.69415Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M23.5866 8.86965C23.1727 7.33412 22.4619 5.9206 21.5193 4.69421L17.3439 8.86965H23.5866Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M0.413361 15.1305C0.827267 16.666 1.53803 18.0795 2.48059 19.3059L6.65594 15.1305H0.413361Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M19.3058 2.48067C18.0794 1.53806 16.6659 0.827297 15.1304 0.413391V6.65606L19.3058 2.48067Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M4.69409 21.5193C5.92048 22.4619 7.334 23.1727 8.86953 23.5866V17.344L4.69409 21.5193Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M8.86948 0.413391C7.33395 0.827297 5.92044 1.53806 4.69409 2.48063L8.86948 6.65602V0.413391Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M15.1305 23.5866C16.666 23.1727 18.0795 22.4619 19.3059 21.5194L15.1305 17.344V23.5866Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M17.3439 15.1305L21.5193 19.3059C22.4619 18.0796 23.1727 16.666 23.5866 15.1305H17.3439Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs76("g", { children: [
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M23.8984 10.4348H13.5653H13.5653V0.101578C13.0529 0.034875 12.5305 0 12 0C11.4694 0 10.9471 0.034875 10.4348 0.101578V10.4347V10.4347H0.101578C0.034875 10.9471 0 11.4695 0 12C0 12.5306 0.034875 13.0529 0.101578 13.5652H10.4347H10.4347V23.8984C10.9471 23.9651 11.4694 24 12 24C12.5305 24 13.0529 23.9652 13.5652 23.8984V13.5653V13.5653H23.8984C23.9651 13.0529 24 12.5306 24 12C24 11.4695 23.9651 10.9471 23.8984 10.4348Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M15.1305 15.1305L20.4853 20.4853C20.7316 20.2391 20.9665 19.9817 21.1906 19.7149L16.6062 15.1304H15.1305V15.1305Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M8.86957 15.1305H8.86947L3.51471 20.4853C3.7609 20.7315 4.01829 20.9665 4.2851 21.1906L8.86957 16.6061V15.1305Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M8.86957 8.86966V8.86957L3.51476 3.51471C3.26848 3.7609 3.03354 4.01829 2.80939 4.2851L7.3939 8.86962H8.86957V8.86966Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx78(
        "path",
        {
          d: "M15.1305 8.86962L20.4853 3.51472C20.2391 3.26843 19.9817 3.0335 19.7149 2.80939L15.1305 7.3939V8.86962Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx78("defs", { children: /* @__PURE__ */ jsx78("clipPath", { id: "cfGB_clip0_0_1", children: /* @__PURE__ */ jsx78("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GD.tsx
import { jsx as jsx79, jsxs as jsxs77 } from "react/jsx-runtime";
var FlagGD = (props) => /* @__PURE__ */ jsxs77(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs77("g", { clipPath: "url(#cfGD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx79(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx79(
      "path",
      {
        d: "M5.23657 2.08697C7.16243 0.770437 9.49109 0 12 0C14.509 0 16.8376 0.770437 18.7635 2.08697L12 2.60869L5.23657 2.08697Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx79("path", { d: "M12 12L11.2342 11.2342L11.0308 11.5984L12 12Z", fill: "#0052B4" }),
    /* @__PURE__ */ jsxs77("g", { children: [
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M3.51471 3.51471C-1.17157 8.20099 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51471Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M20.4853 3.51471C25.1716 8.20099 25.1716 15.799 20.4853 20.4853C18.5488 18.5488 16.6863 16.6863 12 12L20.4853 3.51471Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx79(
      "path",
      {
        d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs77("g", { children: [
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M12 7.82611L12.9355 10.7056H15.9636L13.514 12.4856L14.4495 15.3652L12 13.5855L9.55027 15.3652L10.486 12.4856L8.03644 10.7056H11.0643L12 7.82611Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M12 0C11.9986 0 11.9973 9.375e-05 11.996 9.375e-05H12.004C12.0027 9.375e-05 12.0014 0 12 0Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs77("g", { children: [
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M12.004 0H11.996C8.68413 0.00107812 5.68596 1.34372 3.51532 3.51413H20.4847C18.314 1.34372 15.3158 0.00107812 12.004 0Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M3.51538 20.4859C5.68691 22.6571 8.68663 24 12 24C15.3133 24 18.3131 22.6571 20.4846 20.4859H3.51538Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs77("g", { children: [
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M12 1.30435L12.1943 1.9022H12.8229L12.3144 2.27171L12.5087 2.86955L12 2.50009L11.4915 2.86955L11.6858 2.27171L11.1771 1.9022H11.8057L12 1.30435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M9.09016 1.30435L9.28446 1.9022H9.91305L9.40451 2.27171L9.5988 2.86955L9.09016 2.50009L8.58166 2.86955L8.77591 2.27171L8.26727 1.9022H8.89587L9.09016 1.30435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M14.9099 1.30435L15.1042 1.9022H15.7328L15.2242 2.27171L15.4185 2.86955L14.9099 2.50009L14.4013 2.86955L14.5956 2.27171L14.087 1.9022H14.7156L14.9099 1.30435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M12 21.1305L12.1943 21.7283H12.8229L12.3144 22.0978L12.5087 22.6957L12 22.3261L11.4915 22.6957L11.6858 22.0978L11.1771 21.7283H11.8057L12 21.1305Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M9.09016 21.1305L9.28446 21.7283H9.91305L9.40451 22.0978L9.5988 22.6957L9.09016 22.3261L8.58166 22.6957L8.77591 22.0978L8.26727 21.7283H8.89587L9.09016 21.1305Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M14.9099 21.1305L15.1042 21.7283H15.7328L15.2242 22.0978L15.4185 22.6957L14.9099 22.3261L14.4013 22.6957L14.5956 22.0978L14.087 21.7283H14.7156L14.9099 21.1305Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx79(
        "path",
        {
          d: "M3.65639 11.8018C3.96459 12.2888 3.81975 12.9334 3.33277 13.2417C2.84583 13.5499 2.2012 13.4051 1.89295 12.9181C1.33491 12.0364 1.65848 10.5966 1.65848 10.5966C1.65848 10.5966 3.0983 10.9202 3.65639 11.8018Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx79(
      "path",
      {
        d: "M3.0537 13.3226C3.34184 13.3226 3.57542 13.089 3.57542 12.8009C3.57542 12.5127 3.34184 12.2791 3.0537 12.2791C2.76556 12.2791 2.53198 12.5127 2.53198 12.8009C2.53198 13.089 2.76556 13.3226 3.0537 13.3226Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx79("defs", { children: /* @__PURE__ */ jsx79("clipPath", { id: "cfGD_clip0_0_1", children: /* @__PURE__ */ jsx79("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GE.tsx
import { jsx as jsx80, jsxs as jsxs78 } from "react/jsx-runtime";
var FlagGE = (props) => /* @__PURE__ */ jsxs78(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs78("g", { clipPath: "url(#cfGE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx80(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs78("g", { children: [
      /* @__PURE__ */ jsx80(
        "path",
        {
          d: "M23.8984 10.4348H13.5653H13.5653V0.101578C13.0529 0.034875 12.5305 0 12 0C11.4695 0 10.9471 0.034875 10.4348 0.101578V10.4347V10.4347H0.101578C0.034875 10.9471 0 11.4695 0 12C0 12.5306 0.034875 13.0529 0.101578 13.5652H10.4347H10.4347V23.8984C10.9471 23.9651 11.4695 24 12 24C12.5305 24 13.0529 23.9652 13.5652 23.8984V13.5653V13.5653H23.8984C23.9651 13.0529 24 12.5306 24 12C24 11.4695 23.9651 10.9471 23.8984 10.4348Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx80(
        "path",
        {
          d: "M7.30434 5.73914V4.17389H5.73914V5.73914H4.17389V7.30434H5.73914V8.86954H7.30434V7.30434H8.86954V5.73914H7.30434Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx80(
        "path",
        {
          d: "M18.2609 5.73914V4.17389H16.6957V5.73914H15.1305V7.30434H16.6957V8.86954H18.2609V7.30434H19.8261V5.73914H18.2609Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx80(
        "path",
        {
          d: "M7.30434 16.6957V15.1305H5.73914V16.6957H4.17389V18.2609H5.73914V19.8261H7.30434V18.2609H8.86954V16.6957H7.30434Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx80(
        "path",
        {
          d: "M18.2609 16.6957V15.1305H16.6957V16.6957H15.1305V18.2609H16.6957V19.8261H18.2609V18.2609H19.8261V16.6957H18.2609Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx80("defs", { children: /* @__PURE__ */ jsx80("clipPath", { id: "cfGE_clip0_0_1", children: /* @__PURE__ */ jsx80("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GG.tsx
import { jsx as jsx81, jsxs as jsxs79 } from "react/jsx-runtime";
var FlagGG = (props) => /* @__PURE__ */ jsxs79(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs79("g", { clipPath: "url(#cfGG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx81(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx81(
      "path",
      {
        d: "M23.8984 10.4348H13.5653H13.5653V0.101578C13.0529 0.034875 12.5305 0 12 0C11.4695 0 10.9471 0.034875 10.4348 0.101578V10.4348H0.101578C0.034875 10.9471 0 11.4695 0 12C0 12.5305 0.034875 13.0529 0.101578 13.5652H10.4347H10.4347V23.8984C10.9471 23.9651 11.4695 24 12 24C12.5305 24 13.0529 23.9652 13.5652 23.8984V13.5653V13.5653H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9471 23.8984 10.4348Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx81(
      "path",
      {
        d: "M15.3913 12.5217L16.1739 13.0435V10.9565L15.3913 11.4783H12.5217V8.60869L13.0435 7.82611H10.9565L11.4783 8.60869V11.4783H8.60869L7.82611 10.9565V13.0435L8.60869 12.5217H11.4783V15.3913L10.9565 16.1739H13.0435L12.5217 15.3913V12.5217H15.3913Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx81("defs", { children: /* @__PURE__ */ jsx81("clipPath", { id: "cfGG_clip0_0_1", children: /* @__PURE__ */ jsx81("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GH.tsx
import { jsx as jsx82, jsxs as jsxs80 } from "react/jsx-runtime";
var FlagGH = (props) => /* @__PURE__ */ jsxs80(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs80("g", { clipPath: "url(#cfGH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx82(
      "path",
      {
        d: "M0 12C0 13.4679 0.264047 14.8739 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.8739 24 13.4679 24 12C24 10.5322 23.736 9.1261 23.2536 7.82612L12 7.30435L0.746391 7.82607C0.264047 9.1261 0 10.5322 0 12H0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx82(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx82(
      "path",
      {
        d: "M23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx82(
      "path",
      {
        d: "M11.9999 7.82611L13.0358 11.0145H16.3887L13.6764 12.9853L14.7123 16.1739L11.9999 14.2033L9.28747 16.1739L10.3236 12.9853L7.61127 11.0145H10.964L11.9999 7.82611Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx82("defs", { children: /* @__PURE__ */ jsx82("clipPath", { id: "cfGH_clip0_0_1", children: /* @__PURE__ */ jsx82("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GI.tsx
import { jsx as jsx83, jsxs as jsxs81 } from "react/jsx-runtime";
var FlagGI = (props) => /* @__PURE__ */ jsxs81(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs81("g", { clipPath: "url(#cfGI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx83(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F9F9F9"
      }
    ),
    /* @__PURE__ */ jsxs81("g", { children: [
      /* @__PURE__ */ jsx83(
        "path",
        {
          d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx83(
        "path",
        {
          d: "M16.6957 9.91301V8.34781H17.2174V7.30433H16.1739V7.82609H15.1305V7.30433H14.087V8.34781H14.6087V9.91301H13.5652V6.26094H14.087V5.2175H13.0435V5.73912H12.5217V5.2175H11.4783V5.73912H10.9565V5.2175H9.91304V6.26094H10.4348V9.91301H9.39132V8.34781H9.91304V7.30433H8.86955V7.82609H7.82611V7.30433H6.78263V8.34781H7.30435V9.91301H6.26086V14.087H17.7391V9.91301H16.6957Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx83(
      "path",
      {
        d: "M12 13.5652C11.1356 13.5652 10.4348 14.266 10.4348 15.1304C10.4348 15.8118 10.8705 16.3913 11.4783 16.6062V18.7826H9.91309V20.8696H12.5218V16.6062C13.1296 16.3914 13.5653 15.8119 13.5653 15.1304C13.5652 14.266 12.8644 13.5652 12 13.5652ZM12 15.6522C11.7119 15.6522 11.4783 15.4185 11.4783 15.1304C11.4783 14.8423 11.7119 14.6087 12 14.6087C12.2881 14.6087 12.5217 14.8423 12.5217 15.1304C12.5217 15.4185 12.2881 15.6522 12 15.6522Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx83("defs", { children: /* @__PURE__ */ jsx83("clipPath", { id: "cfGI_clip0_0_1", children: /* @__PURE__ */ jsx83("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GL.tsx
import { jsx as jsx84, jsxs as jsxs82 } from "react/jsx-runtime";
var FlagGL = (props) => /* @__PURE__ */ jsxs82(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs82("g", { clipPath: "url(#cfGL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx84(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12C23.4783 12 12 13.5652 12 13.5652L0 12Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx84(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx84(
      "path",
      {
        d: "M8.34784 17.7391C11.5175 17.7391 14.087 15.1696 14.087 12C14.087 8.83037 11.5175 6.26086 8.34784 6.26086C5.1782 6.26086 2.6087 8.83037 2.6087 12C2.6087 15.1696 5.1782 17.7391 8.34784 17.7391Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx84(
      "path",
      {
        d: "M2.6087 12C2.6087 8.83046 5.1782 6.26086 8.34784 6.26086C11.5175 6.26086 14.087 8.83041 14.087 12",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx84("defs", { children: /* @__PURE__ */ jsx84("clipPath", { id: "cfGL_clip0_0_1", children: /* @__PURE__ */ jsx84("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GM.tsx
import { jsx as jsx85, jsxs as jsxs83 } from "react/jsx-runtime";
var FlagGM = (props) => /* @__PURE__ */ jsxs83(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs83("g", { clipPath: "url(#cfGM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx85(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx85(
      "path",
      {
        d: "M12 0C7.03898 0 2.78142 3.01059 0.953812 7.30434H23.0462C21.2186 3.01059 16.961 0 12 0Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx85(
      "path",
      {
        d: "M12 24C16.961 24 21.2186 20.9894 23.0462 16.6956H0.953812C2.78142 20.9894 7.03898 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx85(
      "path",
      {
        d: "M23.5866 8.86954H0.413391C0.144375 9.86765 0 10.9169 0 12C0 13.0831 0.144375 14.1323 0.413391 15.1304H23.5867C23.8556 14.1323 24 13.0831 24 12C24 10.9169 23.8556 9.86765 23.5866 8.86954Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx85("defs", { children: /* @__PURE__ */ jsx85("clipPath", { id: "cfGM_clip0_0_1", children: /* @__PURE__ */ jsx85("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GN.tsx
import { jsx as jsx86, jsxs as jsxs84 } from "react/jsx-runtime";
var FlagGN = (props) => /* @__PURE__ */ jsxs84(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs84("g", { clipPath: "url(#cfGN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx86(
      "path",
      {
        d: "M16.174 0.746437C14.874 0.264047 13.4679 0 12 0C10.5322 0 9.12614 0.264047 7.8262 0.746344L7.30444 12L7.82615 23.2537C9.12614 23.736 10.5322 24 12 24C13.4679 24 14.874 23.736 16.174 23.2536L16.6957 12L16.174 0.746437Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx86(
      "path",
      {
        d: "M0 12C0 17.1595 3.2565 21.5581 7.82611 23.2536V0.746338C3.2565 2.4419 0 6.84037 0 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx86(
      "path",
      {
        d: "M24 12C24 6.84037 20.7435 2.4419 16.1739 0.746338V23.2536C20.7435 21.5581 24 17.1595 24 12Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx86("defs", { children: /* @__PURE__ */ jsx86("clipPath", { id: "cfGN_clip0_0_1", children: /* @__PURE__ */ jsx86("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GQ.tsx
import { jsx as jsx87, jsxs as jsxs85 } from "react/jsx-runtime";
var FlagGQ = (props) => /* @__PURE__ */ jsxs85(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs85("g", { clipPath: "url(#cfGQ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M7.30437 7.82611H23.2536C21.5581 3.2565 17.1596 0 12 0C8.68625 0 5.68657 1.34339 3.51514 3.51511L7.30437 7.82611Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M7.30437 16.1739H23.2536C21.5581 20.7435 17.1596 24 12 24C8.68625 24 5.68657 22.6566 3.51514 20.4849L7.30437 16.1739Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M3.51471 3.51468C-1.17157 8.20096 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51468Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M14.087 9.91302V12.5217C14.087 14.1192 16.1739 14.6087 16.1739 14.6087C16.1739 14.6087 18.2609 14.1192 18.2609 12.5217V9.91302H14.087Z",
        fill: "#DEDDE0"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M15.6522 12.0522H16.6956V13.5652H15.6522V12.0522Z",
        fill: "#786145"
      }
    ),
    /* @__PURE__ */ jsx87(
      "path",
      {
        d: "M17.2174 11.4783C17.2174 10.9019 16.7502 10.4348 16.1739 10.4348C15.5976 10.4348 15.1304 10.9019 15.1304 11.4783C14.8423 11.4783 14.6087 11.7118 14.6087 12C14.6087 12.2881 14.8423 12.5217 15.1304 12.5217H17.2174C17.5055 12.5217 17.7391 12.2881 17.7391 12C17.7391 11.7118 17.5056 11.4783 17.2174 11.4783Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx87("defs", { children: /* @__PURE__ */ jsx87("clipPath", { id: "cfGQ_clip0_0_1", children: /* @__PURE__ */ jsx87("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GR.tsx
import { jsx as jsx88, jsxs as jsxs86 } from "react/jsx-runtime";
var FlagGR = (props) => /* @__PURE__ */ jsxs86(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs86("g", { clipPath: "url(#cfGR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx88(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs86("g", { children: [
      /* @__PURE__ */ jsx88(
        "path",
        {
          d: "M12 8.86953H23.5866C23.2857 7.75325 22.8284 6.70132 22.2387 5.73907H12V8.86953Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx88(
        "path",
        {
          d: "M4.53015 21.3913H19.4698C20.565 20.5191 21.5042 19.4596 22.2387 18.2609H1.76129C2.49587 19.4596 3.43506 20.5191 4.53015 21.3913Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx88(
        "path",
        {
          d: "M4.1739 2.90341C3.23049 3.7158 2.41492 4.67247 1.76129 5.73916H4.1739V2.90341Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx88(
        "path",
        {
          d: "M12 12C12 10.7777 12 9.75472 12 8.86954H7.30434V12H4.17389V8.86954H0.413391C0.144375 9.86769 0 10.9169 0 12C0 13.0831 0.144375 14.1323 0.413391 15.1304H23.5867C23.8556 14.1323 24 13.0831 24 12H12Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx88(
        "path",
        {
          d: "M12 0C10.3336 0 8.74667 0.339984 7.30432 0.953812V5.73914H12C12 4.64639 12 3.717 12 2.60869H19.4698C17.4199 0.976125 14.8241 0 12 0Z",
          fill: "#338AF3"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx88("defs", { children: /* @__PURE__ */ jsx88("clipPath", { id: "cfGR_clip0_0_1", children: /* @__PURE__ */ jsx88("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GT.tsx
import { jsx as jsx89, jsxs as jsxs87 } from "react/jsx-runtime";
var FlagGT = (props) => /* @__PURE__ */ jsxs87(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs87("g", { clipPath: "url(#cfGT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx89(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs87("g", { children: [
      /* @__PURE__ */ jsx89(
        "path",
        {
          d: "M24 12C24 7.24248 21.2313 3.13173 17.2173 1.19073V22.8092C21.2313 20.8683 24 16.7575 24 12Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx89(
        "path",
        {
          d: "M0 12C0 16.7575 2.76867 20.8683 6.78263 22.8093V1.19073C2.76867 3.13173 0 7.24248 0 12Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx89(
      "path",
      {
        d: "M15.1359 14.0291L13.1068 12.0001L15.0382 10.0686L14.9507 9.04851L14.3978 8.49548L12 10.8932L9.60228 8.49548L9.04934 9.04851L8.96182 10.0686L10.8932 12.0001L8.86414 14.0291L9.97095 15.1358L12 13.1069L14.0291 15.1358L15.1359 14.0291Z",
        fill: "#ACABB1"
      }
    ),
    /* @__PURE__ */ jsx89(
      "path",
      {
        d: "M14.9514 9.04861L13.8446 10.1554C14.3167 10.6275 14.6087 11.2796 14.6087 12C14.6087 13.4408 13.4408 14.6087 12 14.6087C10.5593 14.6087 9.39131 13.4408 9.39131 12C9.39131 11.2796 9.68335 10.6275 10.1554 10.1554L9.04866 9.04861C8.29327 9.80386 7.82611 10.8473 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 10.8474 15.7067 9.80391 14.9514 9.04861Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx89("defs", { children: /* @__PURE__ */ jsx89("clipPath", { id: "cfGT_clip0_0_1", children: /* @__PURE__ */ jsx89("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GU.tsx
import { jsx as jsx90, jsxs as jsxs88 } from "react/jsx-runtime";
var FlagGU = (props) => /* @__PURE__ */ jsxs88(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs88("g", { clipPath: "url(#cfGU_clip0_0_1)", children: [
    /* @__PURE__ */ jsxs88("g", { children: [
      /* @__PURE__ */ jsx90(
        "path",
        {
          d: "M5.23657 21.913C7.16238 23.2295 9.49114 24 12 24C14.5089 24 16.8376 23.2295 18.7635 21.913L12 20.8695L5.23657 21.913Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx90(
        "path",
        {
          d: "M18.7635 2.08697C16.8376 0.770484 14.5089 0 12 0C9.49114 0 7.16238 0.770484 5.23657 2.08697L12 3.13045L18.7635 2.08697Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M24 12C24 8.02922 21.6227 4.04157 18.7635 2.08698H5.23655C2.07516 4.2481 0 7.88157 0 12C0 16.1186 2.07516 19.7519 5.23655 21.913H18.7635C21.9249 19.7519 24 16.1186 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M11.3209 19.5749C11.1569 19.4343 7.30432 16.0851 7.30432 12C7.30432 7.91492 11.1569 4.56575 11.3209 4.42517L12 3.84308L12.6791 4.42513C12.843 4.56575 16.6956 7.91488 16.6956 12C16.6956 16.0852 12.8431 19.4344 12.6791 19.5749L12 20.157L11.3209 19.5749Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M15.6522 12C15.6522 8.3478 12 5.21735 12 5.21735C12 5.21735 8.34784 8.34789 8.34784 12C8.34784 13.1172 8.68989 14.1855 9.16435 15.1304H14.8357C15.3101 14.1855 15.6522 13.1172 15.6522 12Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M15.6522 12C15.6522 13.1173 15.3101 14.1855 14.8357 15.1305L12 15.6522L9.16435 15.1305C8.68989 14.1855 8.34784 13.1173 8.34784 12H15.6522Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M12 18.7826C12 18.7826 13.7591 17.2743 14.8357 15.1305H9.16437C10.2409 17.2743 12 18.7826 12 18.7826Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M9.65216 9.91302H14.3478L12 12.2609L9.65216 9.91302Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx90(
      "path",
      {
        d: "M11.2173 11.4783H12.7825V16.1739H11.2173V11.4783Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx90("defs", { children: /* @__PURE__ */ jsx90("clipPath", { id: "cfGU_clip0_0_1", children: /* @__PURE__ */ jsx90("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GW.tsx
import { jsx as jsx91, jsxs as jsxs89 } from "react/jsx-runtime";
var FlagGW = (props) => /* @__PURE__ */ jsxs89(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs89("g", { clipPath: "url(#cfGW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx91(
      "path",
      {
        d: "M12 0C10.9169 0 9.86766 0.144375 8.86955 0.413391L7.82611 12L8.86959 23.5866C9.86766 23.8556 10.9169 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx91(
      "path",
      {
        d: "M7.82611 12L8.86959 23.5866C9.86766 23.8556 10.9169 24 12 24C18.6274 24 24 18.6274 24 12H7.82611Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs89("g", { children: [
      /* @__PURE__ */ jsx91(
        "path",
        {
          d: "M0 12C0 16.5485 2.53078 20.5057 6.26086 22.5409V1.45905C2.53078 3.49426 0 7.45145 0 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx91(
        "path",
        {
          d: "M0 12C0 17.5443 3.76036 22.2096 8.86955 23.5866V0.413391C3.76036 1.79039 0 6.45567 0 12Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx91(
      "path",
      {
        d: "M4.53448 8.86954L5.31143 11.2609H7.82609L5.79181 12.739L6.56872 15.1304L4.53448 13.6524L2.50015 15.1304L3.27725 12.739L1.24297 11.2609H3.75748L4.53448 8.86954Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx91("defs", { children: /* @__PURE__ */ jsx91("clipPath", { id: "cfGW_clip0_0_1", children: /* @__PURE__ */ jsx91("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/GY.tsx
import { jsx as jsx92, jsxs as jsxs90 } from "react/jsx-runtime";
var FlagGY = (props) => /* @__PURE__ */ jsxs90(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs90("g", { clipPath: "url(#cfGY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 5.37262 12 0 12 0C12 0 24 5.37262 24 12Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M16.1739 15.1304L12 14.6087L7.82611 15.1304V8.86954H16.1739V15.1304Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M12 14.6087C13.1526 14.6087 14.087 13.6743 14.087 12.5217C14.087 11.3691 13.1526 10.4348 12 10.4348C10.8474 10.4348 9.91302 11.3691 9.91302 12.5217C9.91302 13.6743 10.8474 14.6087 12 14.6087Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M12 13.5652C12.5763 13.5652 13.0435 13.098 13.0435 12.5217C13.0435 11.9454 12.5763 11.4782 12 11.4782C11.4237 11.4782 10.9565 11.9454 10.9565 12.5217C10.9565 13.098 11.4237 13.5652 12 13.5652Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M10.4348 9.91302H13.5652L12 11.4783L10.4348 9.91302Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M11.4783 10.9565H12.5218V14.087H11.4783V10.9565Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx92(
      "path",
      {
        d: "M13.6695 13.7739H10.3304L7.82611 15.1305H16.1739L13.6695 13.7739Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx92("defs", { children: /* @__PURE__ */ jsx92("clipPath", { id: "cfGY_clip0_0_1", children: /* @__PURE__ */ jsx92("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/HK.tsx
import { jsx as jsx93, jsxs as jsxs91 } from "react/jsx-runtime";
var FlagHK = (props) => /* @__PURE__ */ jsxs91(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs91("g", { clipPath: "url(#cfHK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx93(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs91("g", { children: [
      /* @__PURE__ */ jsx93(
        "path",
        {
          d: "M13.2389 9.07819C12.9662 10.2125 12.4816 9.99619 12.2439 10.9854C10.8431 10.6486 9.9804 9.24005 10.3172 7.83919C10.6539 6.43842 12.0626 5.57573 13.4634 5.91253C12.9878 7.89099 13.4829 8.06367 13.2389 9.07819Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx93(
        "path",
        {
          d: "M9.60408 9.91874C10.5986 10.5286 10.2431 10.9227 11.1104 11.4546C10.3572 12.6827 8.75104 13.0678 7.52283 12.3147C6.29465 11.5615 5.90948 9.95525 6.66267 8.72708C8.39733 9.79077 8.71444 9.37325 9.60408 9.91874Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx93(
        "path",
        {
          d: "M9.28019 13.6356C10.1676 12.8782 10.4324 13.338 11.2062 12.6776C12.1416 13.7733 12.0115 15.42 10.9158 16.3554C9.81991 17.2908 8.17329 17.1605 7.23804 16.0648C8.78557 14.7437 8.48646 14.313 9.28019 13.6356Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx93(
        "path",
        {
          d: "M12.715 15.0921C12.2688 14.0141 12.7879 13.9043 12.3989 12.9643C13.7301 12.4133 15.256 13.0459 15.807 14.3771C16.3579 15.7083 15.7253 17.2341 14.3941 17.7851C13.6161 15.905 13.1141 16.0563 12.715 15.0921Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx93(
        "path",
        {
          d: "M15.1617 12.2754C13.9986 12.3667 14.0544 11.8389 13.0403 11.9185C12.9276 10.4823 14.0008 9.22645 15.4371 9.11371C16.8733 9.00126 18.1291 10.0743 18.2417 11.5107C16.2131 11.6697 16.2019 12.1939 15.1617 12.2754Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx93("defs", { children: /* @__PURE__ */ jsx93("clipPath", { id: "cfHK_clip0_0_1", children: /* @__PURE__ */ jsx93("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/HN.tsx
import { jsx as jsx94, jsxs as jsxs92 } from "react/jsx-runtime";
var FlagHN = (props) => /* @__PURE__ */ jsxs92(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs92("g", { clipPath: "url(#cfHN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx94(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs92("g", { children: [
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M7.38492 7.82611L7.77342 9.02185H9.03065L8.01356 9.76078L8.4021 10.9565L7.38492 10.2175L6.36773 10.9565L6.75628 9.76078L5.73914 9.02185H6.99637L7.38492 7.82611Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M7.38492 13.0435L7.77342 14.2392H9.03065L8.01356 14.9782L8.4021 16.1739L7.38492 15.4349L6.36773 16.1739L6.75628 14.9782L5.73914 14.2392H6.99637L7.38492 13.0435Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M16.6151 7.82611L17.0036 9.02185H18.2609L17.2437 9.76078L17.6323 10.9565L16.6151 10.2175L15.5979 10.9565L15.9864 9.76078L14.9693 9.02185H16.2266L16.6151 7.82611Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M16.6151 13.0435L17.0036 14.2392H18.2609L17.2437 14.9782L17.6323 16.1739L16.6151 15.4349L15.5979 16.1739L15.9864 14.9782L14.9693 14.2392H16.2266L16.6151 13.0435Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx94(
        "path",
        {
          d: "M12 10.4348L12.3886 11.6306H13.6458L12.6287 12.3695L13.0172 13.5652L12 12.8262L10.9828 13.5652L11.3714 12.3695L10.3542 11.6306H11.6115L12 10.4348Z",
          fill: "#338AF3"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx94("defs", { children: /* @__PURE__ */ jsx94("clipPath", { id: "cfHN_clip0_0_1", children: /* @__PURE__ */ jsx94("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/HR.tsx
import { jsx as jsx95, jsxs as jsxs93 } from "react/jsx-runtime";
var FlagHR = (props) => /* @__PURE__ */ jsxs93(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs93("g", { clipPath: "url(#cfHR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx95(
      "path",
      {
        d: "M24 12C24 10.5322 23.736 9.12605 23.2536 7.82612L12 7.30435L0.746391 7.82607C0.264047 9.12605 0 10.5322 0 12C0 13.4679 0.264047 14.874 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.874 24 13.4679 24 12Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx95(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx95(
      "path",
      {
        d: "M0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs93("g", { children: [
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M15.1304 8.34787H13.0435L13.3913 5.73913L14.7826 4.69565L16.1739 5.73913V7.8261L15.1304 8.34787Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M8.86955 8.34787H10.9565L10.6087 5.73913L9.21736 4.69565L7.82611 5.73913V7.8261L8.86955 8.34787Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx95(
      "path",
      {
        d: "M13.3914 8.34787H10.6087V5.73913L12 4.69565L13.3914 5.73913V8.34787Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx95(
      "path",
      {
        d: "M7.82611 7.82614V13.5652C7.82611 14.9318 8.4862 16.1471 9.50433 16.9089L10.2348 16.7262L11.1565 17.6534C11.4291 17.7096 11.7112 17.7392 12 17.7392C12.2874 17.7392 12.568 17.71 12.8392 17.6544L13.8914 16.7783L14.4957 16.9072C15.5134 16.1454 16.1739 14.9314 16.1739 13.5652V7.82614H7.82611Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs93("g", { children: [
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M7.82611 7.82611H9.49566V9.49566H7.82611V7.82611Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M11.1652 7.82611H12.8348V9.49566H11.1652V7.82611Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M14.5043 7.82611H16.1739V9.49566H14.5043V7.82611Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M9.49567 9.49567H11.1652V11.1652H9.49567V9.49567Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M12.8348 9.49567H14.5043V11.1652H12.8348V9.49567Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M7.82611 11.1647H9.49566V12.8343H7.82611V11.1647Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M9.49567 12.8343H11.1652V14.5038H9.49567V12.8343Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M11.1652 11.1647H12.8348V12.8343H11.1652V11.1647Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M14.5043 11.1647H16.1739V12.8343H14.5043V11.1647Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M12.8348 12.8343H14.5043V14.5038H12.8348V12.8343Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M11.1652 14.5038H12.8348V16.1734H11.1652V14.5038Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M9.49567 14.504H7.93384C8.07709 15.1246 8.35871 15.6925 8.74487 16.1735H9.49567V14.504Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M14.5043 16.1736H15.2551C15.6413 15.6925 15.9229 15.1246 16.0661 14.504H14.5043V16.1736Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M9.49567 16.1736V16.902C9.98124 17.2674 10.5487 17.5294 11.1652 17.6551V16.1736H9.49567Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx95(
        "path",
        {
          d: "M12.8348 16.1736V17.6552C13.4513 17.5295 14.0187 17.2675 14.5043 16.9021V16.1736H12.8348Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx95("defs", { children: /* @__PURE__ */ jsx95("clipPath", { id: "cfHR_clip0_0_1", children: /* @__PURE__ */ jsx95("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/HT.tsx
import { jsx as jsx96, jsxs as jsxs94 } from "react/jsx-runtime";
var FlagHT = (props) => /* @__PURE__ */ jsxs94(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs94("g", { clipPath: "url(#cfHT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M12 0C9.87837 0 7.88581 0.551344 6.15649 1.51744V22.4826C7.88585 23.4487 9.87837 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M6.19184 1.49771C5.43692 1.91607 4.73201 2.41332 4.088 2.97863C4.15311 2.92154 4.21887 2.8651 4.28515 2.80941L13.4757 12L4.28511 21.1906C4.21878 21.1349 4.15306 21.0785 4.08795 21.0214C4.73197 21.5867 5.43692 22.0839 6.1918 22.5023L24 12L6.19184 1.49771Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M5.17831 2.12729C4.81235 2.3807 4.46088 2.65337 4.12596 2.94479L12 12L4.12592 21.0552C4.46084 21.3466 4.81231 21.6194 5.17826 21.8727L21.9459 12L5.17831 2.12729Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M4.2851 2.80939C4.01833 3.03354 3.7609 3.26853 3.51471 3.51467L10.9565 12L3.51471 20.4853C3.7609 20.7315 4.01829 20.9665 4.2851 21.1906L13.4757 12L4.2851 2.80939Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx96(
      "path",
      {
        d: "M3.51471 3.51471C-1.17157 8.20099 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51471Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx96("defs", { children: /* @__PURE__ */ jsx96("clipPath", { id: "cfHT_clip0_0_1", children: /* @__PURE__ */ jsx96("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/HU.tsx
import { jsx as jsx97, jsxs as jsxs95 } from "react/jsx-runtime";
var FlagHU = (props) => /* @__PURE__ */ jsxs95(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs95("g", { clipPath: "url(#cfHU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx97(
      "path",
      {
        d: "M0.746391 7.82614C0.264047 9.12617 0 10.5322 0 12C0 13.4679 0.264047 14.874 0.746391 16.1739L12 17.2174L23.2536 16.1739C23.736 14.874 24 13.4679 24 12C24 10.5322 23.736 9.12617 23.2536 7.82614L12 6.78265L0.746391 7.82614Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx97(
      "path",
      {
        d: "M12 0C6.84049 0 2.44193 3.2565 0.74646 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx97(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2537 16.1739H0.74646C2.44193 20.7435 6.84049 24 12 24Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx97("defs", { children: /* @__PURE__ */ jsx97("clipPath", { id: "cfHU_clip0_0_1", children: /* @__PURE__ */ jsx97("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ID.tsx
import { jsx as jsx98, jsxs as jsxs96 } from "react/jsx-runtime";
var FlagID = (props) => /* @__PURE__ */ jsxs96(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs96("g", { clipPath: "url(#cfID_clip0_0_1)", children: [
    /* @__PURE__ */ jsx98(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx98(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx98("defs", { children: /* @__PURE__ */ jsx98("clipPath", { id: "cfID_clip0_0_1", children: /* @__PURE__ */ jsx98("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IE.tsx
import { jsx as jsx99, jsxs as jsxs97 } from "react/jsx-runtime";
var FlagIE = (props) => /* @__PURE__ */ jsxs97(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs97("g", { clipPath: "url(#cfIE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx99(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx99(
      "path",
      {
        d: "M24 12C24 6.84046 20.7435 2.4419 16.1739 0.746429V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx99(
      "path",
      {
        d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746429C3.2565 2.4419 0 6.84046 0 12Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx99("defs", { children: /* @__PURE__ */ jsx99("clipPath", { id: "cfIE_clip0_0_1", children: /* @__PURE__ */ jsx99("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IL.tsx
import { jsx as jsx100, jsxs as jsxs98 } from "react/jsx-runtime";
var FlagIL = (props) => /* @__PURE__ */ jsxs98(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs98("g", { clipPath: "url(#cfIL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx100(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs98("g", { children: [
      /* @__PURE__ */ jsx100(
        "path",
        {
          d: "M16.5184 9.39134H13.5061L12 6.78265L10.4939 9.39134H7.48157L8.98785 12L7.48157 14.6087H10.4939L12 17.2174L13.5061 14.6087H16.5184L15.0121 12L16.5184 9.39134ZM13.8504 12L12.9253 13.6026H11.0749L10.1496 12L11.0748 10.3975H12.9252L13.8504 12ZM12 8.79486L12.3443 9.39129H11.6557L12 8.79486ZM9.22433 10.3975H9.91302L9.56868 10.9939L9.22433 10.3975ZM9.22433 13.6026L9.56872 13.0062L9.91307 13.6026H9.22433ZM12 15.2052L11.6557 14.6087H12.3443L12 15.2052ZM14.7756 13.6026H14.087L14.4313 13.0062L14.7756 13.6026ZM14.087 10.3975H14.7756L14.4313 10.9939L14.087 10.3975Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx100(
        "path",
        {
          d: "M19.4699 2.6087H4.53015C3.43501 3.48095 2.49582 4.54042 1.76129 5.73916H22.2387C21.5041 4.54047 20.565 3.48095 19.4699 2.6087Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx100(
        "path",
        {
          d: "M4.53015 21.3913H19.4698C20.565 20.5191 21.5042 19.4596 22.2387 18.2609H1.76129C2.49587 19.4596 3.43506 20.5191 4.53015 21.3913Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx100("defs", { children: /* @__PURE__ */ jsx100("clipPath", { id: "cfIL_clip0_0_1", children: /* @__PURE__ */ jsx100("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IM.tsx
import { jsx as jsx101, jsxs as jsxs99 } from "react/jsx-runtime";
var FlagIM = (props) => /* @__PURE__ */ jsxs99(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs99("g", { clipPath: "url(#cfIM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx101(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx101(
      "path",
      {
        d: "M16.4431 8.04431L15.595 11.0711L13.0501 10.6012L11.412 7.22452L6.98672 8.79488L6.63773 7.81144L5.47992 7.6688L6.35231 10.1273L9.39773 9.34852L10.2632 11.7874L8.15793 14.8943L11.7306 17.9415L11.0535 18.7354L11.5088 19.8095L13.2018 17.8246L11.0046 15.5767L12.684 13.6078L16.4273 13.8775L17.28 9.25983L18.3061 9.44935L19.0087 8.51794L16.4431 8.04431Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx101("defs", { children: /* @__PURE__ */ jsx101("clipPath", { id: "cfIM_clip0_0_1", children: /* @__PURE__ */ jsx101("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IN.tsx
import { jsx as jsx102, jsxs as jsxs100 } from "react/jsx-runtime";
var FlagIN = (props) => /* @__PURE__ */ jsxs100(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs100("g", { clipPath: "url(#cfIN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 14.6087C13.4407 14.6087 14.6087 13.4407 14.6087 12C14.6087 10.5592 13.4407 9.3913 12 9.3913C10.5592 9.3913 9.3913 10.5592 9.3913 12C9.3913 13.4407 10.5592 14.6087 12 14.6087Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx102(
      "path",
      {
        d: "M12 8.78091L12.8048 10.6061L14.7878 10.3905L13.6096 12L14.7878 13.6096L12.8048 13.3939L12 15.2191L11.1952 13.3939L9.21222 13.6095L10.3905 12L9.21222 10.3905L11.1952 10.6061L12 8.78091Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx102("defs", { children: /* @__PURE__ */ jsx102("clipPath", { id: "cfIN_clip0_0_1", children: /* @__PURE__ */ jsx102("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IO.tsx
import { jsx as jsx103, jsxs as jsxs101 } from "react/jsx-runtime";
var FlagIO = (props) => /* @__PURE__ */ jsxs101(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs101("g", { clipPath: "url(#cfIO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs101("g", { children: [
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M21.9998 13.4361C20.9999 13.4361 20.9999 12.5218 19.9998 12.5218C18.9998 12.5218 18.9998 13.4361 17.9999 13.4361C17 13.4361 16.9999 12.5218 16 12.5218C15 12.5218 15 13.4361 14 13.4361C12.9999 13.4361 12.9999 12.5218 11.9998 12.5218C10.9998 12.5218 10.9998 13.4361 9.99989 13.4361C9.00005 13.4361 9.00005 12.5218 8.00006 12.5218C6.99999 12.5218 6.99998 13.4361 5.99991 13.4361C4.99983 13.4361 4.99983 12.5218 3.99975 12.5218C2.99991 12.5218 2.99991 13.4361 1.99992 13.4361C1.00533 13.4361 0.999563 12.5318 0.0156097 12.5222C0.0433129 13.071 0.108844 13.61 0.207938 14.137C1.005 14.254 1.0748 15.036 2.00002 15.036C3 15.036 3 14.1218 3.99984 14.1218C4.99992 14.1218 4.99992 15.036 6 15.036C7.00008 15.036 7.00008 14.1218 8.00016 14.1218C9.00014 14.1218 9.00014 15.036 9.99998 15.036C11 15.036 11 14.1218 11.9999 14.1218C13 14.1218 13 15.036 14.0001 15.036C15.0001 15.036 15.0001 14.1218 16.0001 14.1218C17.0001 14.1218 17.0001 15.036 18 15.036C19 15.036 19 14.1218 19.9999 14.1218C21 14.1218 21 15.036 21.9999 15.036C22.9252 15.036 22.995 14.2541 23.7921 14.137C23.8913 13.6099 23.9567 13.0711 23.9845 12.5222C23.0003 12.5319 22.9945 13.4361 21.9998 13.4361Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M19.9998 15.6522C18.9998 15.6522 18.9998 16.5665 17.9999 16.5665C17 16.5665 16.9999 15.6522 16 15.6522C15 15.6522 15 16.5665 14 16.5665C12.9999 16.5665 12.9999 15.6522 11.9998 15.6522C10.9998 15.6522 10.9998 16.5665 9.99989 16.5665C9.00005 16.5665 9.00005 15.6522 8.00007 15.6522C6.99999 15.6522 6.99999 16.5665 5.99991 16.5665C4.99983 16.5665 4.99983 15.6522 3.99975 15.6522C2.99991 15.6522 2.99991 16.5665 1.99992 16.5665C1.26328 16.5665 1.0688 16.0707 0.617722 15.8095C0.890909 16.626 1.24974 17.4031 1.6831 18.1308C1.77694 18.1535 1.88128 18.1665 1.99988 18.1665C2.99986 18.1665 2.99986 17.2523 3.99971 17.2523C4.99978 17.2523 4.99978 18.1665 5.99986 18.1665C6.99994 18.1665 6.99994 17.2523 8.00002 17.2523C9 17.2523 9 18.1665 9.99985 18.1665C10.9998 18.1665 10.9998 17.2523 11.9998 17.2523C12.9998 17.2523 12.9998 18.1665 13.9999 18.1665C15 18.1665 15 17.2523 15.9999 17.2523C16.9999 17.2523 16.9999 18.1665 17.9999 18.1665C18.9998 18.1665 18.9998 17.2523 19.9998 17.2523C20.9999 17.2523 20.9999 18.1665 21.9998 18.1665C22.1185 18.1665 22.2229 18.1535 22.3168 18.1308C22.7502 17.4031 23.109 16.626 23.3821 15.8095C22.9311 16.0707 22.7365 16.5666 21.9998 16.5666C20.9999 16.5665 20.9999 15.6522 19.9998 15.6522Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M23.7208 9.41911C22.9898 9.57084 22.8966 10.3057 21.9998 10.3057C20.9999 10.3057 20.9999 9.39131 19.9998 9.39131C18.9998 9.39131 18.9998 10.3057 17.9999 10.3057C17 10.3057 16.9999 9.39131 16 9.39131C15 9.39131 15 10.3057 14 10.3057C12.9999 10.3057 12.9999 9.39131 11.9998 9.39131C11.4595 9.39131 11.211 9.65822 10.9391 9.90366V11.5036C11.211 11.2582 11.4594 10.9914 11.9998 10.9914C12.9999 10.9914 12.9999 11.9056 14 11.9056C15 11.9056 15 10.9914 16 10.9914C17 10.9914 17 11.9056 17.9999 11.9056C18.9998 11.9056 18.9999 10.9914 19.9998 10.9914C20.9999 10.9914 20.9999 11.9056 21.9998 11.9056C22.9251 11.9056 22.9949 11.1237 23.792 11.0066C23.8431 11.0066 23.9002 11.0061 23.9586 11.0054C23.9144 10.4657 23.8342 9.93619 23.7208 9.41911Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M22.8443 6.8566C22.637 7.03065 22.3975 7.17521 21.9999 7.17521C20.9999 7.17521 20.9999 6.26086 19.9999 6.26086C18.9999 6.26086 18.9999 7.17521 18 7.17521C17 7.17521 17 6.26086 16 6.26086C15.0001 6.26086 15.0001 7.17521 14 7.17521C13 7.17521 12.9999 6.26086 11.9999 6.26086C11.4595 6.26086 11.2111 6.52777 10.9392 6.77321V8.37319C11.2111 8.1278 11.4595 7.86094 11.9999 7.86094C12.9999 7.86094 12.9999 8.77519 14 8.77519C15 8.77519 15 7.86094 16 7.86094C17 7.86094 17 8.77519 17.9999 8.77519C18.9998 8.77519 18.9999 7.86094 19.9998 7.86094C20.9999 7.86094 20.9999 8.77519 21.9998 8.77519C22.7094 8.77519 22.9161 8.31558 23.3333 8.0483C23.1911 7.64063 23.0278 7.2429 22.8443 6.8566Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M20.0852 3.13288C20.0575 3.13138 20.0292 3.13045 19.9998 3.13045C18.9999 3.13045 18.9999 4.04479 17.9999 4.04479C16.9999 4.04479 16.9999 3.13045 16 3.13045C15 3.13045 15 4.04479 13.9999 4.04479C12.9999 4.04479 12.9999 3.13045 11.9998 3.13045C11.4594 3.13045 11.211 3.39735 10.9391 3.64279V5.24278C11.211 4.99738 11.4594 4.73053 11.9998 4.73053C12.9999 4.73053 12.9999 5.64478 13.9999 5.64478C15 5.64478 15 4.73053 15.9999 4.73053C16.9999 4.73053 16.9999 5.64478 17.9999 5.64478C18.9998 5.64478 18.9999 4.73053 19.9998 4.73053C20.9999 4.73053 20.9999 5.64478 21.9998 5.64478C22.0614 5.64478 22.1189 5.64112 22.1734 5.6346C21.5926 4.70817 20.8892 3.86643 20.0852 3.13288Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M15.0671 0.396279C14.7926 0.643217 14.5446 0.914435 13.9999 0.914435C12.9998 0.914435 12.9998 0.000185303 11.9998 9.15527e-05H11.9996C11.4825 0.000138428 11.2328 0.244685 10.9739 0.480888V2.08092C11.2328 1.84472 11.4825 1.60012 11.9998 1.60012C12.9999 1.60012 12.9999 2.51437 14 2.51437C15 2.51437 15 1.60012 16 1.60012C17 1.60012 17 2.51437 17.9999 2.51437C18.4195 2.51437 18.6629 2.35331 18.8781 2.16647C17.7374 1.36701 16.4512 0.761107 15.0671 0.396279Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M13.9999 22.8274C12.9998 22.8274 12.9998 21.913 11.9998 21.913C10.9998 21.913 10.9998 22.8274 9.99984 22.8274C9 22.8274 9 21.913 8.00002 21.913C7.23403 21.913 7.05436 22.449 6.56311 22.7002C7.9178 23.3899 9.42094 23.8295 11.0119 23.9592C11.2579 23.7344 11.5079 23.5131 11.9998 23.5131C12.4918 23.5131 12.7418 23.7344 12.9878 23.9593C14.5788 23.8296 16.0821 23.3899 17.4368 22.7002C16.9455 22.4491 16.7658 21.913 15.9999 21.913C15 21.913 15 22.8274 13.9999 22.8274Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M17.9999 19.697C16.9999 19.697 16.9999 18.7826 16 18.7826C15 18.7826 15 19.697 14 19.697C12.9999 19.697 12.9999 18.7826 11.9998 18.7826C10.9998 18.7826 10.9998 19.697 9.99988 19.697C9.00004 19.697 9.00004 18.7826 8.00006 18.7826C6.99998 18.7826 6.99998 19.697 5.9999 19.697C4.99982 19.697 4.99982 18.7826 3.99974 18.7826C3.28063 18.7826 3.0787 19.2556 2.65002 19.5211C2.91871 19.8548 3.20437 20.1742 3.50666 20.477C3.64209 20.4192 3.80099 20.3827 3.9997 20.3827C4.99977 20.3827 4.99977 21.2969 5.99985 21.2969C6.99993 21.2969 6.99993 20.3827 8.00001 20.3827C8.99999 20.3827 8.99999 21.2969 9.99984 21.2969C10.9998 21.2969 10.9998 20.3827 11.9998 20.3827C12.9998 20.3827 12.9998 21.2969 13.9999 21.2969C15 21.2969 15 20.3827 15.9999 20.3827C16.9999 20.3827 16.9999 21.2969 17.9999 21.2969C18.9998 21.2969 18.9998 20.3827 19.9998 20.3827C20.1987 20.3827 20.3576 20.4192 20.4931 20.4771C20.7955 20.1742 21.0811 19.8548 21.3498 19.5212C20.9211 19.2557 20.7192 18.7827 19.9998 18.7827C18.9998 18.7826 18.9998 19.697 17.9999 19.697Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx103("g", { children: /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
        fill: "#F0F0F0"
      }
    ) }),
    /* @__PURE__ */ jsxs101("g", { children: [
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M12 6.26085V9.78646L7.82611 5.73914L12 6.26085Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M6.2609 12H9.78637L6.26085 7.30434L5.73914 10.9565L6.2609 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.3715 0.001125 0 5.37323 0 12H6.26091V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs101("g", { children: [
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M6.07101 1.5652C4.19419 2.6339 2.6339 4.19419 1.5652 6.07101V12H4.69565V4.69575V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M15.6522 6.26086H20.3478L18 8.60869L15.6522 6.26086Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs101("g", { children: [
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M17.4783 8.08698H18.5218V10.9565H17.4783V8.08698Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx103(
        "path",
        {
          d: "M17.4783 15.6522H18.5218V18.5217H17.4783V15.6522Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M21.1305 14.087C21.1305 13.2225 20.4297 12.5218 19.5653 12.5218C19.1643 12.5218 18.7988 12.6727 18.5218 12.9206V12H19.0435V10.9565H18.5218V10.4348H17.4783V10.9565H16.9566V12H17.4783V12.9206C17.2013 12.6727 16.8358 12.5218 16.4348 12.5218C15.5704 12.5218 14.8696 13.2225 14.8696 14.087C14.8696 14.5504 15.0712 14.9667 15.3913 15.2533V16.1739H20.6087V15.2533C20.9288 14.9667 21.1305 14.5504 21.1305 14.087Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx103(
      "path",
      {
        d: "M19.5652 13.5652C19.4006 13.5652 19.2854 13.6375 19.2177 13.698L18.5217 14.3211H17.4783L16.7823 13.698C16.7146 13.6375 16.5994 13.5652 16.4348 13.5652C16.1471 13.5652 15.9131 13.7992 15.9131 14.0869C15.9131 14.2896 16.0224 14.4176 16.0875 14.4759L16.4349 14.787V15.1304H19.5653V14.787L19.9127 14.4759C19.9778 14.4176 20.0871 14.2896 20.0871 14.0869C20.087 13.7992 19.8529 13.5652 19.5652 13.5652Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx103("defs", { children: /* @__PURE__ */ jsx103("clipPath", { id: "cfIO_clip0_0_1", children: /* @__PURE__ */ jsx103("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IQ.tsx
import { jsx as jsx104, jsxs as jsxs102 } from "react/jsx-runtime";
var FlagIQ = (props) => /* @__PURE__ */ jsxs102(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs102("g", { clipPath: "url(#cfIQ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx104(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx104(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx104(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs102("g", { children: [
      /* @__PURE__ */ jsx104(
        "path",
        {
          d: "M9.13045 11.2174C9.12389 11.2174 9.11747 11.2178 9.1109 11.2179V11.2174H6.81698C6.93328 10.7681 7.341 10.4348 7.82611 10.4348V8.86957C6.38765 8.86957 5.21742 10.0398 5.21742 11.4783V12.7565V12.7826H9.1109H9.13045C9.27431 12.7826 9.39131 12.8997 9.39131 13.0435V13.5652H4.17389V15.1304H10.9565V13.0435C10.9565 12.0366 10.1373 11.2174 9.13045 11.2174Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx104(
        "path",
        {
          d: "M13.0435 13.5652V8.86957H11.4783V15.1305H14.087V13.5652H13.0435Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx104(
        "path",
        {
          d: "M18.2609 13.5653V8.86957H16.6957V13.5653H16.1739V12.0001H14.6087V15.1305H19.3044V13.5653H18.2609Z",
          fill: "#496E2D"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx104("defs", { children: /* @__PURE__ */ jsx104("clipPath", { id: "cfIQ_clip0_0_1", children: /* @__PURE__ */ jsx104("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IR.tsx
import { jsx as jsx105, jsxs as jsxs103 } from "react/jsx-runtime";
var FlagIR = (props) => /* @__PURE__ */ jsxs103(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs103("g", { clipPath: "url(#cfIR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx105(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx105(
      "path",
      {
        d: "M15.8975 8.86955H14.3275C14.3404 9.0421 14.3478 9.21619 14.3478 9.39127C14.3478 10.5521 14.0576 11.6772 13.5518 12.4781C13.3958 12.7252 13.1336 13.074 12.7826 13.3093V8.86951H11.2174V13.3093C10.8664 13.074 10.6041 12.7252 10.4481 12.4781C9.94221 11.6772 9.6521 10.5521 9.6521 9.39127C9.6521 9.21619 9.65951 9.04205 9.67245 8.86955H8.10242C8.09234 9.04126 8.08685 9.21516 8.08685 9.39127C8.08685 12.6095 9.80567 15.1304 11.9999 15.1304C14.1941 15.1304 15.9129 12.6095 15.9129 9.39127C15.913 9.21516 15.9075 9.04126 15.8975 8.86955Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx105(
      "path",
      {
        d: "M4.95653 5.73914H6.52174V6.78263H8.08694V5.73914H9.65214V6.78263H11.2173V5.73914H12.7825V6.78263H14.3477V5.73914H15.913V6.78263H17.4782V5.73914H19.0434V6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0C7.24253 0 3.13173 2.76867 1.19073 6.78263H4.95653V5.73914Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx105(
      "path",
      {
        d: "M19.0435 17.2173V18.2608H17.4783V17.2173H15.9131V18.2608H14.3479V17.2173H12.7827V18.2608H11.2175V17.2173H9.65228V18.2608H8.08708V17.2173H6.52173V18.2608H4.95653V17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H19.0435Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx105("defs", { children: /* @__PURE__ */ jsx105("clipPath", { id: "cfIR_clip0_0_1", children: /* @__PURE__ */ jsx105("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IS.tsx
import { jsx as jsx106, jsxs as jsxs104 } from "react/jsx-runtime";
var FlagIS = (props) => /* @__PURE__ */ jsxs104(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs104("g", { clipPath: "url(#cfIS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx106(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs104("g", { children: [
      /* @__PURE__ */ jsx106(
        "path",
        {
          d: "M0.413391 15.1305C1.1077 17.7067 2.63794 19.9395 4.69566 21.5206V15.1305H0.413391Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx106(
        "path",
        {
          d: "M10.9565 23.9544C11.3005 23.984 11.6484 24 12 24C17.5444 24 22.2096 20.2396 23.5866 15.1305H10.9565V23.9544Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx106(
        "path",
        {
          d: "M23.5866 8.86955C22.2096 3.76036 17.5444 0 12 0C11.6484 0 11.3005 0.0160312 10.9565 0.0456562V8.86955H23.5866Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx106(
        "path",
        {
          d: "M4.69566 2.47943C2.63794 4.06052 1.1077 6.29332 0.413391 8.86952H4.69566V2.47943Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx106(
      "path",
      {
        d: "M23.8984 10.4348H9.39136H9.39131V0.285248C8.28234 0.531154 7.23098 0.929732 6.26086 1.45909V10.4348H0.101578C0.0347813 10.9472 0 11.4695 0 12C0 12.5305 0.0347813 13.0529 0.101578 13.5652H6.26081H6.26086V22.5409C7.23098 23.0702 8.28234 23.4689 9.39131 23.7147V13.5654V13.5653H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9472 23.8984 10.4348Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx106("defs", { children: /* @__PURE__ */ jsx106("clipPath", { id: "cfIS_clip0_0_1", children: /* @__PURE__ */ jsx106("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/IT.tsx
import { jsx as jsx107, jsxs as jsxs105 } from "react/jsx-runtime";
var FlagIT = (props) => /* @__PURE__ */ jsxs105(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs105("g", { clipPath: "url(#cfIT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx107(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx107(
      "path",
      {
        d: "M24 12C24 6.84043 20.7435 2.44191 16.1739 0.746399V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx107(
      "path",
      {
        d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746399C3.2565 2.44191 0 6.84043 0 12Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx107("defs", { children: /* @__PURE__ */ jsx107("clipPath", { id: "cfIT_clip0_0_1", children: /* @__PURE__ */ jsx107("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/JE.tsx
import { jsx as jsx108, jsxs as jsxs106 } from "react/jsx-runtime";
var FlagJE = (props) => /* @__PURE__ */ jsxs106(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs106("g", { clipPath: "url(#cfJE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx108(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx108(
      "path",
      {
        d: "M21.5202 19.3067L14.2136 12.0001H14.2135L21.5202 4.69333C21.2051 4.28383 20.8604 3.88984 20.4853 3.5147C20.1102 3.13961 19.7162 2.79484 19.3067 2.4798L12.0001 9.78644L12 9.78648L4.69338 2.4798C4.28392 2.79489 3.88989 3.13956 3.51475 3.5147C3.13961 3.88984 2.79489 4.28383 2.47984 4.69333L9.78648 11.9999L9.78653 12L2.4798 19.3067C2.79494 19.7162 3.13966 20.1102 3.5147 20.4853C3.88984 20.8604 4.28378 21.2052 4.69333 21.5202L12 14.2136L12.0001 14.2135L19.3067 21.5202C19.7162 21.2051 20.1102 20.8604 20.4853 20.4853C20.8605 20.1101 21.2051 19.7162 21.5202 19.3067Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx108(
      "path",
      {
        d: "M9.91302 3.65214L12 4.17386L14.087 3.65214V1.87825L13.2522 2.29563L12 1.04346L10.7478 2.29563L9.91302 1.87825V3.65214Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx108(
      "path",
      {
        d: "M9.91302 3.65216V4.9566C9.91302 6.5541 12 7.04357 12 7.04357C12 7.04357 14.087 6.55405 14.087 4.9566V3.65216H9.91302Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx108("defs", { children: /* @__PURE__ */ jsx108("clipPath", { id: "cfJE_clip0_0_1", children: /* @__PURE__ */ jsx108("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/JM.tsx
import { jsx as jsx109, jsxs as jsxs107 } from "react/jsx-runtime";
var FlagJM = (props) => /* @__PURE__ */ jsxs107(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs107("g", { clipPath: "url(#cfJM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx109(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx109(
      "path",
      {
        d: "M19.3058 2.48062C17.2823 0.925266 14.7494 0 12 0C9.2506 0 6.71767 0.925312 4.69421 2.48062L12 9.78638L19.3058 2.48062Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx109(
      "path",
      {
        d: "M2.48058 4.69421C0.925312 6.71767 0 9.25056 0 12C0 14.7494 0.925312 17.2823 2.48062 19.3058L9.78642 12L2.48058 4.69421Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx109(
      "path",
      {
        d: "M4.69421 21.5194C6.71771 23.0747 9.2506 24 12 24C14.7494 24 17.2823 23.0747 19.3058 21.5194L12 14.2136L4.69421 21.5194Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx109(
      "path",
      {
        d: "M21.5194 19.3057C23.0747 17.2823 24 14.7494 24 12C24 9.25056 23.0747 6.71767 21.5194 4.69421L14.2136 12L21.5194 19.3057Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx109("defs", { children: /* @__PURE__ */ jsx109("clipPath", { id: "cfJM_clip0_0_1", children: /* @__PURE__ */ jsx109("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/JO.tsx
import { jsx as jsx110, jsxs as jsxs108 } from "react/jsx-runtime";
var FlagJO = (props) => /* @__PURE__ */ jsxs108(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs108("g", { clipPath: "url(#cfJO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx110(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx110(
      "path",
      {
        d: "M7.30437 7.82611H23.2536C21.5581 3.2565 17.1596 0 12 0C8.68625 0 5.68657 1.34339 3.51514 3.51511L7.30437 7.82611Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx110(
      "path",
      {
        d: "M7.30437 16.1739H23.2536C21.5581 20.7435 17.1596 24 12 24C8.68625 24 5.68657 22.6566 3.51514 20.4849L7.30437 16.1739Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx110(
      "path",
      {
        d: "M3.51465 3.51471C-1.17163 8.20099 -1.17163 15.799 3.51465 20.4853C5.4512 18.5488 7.31368 16.6863 12 12L3.51465 3.51471Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx110(
      "path",
      {
        d: "M4.76274 9.3913L5.42129 10.7684L6.90858 10.4247L6.24249 11.7982L7.43851 12.7467L5.94943 13.0822L5.95355 14.6087L4.76274 13.6537L3.57188 14.6087L3.57605 13.0822L2.08698 12.7467L3.28294 11.7982L2.61694 10.4247L4.10415 10.7684L4.76274 9.3913Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx110("defs", { children: /* @__PURE__ */ jsx110("clipPath", { id: "cfJO_clip0_0_1", children: /* @__PURE__ */ jsx110("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/JP.tsx
import { jsx as jsx111, jsxs as jsxs109 } from "react/jsx-runtime";
var FlagJP = (props) => /* @__PURE__ */ jsxs109(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs109("g", { clipPath: "url(#cfJP_clip0_0_1)", children: [
    /* @__PURE__ */ jsx111(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx111(
      "path",
      {
        d: "M12 17.2174C14.8814 17.2174 17.2173 14.8815 17.2173 12C17.2173 9.11855 14.8814 6.78265 12 6.78265C9.11849 6.78265 6.78259 9.11855 6.78259 12C6.78259 14.8815 9.11849 17.2174 12 17.2174Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx111("defs", { children: /* @__PURE__ */ jsx111("clipPath", { id: "cfJP_clip0_0_1", children: /* @__PURE__ */ jsx111("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KE.tsx
import { jsx as jsx112, jsxs as jsxs110 } from "react/jsx-runtime";
var FlagKE = (props) => /* @__PURE__ */ jsxs110(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs110("g", { clipPath: "url(#cfKE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M12 24C7.24248 24 3.13173 21.2313 1.19073 17.2173H22.8092C20.8683 21.2313 16.7575 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M23.4333 8.34784H0.566672C0.199078 9.49951 0 10.7265 0 12C0 13.2736 0.199078 14.5005 0.566672 15.6522H23.4333C23.8009 14.5005 24 13.2736 24 12C24 10.7265 23.8009 9.49951 23.4333 8.34784Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs110("g", { children: [
      /* @__PURE__ */ jsx112(
        "path",
        {
          d: "M15.7366 5.54533L14.3155 4.8894L12 10.1324L9.68448 4.8894L8.26337 5.54533L11.1381 12L8.26337 18.4546L9.68448 19.1105L12 13.8675L14.3155 19.1105L15.7366 18.4546L12.8619 12L15.7366 5.54533Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx112(
        "path",
        {
          d: "M12.9652 6.16352C12.4151 5.56272 12 5.21735 12 5.21735C12 5.21735 11.5849 5.56267 11.0348 6.16352V17.8363C11.5849 18.4372 12 18.7826 12 18.7826C12 18.7826 12.4151 18.4373 12.9652 17.8364V6.16352Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs110("g", { children: [
      /* @__PURE__ */ jsx112(
        "path",
        {
          d: "M9.3913 8.40202V15.5979C9.97264 16.628 10.6842 17.4723 11.2174 18.0321V5.9679C10.6842 6.52763 9.97259 7.37199 9.3913 8.40202Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx112(
        "path",
        {
          d: "M14.6086 8.40202C14.0273 7.37194 13.3158 6.52763 12.7825 5.9679V18.0321C13.3157 17.4723 14.0273 16.628 14.6086 15.5979V8.40202Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M14.6087 8.40204V15.5979C15.1972 14.555 15.6522 13.3218 15.6522 12C15.6522 10.6783 15.1972 9.44501 14.6087 8.40204Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx112(
      "path",
      {
        d: "M9.39132 8.40204V15.5979C8.80281 14.555 8.34784 13.3218 8.34784 12C8.34784 10.6783 8.80281 9.44501 9.39132 8.40204Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx112("defs", { children: /* @__PURE__ */ jsx112("clipPath", { id: "cfKE_clip0_0_1", children: /* @__PURE__ */ jsx112("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KG.tsx
import { jsx as jsx113, jsxs as jsxs111 } from "react/jsx-runtime";
var FlagKG = (props) => /* @__PURE__ */ jsxs111(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs111("g", { clipPath: "url(#cfKG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx113(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx113(
      "path",
      {
        d: "M17.8686 12L15.4707 13.1279L16.7477 15.4504L14.1436 14.9522L13.8138 17.5826L12 15.6481L10.1863 17.5826L9.85633 14.9522L7.25252 15.4503L8.52939 13.1278L6.13141 12L8.52939 10.8721L7.25252 8.54956L9.85624 9.04784L10.1864 6.41736L12 8.35194L13.8139 6.41736L14.1436 9.04784L16.7478 8.54956L15.4708 10.8723L17.8686 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx113(
      "path",
      {
        d: "M12 15.6522C14.017 15.6522 15.6522 14.017 15.6522 12C15.6522 9.98297 14.017 8.34784 12 8.34784C9.98297 8.34784 8.34784 9.98297 8.34784 12C8.34784 14.017 9.98297 15.6522 12 15.6522Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs111("g", { children: [
      /* @__PURE__ */ jsx113(
        "path",
        {
          d: "M10.1739 12C10.0859 12 9.99906 12.0045 9.91333 12.013C9.91647 12.5189 10.0995 12.9818 10.4017 13.3415C10.563 12.9076 10.8037 12.5119 11.1073 12.1733C10.8174 12.0618 10.5028 12 10.1739 12Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx113(
        "path",
        {
          d: "M11.2981 13.9656C11.5175 14.044 11.7537 14.087 12 14.087C12.2464 14.087 12.4826 14.044 12.702 13.9656C12.5824 13.4963 12.3355 13.0774 12 12.7481C11.6646 13.0773 11.4177 13.4963 11.2981 13.9656Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx113(
        "path",
        {
          d: "M13.8073 10.9568C13.4464 10.3331 12.7725 9.91302 12 9.91302C11.2275 9.91302 10.5536 10.3331 10.1927 10.9568C10.8506 10.9602 11.4675 11.139 12 11.4479C12.5326 11.139 13.1493 10.9602 13.8073 10.9568Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx113(
        "path",
        {
          d: "M12.8926 12.1734C13.1962 12.5119 13.437 12.9076 13.5982 13.3416C13.9005 12.9819 14.0835 12.5189 14.0866 12.0131C14.0009 12.0045 13.914 12.0001 13.8261 12.0001C13.4973 12 13.1827 12.0618 12.8926 12.1734Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx113("defs", { children: /* @__PURE__ */ jsx113("clipPath", { id: "cfKG_clip0_0_1", children: /* @__PURE__ */ jsx113("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KH.tsx
import { jsx as jsx114, jsxs as jsxs112 } from "react/jsx-runtime";
var FlagKH = (props) => /* @__PURE__ */ jsxs112(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs112("g", { clipPath: "url(#cfKH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx114(
      "path",
      {
        d: "M0 12C0 14.0789 0.528891 16.0343 1.45908 17.7391L12 18.7826L22.5409 17.7391C23.4711 16.0343 24 14.0789 24 12C24 9.9211 23.4711 7.96575 22.5409 6.26086L12 5.21738L1.45908 6.26086C0.528891 7.96575 0 9.9211 0 12H0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs112("g", { children: [
      /* @__PURE__ */ jsx114(
        "path",
        {
          d: "M1.45911 6.26086H22.5409C20.5057 2.53073 16.5485 0 12 0C7.45151 0 3.49437 2.53073 1.45911 6.26086Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx114(
        "path",
        {
          d: "M12 24C16.5485 24 20.5057 21.4693 22.5409 17.7391H1.45911C3.49437 21.4693 7.45151 24 12 24Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx114(
      "path",
      {
        d: "M16.1739 14.3478V12.7826H15.1305V10.6957L14.087 9.65217L13.0435 10.6957V8.60868L12 7.5652L10.9565 8.60868V10.6957L9.91306 9.65217L8.86958 10.6957V12.7826H7.82614V14.3478H6.78265V15.913H17.2174V14.3478H16.1739Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx114("defs", { children: /* @__PURE__ */ jsx114("clipPath", { id: "cfKH_clip0_0_1", children: /* @__PURE__ */ jsx114("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KI.tsx
import { jsx as jsx115, jsxs as jsxs113 } from "react/jsx-runtime";
var FlagKI = (props) => /* @__PURE__ */ jsxs113(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs113("g", { clipPath: "url(#cfKI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx115(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs113("g", { children: [
      /* @__PURE__ */ jsx115(
        "path",
        {
          d: "M16.1739 11.1599L14.4683 11.9621L15.3766 13.6139L13.5246 13.2596L13.29 15.1305L12 13.7545L10.71 15.1305L10.4754 13.2596L8.62341 13.6138L9.53161 11.9621L7.82611 11.1599L9.53161 10.3576L8.62341 8.70599L10.4753 9.06017L10.71 7.18939L12 8.56531L13.29 7.18939L13.5246 9.06017L15.3766 8.70599L14.4685 10.3578L16.1739 11.1599Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx115(
        "path",
        {
          d: "M15.1305 3.91306H12.7826C12.7826 3.48092 12.4322 3.13043 12 3.13043C11.5678 3.13043 11.2174 3.48087 11.2174 3.91306H8.86957C8.86957 4.34524 9.24607 4.69568 9.67826 4.69568H9.65215C9.65215 5.12787 10.0025 5.47831 10.4348 5.47831C10.4348 5.91049 10.7851 6.26093 11.2174 6.26093H12.7826C13.2147 6.26093 13.5652 5.91054 13.5652 5.47831C13.9974 5.47831 14.3478 5.12792 14.3478 4.69568H14.3217C14.754 4.69568 15.1305 4.34529 15.1305 3.91306Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx115(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12H24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs113("g", { children: [
      /* @__PURE__ */ jsx115(
        "path",
        {
          d: "M12 24C15.751 24 19.0995 22.2785 21.3001 19.583C20.9043 19.3132 20.6906 18.877 19.9998 18.877C18.9998 18.877 18.9998 19.7913 17.9999 19.7913C17 19.7913 16.9999 18.877 16 18.877C15 18.877 15 19.7913 14 19.7913C12.9999 19.7913 12.9999 18.877 11.9998 18.877C10.9998 18.877 10.9998 19.7913 9.99989 19.7913C9.00005 19.7913 9.00005 18.877 8.00006 18.877C6.99999 18.877 6.99999 19.7913 5.99991 19.7913C4.99983 19.7913 4.99983 18.877 3.99975 18.877C3.3091 18.877 3.09539 19.3131 2.69977 19.5829C4.90027 22.2785 8.24892 24 12 24Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx115(
        "path",
        {
          d: "M21.9999 13.5304C20.9999 13.5304 20.9999 12.6161 19.9998 12.6161C18.9999 12.6161 18.9999 13.5304 17.9999 13.5304C17 13.5304 16.9999 12.6161 16 12.6161C15 12.6161 15 13.5304 14 13.5304C13 13.5304 12.9999 12.6161 11.9998 12.6161C10.9998 12.6161 10.9998 13.5304 9.99991 13.5304C9.00006 13.5304 9.00006 12.6161 8.00008 12.6161C7 12.6161 7 13.5304 5.99992 13.5304C4.99984 13.5304 4.99984 12.6161 3.99977 12.6161C2.99992 12.6161 2.99992 13.5304 1.99994 13.5304C1.00534 13.5304 0.999578 12.6262 0.015625 12.6165C0.0433281 13.1654 0.108859 13.7043 0.207953 14.2313C1.00502 14.3484 1.07481 15.1304 1.99998 15.1304C2.99997 15.1304 2.99997 14.2162 3.99981 14.2162C4.99989 14.2162 4.99989 15.1304 5.99997 15.1304C7.00005 15.1304 7.00005 14.2162 8.00013 14.2162C9.00011 14.2162 9.00011 15.1304 9.99995 15.1304C10.9999 15.1304 10.9999 14.2162 11.9999 14.2162C13 14.2162 13 15.1304 14 15.1304C15.0001 15.1304 15.0001 14.2162 16 14.2162C17 14.2162 17 15.1304 18 15.1304C18.9999 15.1304 19 14.2162 19.9999 14.2162C21 14.2162 21 15.1304 21.9999 15.1304C22.9252 15.1304 22.995 14.3484 23.7921 14.2313C23.8912 13.7043 23.9567 13.1655 23.9845 12.6166C23.0004 12.6263 22.9945 13.5304 21.9999 13.5304Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx115(
        "path",
        {
          d: "M19.9998 15.7465C18.9998 15.7465 18.9998 16.6609 17.9999 16.6609C17 16.6609 16.9999 15.7465 16 15.7465C15 15.7465 15 16.6609 14 16.6609C12.9999 16.6609 12.9999 15.7465 11.9998 15.7465C10.9998 15.7465 10.9998 16.6609 9.99989 16.6609C9.00004 16.6609 9.00005 15.7465 8.00006 15.7465C6.99998 15.7465 6.99998 16.6609 5.9999 16.6609C4.99983 16.6609 4.99983 15.7465 3.99975 15.7465C2.9999 15.7465 2.9999 16.6609 1.99992 16.6609C1.2854 16.6609 1.08131 16.1942 0.658264 15.9276C0.939889 16.7413 1.30626 17.5153 1.7475 18.2389C1.82423 18.253 1.90776 18.2608 1.99992 18.2608C2.9999 18.2608 2.9999 17.3466 3.99975 17.3466C4.99983 17.3466 4.99983 18.2608 5.9999 18.2608C6.99998 18.2608 6.99998 17.3466 8.00006 17.3466C9.00005 17.3466 9.00004 18.2608 9.99989 18.2608C10.9999 18.2608 10.9999 17.3466 11.9998 17.3466C12.9999 17.3466 12.9999 18.2608 14 18.2608C15 18.2608 15 17.3466 16 17.3466C17 17.3466 17 18.2608 17.9999 18.2608C18.9998 18.2608 18.9999 17.3466 19.9998 17.3466C20.9999 17.3466 20.9999 18.2608 21.9998 18.2608C22.0921 18.2608 22.1756 18.253 22.2524 18.2388C22.6936 17.5153 23.0599 16.7413 23.3417 15.9276C22.9186 16.1941 22.7145 16.6609 21.9998 16.6609C20.9999 16.6609 20.9999 15.7465 19.9998 15.7465Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx115(
      "path",
      {
        d: "M23.9628 11.0519C23.0004 11.0747 22.9872 11.9652 21.9998 11.9652C20.9999 11.9652 20.9999 11.0509 19.9998 11.0509C18.9998 11.0509 18.9998 11.9652 17.9999 11.9652C17 11.9652 16.9999 11.0509 16 11.0509C15 11.0509 15 11.9652 14 11.9652C12.9999 11.9652 12.9999 11.0509 11.9998 11.0509C10.9998 11.0509 10.9998 11.9652 9.99989 11.9652C9.00005 11.9652 9.00005 11.0509 8.00006 11.0509C6.99998 11.0509 6.99998 11.9652 5.99991 11.9652C4.99983 11.9652 4.99983 11.0509 3.99975 11.0509C2.99991 11.0509 2.99991 11.9652 1.99992 11.9652C1.01278 11.9652 0.999422 11.0748 0.0371719 11.0519C0.01275 11.3648 0 11.6809 0 12C0 12.2186 0.0061875 12.4357 0.0178594 12.6514C0.999563 12.6624 1.00608 13.5652 1.99997 13.5652C2.99995 13.5652 2.99995 12.6509 3.9998 12.6509C4.99988 12.6509 4.99988 13.5652 5.99995 13.5652C7.00003 13.5652 7.00003 12.6509 8.00011 12.6509C9.00009 12.6509 9.00009 13.5652 9.99994 13.5652C10.9999 13.5652 10.9999 12.6509 11.9999 12.6509C12.9999 12.6509 12.9999 13.5652 14 13.5652C15.0001 13.5652 15.0001 12.6509 16 12.6509C17 12.6509 17 13.5652 18 13.5652C18.9999 13.5652 18.9999 12.6509 19.9999 12.6509C21 12.6509 21 13.5652 21.9999 13.5652C22.9939 13.5652 23.0004 12.6624 23.9823 12.6514C23.9938 12.4357 24 12.2186 24 12C24 11.6809 23.9872 11.3648 23.9628 11.0519Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx115("defs", { children: /* @__PURE__ */ jsx115("clipPath", { id: "cfKI_clip0_0_1", children: /* @__PURE__ */ jsx115("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KM.tsx
import { jsx as jsx116, jsxs as jsxs114 } from "react/jsx-runtime";
var FlagKM = (props) => /* @__PURE__ */ jsxs114(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs114("g", { clipPath: "url(#cfKM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx116(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx116(
      "path",
      {
        d: "M23.9996 11.9653H10.9913C8.40863 14.548 6.84685 16.5419 5.25216 18.1366L12.0348 18.7478L22.5598 17.7044C23.4783 16.0077 24 14.0648 24 12C24 11.9884 23.9996 11.9769 23.9996 11.9653Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx116(
      "path",
      {
        d: "M5.2522 6.22613H22.5215C20.4807 2.51513 16.5343 0 12 0C8.69508 0 5.70276 1.33645 3.53268 3.498L5.2522 6.22613Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx116(
      "path",
      {
        d: "M5.77392 17.7043C4.69514 18.7831 4.27823 19.687 3.49772 20.4674C5.67061 22.6493 8.67741 24 12 24C16.5626 24 20.5303 21.4535 22.5599 17.7043H5.77392Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx116(
      "path",
      {
        d: "M3.51469 3.47995C-1.17159 8.16623 -1.17159 15.7642 3.51469 20.4506C5.45123 18.514 7.31372 16.6515 12 11.9653L3.51469 3.47995Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs114("g", { children: [
      /* @__PURE__ */ jsx116(
        "path",
        {
          d: "M3.16523 11.9653C3.16523 10.2169 4.39397 8.75633 6.03478 8.39802C5.78259 8.34294 5.52094 8.31308 5.25215 8.31308C3.23508 8.31308 1.59998 9.94817 1.59998 11.9653C1.59998 13.9823 3.23508 15.6174 5.25215 15.6174C5.52089 15.6174 5.78255 15.5876 6.03478 15.5325C4.39397 15.1741 3.16523 13.7136 3.16523 11.9653Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx116(
        "path",
        {
          d: "M5.99451 8.83481L6.18881 9.43265H6.8174L6.30881 9.80217L6.50311 10.4L5.99451 10.0305L5.48592 10.4L5.68022 9.80217L5.17162 9.43265H5.80022L5.99451 8.83481Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx116(
        "path",
        {
          d: "M5.99451 10.4L6.18881 10.9979H6.8174L6.30881 11.3674L6.50311 11.9652L5.99451 11.5957L5.48592 11.9652L5.68022 11.3674L5.17162 10.9979H5.80022L5.99451 10.4Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx116(
        "path",
        {
          d: "M5.99451 11.9652L6.18881 12.5631H6.8174L6.30881 12.9326L6.50311 13.5304L5.99451 13.1609L5.48592 13.5304L5.68022 12.9326L5.17162 12.5631H5.80022L5.99451 11.9652Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx116(
        "path",
        {
          d: "M5.99451 13.5305L6.18881 14.1283H6.8174L6.30881 14.4978L6.50311 15.0957L5.99451 14.7262L5.48592 15.0957L5.68022 14.4978L5.17162 14.1283H5.80022L5.99451 13.5305Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx116("defs", { children: /* @__PURE__ */ jsx116("clipPath", { id: "cfKM_clip0_0_1", children: /* @__PURE__ */ jsx116("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KP.tsx
import { jsx as jsx117, jsxs as jsxs115 } from "react/jsx-runtime";
var FlagKP = (props) => /* @__PURE__ */ jsxs115(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs115("g", { clipPath: "url(#cfKP_clip0_0_1)", children: [
    /* @__PURE__ */ jsx117(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs115("g", { children: [
      /* @__PURE__ */ jsx117(
        "path",
        {
          d: "M12.0001 0C8.36297 0 5.10408 1.61831 2.90344 4.17389H21.0966C18.896 1.61831 15.6371 0 12.0001 0Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx117(
        "path",
        {
          d: "M21.0967 19.8261H2.90344C5.10408 22.3817 8.36297 24 12.0001 24C15.6371 24 18.896 22.3817 21.0967 19.8261Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx117(
      "path",
      {
        d: "M22.2387 5.73914H1.76128C0.644297 7.56187 0 9.7056 0 12C0 14.2944 0.644297 16.4382 1.76128 18.2609H22.2387C23.3557 16.4382 24 14.2944 24 12C24 9.7056 23.3557 7.56187 22.2387 5.73914Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx117(
      "path",
      {
        d: "M7.38263 16.6174C9.93274 16.6174 12 14.5501 12 12C12 9.4499 9.93274 7.38263 7.38263 7.38263C4.83253 7.38263 2.76526 9.4499 2.76526 12C2.76526 14.5501 4.83253 16.6174 7.38263 16.6174Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx117(
      "path",
      {
        d: "M7.38261 7.38263L8.41859 10.5711H11.7766L9.05895 12.5418L10.103 15.7398L7.38261 13.7597L4.66629 15.7361L5.70626 12.5418L2.99103 10.5711H6.34657L7.38261 7.38263Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx117("defs", { children: /* @__PURE__ */ jsx117("clipPath", { id: "cfKP_clip0_0_1", children: /* @__PURE__ */ jsx117("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KR.tsx
import { jsx as jsx118, jsxs as jsxs116 } from "react/jsx-runtime";
var FlagKR = (props) => /* @__PURE__ */ jsxs116(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs116("g", { clipPath: "url(#cfKR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M16.1739 12C16.1739 13.0435 14.3052 15.6522 12 15.6522C9.69483 15.6522 7.82611 13.0435 7.82611 12C7.82611 9.69478 9.69483 7.82611 12 7.82611C14.3052 7.82611 16.1739 9.69478 16.1739 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M16.1739 12C16.1739 14.3052 14.3052 16.1739 12 16.1739C9.69483 16.1739 7.82611 14.3052 7.82611 12",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M16.4238 15.6895L17.5304 14.5829L18.2682 15.3206L17.1616 16.4272L16.4238 15.6895Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M14.579 17.5339L15.6856 16.4273L16.4233 17.165L15.3167 18.2716L14.579 17.5339Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M18.6372 17.9025L19.7438 16.7959L20.4815 17.5337L19.375 18.6403L18.6372 17.9025Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M16.7924 19.7473L17.899 18.6407L18.6367 19.3784L17.5301 20.485L16.7924 19.7473Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M17.5304 16.7961L18.637 15.6895L19.3747 16.4272L18.2681 17.5338L17.5304 16.7961Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M15.6861 18.6404L16.7927 17.5338L17.5304 18.2716L16.4238 19.3782L15.6861 18.6404Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M18.6421 8.31256L15.6911 5.36162L16.4289 4.62388L19.3798 7.57482L18.6421 8.31256Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M15.6906 7.57516L14.584 6.46857L15.3217 5.73086L16.4283 6.83745L15.6906 7.57516Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M17.5349 9.41995L16.4283 8.31335L17.166 7.57564L18.2726 8.68224L17.5349 9.41995Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M17.9036 5.36157L16.797 4.25497L17.5347 3.51723L18.6413 4.62383L17.9036 5.36157Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M19.7489 7.20657L18.6423 6.09998L19.38 5.36223L20.4866 6.46883L19.7489 7.20657Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M4.25409 16.794L7.20503 19.7449L6.46729 20.4827L3.51635 17.5317L4.25409 16.794Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M7.20493 17.5312L8.31152 18.6378L7.57381 19.3755L6.46722 18.2689L7.20493 17.5312Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M5.36041 15.6871L6.46701 16.7937L5.72927 17.5315L4.62267 16.4249L5.36041 15.6871Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M6.4671 14.5802L9.41804 17.5312L8.6803 18.2689L5.72936 15.318L6.4671 14.5802Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M7.20474 4.25266L4.2538 7.2036L3.51606 6.46585L6.467 3.51491L7.20474 4.25266Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M8.31134 5.35913L5.3604 8.31007L4.62266 7.57233L7.5736 4.62139L8.31134 5.35913Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx118(
      "path",
      {
        d: "M9.41907 6.46643L6.46813 9.41737L5.73042 8.67966L8.68136 5.72872L9.41907 6.46643Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx118("defs", { children: /* @__PURE__ */ jsx118("clipPath", { id: "cfKR_clip0_0_1", children: /* @__PURE__ */ jsx118("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KW.tsx
import { jsx as jsx119, jsxs as jsxs117 } from "react/jsx-runtime";
var FlagKW = (props) => /* @__PURE__ */ jsxs117(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs117("g", { clipPath: "url(#cfKW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx119(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.8739 24 13.4679 24 12C24 10.5322 23.736 9.12612 23.2536 7.82614L12 6.78265L0.746391 7.82614C0.264047 9.12612 0 10.5322 0 12C0 13.4679 0.264047 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx119(
      "path",
      {
        d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx119(
      "path",
      {
        d: "M12 0C6.84037 0 2.4419 3.2565 0.746338 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx119(
      "path",
      {
        d: "M3.51471 3.51471C-1.17157 8.20099 -1.17157 15.799 3.51471 20.4853C4.73791 19.2621 5.93182 18.0682 7.82609 16.174V7.82613L3.51471 3.51471Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx119("defs", { children: /* @__PURE__ */ jsx119("clipPath", { id: "cfKW_clip0_0_1", children: /* @__PURE__ */ jsx119("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KY.tsx
import { jsx as jsx120, jsxs as jsxs118 } from "react/jsx-runtime";
var FlagKY = (props) => /* @__PURE__ */ jsxs118(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs118("g", { clipPath: "url(#cfKY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx120(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs118("g", { children: [
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx120(
      "path",
      {
        d: "M17.2174 8.8696C18.37 8.8696 19.3043 7.93523 19.3043 6.78263C19.3043 5.63003 18.37 4.69566 17.2174 4.69566C16.0648 4.69566 15.1304 5.63003 15.1304 6.78263C15.1304 7.93523 16.0648 8.8696 17.2174 8.8696Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs118("g", { children: [
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M6.07101 1.5652C4.19419 2.6339 2.63386 4.19419 1.5652 6.07101V12H4.69565V4.69575V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M13.5652 6.26086H20.8695V9.39132H13.5652V6.26086Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx120(
      "path",
      {
        d: "M13.5652 14.087V16.1739H14.6632C15.3216 16.8191 16.2227 17.2174 17.2174 17.2174C18.212 17.2174 19.1132 16.8191 19.7715 16.1739H19.8261H20.8695V14.087H13.5652Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx120(
      "path",
      {
        d: "M13.5652 8.86955V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V8.86955H13.5652Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs118("g", { children: [
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M17.2172 10.5417C16.3042 10.5417 16.3042 11.3765 15.3912 11.3765C14.4782 11.3765 14.4782 10.5417 13.5652 10.5417V12.0025C14.4782 12.0025 14.4782 12.8373 15.3912 12.8373C16.3042 12.8373 16.3042 12.0025 17.2172 12.0025C18.1303 12.0025 18.1303 12.8373 19.0434 12.8373C19.9565 12.8373 19.9565 12.0025 20.8696 12.0025V10.5417C19.9565 10.5417 19.9565 11.3765 19.0434 11.3765C18.1303 11.3765 18.1303 10.5417 17.2172 10.5417Z",
          fill: "#F3F3F3"
        }
      ),
      /* @__PURE__ */ jsx120(
        "path",
        {
          d: "M17.2172 7.61737C16.3042 7.61737 16.3042 8.45217 15.3912 8.45217C14.4782 8.45217 14.4782 7.61737 13.5652 7.61737V9.07823C14.4782 9.07823 14.4782 9.91303 15.3912 9.91303C16.3042 9.91303 16.3042 9.07823 17.2172 9.07823C18.1303 9.07823 18.1303 9.91303 19.0434 9.91303C19.9565 9.91303 19.9565 9.07823 20.8696 9.07823V7.61737C19.9565 7.61737 19.9565 8.45217 19.0434 8.45217C18.1303 8.45217 18.1303 7.61737 17.2172 7.61737Z",
          fill: "#F3F3F3"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx120("defs", { children: /* @__PURE__ */ jsx120("clipPath", { id: "cfKY_clip0_0_1", children: /* @__PURE__ */ jsx120("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/KZ.tsx
import { jsx as jsx121, jsxs as jsxs119 } from "react/jsx-runtime";
var FlagKZ = (props) => /* @__PURE__ */ jsxs119(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs119("g", { clipPath: "url(#cfKZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx121(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx121(
      "path",
      {
        d: "M18.7826 12.1304H5.21738C5.21738 13.0669 6.03305 13.8261 6.96947 13.8261H6.91303C6.91303 14.7626 7.67217 15.5217 8.60869 15.5217C8.60869 16.4583 9.36783 17.2174 10.3043 17.2174H13.6957C14.6322 17.2174 15.3913 16.4583 15.3913 15.5217C16.3278 15.5217 17.087 14.7626 17.087 13.8261H17.0305C17.9669 13.8261 18.7826 13.0669 18.7826 12.1304Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx121(
      "path",
      {
        d: "M16.6957 9.91302C16.6957 12.5063 14.5933 14.6087 12 14.6087C9.40665 14.6087 7.30435 12.5063 7.30435 9.91302",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx121(
      "path",
      {
        d: "M15.5866 9.91305L14.1211 10.6024L14.9015 12.0218L13.3101 11.7174L13.1084 13.325L12 12.1426L10.8915 13.325L10.6899 11.7174L9.09845 12.0217L9.87883 10.6024L8.41333 9.91305L9.87888 9.22371L9.09845 7.80438L10.6898 8.10878L10.8915 6.50116L12 7.68358L13.1085 6.50116L13.3101 8.10878L14.9015 7.80438L14.1211 9.22375L15.5866 9.91305Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx121("defs", { children: /* @__PURE__ */ jsx121("clipPath", { id: "cfKZ_clip0_0_1", children: /* @__PURE__ */ jsx121("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LA.tsx
import { jsx as jsx122, jsxs as jsxs120 } from "react/jsx-runtime";
var FlagLA = (props) => /* @__PURE__ */ jsxs120(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs120("g", { clipPath: "url(#cfLA_clip0_0_1)", children: [
    /* @__PURE__ */ jsxs120("g", { children: [
      /* @__PURE__ */ jsx122(
        "path",
        {
          d: "M22.8093 6.78263C20.8683 2.76867 16.7575 0 12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263L12 7.82611L22.8093 6.78263Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx122(
        "path",
        {
          d: "M1.19073 17.2174C3.13173 21.2313 7.24248 24 12 24C16.7575 24 20.8683 21.2313 22.8093 17.2174L12 16.1739L1.19073 17.2174Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx122(
      "path",
      {
        d: "M22.8093 6.78265H1.19072C0.427875 8.36023 0 10.1301 0 12C0 13.8699 0.427875 15.6398 1.19072 17.2174H22.8092C23.5722 15.6398 24 13.8699 24 12C24 10.1301 23.5722 8.36023 22.8093 6.78265Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx122(
      "path",
      {
        d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx122("defs", { children: /* @__PURE__ */ jsx122("clipPath", { id: "cfLA_clip0_0_1", children: /* @__PURE__ */ jsx122("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LB.tsx
import { jsx as jsx123, jsxs as jsxs121 } from "react/jsx-runtime";
var FlagLB = (props) => /* @__PURE__ */ jsxs121(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs121("g", { clipPath: "url(#cfLB_clip0_0_1)", children: [
    /* @__PURE__ */ jsx123(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs121("g", { children: [
      /* @__PURE__ */ jsx123(
        "path",
        {
          d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx123(
        "path",
        {
          d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx123(
      "path",
      {
        d: "M15.1305 14.087L12 8.34784L8.86957 14.087H11.2174V15.6522H12.7826V14.087H15.1305Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx123("defs", { children: /* @__PURE__ */ jsx123("clipPath", { id: "cfLB_clip0_0_1", children: /* @__PURE__ */ jsx123("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LC.tsx
import { jsx as jsx124, jsxs as jsxs122 } from "react/jsx-runtime";
var FlagLC = (props) => /* @__PURE__ */ jsxs122(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs122("g", { clipPath: "url(#cfLC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx124(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx124(
      "path",
      {
        d: "M7.56519 16.1739H16.4348L12 5.73914L7.56519 16.1739Z",
        fill: "#F3F3F3"
      }
    ),
    /* @__PURE__ */ jsx124(
      "path",
      {
        d: "M9.10648 15.1304L12 8.54877L14.8936 15.1304H9.10648Z",
        fill: "#333333"
      }
    ),
    /* @__PURE__ */ jsx124(
      "path",
      {
        d: "M7.56519 16.1739H16.4348L12 12L7.56519 16.1739Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx124("defs", { children: /* @__PURE__ */ jsx124("clipPath", { id: "cfLC_clip0_0_1", children: /* @__PURE__ */ jsx124("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LI.tsx
import { jsx as jsx125, jsxs as jsxs123 } from "react/jsx-runtime";
var FlagLI = (props) => /* @__PURE__ */ jsxs123(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs123("g", { clipPath: "url(#cfLI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx125(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0.521719 12 12 10.4348 12 10.4348L24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx125(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx125(
      "path",
      {
        d: "M8.86956 8.34783C8.86956 7.4834 8.16878 6.78262 7.30436 6.78262C6.90334 6.78262 6.53786 6.93356 6.26088 7.18148V6.26086H6.78259V5.21737H6.26088V4.69565H5.21739V5.21737H4.69567V6.26086H5.21739V7.18148C4.94041 6.93356 4.57492 6.78262 4.17391 6.78262C3.30948 6.78262 2.6087 7.4834 2.6087 8.34783C2.6087 8.81128 2.81031 9.22753 3.13042 9.51417V10.4348H8.3478V9.51417C8.668 9.22753 8.86956 8.81128 8.86956 8.34783Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx125("defs", { children: /* @__PURE__ */ jsx125("clipPath", { id: "cfLI_clip0_0_1", children: /* @__PURE__ */ jsx125("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LK.tsx
import { jsx as jsx126, jsxs as jsxs124 } from "react/jsx-runtime";
var FlagLK = (props) => /* @__PURE__ */ jsxs124(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs124("g", { clipPath: "url(#cfLK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M4.6875e-05 2.08698H0V21.913H4.6875e-05V12.0001V2.08698Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37257 18.6274 0 12 0C5.37257 0 0 5.37257 0 12C0 18.6274 5.37257 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M9.39136 2.08698H5.2366C5.05247 2.21283 4.87228 2.34385 4.69571 2.47946L3.65222 12L4.69571 21.5205C4.87228 21.6561 5.05247 21.7871 5.2366 21.9129H9.39136V2.08698Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M4.57764e-05 12.0001C4.57764e-05 15.8787 1.84073 19.327 4.6957 21.5206V2.47952C1.84073 4.67318 4.57764e-05 8.1214 4.57764e-05 12.0001Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M19.3044 7.31552V7.61576L19.3155 7.61744L19.3044 7.31552Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx126(
      "path",
      {
        d: "M23.0435 15.1304H20.7826L19.8261 16.1739V18.2609H17.7392V17.2174H18.7827V15.1304H14.087V16.4348H13.0435V13.949C12.7234 13.6624 12.5218 13.2461 12.5218 12.7826V6.00004C12.5218 5.13561 13.2226 4.43483 14.087 4.43483V14.087H16.174L16.89 13.4902C16.765 13.1923 16.6957 12.8652 16.6957 12.5218V10.9566H15.1305V7.82619H18.2609C18.2609 7.30447 19.0435 6.78271 19.0435 6.78271C19.0435 6.78271 19.8261 7.30443 19.8261 7.82619V8.60882V10.9566C20.4066 10.9635 21.3288 10.9485 22.3251 10.9566C22.0634 10.4991 21.9131 9.95629 21.9131 9.39144C21.9131 8.47077 22.3106 7.64305 22.9433 7.07024C22.0313 5.04922 20.576 3.32601 18.7635 2.08701H10.9566V21.9131H18.7635C20.9675 20.4065 22.6433 18.1841 23.4563 15.5807L23.0435 15.1304Z",
        fill: "#A2001D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx126("defs", { children: /* @__PURE__ */ jsx126("clipPath", { id: "cfLK_clip0_0_1", children: /* @__PURE__ */ jsx126("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LR.tsx
import { jsx as jsx127, jsxs as jsxs125 } from "react/jsx-runtime";
var FlagLR = (props) => /* @__PURE__ */ jsxs125(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs125("g", { clipPath: "url(#cfLR_clip0_0_1)", children: [
    /* @__PURE__ */ jsxs125("g", { children: [
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M2.74292 4.36369H21.257C20.5732 3.53564 19.7801 2.80134 18.9003 2.18189H5.09965C4.21986 2.80125 3.42678 3.53559 2.74292 4.36369Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M0 12C0 12.3678 0.0174844 12.7315 0.049875 13.0909H23.9501C23.9825 12.7315 24 12.3678 24 12C24 11.6322 23.9825 11.2685 23.9501 10.9091H0.049875C0.0174844 11.2685 0 11.6322 0 12H0Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M1.30853 17.4545H22.6914C23.0448 16.7633 23.3329 16.0334 23.5481 15.2728H0.451843C0.666953 16.0334 0.955234 16.7633 1.30853 17.4545Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M5.09965 21.8182H18.9003C19.7801 21.1987 20.5732 20.4645 21.257 19.6364H2.74292C3.42678 20.4644 4.21986 21.1987 5.09965 21.8182Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M0.451782 8.72728H23.5481C23.333 7.96659 23.0447 7.23665 22.6914 6.54548H1.30852C0.95522 7.23665 0.666938 7.96659 0.451782 8.72728Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs125("g", { children: [
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M5.7392 2.1818H18.9004C16.9485 0.807516 14.5687 0 12.0001 0C10.4349 0 7.69107 0.807516 5.7392 2.1818Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M11.4783 6.54549H22.6915C22.2924 5.76488 21.8105 5.03367 21.2571 4.36369H11.4783V6.54549Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M11.4783 10.9091H23.9502C23.8827 10.16 23.747 9.4304 23.5482 8.72733H11.4783V10.9091Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M0.451927 15.2728H23.5482C23.747 14.5697 23.8827 13.8401 23.9502 13.091H0.0499268C0.117427 13.8401 0.253083 14.5697 0.451927 15.2728Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M2.74297 19.6363H21.2571C21.8105 18.9662 22.2924 18.2351 22.6915 17.4545H1.30859C1.70764 18.2351 2.18961 18.9663 2.74297 19.6363Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx127(
        "path",
        {
          d: "M12.0001 24C14.5687 24 16.9485 23.1925 18.9004 21.8182H5.09973C7.05161 23.1925 9.43145 24 12.0001 24Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx127(
      "path",
      {
        d: "M0.45188 8.72728C0.253036 9.43041 0.117427 10.1599 0.0499268 10.9091H12.0001V0C6.50724 0 1.8765 3.69056 0.45188 8.72728Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx127(
      "path",
      {
        d: "M7.1432 3.13045L7.92015 5.52178H10.4348L8.40053 6.99984L9.17743 9.39131L7.1432 7.91334L5.10887 9.39131L5.88597 6.99984L3.85168 5.52178H6.3662L7.1432 3.13045Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx127("defs", { children: /* @__PURE__ */ jsx127("clipPath", { id: "cfLR_clip0_0_1", children: /* @__PURE__ */ jsx127("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LS.tsx
import { jsx as jsx128, jsxs as jsxs126 } from "react/jsx-runtime";
var FlagLS = (props) => /* @__PURE__ */ jsxs126(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs126("g", { clipPath: "url(#cfLS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx128(
      "path",
      {
        d: "M0 12C0 13.8698 0.427828 15.6398 1.19072 17.2174L12 17.7391L22.8093 17.2174C23.5722 15.6398 24 13.8698 24 12C24 10.1302 23.5722 8.36025 22.8093 6.78263L12 6.26086L1.19072 6.78258C0.427828 8.36026 0 10.1302 0 12H0Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx128(
      "path",
      {
        d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx128(
      "path",
      {
        d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx128(
      "path",
      {
        d: "M12.7826 11.7391V8.86957H11.2174V11.7391L9.3269 13.6296C9.8767 14.5296 10.8682 15.1304 12 15.1304C13.1318 15.1304 14.1232 14.5296 14.6731 13.6296L12.7826 11.7391Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx128("defs", { children: /* @__PURE__ */ jsx128("clipPath", { id: "cfLS_clip0_0_1", children: /* @__PURE__ */ jsx128("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LT.tsx
import { jsx as jsx129, jsxs as jsxs127 } from "react/jsx-runtime";
var FlagLT = (props) => /* @__PURE__ */ jsxs127(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs127("g", { clipPath: "url(#cfLT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx129(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.874 24 13.4678 24 12C24 10.5322 23.736 9.12605 23.2536 7.82611L12 6.78263L0.746391 7.82611C0.264 9.12605 0 10.5322 0 12C0 13.4678 0.264 14.874 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx129(
      "path",
      {
        d: "M23.2536 7.82611C21.5581 3.2565 17.1596 0 12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2536Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx129(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx129("defs", { children: /* @__PURE__ */ jsx129("clipPath", { id: "cfLT_clip0_0_1", children: /* @__PURE__ */ jsx129("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LU.tsx
import { jsx as jsx130, jsxs as jsxs128 } from "react/jsx-runtime";
var FlagLU = (props) => /* @__PURE__ */ jsxs128(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs128("g", { clipPath: "url(#cfLU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx130(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx130(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx130(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#338AF3"
      }
    )
  ] }),
  /* @__PURE__ */ jsx130("defs", { children: /* @__PURE__ */ jsx130("clipPath", { id: "cfLU_clip0_0_1", children: /* @__PURE__ */ jsx130("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LV.tsx
import { jsx as jsx131, jsxs as jsxs129 } from "react/jsx-runtime";
var FlagLV = (props) => /* @__PURE__ */ jsxs129(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs129("g", { clipPath: "url(#cfLV_clip0_0_1)", children: [
    /* @__PURE__ */ jsx131(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs129("g", { children: [
      /* @__PURE__ */ jsx131(
        "path",
        {
          d: "M12 0C6.45567 0 1.79039 3.76036 0.413391 8.86955H23.5867C22.2096 3.76036 17.5443 0 12 0Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx131(
        "path",
        {
          d: "M12 24C17.5443 24 22.2096 20.2396 23.5866 15.1304H0.413391C1.79039 20.2396 6.45567 24 12 24Z",
          fill: "#A2001D"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx131("defs", { children: /* @__PURE__ */ jsx131("clipPath", { id: "cfLV_clip0_0_1", children: /* @__PURE__ */ jsx131("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/LY.tsx
import { jsx as jsx132, jsxs as jsxs130 } from "react/jsx-runtime";
var FlagLY = (props) => /* @__PURE__ */ jsxs130(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs130("g", { clipPath: "url(#cfLY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx132(
      "path",
      {
        d: "M1.19072 6.78262C0.427875 8.36025 0 10.1301 0 12C0 13.8699 0.427875 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174C23.5721 15.6397 24 13.8699 24 12C24 10.1301 23.5721 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx132(
      "path",
      {
        d: "M1.19116 17.2174C3.13207 21.2313 7.24249 24 12 24C16.7575 24 20.868 21.2313 22.8089 17.2174H1.19116Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx132(
      "path",
      {
        d: "M1.19116 6.78263H22.8089C20.868 2.76867 16.7575 0 12 0C7.24249 0 3.13207 2.76867 1.19116 6.78263Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs130("g", { children: [
      /* @__PURE__ */ jsx132(
        "path",
        {
          d: "M14.7931 9.8056L15.7777 11.1624L17.3722 10.6454L16.3861 12.001L17.3705 13.3577L15.7766 12.8387L14.7904 14.1944L14.7915 12.5181L13.1974 11.9991L14.792 11.482L14.7931 9.8056Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx132(
        "path",
        {
          d: "M12.106 15.3913C10.233 15.3913 8.71467 13.8729 8.71467 12C8.71467 10.1271 10.233 8.60869 12.106 8.60869C12.6899 8.60869 13.2395 8.75635 13.7192 9.01632C12.9666 8.28033 11.9374 7.82607 10.8016 7.82607C8.49642 7.82607 6.62775 9.69478 6.62775 12C6.62775 14.3051 8.49647 16.1738 10.8016 16.1738C11.9374 16.1738 12.9666 15.7196 13.7192 14.9836C13.2394 15.2437 12.69 15.3913 12.106 15.3913Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx132("defs", { children: /* @__PURE__ */ jsx132("clipPath", { id: "cfLY_clip0_0_1", children: /* @__PURE__ */ jsx132("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MA.tsx
import { jsx as jsx133, jsxs as jsxs131 } from "react/jsx-runtime";
var FlagMA = (props) => /* @__PURE__ */ jsxs131(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs131("g", { clipPath: "url(#cfMA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx133(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx133(
      "path",
      {
        d: "M19.0916 9.84797H13.6741L12 4.69566L10.3259 9.84797H4.90839L9.29125 13.0323L7.61711 18.1847L12 15.0004L16.3829 18.1848L14.7088 13.0323L19.0916 9.84797ZM10.5179 12.6337L11.084 10.8914H12.9159L13.4821 12.6337V12.6337L12 13.7105L10.518 12.6337L10.5179 12.6337ZM12.5769 9.84797H11.4231L12 8.07234L12.5769 9.84797ZM14.3697 11.9888L14.0131 10.8915H15.8801L14.3697 11.9888ZM9.98687 10.8915L9.63029 11.9888L8.11984 10.8915H9.98687ZM9.60193 15.4529L10.1789 13.6773L11.1124 14.3555L9.60193 15.4529ZM12.8876 14.3555L13.8212 13.6773L14.3981 15.4529L12.8876 14.3555Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx133("defs", { children: /* @__PURE__ */ jsx133("clipPath", { id: "cfMA_clip0_0_1", children: /* @__PURE__ */ jsx133("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MC.tsx
import { jsx as jsx134, jsxs as jsxs132 } from "react/jsx-runtime";
var FlagMC = (props) => /* @__PURE__ */ jsxs132(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs132("g", { clipPath: "url(#cfMC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx134(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx134(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx134("defs", { children: /* @__PURE__ */ jsx134("clipPath", { id: "cfMC_clip0_0_1", children: /* @__PURE__ */ jsx134("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MD.tsx
import { jsx as jsx135, jsxs as jsxs133 } from "react/jsx-runtime";
var FlagMD = (props) => /* @__PURE__ */ jsxs133(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs133("g", { clipPath: "url(#cfMD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx135(
      "path",
      {
        d: "M17.2174 1.19072C15.6397 0.427875 13.8698 0 12 0C10.1302 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093C8.36025 23.5721 10.1302 24 12 24C13.8698 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx135(
      "path",
      {
        d: "M6.78263 1.19114C2.76872 3.13209 0 7.24251 0 12C0 16.7575 2.76872 20.8679 6.78263 22.8089V1.19114Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx135(
      "path",
      {
        d: "M17.2173 1.19113V22.8088C21.2313 20.8679 24 16.7575 24 12C24 7.24251 21.2313 3.13208 17.2173 1.19113Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx135(
      "path",
      {
        d: "M16.1739 9.44152H13.3043C13.3043 8.72115 12.7204 8.13718 12 8.13718C11.2796 8.13718 10.6957 8.72115 10.6957 9.44152H7.82611C7.82611 10.1508 8.44397 10.7257 9.15314 10.7257H9.11039C9.11039 11.435 9.68536 12.01 10.3947 12.01C10.3947 12.638 10.8457 13.1598 11.4414 13.2714L10.4294 15.5562C10.9143 15.7533 11.4443 15.8628 12 15.8628C12.5557 15.8628 13.0857 15.7533 13.5706 15.5562L12.5586 13.2714C13.1543 13.1598 13.6053 12.638 13.6053 12.01C14.3146 12.01 14.8896 11.435 14.8896 10.7257H14.8468C15.5561 10.7257 16.1739 10.1507 16.1739 9.44152Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx135(
      "path",
      {
        d: "M12 11.2174L10.3044 12V13.5652L12 14.6087L13.6957 13.5652V12L12 11.2174Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx135("path", { d: "M10.3044 10.4348H13.6957V12H10.3044V10.4348Z", fill: "#D80027" })
  ] }),
  /* @__PURE__ */ jsx135("defs", { children: /* @__PURE__ */ jsx135("clipPath", { id: "cfMD_clip0_0_1", children: /* @__PURE__ */ jsx135("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ME.tsx
import { jsx as jsx136, jsxs as jsxs134 } from "react/jsx-runtime";
var FlagME = (props) => /* @__PURE__ */ jsxs134(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs134("g", { clipPath: "url(#cfME_clip0_0_1)", children: [
    /* @__PURE__ */ jsx136(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx136(
      "path",
      {
        d: "M24 12C24 7.88151 21.9249 4.24805 18.7635 2.08697H5.23655C2.07516 4.24805 0 7.88151 0 12C0 16.1186 2.07516 19.7519 5.23655 21.913H18.7635C21.9249 19.7519 24 16.1186 24 12Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs134("g", { children: [
      /* @__PURE__ */ jsx136(
        "path",
        {
          d: "M9.39136 8.86955H14.6087V6.78263L13.5652 7.30435L12 5.73914L10.4348 7.30435L9.39136 6.78263V8.86955Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx136(
        "path",
        {
          d: "M16.6957 12H14.088C14.1902 11.8231 14.2497 11.6184 14.2497 11.3994C14.2497 10.7344 13.7106 10.1954 13.0457 10.1954C12.5862 10.1954 12.1872 10.4528 11.9842 10.8311C11.7812 10.4527 11.3822 10.1954 10.9228 10.1954C10.2579 10.1954 9.71874 10.7344 9.71874 11.3994C9.71874 11.6184 9.77828 11.8231 9.88046 12H7.30426C7.30426 12.798 7.99928 13.4448 8.79718 13.4448H8.74909C8.74909 14.0993 9.18442 14.652 9.78128 14.8295L8.6086 16.0022L9.71537 17.109L11.2854 15.539C11.3469 15.5616 11.4105 15.5796 11.4762 15.5919L10.5276 17.7339C10.9822 17.9188 11.479 18.0214 12 18.0214C12.521 18.0214 13.0178 17.9188 13.4724 17.7339L12.5238 15.5919C12.5895 15.5796 12.6531 15.5616 12.7146 15.539L14.2846 17.109L15.3913 16.0022L14.2186 14.8295C14.8154 14.6519 15.2507 14.0993 15.2507 13.4448H15.2026C16.0006 13.4448 16.6957 12.798 16.6957 12Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx136(
      "path",
      {
        d: "M12 12.7826L10.3043 13.5652V15.1305L12 16.1739L13.6956 15.1305V13.5652L12 12.7826Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx136("path", { d: "M10.3043 12H13.6956V14.087H10.3043V12Z", fill: "#0052B4" })
  ] }),
  /* @__PURE__ */ jsx136("defs", { children: /* @__PURE__ */ jsx136("clipPath", { id: "cfME_clip0_0_1", children: /* @__PURE__ */ jsx136("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MG.tsx
import { jsx as jsx137, jsxs as jsxs135 } from "react/jsx-runtime";
var FlagMG = (props) => /* @__PURE__ */ jsxs135(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs135("g", { clipPath: "url(#cfMG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx137(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx137(
      "path",
      {
        d: "M7.82611 12V23.2536C9.12605 23.736 10.5322 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37263 7.82611 12 7.82611 12Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx137(
      "path",
      {
        d: "M12 0C10.5322 0 9.12605 0.264047 7.82611 0.746391V12H24C24 5.37262 18.6274 0 12 0Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx137("defs", { children: /* @__PURE__ */ jsx137("clipPath", { id: "cfMG_clip0_0_1", children: /* @__PURE__ */ jsx137("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MH.tsx
import { jsx as jsx138, jsxs as jsxs136 } from "react/jsx-runtime";
var FlagMH = (props) => /* @__PURE__ */ jsxs136(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs136("g", { clipPath: "url(#cfMH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx138(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 9.98611 23.5031 8.08847 22.6265 6.42188L11.9859 12.0141L17.5781 1.37353C15.9115 0.496875 14.0139 0 12 0C5.37262 0 0 5.37262 0 12C0 15.3137 1.34316 18.3137 3.51469 20.4853C5.68627 22.6569 8.68627 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx138(
      "path",
      {
        d: "M22.6138 6.43495C22.0645 5.38945 21.3504 4.40784 20.4713 3.52875C19.5922 2.64965 18.6107 1.93551 17.5651 1.38628L3.50067 20.4993L22.6138 6.43495Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx138(
      "path",
      {
        d: "M3.50067 20.4993L22.6138 6.43495C22.0645 5.38945 21.3504 4.40784 20.4713 3.52875L3.50067 20.4993Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx138(
      "path",
      {
        d: "M10.4207 7.03697L8.95522 7.72631L9.73564 9.14569L8.14423 8.84123L7.94258 10.4489L6.83408 9.26644L5.72558 10.4489L5.52401 8.84123L3.93261 9.14559L4.71294 7.72626L3.24744 7.03697L4.71298 6.34758L3.93261 4.9283L5.52392 5.23266L5.72562 3.62503L6.83408 4.80745L7.94258 3.62503L8.14423 5.23266L9.73569 4.9283L8.95526 6.34762L10.4207 7.03697Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx138("defs", { children: /* @__PURE__ */ jsx138("clipPath", { id: "cfMH_clip0_0_1", children: /* @__PURE__ */ jsx138("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MK.tsx
import { jsx as jsx139, jsxs as jsxs137 } from "react/jsx-runtime";
var FlagMK = (props) => /* @__PURE__ */ jsxs137(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs137("g", { clipPath: "url(#cfMK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx139(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs137("g", { children: [
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M13.8249 23.862C15.7766 23.5643 17.5732 22.7957 19.0963 21.6768L12 12L13.8249 23.862Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M10.175 0.137939C8.22341 0.435689 6.42688 1.20425 4.90363 2.32311L12 12L10.175 0.137939Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M4.90363 21.6768C6.42678 22.7957 8.22331 23.5643 10.175 23.8621L12 12L4.90363 21.6768Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M2.32314 4.90359C1.20424 6.42684 0.435767 8.22333 0.13797 10.175L12 12L2.32314 4.90359Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M0.13797 13.8249C0.43572 15.7766 1.20428 17.5732 2.32319 19.0963L12 12L0.13797 13.8249Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M23.862 10.175C23.5642 8.22336 22.7957 6.42683 21.6768 4.90363L12 12L23.862 10.175Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M19.0963 2.32306C17.5731 1.20421 15.7766 0.435689 13.8249 0.137939L12 12L19.0963 2.32306Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M21.6769 19.0964C22.7958 17.5732 23.5643 15.7766 23.8621 13.825L12 12L21.6769 19.0964Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx139(
        "path",
        {
          d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx139(
      "path",
      {
        d: "M12 15.1304C13.7289 15.1304 15.1304 13.7289 15.1304 12C15.1304 10.2711 13.7289 8.86954 12 8.86954C10.2711 8.86954 8.86954 10.2711 8.86954 12C8.86954 13.7289 10.2711 15.1304 12 15.1304Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx139("defs", { children: /* @__PURE__ */ jsx139("clipPath", { id: "cfMK_clip0_0_1", children: /* @__PURE__ */ jsx139("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ML.tsx
import { jsx as jsx140, jsxs as jsxs138 } from "react/jsx-runtime";
var FlagML = (props) => /* @__PURE__ */ jsxs138(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs138("g", { clipPath: "url(#cfML_clip0_0_1)", children: [
    /* @__PURE__ */ jsx140(
      "path",
      {
        d: "M16.1739 0.746391C14.8739 0.264047 13.4679 0 12 0C10.5322 0 9.12612 0.264047 7.82614 0.746391L6.78265 12L7.82614 23.2536C9.12612 23.736 10.5322 24 12 24C13.4679 24 14.8739 23.736 16.1739 23.2536L17.2174 12L16.1739 0.746391Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx140(
      "path",
      {
        d: "M24 12C24 6.84047 20.7435 2.44191 16.1739 0.746437V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx140(
      "path",
      {
        d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2537V0.746437C3.2565 2.44191 0 6.84047 0 12Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx140("defs", { children: /* @__PURE__ */ jsx140("clipPath", { id: "cfML_clip0_0_1", children: /* @__PURE__ */ jsx140("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MM.tsx
import { jsx as jsx141, jsxs as jsxs139 } from "react/jsx-runtime";
var FlagMM = (props) => /* @__PURE__ */ jsxs139(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs139("g", { clipPath: "url(#cfMM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx141(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.874 24 13.4678 24 12C24 10.5322 23.736 9.12605 23.2536 7.82611L12 6.78262L0.746391 7.82611C0.264047 9.12605 0 10.5322 0 12C0 13.4678 0.264047 14.874 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx141(
      "path",
      {
        d: "M23.2537 7.82611C21.5582 3.2565 17.1596 0 12.0001 0C6.84049 0 2.44198 3.2565 0.74646 7.82611H23.2537Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx141(
      "path",
      {
        d: "M12.0001 24C17.1596 24 21.5582 20.7435 23.2537 16.1739H0.74646C2.44198 20.7435 6.84049 24 12.0001 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx141(
      "path",
      {
        d: "M20.2288 10.1525H13.9426L12 4.17389L10.0574 10.1525H3.77112L8.85682 13.8474L6.91423 19.8261L12 16.1739L17.0857 19.8261L15.1432 13.8474L20.2288 10.1525Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx141("defs", { children: /* @__PURE__ */ jsx141("clipPath", { id: "cfMM_clip0_0_1", children: /* @__PURE__ */ jsx141("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MN.tsx
import { jsx as jsx142, jsxs as jsxs140 } from "react/jsx-runtime";
var FlagMN = (props) => /* @__PURE__ */ jsxs140(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs140("g", { clipPath: "url(#cfMN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx142(
      "path",
      {
        d: "M12 24C13.4678 24 14.8739 23.736 16.1739 23.2536L16.6956 12L16.1739 0.746391C14.8739 0.264047 13.4678 0 12 0C10.5321 0 9.12602 0.264047 7.82609 0.746391L7.30432 12L7.82604 23.2536C9.12602 23.736 10.5321 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs140("g", { children: [
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746391C3.2565 2.44191 0 6.84042 0 12Z",
          fill: "#A2001D"
        }
      ),
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M16.1739 0.746391V23.2537C20.7435 21.5581 24 17.1596 24 12C24 6.84042 20.7435 2.44191 16.1739 0.746391Z",
          fill: "#A2001D"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs140("g", { children: [
      /* @__PURE__ */ jsx142("path", { d: "M5.73914 12H6.78262V16.1739H5.73914V12Z", fill: "#FFDA44" }),
      /* @__PURE__ */ jsx142("path", { d: "M1.56519 12H2.60867V16.1739H1.56519V12Z", fill: "#FFDA44" }),
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M4.17386 14.6087C4.75016 14.6087 5.21734 14.1415 5.21734 13.5652C5.21734 12.9889 4.75016 12.5217 4.17386 12.5217C3.59756 12.5217 3.13037 12.9889 3.13037 13.5652C3.13037 14.1415 3.59756 14.6087 4.17386 14.6087Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M4.17388 10.4347C4.46202 10.4347 4.6956 10.2012 4.6956 9.91303C4.6956 9.62489 4.46202 9.39131 4.17388 9.39131C3.88574 9.39131 3.65216 9.62489 3.65216 9.91303C3.65216 10.2012 3.88574 10.4347 4.17388 10.4347Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M3.13043 15.1304H5.2174V16.1739H3.13043V15.1304Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx142("path", { d: "M3.13043 10.9565H5.2174V12H3.13043V10.9565Z", fill: "#FFDA44" }),
      /* @__PURE__ */ jsx142(
        "path",
        {
          d: "M4.17385 6.26086L4.54341 7.39804H5.7391L4.77183 8.10089L5.14111 9.23808L4.17385 8.53523L3.20663 9.23808L3.57591 8.10089L2.60864 7.39804H3.80433L4.17385 6.26086Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx142("defs", { children: /* @__PURE__ */ jsx142("clipPath", { id: "cfMN_clip0_0_1", children: /* @__PURE__ */ jsx142("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MO.tsx
import { jsx as jsx143, jsxs as jsxs141 } from "react/jsx-runtime";
var FlagMO = (props) => /* @__PURE__ */ jsxs141(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs141("g", { clipPath: "url(#cfMO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx143(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx143(
      "path",
      {
        d: "M14.087 11.4996C13.7924 11.4996 13.5128 11.5583 13.2531 11.6518C13.3953 11.3348 13.4907 10.982 13.4907 10.6052C13.4907 9.11456 12.0001 7.99655 12.0001 7.99655C12.0001 7.99655 10.5094 9.11456 10.5094 10.6052C10.5094 10.9819 10.6048 11.3348 10.747 11.6518C10.4873 11.5584 10.2076 11.4996 9.91313 11.4996C8.42246 11.4996 7.30444 12.9903 7.30444 12.9903C7.30444 12.9903 8.42246 14.481 9.91313 14.481C10.7871 14.481 11.5325 13.9688 12.0001 13.5447C12.4677 13.9687 13.2131 14.481 14.0871 14.481C15.5777 14.481 16.6958 12.9903 16.6958 12.9903C16.6958 12.9903 15.5776 11.4996 14.087 11.4996Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs141("g", { children: [
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M12 4.69566L12.3079 5.64328H13.3043L12.4983 6.22903L12.806 7.17666L12 6.59091L11.1939 7.17666L11.5016 6.22903L10.6956 5.64328H11.692L12 4.69566Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M8.21098 6.35311L8.80339 6.88645L9.49371 6.48792L9.16953 7.21612L9.76189 7.74956L8.96914 7.66622L8.64495 8.39442L8.4791 7.61475L7.6864 7.53141L8.37668 7.13278L8.21098 6.35311Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M5.43719 9.12689L6.21691 9.29259L6.61549 8.60226L6.69884 9.39497L7.47846 9.56081L6.7503 9.88505L6.83365 10.6777L6.30016 10.0854L5.57201 10.4096L5.97059 9.71925L5.43719 9.12689Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M15.789 6.35311L15.1967 6.88645L14.5063 6.48792L14.8305 7.21612L14.2382 7.74956L15.0309 7.66622L15.3551 8.39442L15.5209 7.61475L16.3136 7.53141L15.6234 7.13278L15.789 6.35311Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M18.5628 9.12689L17.7831 9.29259L17.3846 8.60226L17.3012 9.39497L16.5215 9.56081L17.2497 9.88505L17.1664 10.6777L17.6998 10.0854L18.428 10.4096L18.0294 9.71925L18.5628 9.12689Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs141("g", { children: [
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M12 18.6549C13.1841 18.6549 14.2755 18.2599 15.151 17.5951H8.84894C9.72447 18.2598 10.8159 18.6549 12 18.6549Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx143(
        "path",
        {
          d: "M7.21722 15.5245C7.38283 15.9035 7.59283 16.2585 7.83949 16.5843H16.1606C16.4073 16.2585 16.6173 15.9035 16.7829 15.5245H7.21722Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx143("defs", { children: /* @__PURE__ */ jsx143("clipPath", { id: "cfMO_clip0_0_1", children: /* @__PURE__ */ jsx143("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MP.tsx
import { jsx as jsx144, jsxs as jsxs142 } from "react/jsx-runtime";
var FlagMP = (props) => /* @__PURE__ */ jsxs142(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs142("g", { clipPath: "url(#cfMP_clip0_0_1)", children: [
    /* @__PURE__ */ jsx144(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx144(
      "path",
      {
        d: "M11.6217 19.5558C11.2982 19.5401 11.0305 19.3299 10.9257 19.0445C10.7405 19.286 10.4222 19.4076 10.1087 19.3267C9.79455 19.2459 9.57494 18.986 9.52952 18.6845C9.29889 18.8837 8.96289 18.938 8.67231 18.7956C8.38117 18.6526 8.21842 18.3536 8.23511 18.0494C7.96956 18.1986 7.62939 18.1841 7.37322 17.9858C7.11756 17.7875 7.01791 17.4625 7.09511 17.1677C6.805 17.2605 6.47528 17.1781 6.26397 16.9324C6.05266 16.6872 6.02027 16.3485 6.15494 16.0757C5.85231 16.108 5.54556 15.9609 5.38797 15.6781C5.22986 15.3953 5.26642 15.0567 5.45369 14.8161C5.15055 14.7869 4.87923 14.5808 4.7822 14.272C4.68466 13.9631 4.789 13.6386 5.02117 13.4408C4.73003 13.351 4.50672 13.0943 4.47386 12.7725C4.441 12.4506 4.60797 12.1548 4.87455 12.0076C4.60792 11.8615 4.43992 11.5657 4.47175 11.2443H4.47227C4.50409 10.9219 4.72741 10.6647 5.01798 10.5739C4.78581 10.3767 4.68044 10.0527 4.77695 9.74375C4.87347 9.43437 5.14422 9.22775 5.44741 9.19747C5.26009 8.95747 5.22255 8.61879 5.37958 8.33558C5.53661 8.05278 5.84341 7.90512 6.14655 7.93695C6.01089 7.66409 6.04272 7.32547 6.25352 7.07923C6.4638 6.83351 6.79352 6.75054 7.08414 6.84237C7.00586 6.54809 7.105 6.22254 7.36066 6.02379C7.6158 5.82495 7.95597 5.80981 8.22152 5.95798C8.20431 5.65386 8.36655 5.35484 8.65717 5.21136C8.94728 5.06839 9.2838 5.12164 9.51489 5.3209C9.55923 5.0194 9.77889 4.75854 10.0924 4.67708C10.4055 4.5957 10.7238 4.71622 10.9095 4.95725C11.0143 4.67187 11.2815 4.46108 11.605 4.4449C11.9279 4.42826 12.2148 4.60976 12.3484 4.88272C12.5081 4.62392 12.8117 4.47054 13.1316 4.51906C13.4519 4.56706 13.6977 4.80284 13.7739 5.09769C13.9825 4.87536 14.3112 4.78723 14.6149 4.89884C14.9191 5.01101 15.1121 5.2917 15.1267 5.59639C15.3756 5.42112 15.7157 5.40073 15.9907 5.57187C16.2657 5.7425 16.3977 6.05604 16.3507 6.35708C16.6299 6.23553 16.9669 6.28404 17.2017 6.50628C17.4365 6.72908 17.5032 7.06245 17.3968 7.34783C17.6947 7.28469 18.0151 7.39948 18.2008 7.66503C18.3865 7.93006 18.385 8.27023 18.2238 8.52851C18.528 8.5264 18.8186 8.70383 18.9475 9.00069C19.0758 9.29806 19.0053 9.63148 18.7951 9.85217C19.0941 9.91165 19.3429 10.1438 19.4087 10.461C19.4739 10.7782 19.3377 11.0897 19.0878 11.2635C19.3675 11.382 19.5647 11.659 19.5652 11.9825V11.9929V11.9997C19.5652 12.3231 19.369 12.6007 19.0893 12.7202C19.3398 12.893 19.477 13.2044 19.4118 13.5216C19.3471 13.8388 19.0988 14.0715 18.7998 14.132C19.0106 14.3522 19.0821 14.6851 18.9543 14.983C18.8264 15.2804 18.5358 15.4583 18.2311 15.4567C18.3928 15.715 18.3955 16.0552 18.2102 16.3207C18.025 16.5864 17.7052 16.7022 17.4073 16.6395C17.5142 16.9244 17.448 17.2583 17.2137 17.4816C16.9789 17.7045 16.6424 17.7535 16.3633 17.633C16.4108 17.9335 16.2793 18.2476 16.0048 18.4192C15.7304 18.5909 15.3902 18.571 15.1408 18.3963C15.1267 18.701 14.9347 18.9822 14.6311 19.0949C14.3275 19.2076 14.6311 19.0949 14.6306 19.0949C14.3269 19.2071 13.9983 19.1195 13.789 18.8977C13.7139 19.193 13.4687 19.4293 13.1484 19.4784C12.8285 19.5269 12.5244 19.3746 12.3642 19.1158C12.2369 19.3782 11.9677 19.5567 11.6609 19.5567C11.6478 19.5568 11.6348 19.5563 11.6217 19.5558Z",
        fill: "#F3F3F3"
      }
    ),
    /* @__PURE__ */ jsx144(
      "path",
      {
        d: "M12 17.2174C14.8815 17.2174 17.2174 14.8815 17.2174 12C17.2174 9.11852 14.8815 6.78262 12 6.78262C9.11855 6.78262 6.78265 9.11852 6.78265 12C6.78265 14.8815 9.11855 17.2174 12 17.2174Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx144(
      "path",
      {
        d: "M13.142 10.229C13.3983 10.0389 13.5652 9.735 13.5652 9.39131V8.8696C13.5652 8.29327 13.098 7.82611 12.5217 7.82611H11.4783C10.9019 7.82611 10.4348 8.29327 10.4348 8.8696V9.39131C10.4348 9.735 10.6016 10.0389 10.858 10.229L9.91306 19.8261C9.91306 19.8261 10.4348 20.3479 12 20.3479C13.5653 20.3479 14.087 19.8261 14.087 19.8261L13.142 10.229Z",
        fill: "#BDBCC1"
      }
    ),
    /* @__PURE__ */ jsx144(
      "path",
      {
        d: "M11.9999 7.82611L12.9064 10.616H15.8401L13.4668 12.3405L14.3732 15.1305L11.9999 13.4062L9.62657 15.1305L10.5332 12.3405L8.15985 10.616H11.0934L11.9999 7.82611Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx144("defs", { children: /* @__PURE__ */ jsx144("clipPath", { id: "cfMP_clip0_0_1", children: /* @__PURE__ */ jsx144("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MQ.tsx
import { jsx as jsx145, jsxs as jsxs143 } from "react/jsx-runtime";
var FlagMQ = (props) => /* @__PURE__ */ jsxs143(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs143("g", { clipPath: "url(#cfMQ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx145(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs143("g", { children: [
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M23.8971 10.4348C23.5661 7.90416 22.4294 5.45888 20.4852 3.51474C18.5411 1.5706 16.0958 0.43383 13.5652 0.102798L13.5652 10.4348H23.8971Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M10.4347 0.102798C7.90414 0.43383 5.45881 1.57055 3.51467 3.51469C1.57058 5.45888 0.433814 7.90421 0.102783 10.4348L10.4348 10.4348L10.4347 0.102798Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M0.102783 13.5653C0.433814 16.0958 1.57058 18.5412 3.51467 20.4853C5.45881 22.4294 7.9041 23.5661 10.4348 23.8973L10.4347 13.5653L0.102783 13.5653Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M13.5652 23.8972C16.0958 23.5661 18.5411 22.4294 20.4853 20.4853C22.4294 18.5411 23.5661 16.0958 23.8972 13.5653H13.5652V23.8972Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs143("g", { children: [
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M16.0963 7.61428C16.0963 7.45472 16.226 7.32502 16.3856 7.32502H17.0055C17.9854 7.32502 18.7826 6.52786 18.7826 5.54803C18.7826 4.56821 17.9854 3.771 17.0055 3.771H16.4168C16.2247 3.62667 15.9669 3.53766 15.6826 3.53766C15.0895 3.53766 14.6087 3.92227 14.6087 4.39674C14.6087 4.86661 15.0803 5.2478 15.6655 5.25521L15.6652 5.25863H17.0055C17.1651 5.25863 17.2948 5.38838 17.2948 5.54794C17.2948 5.7075 17.1651 5.83721 17.0055 5.83721H16.3856C15.4058 5.83721 14.6086 6.63436 14.6086 7.61419C14.6086 8.59402 15.4057 9.39122 16.3856 9.39122H18.7825V7.9035H16.3856C16.226 7.90355 16.0963 7.77385 16.0963 7.61428Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M6.70509 7.61428C6.70509 7.45472 6.8348 7.32502 6.99441 7.32502H7.61428C8.59416 7.32502 9.39136 6.52786 9.39136 5.54803C9.39136 4.56821 8.5942 3.771 7.61428 3.771H7.02562C6.83348 3.62667 6.57572 3.53766 6.29137 3.53766C5.69827 3.53766 5.21747 3.92227 5.21747 4.39674C5.21747 4.86661 5.68908 5.2478 6.27427 5.25521L6.27398 5.25863H7.61433C7.77394 5.25863 7.90364 5.38838 7.90364 5.54794C7.90364 5.7075 7.77394 5.83721 7.61433 5.83721H6.99445C6.01458 5.83721 5.21747 6.63436 5.21747 7.61419C5.21747 8.59402 6.01453 9.39122 6.99445 9.39122H9.39131V7.9035H6.99441C6.8348 7.90355 6.70509 7.77385 6.70509 7.61428Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M16.0964 18.6853C16.0964 18.5258 16.2261 18.396 16.3857 18.396H17.0055C17.9854 18.396 18.7826 17.5989 18.7826 16.6191C18.7826 15.6392 17.9855 14.842 17.0055 14.842H16.4169C16.2248 14.6977 15.967 14.6087 15.6826 14.6087C15.0895 14.6087 14.6087 14.9933 14.6087 15.4678C14.6087 15.9376 15.0803 16.3188 15.6655 16.3262L15.6652 16.3297H17.0055C17.1651 16.3297 17.2948 16.4594 17.2948 16.619C17.2948 16.7785 17.1651 16.9082 17.0055 16.9082H16.3856C15.4058 16.9082 14.6086 17.7054 14.6086 18.6852C14.6086 19.665 15.4057 20.4623 16.3856 20.4623H18.7826V18.9746H16.3857C16.2261 18.9746 16.0964 18.8448 16.0964 18.6853Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx145(
        "path",
        {
          d: "M6.70508 18.6853C6.70508 18.5258 6.83478 18.396 6.99439 18.396H7.61427C8.59414 18.396 9.39134 17.5989 9.39134 16.6191C9.39134 15.6392 8.59419 14.842 7.61427 14.842H7.02561C6.83347 14.6977 6.5757 14.6087 6.29136 14.6087C5.6982 14.6087 5.21741 14.9933 5.21741 15.4678C5.21741 15.9376 5.68902 16.3188 6.2742 16.3262L6.27392 16.3297H7.61427C7.77388 16.3297 7.90358 16.4594 7.90358 16.619C7.90358 16.7785 7.77388 16.9082 7.61427 16.9082H6.99439C6.01452 16.9082 5.21741 17.7054 5.21741 18.6852C5.21741 19.665 6.01447 20.4623 6.99439 20.4623H9.3913V18.9746H6.99439C6.83478 18.9746 6.70508 18.8448 6.70508 18.6853Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx145("defs", { children: /* @__PURE__ */ jsx145("clipPath", { id: "cfMQ_clip0_0_1", children: /* @__PURE__ */ jsx145("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MR.tsx
import { jsx as jsx146, jsxs as jsxs144 } from "react/jsx-runtime";
var FlagMR = (props) => /* @__PURE__ */ jsxs144(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs144("g", { clipPath: "url(#cfMR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx146(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsxs144("g", { children: [
      /* @__PURE__ */ jsx146(
        "path",
        {
          d: "M12 13.8633C10.0019 13.8633 8.33269 12.459 7.92314 10.5838C7.86024 10.8721 7.82611 11.171 7.82611 11.4782C7.82611 13.7835 9.69478 15.6521 12 15.6521C14.3052 15.6521 16.1739 13.7835 16.1739 11.4782C16.1739 11.171 16.1398 10.8721 16.0769 10.5838C15.6673 12.4591 13.9981 13.8633 12 13.8633Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx146(
        "path",
        {
          d: "M11.9999 8.34782L12.3884 9.54356H13.6457L12.6286 10.2825L13.0171 11.4783L11.9999 10.7392L10.9828 11.4783L11.3713 10.2825L10.3542 9.54356H11.6114L11.9999 8.34782Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx146("defs", { children: /* @__PURE__ */ jsx146("clipPath", { id: "cfMR_clip0_0_1", children: /* @__PURE__ */ jsx146("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MS.tsx
import { jsx as jsx147, jsxs as jsxs145 } from "react/jsx-runtime";
var FlagMS = (props) => /* @__PURE__ */ jsxs145(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs145("g", { clipPath: "url(#cfMS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx147(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs145("g", { children: [
      /* @__PURE__ */ jsx147(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx147(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs145("g", { children: [
      /* @__PURE__ */ jsx147(
        "path",
        {
          d: "M6.071 1.56521C4.19417 2.63391 2.63384 4.19419 1.56519 6.07102V12H4.69564V4.69576V4.69566H12C12 3.70833 12 2.76774 12 1.56521H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx147(
        "path",
        {
          d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx147(
      "path",
      {
        d: "M13.5652 6.26086V11.9999C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 11.9999V6.26086H13.5652Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx147(
      "path",
      {
        d: "M13.5652 12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12H13.5652Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx147(
      "path",
      {
        d: "M18.7826 8.86964H17.7391V7.82611H16.6956V8.86964H15.6522V9.91308H16.6956V13.0435H17.7391V9.91308H18.7826V8.86964Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx147("defs", { children: /* @__PURE__ */ jsx147("clipPath", { id: "cfMS_clip0_0_1", children: /* @__PURE__ */ jsx147("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MT.tsx
import { jsx as jsx148, jsxs as jsxs146 } from "react/jsx-runtime";
var FlagMT = (props) => /* @__PURE__ */ jsxs146(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs146("g", { clipPath: "url(#cfMT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx148(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx148(
      "path",
      {
        d: "M12 0C18.6274 0 24 5.37262 24 12C24 18.6274 18.6274 24 12 24",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx148(
      "path",
      {
        d: "M8.3478 4.69565V3.13045H6.7826V4.69565H5.21735V6.26085H6.7826V7.8261H8.3478V6.26085H9.913V4.69565H8.3478Z",
        fill: "#ACABB1"
      }
    )
  ] }),
  /* @__PURE__ */ jsx148("defs", { children: /* @__PURE__ */ jsx148("clipPath", { id: "cfMT_clip0_0_1", children: /* @__PURE__ */ jsx148("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MU.tsx
import { jsx as jsx149, jsxs as jsxs147 } from "react/jsx-runtime";
var FlagMU = (props) => /* @__PURE__ */ jsxs147(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs147("g", { clipPath: "url(#cfMU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx149(
      "path",
      {
        d: "M1.45911 6.26086L12 7.30434L22.5409 6.26086C20.5057 2.53073 16.5485 0 12 0C7.45151 0 3.49437 2.53073 1.45911 6.26086Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx149(
      "path",
      {
        d: "M1.45908 17.7391L12 18.7826L22.5409 17.7391C23.4711 16.0342 24 14.0789 24 12L12 10.9565L0 12C0 14.0789 0.528891 16.0342 1.45908 17.7391Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx149(
      "path",
      {
        d: "M1.45908 6.26086C0.528891 7.96575 0 9.92109 0 12H24C24 9.92109 23.4711 7.96575 22.5409 6.26086H12H1.45908Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx149(
      "path",
      {
        d: "M12 24C16.5485 24 20.5057 21.4693 22.5409 17.7391H1.45911C3.49437 21.4693 7.45151 24 12 24Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx149("defs", { children: /* @__PURE__ */ jsx149("clipPath", { id: "cfMU_clip0_0_1", children: /* @__PURE__ */ jsx149("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MV.tsx
import { jsx as jsx150, jsxs as jsxs148 } from "react/jsx-runtime";
var FlagMV = (props) => /* @__PURE__ */ jsxs148(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs148("g", { clipPath: "url(#cfMV_clip0_0_1)", children: [
    /* @__PURE__ */ jsx150(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx150(
      "path",
      {
        d: "M4.17389 6.26086H19.8261V17.7391H4.17389V6.26086Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx150(
      "path",
      {
        d: "M13.9325 15.3913C12.0596 15.3913 10.5412 13.8729 10.5412 12C10.5412 10.1271 12.0596 8.60869 13.9325 8.60869C14.5165 8.60869 15.066 8.75635 15.5457 9.01632C14.7931 8.28033 13.764 7.82607 12.6282 7.82607C10.3231 7.82607 8.45428 9.69478 8.45428 12C8.45428 14.3051 10.3231 16.1738 12.6282 16.1738C13.764 16.1738 14.7932 15.7196 15.5457 14.9836C15.066 15.2437 14.5165 15.3913 13.9325 15.3913Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx150("defs", { children: /* @__PURE__ */ jsx150("clipPath", { id: "cfMV_clip0_0_1", children: /* @__PURE__ */ jsx150("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MW.tsx
import { jsx as jsx151, jsxs as jsxs149 } from "react/jsx-runtime";
var FlagMW = (props) => /* @__PURE__ */ jsxs149(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs149("g", { clipPath: "url(#cfMW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx151(
      "path",
      {
        d: "M0 12C0 13.4678 0.264047 14.8739 0.746391 16.1739L12 16.6957L23.2536 16.1739C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.12609 23.2536 7.82611L12 7.30434L0.746391 7.82606C0.264047 9.12609 0 10.5322 0 12H0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx151(
      "path",
      {
        d: "M12.0001 0C6.84049 0 2.44198 3.2565 0.74646 7.82611H23.2537C21.5582 3.2565 17.1596 0 12.0001 0Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx151(
      "path",
      {
        d: "M23.2537 16.1739H0.74646C2.44198 20.7435 6.84049 24 12.0001 24C17.1596 24 21.5582 20.7435 23.2537 16.1739Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx151(
      "path",
      {
        d: "M15.5866 5.73914L14.1211 5.0498L14.9015 3.63047L13.3101 3.93492L13.1085 2.3272L12 3.50962L10.8915 2.3272L10.6898 3.93492L9.09845 3.63047L9.87888 5.0498L8.41333 5.73914H15.5866Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx151("defs", { children: /* @__PURE__ */ jsx151("clipPath", { id: "cfMW_clip0_0_1", children: /* @__PURE__ */ jsx151("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MX.tsx
import { jsx as jsx152, jsxs as jsxs150 } from "react/jsx-runtime";
var FlagMX = (props) => /* @__PURE__ */ jsxs150(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs150("g", { clipPath: "url(#cfMX_clip0_0_1)", children: [
    /* @__PURE__ */ jsx152(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx152(
      "path",
      {
        d: "M24 12C24 7.24247 21.2314 3.13172 17.2174 1.19072V22.8092C21.2314 20.8683 24 16.7575 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs150("g", { children: [
      /* @__PURE__ */ jsx152(
        "path",
        {
          d: "M0 12C0 16.7575 2.76867 20.8683 6.78263 22.8093V1.19072C2.76867 3.13172 0 7.24247 0 12Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx152(
        "path",
        {
          d: "M8.86957 12C8.86957 13.7289 10.2711 15.1304 12 15.1304C13.7289 15.1304 15.1305 13.7289 15.1305 12V10.9565H8.86957V12Z",
          fill: "#6DA544"
        }
      )
    ] }),
    /* @__PURE__ */ jsx152(
      "path",
      {
        d: "M16.1739 9.91303H13.0434C13.0434 9.33675 12.5762 8.86954 11.9999 8.86954C11.4236 8.86954 10.9564 9.33675 10.9564 9.91303H7.82599C7.82599 10.4894 8.32797 10.9565 8.90421 10.9565H8.86947C8.86947 11.5328 9.33663 12 9.91296 12C9.91296 12.5763 10.3801 13.0435 10.9564 13.0435H13.0434C13.6197 13.0435 14.0869 12.5763 14.0869 12C14.6632 12 15.1304 11.5328 15.1304 10.9565H15.0956C15.6719 10.9565 16.1739 10.4894 16.1739 9.91303Z",
        fill: "#FF9811"
      }
    )
  ] }),
  /* @__PURE__ */ jsx152("defs", { children: /* @__PURE__ */ jsx152("clipPath", { id: "cfMX_clip0_0_1", children: /* @__PURE__ */ jsx152("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MY.tsx
import { jsx as jsx153, jsxs as jsxs151 } from "react/jsx-runtime";
var FlagMY = (props) => /* @__PURE__ */ jsxs151(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs151("g", { clipPath: "url(#cfMY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx153(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs151("g", { children: [
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M11.4782 12H23.9999C23.9999 10.9169 23.8556 9.8677 23.5865 8.86954H11.4782V12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M11.4782 5.73914H22.2386C21.5041 4.54045 20.5649 3.48094 19.4698 2.60869H11.4782V5.73914Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M11.9999 24C14.8241 24 17.4199 23.0239 19.4698 21.3913H4.53009C6.57998 23.0239 9.17578 24 11.9999 24Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M1.76122 18.2609H22.2387C22.8284 17.2986 23.2857 16.2467 23.5865 15.1304H0.41333C0.714127 16.2467 1.17149 17.2986 1.76122 18.2609Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx153(
      "path",
      {
        d: "M12 12C12 5.37262 12 4.59131 12 0C5.37262 0 0 5.37262 0 12H12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs151("g", { children: [
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M7.9797 10.2717C6.34086 10.2717 5.01233 8.94314 5.01233 7.30435C5.01233 5.66555 6.34091 4.33697 7.9797 4.33697C8.49069 4.33697 8.97148 4.46621 9.39125 4.69364C8.73275 4.04972 7.83219 3.65222 6.83839 3.65222C4.82141 3.65222 3.18622 5.28736 3.18622 7.30439C3.18622 9.32142 4.82141 10.9566 6.83839 10.9566C7.83219 10.9566 8.73275 10.5591 9.39125 9.91514C8.97148 10.1425 8.49073 10.2717 7.9797 10.2717Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx153(
        "path",
        {
          d: "M8.81586 5.21738L9.34269 6.31903L10.5324 6.04416L9.99964 7.14286L10.9565 7.90163L9.76517 8.17018L9.7685 9.39132L8.81586 8.62735L7.86317 9.39132L7.86645 8.17018L6.67517 7.90163L7.63198 7.14286L7.09916 6.04416L8.28898 6.31903L8.81586 5.21738Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx153("defs", { children: /* @__PURE__ */ jsx153("clipPath", { id: "cfMY_clip0_0_1", children: /* @__PURE__ */ jsx153("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/MZ.tsx
import { jsx as jsx154, jsxs as jsxs152 } from "react/jsx-runtime";
var FlagMZ = (props) => /* @__PURE__ */ jsxs152(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs152("g", { clipPath: "url(#cfMZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M6.48617 16.6957L3.51514 20.4849C5.68657 22.6566 8.68625 24 12 24C16.9608 24 21.2177 20.9893 23.0452 16.6957H6.48617Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M23.0639 7.3478C21.2466 3.03103 16.9776 0 12 0C8.68625 0 5.68657 1.34339 3.51514 3.51511L6.52029 7.3478H23.0639Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M2.6087 8.8261V15.1304H23.5867C23.8558 14.1324 24 13.0831 24 12C24 10.9012 23.852 9.8371 23.5753 8.8261H2.6087Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M3.51465 3.51469C-1.17163 8.20097 -1.17163 15.799 3.51465 20.4853C5.4512 18.5488 7.31368 16.6863 12 12L3.51465 3.51469Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M4.8567 8.86955L5.63365 11.2609H8.14831L6.11403 12.739L6.89094 15.1305L4.8567 13.6524L2.82237 15.1305L3.59947 12.739L1.56519 11.2609H4.07965L4.8567 8.86955Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx154("path", { d: "M2.58313 12H7.13113V14.087H2.58313V12Z", fill: "#F0F0F0" }),
    /* @__PURE__ */ jsx154(
      "path",
      {
        d: "M7.99216 9.60746L7.2543 8.86955L4.85674 11.2671L2.45917 8.86955L1.72131 9.60746L4.12099 12.0029L1.72131 14.4026L2.45917 15.1305L4.85674 12.7372L7.2543 15.1305L7.99216 14.4026L5.59249 12.0029L7.99216 9.60746Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx154("defs", { children: /* @__PURE__ */ jsx154("clipPath", { id: "cfMZ_clip0_0_1", children: /* @__PURE__ */ jsx154("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NA.tsx
import { jsx as jsx155, jsxs as jsxs153 } from "react/jsx-runtime";
var FlagNA = (props) => /* @__PURE__ */ jsxs153(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs153("g", { clipPath: "url(#cfNA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx155(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx155(
      "path",
      {
        d: "M3.51475 20.4853C4.01814 20.9887 4.55524 21.4376 5.11863 21.8329L21.8328 5.11866C21.4376 4.55527 20.9887 4.01817 20.4852 3.51478C19.9818 3.01139 19.4448 2.56252 18.8814 2.16727L2.16724 18.8815C2.56244 19.4448 3.01136 19.9819 3.51475 20.4853Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx155(
      "path",
      {
        d: "M3.51471 3.51469C-0.271105 7.3005 -0.997855 12.9861 1.33291 17.5021L17.5021 1.33298C12.986 -0.997783 7.30043 -0.27094 3.51471 3.51469Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx155(
      "path",
      {
        d: "M20.4852 20.4853C24.2709 16.6996 24.9977 11.014 22.667 6.49791L6.49786 22.6671C11.0138 24.9978 16.6995 24.2711 20.4852 20.4853Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx155(
      "path",
      {
        d: "M9.91307 6.78263L8.56842 7.41511L9.28448 8.71739L7.82437 8.43811L7.63931 9.91304L6.62231 8.82821L5.60526 9.91304L5.42029 8.43811L3.96014 8.71735L4.67615 7.41507L3.33154 6.78263L4.6762 6.1501L3.96014 4.84791L5.42025 5.12719L5.60531 3.65218L6.62231 4.73705L7.63936 3.65218L7.82437 5.12719L9.28448 4.84791L8.56851 6.15014L9.91307 6.78263Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx155("defs", { children: /* @__PURE__ */ jsx155("clipPath", { id: "cfNA_clip0_0_1", children: /* @__PURE__ */ jsx155("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NE.tsx
import { jsx as jsx156, jsxs as jsxs154 } from "react/jsx-runtime";
var FlagNE = (props) => /* @__PURE__ */ jsxs154(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs154("g", { clipPath: "url(#cfNE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx156(
      "path",
      {
        d: "M1.19072 6.78262C0.427875 8.36025 0 10.1301 0 12C0 13.8699 0.427875 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174C23.5721 15.6397 24 13.8699 24 12C24 10.1301 23.5721 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx156(
      "path",
      {
        d: "M1.19116 17.2174C3.13207 21.2313 7.24249 24 12 24C16.7576 24 20.868 21.2313 22.8089 17.2174H1.19116Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs154("g", { children: [
      /* @__PURE__ */ jsx156(
        "path",
        {
          d: "M1.19116 6.78263H22.8089C20.868 2.76867 16.7575 0 12 0C7.24254 0 3.13207 2.76867 1.19116 6.78263Z",
          fill: "#FF9811"
        }
      ),
      /* @__PURE__ */ jsx156(
        "path",
        {
          d: "M12 16.1739C14.3052 16.1739 16.1739 14.3052 16.1739 12C16.1739 9.69483 14.3052 7.82611 12 7.82611C9.69483 7.82611 7.82611 9.69483 7.82611 12C7.82611 14.3052 9.69483 16.1739 12 16.1739Z",
          fill: "#FF9811"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx156("defs", { children: /* @__PURE__ */ jsx156("clipPath", { id: "cfNE_clip0_0_1", children: /* @__PURE__ */ jsx156("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NF.tsx
import { jsx as jsx157, jsxs as jsxs155 } from "react/jsx-runtime";
var FlagNF = (props) => /* @__PURE__ */ jsxs155(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs155("g", { clipPath: "url(#cfNF_clip0_0_1)", children: [
    /* @__PURE__ */ jsx157(
      "path",
      {
        d: "M17.2174 1.19072C15.6397 0.427875 13.8699 0 12 0C10.1301 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093C8.36025 23.5721 10.1301 24 12 24C13.8699 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs155("g", { children: [
      /* @__PURE__ */ jsx157(
        "path",
        {
          d: "M6.78263 1.19115C2.76867 3.13205 0 7.24248 0 12C0 16.7575 2.76867 20.868 6.78263 22.8089V1.19115Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx157(
        "path",
        {
          d: "M17.2174 1.19115V22.8089C21.2314 20.868 24 16.7575 24 12C24 7.24252 21.2314 3.13205 17.2174 1.19115Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx157(
        "path",
        {
          d: "M15.1305 15.6522L12 5.73915L8.86957 15.6522H11.2174V18.2609H12.7826V15.6522H15.1305Z",
          fill: "#6DA544"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx157("defs", { children: /* @__PURE__ */ jsx157("clipPath", { id: "cfNF_clip0_0_1", children: /* @__PURE__ */ jsx157("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NG.tsx
import { jsx as jsx158, jsxs as jsxs156 } from "react/jsx-runtime";
var FlagNG = (props) => /* @__PURE__ */ jsxs156(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs156("g", { clipPath: "url(#cfNG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx158(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs156("g", { children: [
      /* @__PURE__ */ jsx158(
        "path",
        {
          d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2537V0.746445C3.2565 2.44191 0 6.84048 0 12Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx158(
        "path",
        {
          d: "M24 12C24 6.84048 20.7435 2.44191 16.1739 0.746445V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
          fill: "#6DA544"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx158("defs", { children: /* @__PURE__ */ jsx158("clipPath", { id: "cfNG_clip0_0_1", children: /* @__PURE__ */ jsx158("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NI.tsx
import { jsx as jsx159, jsxs as jsxs157 } from "react/jsx-runtime";
var FlagNI = (props) => /* @__PURE__ */ jsxs157(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs157("g", { clipPath: "url(#cfNI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx159(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs157("g", { children: [
      /* @__PURE__ */ jsx159(
        "path",
        {
          d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx159(
        "path",
        {
          d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2174H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx159(
      "path",
      {
        d: "M12 8.34782C9.98298 8.34782 8.34784 9.98296 8.34784 12C8.34784 14.017 9.98298 15.6522 12 15.6522C14.017 15.6522 15.6522 14.017 15.6522 12C15.6522 9.98296 14.017 8.34782 12 8.34782ZM12 14.087C10.8474 14.087 9.91304 13.1527 9.91304 12C9.91304 10.8473 10.8474 9.91303 12 9.91303C13.1526 9.91303 14.087 10.8473 14.087 12C14.087 13.1527 13.1526 14.087 12 14.087Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx159(
      "path",
      {
        d: "M13.8074 12.5217L12 12L10.1926 12.5217L9.59015 13.5652H14.4098L13.8074 12.5217Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx159(
      "path",
      {
        d: "M12 9.39131L10.7951 11.4783L12 12L13.2049 11.4783L12 9.39131Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx159(
      "path",
      {
        d: "M10.1926 12.5217H13.8074L13.205 11.4783H10.7951L10.1926 12.5217Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx159("defs", { children: /* @__PURE__ */ jsx159("clipPath", { id: "cfNI_clip0_0_1", children: /* @__PURE__ */ jsx159("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NL.tsx
import { jsx as jsx160, jsxs as jsxs158 } from "react/jsx-runtime";
var FlagNL = (props) => /* @__PURE__ */ jsxs158(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs158("g", { clipPath: "url(#cfNL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx160(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx160(
      "path",
      {
        d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx160(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx160("defs", { children: /* @__PURE__ */ jsx160("clipPath", { id: "cfNL_clip0_0_1", children: /* @__PURE__ */ jsx160("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NO.tsx
import { jsx as jsx161, jsxs as jsxs159 } from "react/jsx-runtime";
var FlagNO = (props) => /* @__PURE__ */ jsxs159(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs159("g", { clipPath: "url(#cfNO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx161(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs159("g", { children: [
      /* @__PURE__ */ jsx161(
        "path",
        {
          d: "M0.413391 15.1304C1.1077 17.7067 2.63794 19.9395 4.69566 21.5205V15.1304H0.413391Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx161(
        "path",
        {
          d: "M10.9565 23.9543C11.3005 23.984 11.6483 24 12 24C17.5443 24 22.2096 20.2396 23.5866 15.1304H10.9565V23.9543Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx161(
        "path",
        {
          d: "M23.5866 8.86955C22.2096 3.76036 17.5443 0 12 0C11.6483 0 11.3005 0.0160312 10.9565 0.0456562V8.86955H23.5866Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx161(
        "path",
        {
          d: "M4.69566 2.47945C2.63794 4.06054 1.1077 6.29334 0.413391 8.86954H4.69566V2.47945Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx161(
      "path",
      {
        d: "M23.8984 10.4348H9.39136H9.39131V0.285233C8.28234 0.531139 7.23098 0.929717 6.26086 1.45908V10.4347V10.4347H0.101578C0.0347813 10.9471 0 11.4695 0 12C0 12.5305 0.0347813 13.0529 0.101578 13.5652H6.26081H6.26086V22.5409C7.23098 23.0702 8.28234 23.4689 9.39131 23.7147V13.5653V13.5652H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9471 23.8984 10.4348Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx161("defs", { children: /* @__PURE__ */ jsx161("clipPath", { id: "cfNO_clip0_0_1", children: /* @__PURE__ */ jsx161("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NP.tsx
import { jsx as jsx162, jsxs as jsxs160 } from "react/jsx-runtime";
var FlagNP = (props) => /* @__PURE__ */ jsxs160(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs160("g", { clipPath: "url(#cfNP_clip0_0_1)", children: [
    /* @__PURE__ */ jsx162(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs160("g", { children: [
      /* @__PURE__ */ jsx162(
        "path",
        {
          d: "M23.9296 13.3043L23.9293 13.304L23.9292 13.3043H23.9296Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx162(
        "path",
        {
          d: "M23.9292 13.3043C23.9292 13.3043 23.9293 13.3042 23.9293 13.304L10.7693 0.0623932C10.1757 0.122909 9.59475 0.22669 9.02986 0.370549C3.83897 1.69219 0 12 0 12C0 12 16.7999 23.2423 18.7165 21.9452C19.1 21.6857 19.4677 21.4046 19.8177 21.1036L12.0185 13.3043H23.9292Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx162(
      "path",
      {
        d: "M20.8695 12L9.19898 0.329483C3.92287 1.59122 0 6.33712 0 12C0 18.6274 5.37262 24 12 24C14.5139 24 16.8467 23.2265 18.7748 21.9052L8.86955 12H20.8695Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs160("g", { children: [
      /* @__PURE__ */ jsx162(
        "path",
        {
          d: "M11.4128 17.7184L9.94722 17.0291L10.7276 15.6098L9.13624 15.9142L8.93458 14.3065L7.82613 15.4889L6.71768 14.3065L6.51597 15.9142L4.92461 15.6097L5.70503 17.0291L4.23944 17.7184L5.70503 18.4077L4.92461 19.8271L6.51607 19.5227L6.71763 21.1303L7.82613 19.9479L8.93458 21.1303L9.13624 19.5227L10.7276 19.8271L9.94718 18.4078L11.4128 17.7184Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx162(
        "path",
        {
          d: "M10.4348 6.65545L9.36889 6.15412L9.9365 5.1218L8.77902 5.34323L8.63234 4.17389L7.82614 5.0339L7.01994 4.17389L6.87322 5.34323L5.71574 5.1218L6.28339 6.15412L5.21741 6.65545L7.82614 7.17726L10.4348 6.65545Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx162(
        "path",
        {
          d: "M10.9566 6.65546C10.9566 8.38435 9.55499 9.78591 7.8261 9.78591C6.09721 9.78591 4.69565 8.38435 4.69565 6.65546",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx162("defs", { children: /* @__PURE__ */ jsx162("clipPath", { id: "cfNP_clip0_0_1", children: /* @__PURE__ */ jsx162("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NR.tsx
import { jsx as jsx163, jsxs as jsxs161 } from "react/jsx-runtime";
var FlagNR = (props) => /* @__PURE__ */ jsxs161(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs161("g", { clipPath: "url(#cfNR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx163(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx163(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs161("g", { children: [
      /* @__PURE__ */ jsx163(
        "path",
        {
          d: "M12 0C5.72417 0 0.574575 4.81786 0.0456848 10.9565H23.9543C23.4254 4.81786 18.2758 0 12 0Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx163(
        "path",
        {
          d: "M12 24C18.2758 24 23.4254 19.1821 23.9543 13.0435H0.0456848C0.574575 19.1821 5.72417 24 12 24Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx163(
      "path",
      {
        d: "M9.91309 16.1739L8.56843 16.8064L9.28445 18.1087L7.82434 17.8293L7.63933 19.3043L6.62228 18.2195L5.60528 19.3043L5.42026 17.8293L3.96011 18.1086L4.67617 16.8064L3.33151 16.1739L4.67622 15.5414L3.96011 14.2392L5.42022 14.5185L5.60533 13.0435L6.62228 14.1283L7.63937 13.0435L7.82434 14.5185L9.2845 14.2392L8.56848 15.5415L9.91309 16.1739Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx163("defs", { children: /* @__PURE__ */ jsx163("clipPath", { id: "cfNR_clip0_0_1", children: /* @__PURE__ */ jsx163("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NU.tsx
import { jsx as jsx164, jsxs as jsxs162 } from "react/jsx-runtime";
var FlagNU = (props) => /* @__PURE__ */ jsxs162(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs162("g", { clipPath: "url(#cfNU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx164(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs162("g", { children: [
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M11.9999 5.73914V9.78646L7.82605 5.73914H11.9999Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M6.26085 12H9.78637L6.26085 7.30434L5.73914 10.9565L6.26085 12Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs162("g", { children: [
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs162("g", { children: [
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M12 10.5243L7.73649 6.26093H6.26086V6.26102L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M6.071 1.56522C5.60839 1.82865 5.16519 2.12223 4.74369 2.44272H5.23606L3.93472 3.38837L4.43169 4.91823L3.13034 3.97276L2.99187 4.0734C2.45154 4.687 1.97248 5.35581 1.56519 6.07103V12H4.69564V4.69576V4.69567H12C12 3.70834 12 2.76775 12 1.56522H6.071Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx164(
      "path",
      {
        d: "M5.12625 2.16338C4.99683 2.25399 4.86924 2.34713 4.74356 2.4427L3.93474 3.38836L3.13036 3.9727L2.99072 4.07414C2.69606 4.40883 2.41974 4.76002 2.16333 5.12625C2.4556 5.26809 2.78367 5.34783 3.13036 5.34783C4.35502 5.34783 5.34774 4.35511 5.34774 3.13045C5.34783 2.78367 5.26814 2.45569 5.12625 2.16338Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs162("g", { children: [
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M2.99084 4.07416L3.13049 3.97272L4.43183 4.91823L3.93481 3.38837L5.23606 2.44272H4.74359C4.10638 2.92736 3.51917 3.47411 2.99084 4.07416Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M8.29421 2.08698L8.55324 2.88408H9.39137L8.71332 3.37679L8.97221 4.1739L8.29421 3.68124L7.61616 4.1739L7.87501 3.37679L7.19696 2.88408H8.03513L8.29421 2.08698Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx164(
        "path",
        {
          d: "M3.13053 7.30435L3.38956 8.10146H4.22768L3.54968 8.59421L3.80858 9.39132L3.13053 8.89862L2.45243 9.39132L2.71133 8.59421L2.03333 8.10146H2.87145L3.13053 7.30435Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx164("defs", { children: /* @__PURE__ */ jsx164("clipPath", { id: "cfNU_clip0_0_1", children: /* @__PURE__ */ jsx164("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/NZ.tsx
import { jsx as jsx165, jsxs as jsxs163 } from "react/jsx-runtime";
var FlagNZ = (props) => /* @__PURE__ */ jsxs163(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs163("g", { clipPath: "url(#cfNZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx165(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs163("g", { children: [
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.3715 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78642 12H11.9679C11.9786 11.9893 11.9894 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs163("g", { children: [
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M6.071 1.56522C4.19417 2.63392 2.63389 4.1942 1.56519 6.07103V12H4.69564V4.69576V4.69567H12C12 3.70834 12 2.76775 12 1.56522H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M12 10.5243L7.73654 6.26093C7.73654 6.26093 6.26086 6.26102 6.26086 6.26093V6.26102L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M20.7811 8.90433L21.0401 9.70148H21.8782L21.2002 10.1941L21.4592 10.9912L20.7811 10.4986L20.1029 10.9912L20.362 10.1941L19.6838 9.70148H20.522L20.7811 8.90433Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M17.7849 14.6434L18.1734 15.8392H19.4307L18.4135 16.5782L18.8021 17.7739L17.7849 17.0349L16.7677 17.7739L17.1563 16.5782L16.1391 15.8392H17.3963L17.7849 14.6434Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M17.898 5.25217L18.2218 6.24868H19.2695L18.4219 6.86439L18.7457 7.86085L17.898 7.24496L17.0504 7.86085L17.3742 6.86439L16.5266 6.24868H17.5743L17.898 5.25217Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx165(
        "path",
        {
          d: "M15.0151 8.86955L15.4036 10.0653H16.6609L15.6437 10.8043L16.0323 12L15.0151 11.261L13.9979 12L14.3865 10.8043L13.3693 10.0653H14.6266L15.0151 8.86955Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx165("defs", { children: /* @__PURE__ */ jsx165("clipPath", { id: "cfNZ_clip0_0_1", children: /* @__PURE__ */ jsx165("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/OM.tsx
import { jsx as jsx166, jsxs as jsxs164 } from "react/jsx-runtime";
var FlagOM = (props) => /* @__PURE__ */ jsxs164(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs164("g", { clipPath: "url(#cfOM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx166(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs164("g", { children: [
      /* @__PURE__ */ jsx166(
        "path",
        {
          d: "M0 12C0 16.5485 2.53078 20.5057 6.26086 22.5409V1.45908C2.53078 3.49429 0 7.45148 0 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx166(
        "path",
        {
          d: "M23.2537 16.1739C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.12605 23.2537 7.82611H0.746437C0.264047 9.12605 0 10.5322 0 12C0 13.4678 0.264047 14.8739 0.746437 16.1739L12 17.2174L23.2537 16.1739Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx166(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746429C2.4419 20.7435 6.84046 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx166(
      "path",
      {
        d: "M0 12C0 17.5443 3.76036 22.2096 8.86955 23.5866V0.413391C3.76036 1.79039 0 6.45567 0 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx166(
      "path",
      {
        d: "M7.34141 5.28065L6.23464 4.17389L5.49683 4.9118L4.75902 4.17389L3.65225 5.28065L4.39011 6.01851L3.65216 6.75642L4.75893 7.86319L5.49679 7.12528L6.23464 7.86319L7.34141 6.75642L6.60355 6.01851L7.34141 5.28065Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx166("defs", { children: /* @__PURE__ */ jsx166("clipPath", { id: "cfOM_clip0_0_1", children: /* @__PURE__ */ jsx166("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PA.tsx
import { jsx as jsx167, jsxs as jsxs165 } from "react/jsx-runtime";
var FlagPA = (props) => /* @__PURE__ */ jsxs165(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs165("g", { clipPath: "url(#cfPA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx167(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx167(
      "path",
      {
        d: "M0 12C0 18.6274 5.37262 24 12 24C12 19.4365 12 12 12 12C12 12 4.17389 12 0 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx167(
      "path",
      {
        d: "M12 0C18.6274 0 24 5.37262 24 12C19.4365 12 12 12 12 12C12 12 12 4.17389 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx167(
      "path",
      {
        d: "M7.14325 4.17389L7.92029 6.56536H10.4348L8.40053 8.04333L9.17753 10.4348L7.14325 8.95678L5.10897 10.4348L5.88597 8.04333L3.85168 6.56536H6.3662L7.14325 4.17389Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx167(
      "path",
      {
        d: "M16.8567 13.5652L17.6338 15.9567H20.1483L18.114 17.4347L18.891 19.8261L16.8567 18.3481L14.8225 19.8261L15.5995 17.4347L13.5652 15.9567H16.0797L16.8567 13.5652Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx167("defs", { children: /* @__PURE__ */ jsx167("clipPath", { id: "cfPA_clip0_0_1", children: /* @__PURE__ */ jsx167("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PE.tsx
import { jsx as jsx168, jsxs as jsxs166 } from "react/jsx-runtime";
var FlagPE = (props) => /* @__PURE__ */ jsxs166(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs166("g", { clipPath: "url(#cfPE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx168(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs166("g", { children: [
      /* @__PURE__ */ jsx168(
        "path",
        {
          d: "M24 12C24 6.84046 20.7435 2.4419 16.1739 0.746429V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx168(
        "path",
        {
          d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746429C3.2565 2.4419 0 6.84046 0 12Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx168("defs", { children: /* @__PURE__ */ jsx168("clipPath", { id: "cfPE_clip0_0_1", children: /* @__PURE__ */ jsx168("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PF.tsx
import { jsx as jsx169, jsxs as jsxs167 } from "react/jsx-runtime";
var FlagPF = (props) => /* @__PURE__ */ jsxs167(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs167("g", { clipPath: "url(#cfPF_clip0_0_1)", children: [
    /* @__PURE__ */ jsx169(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs167("g", { children: [
      /* @__PURE__ */ jsx169(
        "path",
        {
          d: "M1.45911 6.26086H22.5409C20.5057 2.53073 16.5485 0 12 0C7.45151 0 3.49437 2.53073 1.45911 6.26086Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx169(
        "path",
        {
          d: "M12 24C16.5485 24 20.5057 21.4693 22.5409 17.7391H1.45911C3.49437 21.4693 7.45151 24 12 24Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx169(
      "path",
      {
        d: "M16.1739 12C16.1739 14.3052 14.3052 15.6522 12 15.6522C9.69483 15.6522 7.82611 14.3052 7.82611 12C7.82611 9.69478 9.69483 7.82611 12 7.82611C14.3052 7.82611 16.1739 9.69478 16.1739 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx169(
      "path",
      {
        d: "M16.1739 12C16.1739 14.3052 14.3052 16.1739 12 16.1739C9.69483 16.1739 7.82611 14.3052 7.82611 12",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs167("g", { children: [
      /* @__PURE__ */ jsx169(
        "path",
        {
          d: "M9.3913 10.9565H10.4348V13.0435H9.3913V10.9565Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx169(
        "path",
        {
          d: "M13.5652 10.9565H14.6087V13.0435H13.5652V10.9565Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx169(
        "path",
        {
          d: "M11.4783 9.3913H12.5218V13.0435H11.4783V9.3913Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx169("defs", { children: /* @__PURE__ */ jsx169("clipPath", { id: "cfPF_clip0_0_1", children: /* @__PURE__ */ jsx169("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PG.tsx
import { jsx as jsx170, jsxs as jsxs168 } from "react/jsx-runtime";
var FlagPG = (props) => /* @__PURE__ */ jsxs168(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs168("g", { clipPath: "url(#cfPG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx170(
      "path",
      {
        d: "M20.4853 20.4853C15.799 25.1715 8.20108 25.1715 3.5147 20.4853C-1.17158 15.799 -1.17158 8.20097 3.51475 3.51464C8.20103 -1.17164 15.0611 -0.433783 19.7474 4.25254C24.4337 8.93883 25.1716 15.799 20.4853 20.4853Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx170(
      "path",
      {
        d: "M3.51477 3.5147C8.20105 -1.17158 15.799 -1.17149 20.4853 3.51475C25.1716 8.20103 25.1716 15.799 20.4853 20.4853",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs168("g", { children: [
      /* @__PURE__ */ jsx170(
        "path",
        {
          d: "M9.1707 16.1739L9.36495 16.7718H9.99359L9.485 17.1413L9.67929 17.7392L9.1707 17.3697L8.66211 17.7392L8.85636 17.1413L8.34781 16.7718H8.9764L9.1707 16.1739Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx170(
        "path",
        {
          d: "M7.56519 10.4348L7.88905 11.4313H8.93671L8.08907 12.047L8.41284 13.0435L7.56519 12.4276L6.7176 13.0435L7.04141 12.047L6.19373 11.4313H7.24148L7.56519 10.4348Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx170(
        "path",
        {
          d: "M7.56519 17.7392L7.88896 18.7357H8.93671L8.08898 19.3514L8.41284 20.3479L7.56519 19.732L6.7176 20.3479L7.04132 19.3514L6.19373 18.7357H7.24133L7.56519 17.7392Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx170(
        "path",
        {
          d: "M10.6285 13.0435L10.9523 14.04H12L11.1523 14.6557L11.4761 15.6522L10.6285 15.0363L9.78088 15.6522L10.1047 14.6557L9.25705 14.04H10.3048L10.6285 13.0435Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx170(
        "path",
        {
          d: "M4.50187 13.0435L4.82573 14.04H5.87334L5.02575 14.6557L5.34946 15.6522L4.50187 15.0363L3.65423 15.6522L3.97809 14.6557L3.1304 14.04H4.17811L4.50187 13.0435Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx170(
      "path",
      {
        d: "M20.0883 8.88569C19.552 8.34939 18.8551 8.03256 18.1063 7.97814L20.3478 6.23472C19.4551 5.34199 18.2586 4.93881 17.091 5.02408C16.9512 4.52177 16.6847 4.0473 16.2897 3.65228L15.4043 5.42313C15.3462 5.31728 15.2727 5.21767 15.183 5.128C14.6735 4.61861 13.8477 4.61861 13.3383 5.128C12.829 5.63739 12.8289 6.46324 13.3383 6.97263C13.428 7.0623 13.5277 7.13584 13.6335 7.19397L11.8627 8.07939C12.2578 8.47455 12.7326 8.74122 13.2352 8.88091C13.1496 10.0485 13.5524 11.2448 14.4452 12.1376L16.4036 9.61956C16.5509 9.47449 16.7455 9.39438 16.9525 9.39438C17.1616 9.39438 17.3581 9.47575 17.5059 9.62364C17.6537 9.77144 17.7351 9.96799 17.7351 10.177C17.7351 10.3861 17.6537 10.5826 17.5059 10.7304L18.2438 11.4683C18.5886 11.1234 18.7786 10.6648 18.7786 10.177C18.7786 9.77796 18.6513 9.39855 18.4163 9.08486C18.7587 9.17631 19.0824 9.35547 19.3505 9.62364C20.1643 10.4373 20.1643 11.7614 19.3505 12.575L20.0884 13.3129C20.6797 12.7217 21.0053 11.9356 21.0053 11.0994C21.0052 10.2631 20.6795 9.47697 20.0883 8.88569Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx170("defs", { children: /* @__PURE__ */ jsx170("clipPath", { id: "cfPG_clip0_0_1", children: /* @__PURE__ */ jsx170("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PH.tsx
import { jsx as jsx171, jsxs as jsxs169 } from "react/jsx-runtime";
var FlagPH = (props) => /* @__PURE__ */ jsxs169(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs169("g", { clipPath: "url(#cfPH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx171(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx171(
      "path",
      {
        d: "M12 0V12L3.51471 20.4853C5.68629 22.6568 8.68629 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 12 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx171(
      "path",
      {
        d: "M12 0C8.68629 0 5.68629 1.34316 3.51471 3.51469C6.45893 6.45891 7.5041 7.50412 12 12H24C24 5.37262 18.6274 0 12 0Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs169("g", { children: [
      /* @__PURE__ */ jsx171(
        "path",
        {
          d: "M8.21674 12L6.75124 11.3107L7.53166 9.89132L5.94025 10.1958L5.7386 8.5881L4.6301 9.77048L3.52164 8.5881L3.31994 10.1958L1.72858 9.89128L2.509 11.3107L1.04346 12L2.509 12.6893L1.72858 14.1087L3.32008 13.8043L3.5216 15.4119L4.6301 14.2295L5.7386 15.4119L5.94025 13.8043L7.53157 14.1087L6.75119 12.6894L8.21674 12Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx171(
        "path",
        {
          d: "M3.20027 4.69565L3.68796 5.37735L4.48694 5.12427L3.98922 5.79871L4.47691 6.48041L3.68163 6.21552L3.18396 6.88991L3.19019 6.05179L2.39496 5.78685L3.19408 5.53377L3.20027 4.69565Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx171(
        "path",
        {
          d: "M3.20027 17.1101L3.68796 17.7918L4.48694 17.5387L3.98922 18.2131L4.47691 18.8948L3.68163 18.63L3.18396 19.3043L3.19019 18.4662L2.39496 18.2013L3.19408 17.9482L3.20027 17.1101Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx171(
        "path",
        {
          d: "M10.1562 10.9029L9.66854 11.5846L8.86951 11.3314L9.36723 12.0059L8.87954 12.6877L9.67482 12.4227L10.1725 13.0971L10.1663 12.259L10.9615 11.9941L10.1624 11.741L10.1562 10.9029Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx171("defs", { children: /* @__PURE__ */ jsx171("clipPath", { id: "cfPH_clip0_0_1", children: /* @__PURE__ */ jsx171("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PK.tsx
import { jsx as jsx172, jsxs as jsxs170 } from "react/jsx-runtime";
var FlagPK = (props) => /* @__PURE__ */ jsxs170(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs170("g", { clipPath: "url(#cfPK_clip0_0_1)", children: [
    /* @__PURE__ */ jsxs170("g", { children: [
      /* @__PURE__ */ jsx172(
        "path",
        {
          d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx172(
        "path",
        {
          d: "M0 12C0 16.5485 2.53073 20.5057 6.26086 22.5409V1.45908C2.53073 3.49434 0 7.45148 0 12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx172(
      "path",
      {
        d: "M12 0C9.9211 0 7.96575 0.528891 6.26086 1.45908V22.5409C7.96575 23.4711 9.9211 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsxs170("g", { children: [
      /* @__PURE__ */ jsx172(
        "path",
        {
          d: "M17.1306 13.9845C15.6125 15.0815 13.4925 14.74 12.3956 13.2219C11.2987 11.7037 11.6402 9.58382 13.1583 8.48695C13.6316 8.14495 14.1635 7.94277 14.7046 7.87251C13.6636 7.71684 12.5632 7.9514 11.6427 8.61651C9.77431 9.9666 9.35398 12.5757 10.704 14.4442C12.0541 16.3126 14.6632 16.733 16.5318 15.3828C17.4523 14.7177 18.0205 13.7468 18.1995 12.7095C17.9629 13.201 17.604 13.6425 17.1306 13.9845Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx172(
        "path",
        {
          d: "M17.0657 7.8262L17.9208 8.74781L19.0616 8.21944L18.4493 9.31744L19.3044 10.2391L18.0708 9.99609L17.4586 11.0942L17.3085 9.84591L16.075 9.60286L17.2157 9.07448L17.0657 7.8262Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx172("defs", { children: /* @__PURE__ */ jsx172("clipPath", { id: "cfPK_clip0_0_1", children: /* @__PURE__ */ jsx172("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PL.tsx
import { jsx as jsx173, jsxs as jsxs171 } from "react/jsx-runtime";
var FlagPL = (props) => /* @__PURE__ */ jsxs171(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs171("g", { clipPath: "url(#cfPL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx173(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx173(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx173("defs", { children: /* @__PURE__ */ jsx173("clipPath", { id: "cfPL_clip0_0_1", children: /* @__PURE__ */ jsx173("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PN.tsx
import { jsx as jsx174, jsxs as jsxs172 } from "react/jsx-runtime";
var FlagPN = (props) => /* @__PURE__ */ jsxs172(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs172("g", { clipPath: "url(#cfPN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx174(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs172("g", { children: [
      /* @__PURE__ */ jsx174(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx174(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs172("g", { children: [
      /* @__PURE__ */ jsx174(
        "path",
        {
          d: "M6.071 1.56522C4.19417 2.63392 2.63384 4.1942 1.56519 6.07103V12H4.69564V4.69576V4.69567H12C12 3.70834 12 2.76775 12 1.56522H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx174(
        "path",
        {
          d: "M12 10.5243L7.73646 6.26093H6.26083V6.26102L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx174(
      "path",
      {
        d: "M18.7826 7.30434L17.2174 7.8261L15.6522 7.30434L15.1304 5.99999L15.6522 4.69565H18.7826L18.2608 5.99999L18.7826 7.30434Z",
        fill: "#ACABB1"
      }
    ),
    /* @__PURE__ */ jsx174(
      "path",
      {
        d: "M13.5652 7.30435V13.0434C13.5652 15.8391 17.2174 16.6957 17.2174 16.6957C17.2174 16.6957 20.8695 15.8391 20.8695 13.0434V7.30435H13.5652Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx174(
      "path",
      {
        d: "M13.8945 14.4031C14.7922 16.1266 17.2174 16.6957 17.2174 16.6957C17.2174 16.6957 19.6427 16.1266 20.5404 14.4031L17.2174 8.8696L13.8945 14.4031Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx174(
      "path",
      {
        d: "M20.8695 12.9855L17.2174 7.30435L13.5652 12.9855V13.0434C13.5652 13.5609 13.6905 14.0117 13.8944 14.4031L17.2174 9.23396L20.5403 14.4031C20.7442 14.0116 20.8695 13.5608 20.8695 13.0434V12.9855Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx174("defs", { children: /* @__PURE__ */ jsx174("clipPath", { id: "cfPN_clip0_0_1", children: /* @__PURE__ */ jsx174("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PR.tsx
import { jsx as jsx175, jsxs as jsxs173 } from "react/jsx-runtime";
var FlagPR = (props) => /* @__PURE__ */ jsxs173(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs173("g", { clipPath: "url(#cfPR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx175(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs173("g", { children: [
      /* @__PURE__ */ jsx175(
        "path",
        {
          d: "M12.0001 0C8.07418 0 4.58879 1.88545 2.39954 4.8H21.6006C19.4113 1.88545 15.9259 0 12.0001 0Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx175(
        "path",
        {
          d: "M12.0001 24C15.9259 24 19.4113 22.1146 21.6006 19.2H2.39954C4.58879 22.1146 8.07418 24 12.0001 24Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx175(
        "path",
        {
          d: "M0 12C0 12.822 0.082875 13.6246 0.240328 14.4H23.7598C23.9172 13.6246 24 12.822 24 12C24 11.1781 23.9171 10.3755 23.7597 9.60001H0.240328C0.082875 10.3755 0 11.1781 0 12H0Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx175(
      "path",
      {
        d: "M3.51471 3.51468C-1.17157 8.20096 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51468Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx175(
      "path",
      {
        d: "M4.8567 8.86954L5.63365 11.2609H8.14831L6.11403 12.739L6.89094 15.1304L4.8567 13.6524L2.82237 15.1304L3.59947 12.739L1.56519 11.2609H4.07965L4.8567 8.86954Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx175("defs", { children: /* @__PURE__ */ jsx175("clipPath", { id: "cfPR_clip0_0_1", children: /* @__PURE__ */ jsx175("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PRB.tsx
import { jsx as jsx176, jsxs as jsxs174 } from "react/jsx-runtime";
var FlagPRB = (props) => /* @__PURE__ */ jsxs174(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx176(
    "path",
    {
      d: "M23.5866 8.86955C22.2096 3.76036 17.5443 0 12 0C6.45564 0 1.79036 3.76036 0.413361 8.86955L12 9.39131L23.5866 8.86955Z",
      fill: "#A2001D"
    }
  ),
  /* @__PURE__ */ jsx176(
    "path",
    {
      d: "M0.413361 15.1305C1.79036 20.2396 6.45564 24 12 24C17.5443 24 22.2096 20.2397 23.5866 15.1305L12 14.6087L0.413361 15.1305Z",
      fill: "#A2001D"
    }
  ),
  /* @__PURE__ */ jsx176(
    "path",
    {
      d: "M23.5866 8.86957H0.413391C0.144375 9.86768 0 10.9169 0 12C0 13.0832 0.144375 14.1324 0.413391 15.1305H23.5867C23.8556 14.1324 24 13.0832 24 12C24 10.9169 23.8556 9.86768 23.5866 8.86957Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx176("defs", { children: /* @__PURE__ */ jsx176("clipPath", { id: "cfPRB_clip0_2_9184", children: /* @__PURE__ */ jsx176("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PS.tsx
import { jsx as jsx177, jsxs as jsxs175 } from "react/jsx-runtime";
var FlagPS = (props) => /* @__PURE__ */ jsxs175(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs175("g", { clipPath: "url(#cfPS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx177(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx177(
      "path",
      {
        d: "M7.30431 7.82611H23.2535C21.5581 3.2565 17.1595 0 12 0C8.68619 0 5.68651 1.34339 3.51508 3.51511L7.30431 7.82611Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx177(
      "path",
      {
        d: "M7.30431 16.1739H23.2535C21.5581 20.7435 17.1595 24 12 24C8.68619 24 5.68651 22.6566 3.51508 20.4849L7.30431 16.1739Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx177(
      "path",
      {
        d: "M3.51471 3.51468C-1.17157 8.20096 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51468Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx177("defs", { children: /* @__PURE__ */ jsx177("clipPath", { id: "cfPS_clip0_0_1", children: /* @__PURE__ */ jsx177("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PT.tsx
import { jsx as jsx178, jsxs as jsxs176 } from "react/jsx-runtime";
var FlagPT = (props) => /* @__PURE__ */ jsxs176(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs176("g", { clipPath: "url(#cfPT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx178(
      "path",
      {
        d: "M0 12C0 17.1595 3.2565 21.5581 7.82602 23.2536L8.86955 12L7.82602 0.746399C3.2565 2.44196 0 6.84043 0 12Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx178(
      "path",
      {
        d: "M24 12C24 5.37262 18.6273 0 12 0C10.5321 0 9.12602 0.264047 7.82599 0.746391V23.2536C9.12602 23.736 10.5321 24 12 24C18.6273 24 24 18.6274 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx178(
      "path",
      {
        d: "M7.82611 16.1739C10.1313 16.1739 12 14.3052 12 12C12 9.69483 10.1313 7.82611 7.82611 7.82611C5.52094 7.82611 3.65222 9.69483 3.65222 12C3.65222 14.3052 5.52094 16.1739 7.82611 16.1739Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx178(
      "path",
      {
        d: "M5.47827 9.91302V12.5217C5.47827 13.8184 6.5294 14.8695 7.8261 14.8695C9.1228 14.8695 10.1739 13.8184 10.1739 12.5217V9.91302H5.47827Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx178(
      "path",
      {
        d: "M7.82608 13.3043C7.39455 13.3043 7.04346 12.9532 7.04346 12.5217V11.4783H8.60866V12.5218C8.60866 12.9532 8.25757 13.3043 7.82608 13.3043Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx178("defs", { children: /* @__PURE__ */ jsx178("clipPath", { id: "cfPT_clip0_0_1", children: /* @__PURE__ */ jsx178("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PW.tsx
import { jsx as jsx179, jsxs as jsxs177 } from "react/jsx-runtime";
var FlagPW = (props) => /* @__PURE__ */ jsxs177(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs177("g", { clipPath: "url(#cfPW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx179(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx179(
      "path",
      {
        d: "M9.39132 17.2174C12.2728 17.2174 14.6087 14.8815 14.6087 12C14.6087 9.11852 12.2728 6.78262 9.39132 6.78262C6.50985 6.78262 4.17395 9.11852 4.17395 12C4.17395 14.8815 6.50985 17.2174 9.39132 17.2174Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx179("defs", { children: /* @__PURE__ */ jsx179("clipPath", { id: "cfPW_clip0_0_1", children: /* @__PURE__ */ jsx179("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/PY.tsx
import { jsx as jsx180, jsxs as jsxs178 } from "react/jsx-runtime";
var FlagPY = (props) => /* @__PURE__ */ jsxs178(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs178("g", { clipPath: "url(#cfPY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx180(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx180(
      "path",
      {
        d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx180(
      "path",
      {
        d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2174H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx180(
      "path",
      {
        d: "M14.9514 8.52689L13.8446 9.63365C14.3167 10.1057 14.6087 10.7579 14.6087 11.4783C14.6087 12.919 13.4407 14.087 12 14.087C10.5593 14.087 9.39134 12.919 9.39134 11.4783C9.39134 10.7579 9.68338 10.1057 10.1555 9.63365L9.04869 8.52689C8.2933 9.28214 7.82614 10.3256 7.82614 11.4783C7.82614 13.7835 9.69486 15.6522 12 15.6522C14.3052 15.6522 16.1739 13.7834 16.1739 11.4783C16.1739 10.3256 15.7067 9.28214 14.9514 8.52689Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx180(
      "path",
      {
        d: "M12 9.91302L12.3885 11.1088H13.6457L12.6286 11.8477L13.0172 13.0435L12 12.3045L10.9828 13.0435L11.3713 11.8477L10.3542 11.1088H11.6114L12 9.91302Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx180("defs", { children: /* @__PURE__ */ jsx180("clipPath", { id: "cfPY_clip0_0_1", children: /* @__PURE__ */ jsx180("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/QA.tsx
import { jsx as jsx181, jsxs as jsxs179 } from "react/jsx-runtime";
var FlagQA = (props) => /* @__PURE__ */ jsxs179(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs179("g", { clipPath: "url(#cfQA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx181(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx181(
      "path",
      {
        d: "M24 12C24 5.37262 18.6274 0 12 0C9.65252 0 7.4628 0.674766 5.61321 1.83989L8.24351 2.95744L4.69568 4.46484L8.24351 5.9722L4.69568 7.47952L8.24351 8.98678L4.69568 10.4939L8.24351 12.0008L4.69568 13.5082L8.24351 15.0154L4.69568 16.5225L8.24351 18.0297L4.69568 19.537L8.24351 21.0441L5.61438 22.161C7.46374 23.3256 9.65299 24 12 24C18.6274 24 24 18.6274 24 12Z",
        fill: "#751A46"
      }
    )
  ] }),
  /* @__PURE__ */ jsx181("defs", { children: /* @__PURE__ */ jsx181("clipPath", { id: "cfQA_clip0_0_1", children: /* @__PURE__ */ jsx181("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/RO.tsx
import { jsx as jsx182, jsxs as jsxs180 } from "react/jsx-runtime";
var FlagRO = (props) => /* @__PURE__ */ jsxs180(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs180("g", { clipPath: "url(#cfRO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx182(
      "path",
      {
        d: "M16.1739 0.746344C14.8739 0.264047 13.4679 6.23894e-09 12 6.23894e-09C10.5322 -4.68688e-05 9.12612 0.264047 7.82614 0.746344L6.78265 12L7.82614 23.2536C9.12608 23.736 10.5321 24 12 24C13.4678 24 14.8739 23.736 16.1739 23.2536L17.2174 12L16.1739 0.746344Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx182(
      "path",
      {
        d: "M24 12C24 6.84048 20.7435 2.44187 16.1739 0.746399V23.2537C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx182(
      "path",
      {
        d: "M0 11.9999C0 17.1596 3.2565 21.558 7.82602 23.2536L7.82606 0.746429C3.2565 2.4419 0 6.84037 0 11.9999H0Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx182("defs", { children: /* @__PURE__ */ jsx182("clipPath", { id: "cfRO_clip0_0_1", children: /* @__PURE__ */ jsx182("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/RS.tsx
import { jsx as jsx183, jsxs as jsxs181 } from "react/jsx-runtime";
var FlagRS = (props) => /* @__PURE__ */ jsxs181(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs181("g", { clipPath: "url(#cfRS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx183(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx183(
      "path",
      {
        d: "M12 0C5.37262 0 0 5.37262 0 12C0 13.4678 0.264047 14.874 0.746391 16.1739H23.2537C23.736 14.874 24 13.4678 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs181("g", { children: [
      /* @__PURE__ */ jsx183(
        "path",
        {
          d: "M12 0C6.84043 0 2.44191 3.2565 0.746399 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx183(
        "path",
        {
          d: "M3.10306 6.78262V12.7688C3.10306 16.174 7.55154 17.2174 7.55154 17.2174C7.55154 17.2174 12 16.174 12 12.7688V6.78262H3.10306Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs181("g", { children: [
      /* @__PURE__ */ jsx183(
        "path",
        {
          d: "M4.94281 7.8261H10.1602V5.73913L9.11675 6.26085L7.55154 4.69565L5.98629 6.26085L4.94281 5.73913V7.8261Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx183(
        "path",
        {
          d: "M10.9565 13.6023L7.5652 10.211L4.17389 13.6023L5.28065 14.7091L7.5652 12.4245L9.84975 14.7091L10.9565 13.6023Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx183(
      "path",
      {
        d: "M10.9565 10.4348H9.3742C9.46317 10.2812 9.51492 10.1034 9.51492 9.91317C9.51492 9.3368 9.04772 8.86969 8.47144 8.86969C8.07323 8.86969 7.72744 9.09272 7.55151 9.42061C7.37559 9.09277 7.0298 8.86969 6.63159 8.86969C6.05531 8.86969 5.58811 9.3368 5.58811 9.91317C5.58811 10.1034 5.63986 10.2813 5.72883 10.4348H4.17389C4.17389 11.0111 4.67587 11.4783 5.25211 11.4783H5.21737C5.21737 12.0546 5.68453 12.5218 6.26086 12.5218C6.26086 13.032 6.62733 13.456 7.11131 13.5467L6.28912 15.4031C6.68315 15.5633 7.11375 15.6522 7.56525 15.6522C8.01675 15.6522 8.44734 15.5633 8.84137 15.4031L8.01919 13.5467C8.50317 13.4561 8.86964 13.0321 8.86964 12.5218C9.44597 12.5218 9.91312 12.0546 9.91312 11.4783H9.87839C10.4546 11.4783 10.9565 11.0111 10.9565 10.4348Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx183("defs", { children: /* @__PURE__ */ jsx183("clipPath", { id: "cfRS_clip0_0_1", children: /* @__PURE__ */ jsx183("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/RU.tsx
import { jsx as jsx184, jsxs as jsxs182 } from "react/jsx-runtime";
var FlagRU = (props) => /* @__PURE__ */ jsxs182(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs182("g", { clipPath: "url(#cfRU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx184(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx184(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.1261 23.2536 7.82611H0.746391C0.264047 9.1261 0 10.5322 0 12C0 13.4678 0.264047 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx184(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx184("defs", { children: /* @__PURE__ */ jsx184("clipPath", { id: "cfRU_clip0_0_1", children: /* @__PURE__ */ jsx184("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/RW.tsx
import { jsx as jsx185, jsxs as jsxs183 } from "react/jsx-runtime";
var FlagRW = (props) => /* @__PURE__ */ jsxs183(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs183("g", { clipPath: "url(#cfRW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx185(
      "path",
      {
        d: "M0 12C0 14.0789 0.528891 16.0342 1.45908 17.7391L12 18.7826L22.5409 17.7391C23.4711 16.0342 24 14.0789 24 12L12 10.9565L0 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx185(
      "path",
      {
        d: "M22.5409 6.26086C20.5057 2.53073 16.5485 0 12 0C7.45148 0 3.49434 2.53073 1.45908 6.26086C0.528891 7.96575 0 9.92109 0 12H24C24 9.92109 23.4711 7.96575 22.5409 6.26086Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx185(
      "path",
      {
        d: "M12 24C16.5485 24 20.5057 21.4693 22.5409 17.7391H1.45908C3.49434 21.4693 7.45148 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx185(
      "path",
      {
        d: "M13.5652 7.02287L15.0308 7.71226L14.2504 9.13163L15.8418 8.82723L16.0434 10.4348L17.1518 9.25243L18.2603 10.4348L18.4619 8.82723L20.0533 9.13154L19.273 7.71221L20.7385 7.02287L19.2729 6.33357L20.0533 4.91419L18.462 5.2186L18.2603 3.61102L17.1518 4.7934L16.0434 3.61102L15.8418 5.2186L14.2503 4.91419L15.0307 6.33362L13.5652 7.02287Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx185("defs", { children: /* @__PURE__ */ jsx185("clipPath", { id: "cfRW_clip0_0_1", children: /* @__PURE__ */ jsx185("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SA.tsx
import { jsx as jsx186, jsxs as jsxs184 } from "react/jsx-runtime";
var FlagSA = (props) => /* @__PURE__ */ jsxs184(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs184("g", { clipPath: "url(#cfSA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx186(
      "path",
      {
        d: "M17.2174 1.19072C15.6397 0.427875 13.8698 0 12 0C10.1302 0 8.36025 0.427875 6.78262 1.19072L5.73914 12L6.78262 22.8093C8.36025 23.5721 10.1302 24 12 24C13.8698 24 15.6397 23.5721 17.2174 22.8093L18.2609 12L17.2174 1.19072Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs184("g", { children: [
      /* @__PURE__ */ jsx186(
        "path",
        {
          d: "M11.9999 7.82611L13.0358 11.0145H16.3887L13.6763 12.9853L14.7123 16.1739L11.9999 14.2033L9.28747 16.1739L10.3236 12.9853L7.61127 11.0145H10.9639L11.9999 7.82611Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx186(
        "path",
        {
          d: "M6.78263 1.19113C2.76872 3.13208 0 7.24251 0 12C0 16.7575 2.76872 20.8679 6.78263 22.8088V1.19113Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx186(
      "path",
      {
        d: "M17.2173 1.19116V22.8089C21.2313 20.8679 24 16.7575 24 12C24 7.24254 21.2313 3.13212 17.2173 1.19116Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx186("defs", { children: /* @__PURE__ */ jsx186("clipPath", { id: "cfSA_clip0_0_1", children: /* @__PURE__ */ jsx186("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SB.tsx
import { jsx as jsx187, jsxs as jsxs185 } from "react/jsx-runtime";
var FlagSB = (props) => /* @__PURE__ */ jsxs185(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs185("g", { clipPath: "url(#cfSB_clip0_0_1)", children: [
    /* @__PURE__ */ jsx187(
      "path",
      {
        d: "M20.4856 20.4856C24.9861 15.9851 25.1638 8.79988 21.0206 4.08618L12 11.9999L4.08618 21.0206C8.79988 25.1638 15.9851 24.9861 20.4856 20.4856Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx187(
      "path",
      {
        d: "M3.51438 3.5144C8.01488 -0.986107 15.2001 -1.16381 19.9138 2.97942L12 12.0001L2.97944 19.9138C-1.16383 15.2001 -0.986083 8.01491 3.51438 3.5144Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx187(
      "path",
      {
        d: "M19.9137 2.97934L2.97934 19.9137C3.15053 20.1085 3.32828 20.2996 3.51432 20.4855C3.70032 20.6715 3.89143 20.8493 4.08624 21.0206L21.0205 4.08615C20.8493 3.89134 20.6715 3.70028 20.4855 3.51423C20.2996 3.32832 20.1085 3.15053 19.9137 2.97934Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs185("g", { children: [
      /* @__PURE__ */ jsx187(
        "path",
        {
          d: "M5.05597 4.17355L5.31505 4.97075H6.15322L5.47513 5.46336L5.73411 6.26057L5.05597 5.76786L4.37783 6.26057L4.63691 5.46336L3.95877 4.97075H4.79699L5.05597 4.17355Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx187(
        "path",
        {
          d: "M9.33747 4.17355L9.59654 4.97075H10.4347L9.75657 5.46336L10.0156 6.26057L9.33747 5.76786L8.65933 6.26057L8.9184 5.46336L8.24026 4.97075H9.07843L9.33747 4.17355Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx187(
        "path",
        {
          d: "M5.05597 8.34763L5.31505 9.14483H6.15322L5.47513 9.63748L5.73411 10.4347L5.05597 9.94198L4.37783 10.4347L4.63691 9.63748L3.95877 9.14483H4.79699L5.05597 8.34763Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx187(
        "path",
        {
          d: "M9.33747 8.34763L9.59654 9.14483H10.4347L9.75657 9.63748L10.0156 10.4347L9.33747 9.94198L8.65933 10.4347L8.9184 9.63748L8.24026 9.14483H9.07843L9.33747 8.34763Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx187(
        "path",
        {
          d: "M7.19674 6.26059L7.45581 7.05779H8.29394L7.61584 7.55045L7.87487 8.34765L7.19674 7.85495L6.5186 8.34765L6.77763 7.55045L6.09949 7.05779H6.9377L7.19674 6.26059Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx187("defs", { children: /* @__PURE__ */ jsx187("clipPath", { id: "cfSB_clip0_0_1", children: /* @__PURE__ */ jsx187("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SC.tsx
import { jsx as jsx188, jsxs as jsxs186 } from "react/jsx-runtime";
var FlagSC = (props) => /* @__PURE__ */ jsxs186(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs186("g", { clipPath: "url(#cfSC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx188(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx188(
      "path",
      {
        d: "M20.1004 3.14705C17.9657 1.19273 15.1222 0 12 0C11.4888 0 10.9852 0.0322031 10.4908 0.0942187L4.69562 6.78263L0.3797 15.0061C0.658512 16.0871 1.08479 17.1087 1.63478 18.0491L12 12L20.1004 3.14705Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx188(
      "path",
      {
        d: "M21.9905 18.6487L5.20139 21.8887C7.134 23.2199 9.47578 24 12 24C16.1682 24 19.8394 21.8745 21.9905 18.6487Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx188(
      "path",
      {
        d: "M20.1021 3.14862L1.64166 18.0607C2.12452 18.8842 2.70263 19.6451 3.36108 20.328L24 12C24 8.49565 22.4976 5.34246 20.1021 3.14862Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx188(
      "path",
      {
        d: "M0 12C0 13.0381 0.131906 14.0454 0.379734 15.0061L10.4909 0.0942078C4.57584 0.836426 0 5.88374 0 12Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx188("defs", { children: /* @__PURE__ */ jsx188("clipPath", { id: "cfSC_clip0_0_1", children: /* @__PURE__ */ jsx188("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SD.tsx
import { jsx as jsx189, jsxs as jsxs187 } from "react/jsx-runtime";
var FlagSD = (props) => /* @__PURE__ */ jsxs187(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs187("g", { clipPath: "url(#cfSD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx189(
      "path",
      {
        d: "M1.19072 6.78262C0.427875 8.36025 0 10.1301 0 12C0 13.8699 0.427875 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174C23.5721 15.6397 24 13.8699 24 12C24 10.1301 23.5721 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx189(
      "path",
      {
        d: "M1.19116 17.2173C3.13207 21.2313 7.24249 24 12 24C16.7576 24 20.868 21.2313 22.8089 17.2173H1.19116Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx189(
      "path",
      {
        d: "M1.19113 6.78263H22.8088C20.8679 2.76867 16.7575 0 12 0C7.24246 0 3.13204 2.76867 1.19113 6.78263Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx189(
      "path",
      {
        d: "M3.51465 3.51471C-1.17163 8.20099 -1.17163 15.799 3.51465 20.4853C5.4512 18.5488 7.31368 16.6863 12 12L3.51465 3.51471Z",
        fill: "#496E2D"
      }
    )
  ] }),
  /* @__PURE__ */ jsx189("defs", { children: /* @__PURE__ */ jsx189("clipPath", { id: "cfSD_clip0_0_1", children: /* @__PURE__ */ jsx189("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SE.tsx
import { jsx as jsx190, jsxs as jsxs188 } from "react/jsx-runtime";
var FlagSE = (props) => /* @__PURE__ */ jsxs188(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs188("g", { clipPath: "url(#cfSE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx190(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs188("g", { children: [
      /* @__PURE__ */ jsx190(
        "path",
        {
          d: "M9.39143 10.4348H23.8985C23.1315 4.54697 18.097 0 12.0001 0C11.1041 0 10.2313 0.0990469 9.39139 0.285234V10.4348H9.39143Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx190(
        "path",
        {
          d: "M6.2609 10.4347V1.45908C2.96587 3.25692 0.607077 6.55462 0.101624 10.4348H6.2609V10.4347Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx190(
        "path",
        {
          d: "M6.26086 13.5652H0.101624C0.607077 17.4454 2.96587 20.7431 6.2609 22.5409L6.26086 13.5652Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx190(
        "path",
        {
          d: "M9.39139 13.5652V23.7147C10.2313 23.9009 11.1041 24 12.0001 24C18.097 24 23.1315 19.453 23.8985 13.5652H9.39139V13.5652Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx190("defs", { children: /* @__PURE__ */ jsx190("clipPath", { id: "cfSE_clip0_0_1", children: /* @__PURE__ */ jsx190("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SG.tsx
import { jsx as jsx191, jsxs as jsxs189 } from "react/jsx-runtime";
var FlagSG = (props) => /* @__PURE__ */ jsxs189(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs189("g", { clipPath: "url(#cfSG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx191(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx191(
      "path",
      {
        d: "M0 12C0 5.37263 5.37262 0 12 0C18.6274 0 24 5.37263 24 12",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs189("g", { children: [
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M7.30432 6.2608C7.30432 4.51245 8.53306 3.05192 10.1739 2.69356C9.92168 2.63853 9.66007 2.60867 9.39125 2.60867C7.37417 2.60867 5.73907 4.24377 5.73907 6.26085C5.73907 8.27792 7.37417 9.91302 9.39125 9.91302C9.65998 9.91302 9.92164 9.88316 10.1739 9.82808C8.53306 9.46972 7.30432 8.00919 7.30432 6.2608Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M12 2.86954L12.259 3.66665H13.0972L12.4191 4.15935L12.6781 4.95646L12 4.46385L11.3219 4.95646L11.5809 4.15935L10.9028 3.66665H11.741L12 2.86954Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M9.96676 4.43472L10.2257 5.23188H11.0639L10.3858 5.72454L10.6449 6.52164L9.96676 6.02904L9.28857 6.52164L9.54765 5.72454L8.86951 5.23188H9.70768L9.96676 4.43472Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M14.0332 4.43475L14.2923 5.23191H15.1304L14.4523 5.72457L14.7113 6.52168L14.0332 6.02907L13.3551 6.52168L13.6141 5.72457L12.936 5.23191H13.7742L14.0332 4.43475Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M13.2507 6.78256L13.5098 7.57972H14.3479L13.6698 8.07237L13.9288 8.86948L13.2507 8.37687L12.5726 8.86948L12.8317 8.07237L12.1536 7.57972H12.9916L13.2507 6.78256Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx191(
        "path",
        {
          d: "M10.7493 6.78256L11.0083 7.57972H11.8465L11.1684 8.07237L11.4274 8.86948L10.7493 8.37687L10.0712 8.86948L10.3302 8.07237L9.6521 7.57972H10.4903L10.7493 6.78256Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx191("defs", { children: /* @__PURE__ */ jsx191("clipPath", { id: "cfSG_clip0_0_1", children: /* @__PURE__ */ jsx191("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SHP.tsx
import { jsx as jsx192, jsxs as jsxs190 } from "react/jsx-runtime";
var FlagSHP = (props) => /* @__PURE__ */ jsxs190(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
      fill: "#0052B4"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M6.07101 1.5652C4.19419 2.6339 2.63386 4.19419 1.5652 6.07101V12H4.69565V4.69575V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M17.2174 12L13.974 15.2434C14.5818 16.4158 15.8056 17.2174 17.2174 17.2174C18.6291 17.2174 19.8529 16.4158 20.4608 15.2434L17.2174 12Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M13.5652 6.26086V12V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V12V6.26086H13.5652Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx192("path", { d: "M16.4348 8.86955H18V13.0434H16.4348V8.86955Z", fill: "#F0F0F0" }),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M17.2174 9.65216C17.6496 9.65216 18 9.30177 18 8.86954C18 8.43731 17.6496 8.08691 17.2174 8.08691C16.7851 8.08691 16.4348 8.43731 16.4348 8.86954C16.4348 9.30177 16.7851 9.65216 17.2174 9.65216Z",
      fill: "#A2001D"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M14.6087 7.30435H15.6522V8.34784H14.6087V7.30435Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M14.6087 9.65218H15.6522V10.6957H14.6087V9.65218Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx192("path", { d: "M14.6087 12H15.6522V13.0435H14.6087V12Z", fill: "#FFDA44" }),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M18.7826 7.30435H19.8261V8.34784H18.7826V7.30435Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx192(
    "path",
    {
      d: "M18.7826 9.65218H19.8261V10.6957H18.7826V9.65218Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx192("path", { d: "M18.7826 12H19.8261V13.0435H18.7826V12Z", fill: "#FFDA44" }),
  /* @__PURE__ */ jsx192("defs", { children: /* @__PURE__ */ jsx192("clipPath", { id: "cfSHP_clip0_2_7618", children: /* @__PURE__ */ jsx192("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SI.tsx
import { jsx as jsx193, jsxs as jsxs191 } from "react/jsx-runtime";
var FlagSI = (props) => /* @__PURE__ */ jsxs191(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs191("g", { clipPath: "url(#cfSI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx193(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx193(
      "path",
      {
        d: "M23.2536 7.8261H10.4348V4.69565H4.17389V7.8261H0.746391C0.264047 9.12604 0 10.5321 0 12C0 13.4678 0.264047 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739C23.736 14.8739 24 13.4678 24 12C24 10.5321 23.736 9.12604 23.2536 7.8261Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx193(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746399C2.44191 20.7435 6.84043 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx193(
      "path",
      {
        d: "M4.17389 7.8261V8.86954C4.17389 11.2658 7.30434 12 7.30434 12C7.30434 12 10.4348 11.2658 10.4348 8.86954V7.8261L9.39131 8.86959L7.30434 7.30438L5.21737 8.86959L4.17389 7.8261Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx193("defs", { children: /* @__PURE__ */ jsx193("clipPath", { id: "cfSI_clip0_0_1", children: /* @__PURE__ */ jsx193("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SK.tsx
import { jsx as jsx194, jsxs as jsxs192 } from "react/jsx-runtime";
var FlagSK = (props) => /* @__PURE__ */ jsxs192(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs192("g", { clipPath: "url(#cfSK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 10.5322 23.736 9.12605 23.2536 7.82611H0.746391C0.264047 9.12605 0 10.5322 0 12C0 18.6274 5.37262 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746384C2.4419 20.7435 6.84041 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M3.10304 6.78265V12.7689C3.10304 16.1741 7.55153 17.2175 7.55153 17.2175C7.55153 17.2175 12 16.1741 12 12.7689V6.78265H3.10304Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M4.14651 6.78265V12.7689C4.14651 13.1686 4.2353 13.5462 4.41117 13.9001H10.6918C10.8677 13.5462 10.9565 13.1686 10.9565 12.7689V6.78265H4.14651Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M9.63844 10.9565H8.07329V9.91303H9.11677V8.86955H8.07329V7.82611H7.0298V8.86955H5.98637V9.91303H7.0298V10.9565H5.46455V12H7.0298V13.0435H8.07329V12H9.63844V10.9565Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx194(
      "path",
      {
        d: "M5.8346 15.3567C6.50702 15.7793 7.19786 16.0246 7.55153 16.1338C7.90521 16.0247 8.59605 15.7793 9.26847 15.3567C9.94657 14.9305 10.4219 14.4431 10.6918 13.9001C10.3941 13.6895 10.0309 13.5652 9.6385 13.5652C9.49563 13.5652 9.35683 13.5822 9.22338 13.6132C8.94058 12.9707 8.29863 12.5218 7.55158 12.5218C6.80453 12.5218 6.16253 12.9707 5.87978 13.6132C5.74633 13.5822 5.60749 13.5652 5.46466 13.5652C5.07227 13.5652 4.70903 13.6895 4.41133 13.9001C4.6811 14.4431 5.15646 14.9305 5.8346 15.3567Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx194("defs", { children: /* @__PURE__ */ jsx194("clipPath", { id: "cfSK_clip0_0_1", children: /* @__PURE__ */ jsx194("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SL.tsx
import { jsx as jsx195, jsxs as jsxs193 } from "react/jsx-runtime";
var FlagSL = (props) => /* @__PURE__ */ jsxs193(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs193("g", { clipPath: "url(#cfSL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx195(
      "path",
      {
        d: "M23.2536 16.1739C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.12609 23.2536 7.82611L12 6.78262L0.746391 7.82611C0.264 9.12609 0 10.5322 0 12C0 13.4678 0.264 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx195(
      "path",
      {
        d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx195(
      "path",
      {
        d: "M12 0C6.84037 0 2.4419 3.2565 0.746338 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0Z",
        fill: "#6DA544"
      }
    )
  ] }),
  /* @__PURE__ */ jsx195("defs", { children: /* @__PURE__ */ jsx195("clipPath", { id: "cfSL_clip0_0_1", children: /* @__PURE__ */ jsx195("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SLS.tsx
import { jsx as jsx196, jsxs as jsxs194 } from "react/jsx-runtime";
var FlagSLS = (props) => /* @__PURE__ */ jsxs194(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M23.2536 16.1739C23.736 14.8739 24 13.4679 24 12C24 10.5322 23.736 9.12612 23.2536 7.82614L12 6.78265L0.746391 7.82614C0.264047 9.12612 0 10.5322 0 12C0 13.4679 0.264047 14.8739 0.746391 16.1739L12 17.2174L23.2536 16.1739Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M12 24C17.1595 24 21.5581 20.7435 23.2536 16.1739H0.746338C2.4419 20.7435 6.84037 24 12 24Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M12 0C6.84037 0 2.4419 3.2565 0.746338 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0Z",
      fill: "#496E2D"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M12 9.3913L12.6475 11.3841H14.743L13.0477 12.6158L13.6953 14.6087L12 13.3771L10.3047 14.6087L10.9523 12.6158L9.25708 11.3841H11.3525L12 9.3913Z",
      fill: "black"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M15.5652 2.34784V4.78257C15.5652 5.16625 15.2532 5.47825 14.8696 5.47825V6.52173C15.8286 6.52173 16.6087 5.74159 16.6087 4.78257V2.34784H15.5652Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M8.08697 4.78257C8.08697 5.16625 7.77492 5.47825 7.3913 5.47825V6.52173C8.35031 6.52173 9.13045 5.74159 9.13045 4.78257V2.34784H8.08697V4.78257Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M13.9998 2.34784H15.0433V4.78257H13.9998V2.34784Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx196(
    "path",
    {
      d: "M12.4348 3.73909C12.4348 3.835 12.3568 3.91304 12.2609 3.91304C12.165 3.91304 12.0869 3.835 12.0869 3.73909V2.34784H11.0435V3.73909C11.0435 3.835 10.9655 3.91304 10.8696 3.91304C10.7736 3.91304 10.6956 3.835 10.6956 3.73909V2.34784H9.65216V3.73909C9.65216 4.41043 10.1983 4.95653 10.8696 4.95653C11.1281 4.95653 11.3679 4.87529 11.5652 4.73734C11.7626 4.87529 12.0023 4.95653 12.2608 4.95653C12.3129 4.95653 12.3641 4.95287 12.4145 4.94645C12.3405 5.25109 12.0661 5.47825 11.7391 5.47825V6.52173C12.6981 6.52173 13.4782 5.74159 13.4782 4.78257V3.73909V2.34784H12.4348L12.4348 3.73909Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx196("path", { d: "M9.65219 5.4783H11.2174V6.52179H9.65219V5.4783Z", fill: "#F0F0F0" }),
  /* @__PURE__ */ jsx196("defs", { children: /* @__PURE__ */ jsx196("clipPath", { id: "cfSLS_clip0_2_8992", children: /* @__PURE__ */ jsx196("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SM.tsx
import { jsx as jsx197, jsxs as jsxs195 } from "react/jsx-runtime";
var FlagSM = (props) => /* @__PURE__ */ jsxs195(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs195("g", { clipPath: "url(#cfSM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0.521719 12 12 10.4348 12 10.4348L24 12Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M16.7633 8.28018L12 13.0435L7.23677 8.28018C6.62079 9.19457 6.26093 10.2952 6.26093 11.4782V13.0434C6.26093 15.5567 7.88491 17.6976 10.1385 18.4725C9.83772 19.062 9.88844 19.7977 10.3292 20.3478C10.9146 19.8786 11.5206 19.3929 12.0348 18.981C12.5489 19.393 13.155 19.8787 13.7403 20.3478C14.1862 19.7913 14.2333 19.0446 13.9206 18.4516C16.1434 17.6599 17.7391 15.5347 17.7391 13.0434V11.4783C17.7392 10.2952 17.3793 9.19462 16.7633 8.28018Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M12 17.2174C9.69849 17.2174 7.82611 15.3449 7.82611 13.0435V11.4783C7.82611 9.17676 9.69853 7.30438 12 7.30438C14.3015 7.30438 16.1739 9.1768 16.1739 11.4783V13.0435C16.1739 15.3449 14.3015 17.2174 12 17.2174Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M14.6087 13.0435V11.4783C14.6087 10.0398 13.4384 8.86957 12 8.86957C10.5616 8.86957 9.3913 10.0398 9.3913 11.4783V13.0435L12 13.5652L14.6087 13.0435Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M9.3913 13.0435C9.3913 14.4819 10.5616 15.6521 12 15.6521C13.4384 15.6521 14.6087 14.4819 14.6087 13.0435H9.3913Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx197(
      "path",
      {
        d: "M15.1305 7.30433C15.1305 6.43991 14.4297 5.73913 13.5653 5.73913C13.1643 5.73913 12.7988 5.89007 12.5218 6.13799V5.21736H13.0435V4.17388H12.5218V3.65216H11.4783V4.17388H10.9566V5.21736H11.4783V6.13799C11.2013 5.89007 10.8358 5.73913 10.4348 5.73913C9.57041 5.73913 8.86963 6.43991 8.86963 7.30433C8.86963 7.76779 9.07124 8.18404 9.39135 8.47068V9.3913H14.6087V8.47068C14.9289 8.18404 15.1305 7.76779 15.1305 7.30433Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx197("defs", { children: /* @__PURE__ */ jsx197("clipPath", { id: "cfSM_clip0_0_1", children: /* @__PURE__ */ jsx197("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SN.tsx
import { jsx as jsx198, jsxs as jsxs196 } from "react/jsx-runtime";
var FlagSN = (props) => /* @__PURE__ */ jsxs196(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs196("g", { clipPath: "url(#cfSN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx198(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs196("g", { children: [
      /* @__PURE__ */ jsx198(
        "path",
        {
          d: "M6.78262 14.3478C6.78262 15.2122 7.4834 15.913 8.34783 15.913H13.0435C13.0435 16.6334 13.6275 17.2174 14.3478 17.2174H15.913C16.6334 17.2174 17.2174 16.6334 17.2174 15.913V14.3478H6.78262Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx198(
        "path",
        {
          d: "M17.3478 6.78259V10.4348C17.3478 11.0102 16.8797 11.4782 16.3043 11.4782V13.0435C17.7428 13.0435 18.913 11.8732 18.913 10.4348V6.78259H17.3478Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx198(
        "path",
        {
          d: "M6.13046 10.4348C6.13046 11.0102 5.66237 11.4783 5.08698 11.4783V13.0435C6.52543 13.0435 7.69566 11.8732 7.69566 10.4348V6.78262H6.13046V10.4348Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx198("path", { d: "M15 6.78259H16.5652V10.4348H15V6.78259Z", fill: "#F0F0F0" }),
      /* @__PURE__ */ jsx198(
        "path",
        {
          d: "M12.6522 8.86953C12.6522 9.01339 12.5351 9.13039 12.3913 9.13039C12.2475 9.13039 12.1304 9.01334 12.1304 8.86953V6.78256H10.5652V8.86953C10.5652 9.01339 10.4482 9.13039 10.3044 9.13039C10.1606 9.13039 10.0435 9.01334 10.0435 8.86953V6.78256H8.47827V8.86953C8.47827 9.87645 9.29746 10.6956 10.3044 10.6956C10.6922 10.6956 11.0518 10.5738 11.3479 10.3668C11.6439 10.5737 12.0036 10.6956 12.3913 10.6956C12.4694 10.6956 12.5462 10.6902 12.6217 10.6805C12.5108 11.1375 12.0993 11.4782 11.6087 11.4782V13.0434C13.0472 13.0434 14.2174 11.8731 14.2174 10.4347V8.86953V6.78256H12.6522V8.86953H12.6522Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx198(
        "path",
        {
          d: "M8.47827 11.4783H10.8261V13.0435H8.47827V11.4783Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx198("defs", { children: /* @__PURE__ */ jsx198("clipPath", { id: "cfSN_clip0_0_1", children: /* @__PURE__ */ jsx198("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SO.tsx
import { jsx as jsx199, jsxs as jsxs197 } from "react/jsx-runtime";
var FlagSO = (props) => /* @__PURE__ */ jsxs197(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs197("g", { clipPath: "url(#cfSO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx199(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx199(
      "path",
      {
        d: "M12 6.26086L13.2951 10.2466H17.4859L14.0954 12.7099L15.3905 16.6957L12 14.2323L8.60952 16.6957L9.90458 12.7099L6.51407 10.2466H10.7049L12 6.26086Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx199("defs", { children: /* @__PURE__ */ jsx199("clipPath", { id: "cfSO_clip0_0_1", children: /* @__PURE__ */ jsx199("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SR.tsx
import { jsx as jsx200, jsxs as jsxs198 } from "react/jsx-runtime";
var FlagSR = (props) => /* @__PURE__ */ jsxs198(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs198("g", { clipPath: "url(#cfSR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx200(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx200(
      "path",
      {
        d: "M23.2536 7.82611H0.746344C0.264047 9.12614 0 10.5322 0 12C0 13.4678 0.264047 14.874 0.746344 16.1739H23.2536C23.736 14.874 24 13.4678 24 12C24 10.5322 23.736 9.12614 23.2536 7.82611Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs198("g", { children: [
      /* @__PURE__ */ jsx200(
        "path",
        {
          d: "M12.0347 24C16.1448 24 19.7718 21.9332 21.9346 18.7827H2.13498C4.2977 21.9332 7.9246 24 12.0347 24Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx200(
        "path",
        {
          d: "M12.0347 9.15527e-05C16.1448 9.15527e-05 19.7718 2.0669 21.9346 5.21747H2.13498C4.2977 2.0669 7.9246 9.15527e-05 12.0347 9.15527e-05Z",
          fill: "#6DA544"
        }
      )
    ] }),
    /* @__PURE__ */ jsx200(
      "path",
      {
        d: "M12 7.82611L13.036 11.0145H16.3887L13.6763 12.9854L14.7124 16.1739L12 14.2033L9.28756 16.1739L10.3237 12.9854L7.61127 11.0145H10.964L12 7.82611Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx200("defs", { children: /* @__PURE__ */ jsx200("clipPath", { id: "cfSR_clip0_0_1", children: /* @__PURE__ */ jsx200("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SS.tsx
import { jsx as jsx201, jsxs as jsxs199 } from "react/jsx-runtime";
var FlagSS = (props) => /* @__PURE__ */ jsxs199(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs199("g", { clipPath: "url(#cfSS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M23.0638 7.3478C21.2465 3.03103 16.9775 0 12 0C8.68618 0 5.68651 1.34339 3.51508 3.51511L6.52023 7.3478H23.0638Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M6.55431 16.6088L3.51508 20.4849C5.68651 22.6566 8.68618 24 12 24C16.9943 24 21.2755 20.9487 23.0823 16.6088H6.55431Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M2.6087 8.82611V15.1304H23.5867C23.8558 14.1324 24 13.0832 24 12C24 10.9012 23.852 9.83711 23.5753 8.82611H2.6087Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M3.51471 3.51471C-1.17157 8.20099 -1.17157 15.799 3.51471 20.4853C5.45126 18.5488 7.31374 16.6863 12 12L3.51471 3.51471Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx201(
      "path",
      {
        d: "M3.90865 9.01648L5.37326 11.0603L7.76988 10.2988L6.27874 12.3235L7.74335 14.3674L5.35704 13.5748L3.86585 15.5995L3.88226 13.0849L1.49585 12.2923L3.89229 11.5309L3.90865 9.01648Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx201("defs", { children: /* @__PURE__ */ jsx201("clipPath", { id: "cfSS_clip0_0_1", children: /* @__PURE__ */ jsx201("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ST.tsx
import { jsx as jsx202, jsxs as jsxs200 } from "react/jsx-runtime";
var FlagST = (props) => /* @__PURE__ */ jsxs200(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs200("g", { clipPath: "url(#cfST_clip0_0_1)", children: [
    /* @__PURE__ */ jsx202(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs200("g", { children: [
      /* @__PURE__ */ jsx202(
        "path",
        {
          d: "M7.30436 7.82611H23.2536C21.5581 3.2565 17.1595 0 12 0C8.68623 0 5.68656 1.34339 3.51512 3.51511L7.30436 7.82611Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx202(
        "path",
        {
          d: "M7.30436 16.1739H23.2536C21.5581 20.7435 17.1595 24 12 24C8.68623 24 5.68656 22.6566 3.51512 20.4849L7.30436 16.1739Z",
          fill: "#6DA544"
        }
      )
    ] }),
    /* @__PURE__ */ jsx202(
      "path",
      {
        d: "M3.51467 3.51468C-1.17162 8.20096 -1.17162 15.799 3.51467 20.4853C5.45121 18.5488 7.3137 16.6863 12 12L3.51467 3.51468Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx202(
      "path",
      {
        d: "M15.2378 9.91302L15.7558 11.5073H17.4322L16.076 12.4927L16.594 14.087L15.2378 13.1016L13.8816 14.087L14.3996 12.4927L13.0435 11.5073H14.7198L15.2378 9.91302Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx202(
      "path",
      {
        d: "M20.4553 9.91302L20.9733 11.5073H22.6496L21.2935 12.4927L21.8114 14.087L20.4553 13.1016L19.0991 14.087L19.6171 12.4927L18.2609 11.5073H19.9372L20.4553 9.91302Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx202("defs", { children: /* @__PURE__ */ jsx202("clipPath", { id: "cfST_clip0_0_1", children: /* @__PURE__ */ jsx202("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SV.tsx
import { jsx as jsx203, jsxs as jsxs201 } from "react/jsx-runtime";
var FlagSV = (props) => /* @__PURE__ */ jsxs201(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs201("g", { clipPath: "url(#cfSV_clip0_0_1)", children: [
    /* @__PURE__ */ jsx203(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs201("g", { children: [
      /* @__PURE__ */ jsx203(
        "path",
        {
          d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx203(
        "path",
        {
          d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2174H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
          fill: "#0052B4"
        }
      )
    ] }),
    /* @__PURE__ */ jsx203(
      "path",
      {
        d: "M9.59015 12.5217L12 8.34784L14.4098 12.5217H9.59015Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx203(
      "path",
      {
        d: "M15.1305 13.8999L12 15.4651L8.86957 13.8999V11.813H15.1305V13.8999Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx203(
      "path",
      {
        d: "M14.9514 8.52689L13.8446 9.63365C14.3167 10.1057 14.6088 10.7579 14.6088 11.4783C14.6088 12.919 13.4408 14.087 12.0001 14.087C10.5594 14.087 9.39138 12.919 9.39138 11.4783C9.39138 10.7579 9.68341 10.1057 10.1555 9.63365L9.04872 8.52689C8.29333 9.28214 7.82617 10.3256 7.82617 11.4783C7.82617 13.7835 9.69489 15.6522 12.0001 15.6522C14.3052 15.6522 16.174 13.7834 16.174 11.4783C16.1739 10.3256 15.7068 9.28214 14.9514 8.52689Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx203("defs", { children: /* @__PURE__ */ jsx203("clipPath", { id: "cfSV_clip0_0_1", children: /* @__PURE__ */ jsx203("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SY.tsx
import { jsx as jsx204, jsxs as jsxs202 } from "react/jsx-runtime";
var FlagSY = (props) => /* @__PURE__ */ jsxs202(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs202("g", { clipPath: "url(#cfSY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx204(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx204(
      "path",
      {
        d: "M12.0001 0C6.84049 0 2.44198 3.2565 0.74646 7.82611H23.2537C21.5582 3.2565 17.1596 0 12.0001 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx204(
      "path",
      {
        d: "M12.0001 24C17.1596 24 21.5582 20.7435 23.2537 16.1739H0.74646C2.44198 20.7435 6.84049 24 12.0001 24Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs202("g", { children: [
      /* @__PURE__ */ jsx204(
        "path",
        {
          d: "M7.17006 9.13046L7.81755 11.1232H9.913L8.21777 12.355L8.8653 14.3478L7.17006 13.1162L5.47483 14.3478L6.12236 12.355L4.42712 11.1232H6.52258L7.17006 9.13046Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx204(
        "path",
        {
          d: "M16.8299 9.13046L17.4773 11.1232H19.5728L17.8776 12.355L18.5251 14.3478L16.8299 13.1162L15.1346 14.3478L15.7821 12.355L14.0869 11.1232H16.1824L16.8299 9.13046Z",
          fill: "#6DA544"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx204("defs", { children: /* @__PURE__ */ jsx204("clipPath", { id: "cfSY_clip0_0_1", children: /* @__PURE__ */ jsx204("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/SZ.tsx
import { jsx as jsx205, jsxs as jsxs203 } from "react/jsx-runtime";
var FlagSZ = (props) => /* @__PURE__ */ jsxs203(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs203("g", { clipPath: "url(#cfSZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M22.8093 17.2174C23.5721 15.6398 24 13.8698 24 12C24 10.1302 23.5721 8.36021 22.8093 6.78263L12 6.26086L1.19072 6.78258C0.427875 8.36021 0 10.1302 0 12C0 13.8698 0.427875 15.6398 1.19072 17.2174L12 17.7391L22.8093 17.2174Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8092C20.8683 2.76867 16.7575 0 12 0Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M12 24C7.24248 24 3.13173 21.2313 1.19073 17.2173H22.8092C20.8683 21.2313 16.7575 24 12 24Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M23.4333 8.34784H0.566672C0.199078 9.49951 0 10.7265 0 12C0 13.2736 0.199078 14.5005 0.566672 15.6522H23.4333C23.8009 14.5005 24 13.2736 24 12C24 10.7265 23.8009 9.49951 23.4333 8.34784Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs203("g", { children: [
      /* @__PURE__ */ jsx205(
        "path",
        {
          d: "M4.17389 11.4783H19.8261V12.5218H4.17389V11.4783Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx205(
        "path",
        {
          d: "M6.26086 9.3913H17.7391V10.4348H6.26086V9.3913Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M17.8137 12C17.8137 12 15.1304 15.1304 12 15.1304C8.86958 15.1304 8.86954 12 8.86954 12C8.86954 12 8.86954 8.86954 12 8.86954C15.1304 8.86954 17.8137 12 17.8137 12Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M12 15.1304C8.86953 15.1304 6.18631 12 6.18631 12C6.18631 12 8.86953 8.86954 12 8.86954",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M9.91302 10.9565H10.9565V13.0435H9.91302V10.9565Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx205(
      "path",
      {
        d: "M13.0435 10.9565H14.0869V13.0435H13.0435V10.9565Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs203("g", { children: [
      /* @__PURE__ */ jsx205(
        "path",
        {
          d: "M4.17392 13.0435C4.75022 13.0435 5.2174 12.5763 5.2174 12C5.2174 11.4237 4.75022 10.9565 4.17392 10.9565C3.59762 10.9565 3.13043 11.4237 3.13043 12C3.13043 12.5763 3.59762 13.0435 4.17392 13.0435Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx205(
        "path",
        {
          d: "M19.8261 13.0435C20.4024 13.0435 20.8696 12.5763 20.8696 12C20.8696 11.4237 20.4024 10.9565 19.8261 10.9565C19.2498 10.9565 18.7827 11.4237 18.7827 12C18.7827 12.5763 19.2498 13.0435 19.8261 13.0435Z",
          fill: "#0052B4"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx205("defs", { children: /* @__PURE__ */ jsx205("clipPath", { id: "cfSZ_clip0_0_1", children: /* @__PURE__ */ jsx205("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TC.tsx
import { jsx as jsx206, jsxs as jsxs204 } from "react/jsx-runtime";
var FlagTC = (props) => /* @__PURE__ */ jsxs204(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs204("g", { clipPath: "url(#cfTC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs204("g", { children: [
      /* @__PURE__ */ jsx206(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx206(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs204("g", { children: [
      /* @__PURE__ */ jsx206(
        "path",
        {
          d: "M6.071 1.56519C4.19417 2.63389 2.63384 4.19417 1.56519 6.071V12H4.69564V4.69573V4.69564H12C12 3.70831 12 2.76772 12 1.56519H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx206(
        "path",
        {
          d: "M12 10.5243L7.73647 6.26086H6.26085V6.26096L11.9999 12H12C12 12 12 10.9824 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M13.5652 6.26086V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V6.26086H13.5652Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M16.6957 8.34781C16.6957 8.92413 16.2285 10.4348 15.6522 10.4348C15.0759 10.4348 14.6087 8.92413 14.6087 8.34781C14.6087 7.77148 15.6522 7.30432 15.6522 7.30432C15.6522 7.30432 16.6957 7.77148 16.6957 8.34781Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M19.4646 9.48429C19.633 9.07343 19.764 8.47287 19.764 8.1739C19.764 7.69362 19.1378 7.30432 19.1378 7.30432C19.1378 7.30432 18.5117 7.69366 18.5117 8.1739C18.5117 8.47287 18.6427 9.07343 18.8111 9.48429L18.4496 10.3004C18.6621 10.3867 18.8943 10.4347 19.1378 10.4347C19.3813 10.4347 19.6135 10.3867 19.826 10.3004L19.4646 9.48429Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M16.4348 12C16.4348 12 15.9131 13.0435 15.9131 14.087H18.5217C18.5217 13.0435 18 12 18 12L17.2174 11.4783L16.4348 12Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx206(
      "path",
      {
        d: "M18 12V11.7392C18 11.307 17.6496 10.9565 17.2174 10.9565C16.7852 10.9565 16.4348 11.3069 16.4348 11.7392V12H18Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx206("defs", { children: /* @__PURE__ */ jsx206("clipPath", { id: "cfTC_clip0_0_1", children: /* @__PURE__ */ jsx206("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TD.tsx
import { jsx as jsx207, jsxs as jsxs205 } from "react/jsx-runtime";
var FlagTD = (props) => /* @__PURE__ */ jsxs205(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs205("g", { clipPath: "url(#cfTD_clip0_0_1)", children: [
    /* @__PURE__ */ jsx207(
      "path",
      {
        d: "M12 0C10.5322 0 9.12605 0.264047 7.82612 0.746391L7.30435 12L7.82607 23.2536C9.12605 23.736 10.5322 24 12 24C13.4679 24 14.874 23.736 16.1739 23.2536L16.6957 12L16.1739 0.746391C14.874 0.264047 13.4679 0 12 0V0Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx207(
      "path",
      {
        d: "M24 12C24 6.84042 20.7435 2.4419 16.1739 0.746384V23.2536C20.7435 21.5581 24 17.1596 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx207(
      "path",
      {
        d: "M7.82611 23.2536V0.746384C3.2565 2.4419 0 6.84041 0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx207("defs", { children: /* @__PURE__ */ jsx207("clipPath", { id: "cfTD_clip0_0_1", children: /* @__PURE__ */ jsx207("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TG.tsx
import { jsx as jsx208, jsxs as jsxs206 } from "react/jsx-runtime";
var FlagTG = (props) => /* @__PURE__ */ jsxs206(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs206("g", { clipPath: "url(#cfTG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx208(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsxs206("g", { children: [
      /* @__PURE__ */ jsx208(
        "path",
        {
          d: "M12 0C11.9913 9.375e-05 11.4783 4.8 11.4783 4.8H21.6006C19.4113 1.88545 15.9259 0 12 0Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx208(
        "path",
        {
          d: "M12 24C15.9259 24 19.4113 22.1145 21.6005 19.2H2.39951C4.58876 22.1145 8.07415 24 12 24Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx208(
        "path",
        {
          d: "M23.7597 9.59998H11.4783L12 14.4H23.7597C23.9172 13.6245 24 12.8219 24 12C24 11.178 23.9171 10.3754 23.7597 9.59998Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsx208(
      "path",
      {
        d: "M12 14.4C12 11.8957 12 2.81737 12 0C5.37262 0 0 5.37262 0 12C0 12.822 0.082875 13.6245 0.240281 14.4H12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx208(
      "path",
      {
        d: "M6.62142 5.73914L7.39842 8.13046H9.91303L7.8788 9.60857L8.65571 12L6.62142 10.522L4.58714 12L5.36419 9.60857L3.32996 8.13046H5.84447L6.62142 5.73914Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx208("defs", { children: /* @__PURE__ */ jsx208("clipPath", { id: "cfTG_clip0_0_1", children: /* @__PURE__ */ jsx208("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TH.tsx
import { jsx as jsx209, jsxs as jsxs207 } from "react/jsx-runtime";
var FlagTH = (props) => /* @__PURE__ */ jsxs207(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs207("g", { clipPath: "url(#cfTH_clip0_0_1)", children: [
    /* @__PURE__ */ jsx209(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx209(
      "path",
      {
        d: "M23.2536 7.82611H0.746391C0.264 9.1261 0 10.5322 0 12C0 13.4678 0.264 14.8739 0.746391 16.1739H23.2537C23.736 14.8739 24 13.4678 24 12C24 10.5322 23.736 9.1261 23.2536 7.82611Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs207("g", { children: [
      /* @__PURE__ */ jsx209(
        "path",
        {
          d: "M12 0C8.36294 0 5.10405 1.61831 2.90341 4.17389H21.0966C18.896 1.61831 15.6371 0 12 0Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx209(
        "path",
        {
          d: "M21.0966 19.8261H2.90341C5.10405 22.3817 8.36294 24 12 24C15.6371 24 18.896 22.3817 21.0966 19.8261Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx209("defs", { children: /* @__PURE__ */ jsx209("clipPath", { id: "cfTH_clip0_0_1", children: /* @__PURE__ */ jsx209("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TJ.tsx
import { jsx as jsx210, jsxs as jsxs208 } from "react/jsx-runtime";
var FlagTJ = (props) => /* @__PURE__ */ jsxs208(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs208("g", { clipPath: "url(#cfTJ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx210(
      "path",
      {
        d: "M1.19072 6.78262C0.427875 8.36025 0 10.1302 0 12C0 13.8698 0.427875 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174C23.5721 15.6397 24 13.8698 24 12C24 10.1302 23.5721 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx210(
      "path",
      {
        d: "M1.19115 17.2173C3.1321 21.2313 7.24252 24 12 24C16.7575 24 20.868 21.2313 22.8089 17.2173H1.19115Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx210(
      "path",
      {
        d: "M1.19114 6.78263H22.8089C20.868 2.76872 16.7575 0 12 0C7.24251 0 3.13209 2.76872 1.19114 6.78263Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs208("g", { children: [
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M9.91301 15.1305H14.0869V13.3565L13.2522 13.7739L12 12.5217L10.7478 13.7739L9.91301 13.3565V15.1305Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M7.52492 13.0435L7.71921 13.6414H8.34781L7.83921 14.0109L8.03351 14.6087L7.52492 14.2393L7.01632 14.6087L7.21062 14.0109L6.70203 13.6414H7.33062L7.52492 13.0435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M8.1272 10.9565L8.32149 11.5544H8.95009L8.44149 11.9239L8.63574 12.5217L8.1272 12.1523L7.6186 12.5217L7.8129 11.9239L7.30431 11.5544H7.9329L8.1272 10.9565Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M9.84903 9.39136L10.0433 9.9892H10.6719L10.1634 10.3587L10.3576 10.9566L9.84903 10.5871L9.34048 10.9566L9.53473 10.3587L9.02614 9.9892H9.65478L9.84903 9.39136Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M16.475 13.0435L16.2807 13.6414H15.6521L16.1607 14.0109L15.9664 14.6087L16.475 14.2393L16.9836 14.6087L16.7893 14.0109L17.2979 13.6414H16.6693L16.475 13.0435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M15.8727 10.9565L15.6784 11.5544H15.0499L15.5584 11.9239L15.3641 12.5217L15.8727 12.1523L16.3813 12.5217L16.187 11.9239L16.6956 11.5544H16.067L15.8727 10.9565Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M14.151 9.39136L13.9567 9.9892H13.3281L13.8366 10.3587L13.6424 10.9566L14.151 10.5871L14.6595 10.9566L14.4653 10.3587L14.9738 9.9892H14.3452L14.151 9.39136Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx210(
        "path",
        {
          d: "M12 8.60864L12.1943 9.20649H12.8229L12.3143 9.576L12.5086 10.1738L12 9.80438L11.4914 10.1738L11.6857 9.576L11.1771 9.20649H11.8057L12 8.60864Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx210("defs", { children: /* @__PURE__ */ jsx210("clipPath", { id: "cfTJ_clip0_0_1", children: /* @__PURE__ */ jsx210("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TK.tsx
import { jsx as jsx211, jsxs as jsxs209 } from "react/jsx-runtime";
var FlagTK = (props) => /* @__PURE__ */ jsxs209(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs209("g", { clipPath: "url(#cfTK_clip0_0_1)", children: [
    /* @__PURE__ */ jsx211(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx211(
      "path",
      {
        d: "M19.3043 5.73914C19.1967 6.25641 19.1478 6.80733 19.1478 7.35652C19.1478 10.5222 21.035 13.2621 23.7394 14.4955C23.9097 13.6904 24 12.8558 24 12C24 5.37262 18.6274 0 12 0C5.37262 0 0 5.37262 0 12C0 18.6274 5.37262 24 12 24C16.1101 24 19.7371 21.9332 21.8998 18.7826H6.78263V17.2174H22.8093C22.9733 16.8782 23.1215 16.53 23.2537 16.1739H6.78263L19.3043 5.73914Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs209("g", { children: [
      /* @__PURE__ */ jsx211(
        "path",
        {
          d: "M8.81589 6.26086L9.07492 7.05802H9.91305L9.235 7.55072L9.49403 8.34783L8.81589 7.85522L8.13785 8.34783L8.39683 7.55072L7.71869 7.05802H8.55686L8.81589 6.26086Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx211(
        "path",
        {
          d: "M5.41119 12L5.735 12.9965H6.78266L5.93501 13.6122L6.25883 14.6087L5.41119 13.9928L4.56359 14.6087L4.88736 13.6122L4.03976 12.9965H5.08742L5.41119 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx211(
        "path",
        {
          d: "M5.41119 3.65216L5.735 4.64868H6.78266L5.93501 5.26438L6.25883 6.26085L5.41119 5.645L4.56359 6.26085L4.88736 5.26438L4.03976 4.64868H5.08742L5.41119 3.65216Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx211(
        "path",
        {
          d: "M2.80248 8.34784L3.1263 9.34435H4.1739L3.32631 9.96006L3.65012 10.9565L2.80248 10.3407L1.95489 10.9565L2.27865 9.96006L1.43106 9.34435H2.47867L2.80248 8.34784Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx211("defs", { children: /* @__PURE__ */ jsx211("clipPath", { id: "cfTK_clip0_0_1", children: /* @__PURE__ */ jsx211("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TL.tsx
import { jsx as jsx212, jsxs as jsxs210 } from "react/jsx-runtime";
var FlagTL = (props) => /* @__PURE__ */ jsxs210(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs210("g", { clipPath: "url(#cfTL_clip0_0_1)", children: [
    /* @__PURE__ */ jsx212(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx212(
      "path",
      {
        d: "M12 0C9.06202 0 6.37111 1.05642 4.28508 2.80941L17.2174 12L4.28508 21.1906C6.37111 22.9436 9.06202 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx212(
      "path",
      {
        d: "M3.51467 3.51468C-1.17162 8.20096 -1.17162 15.799 3.51467 20.4853C5.45121 18.5488 7.3137 16.6863 12 12L3.51467 3.51468Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx212(
      "path",
      {
        d: "M3.32713 9.25101L5.15338 10.9794L7.36157 9.77638L6.28227 12.0475L8.10857 13.7759L5.6151 13.4511L4.53566 15.7222L4.07408 13.2504L1.58061 12.9255L3.78871 11.7227L3.32713 9.25101Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx212("defs", { children: /* @__PURE__ */ jsx212("clipPath", { id: "cfTL_clip0_0_1", children: /* @__PURE__ */ jsx212("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TM.tsx
import { jsx as jsx213, jsxs as jsxs211 } from "react/jsx-runtime";
var FlagTM = (props) => /* @__PURE__ */ jsxs211(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs211("g", { clipPath: "url(#cfTM_clip0_0_1)", children: [
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M12 0C11.104 0 10.2312 0.0990469 9.39132 0.285234L8.8696 12L9.39132 23.7147C10.2313 23.9009 11.104 24 12 24C18.6274 24 24 18.6274 24 12C23.9999 5.37258 18.6274 0 12 0V0Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M3.13045 3.91742C1.18589 6.05004 4.57764e-05 8.88645 4.57764e-05 12C4.57764e-05 15.1135 1.18594 17.9499 3.1305 20.0826L3.65222 12L3.13045 3.91742Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M17.551 9.0515C18.1414 7.4059 17.4781 5.61617 16.0547 4.72479C16.3107 4.75812 16.567 4.8184 16.8199 4.9091C18.7185 5.59029 19.7054 7.68148 19.0242 9.58001C18.343 11.4787 16.2519 12.4655 14.3533 11.7843C14.1003 11.6936 13.8641 11.5771 13.6453 11.4401C15.3108 11.6569 16.9606 10.6971 17.551 9.0515Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M14.2764 4.27374L13.7634 4.93665L12.9745 4.65357L13.4464 5.34629L12.9334 6.0091L13.738 5.77445L14.21 6.46707L14.2353 5.62932L15.0399 5.39462L14.2511 5.11163L14.2764 4.27374Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M15.6617 6.43372L15.1487 7.09658L14.3598 6.8135L14.8317 7.50622L14.3187 8.16903L15.1233 7.93428L15.5952 8.62704L15.6206 7.78925L16.4252 7.5545L15.6363 7.27147L15.6617 6.43372Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M11.8341 5.06042L11.321 5.72324L10.5322 5.44025L11.0041 6.13293L10.4911 6.79578L11.2956 6.56108L11.7676 7.25375L11.793 6.416L12.5976 6.1813L11.8087 5.89822L11.8341 5.06042Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M11.7778 7.53455L11.2648 8.19741L10.4759 7.91437L10.9479 8.60709L10.4348 9.26991L11.2395 9.03525L11.7114 9.72787L11.7367 8.89012L12.5414 8.65542L11.7524 8.37234L11.7778 7.53455Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M14.1322 8.37927L13.6191 9.04208L12.8302 8.75905L13.3022 9.45177L12.7891 10.1145L13.5938 9.87988L14.0657 10.5726L14.091 9.73476L14.8957 9.5001L14.1067 9.21707L14.1322 8.37927Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx213(
      "path",
      {
        d: "M3.13046 3.91737V20.0825C4.76546 21.8757 6.93717 23.1706 9.39131 23.7147V0.285217C6.93713 0.829249 4.7655 2.12417 3.13046 3.91737Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx213(
      "path",
      {
        d: "M5.51965 6.31804L4.47148 5.53719V4.43296L5.51965 3.65216H7.00208L8.05025 4.43296V5.53719L7.00208 6.31804H5.51965Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26089 3.65216H5.51965L4.47148 4.43296V4.97282H6.26089V3.65216Z",
          fill: "#FF9811"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26088 6.31802H7.00207L8.05024 5.53717V4.97388H6.26088V6.31802Z",
          fill: "#FF9811"
        }
      )
    ] }),
    /* @__PURE__ */ jsx213(
      "path",
      {
        d: "M5.51965 20.3478L4.47148 19.5669V18.4627L5.51965 17.6819H7.00208L8.05025 18.4627V19.5669L7.00208 20.3478H5.51965Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26089 17.6819H5.51965L4.47148 18.4627V19.0026H6.26089V17.6819Z",
          fill: "#FF9811"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26088 20.3478H7.00207L8.05024 19.5669V19.0037H6.26088V20.3478Z",
          fill: "#FF9811"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M5.5197 13.3329L4.47153 12.552V11.4478L5.5197 10.667H7.00212L8.05029 11.4478V12.552L7.00212 13.3329H5.5197Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M8.08699 15.0508H7.63042V14.5943H6.82816L6.26088 14.027L5.69364 14.5943H4.89138V15.0508H4.43486V15.9639H4.89138V16.4204H5.69359L6.26092 16.9877L6.82816 16.4204H7.63042V15.9639H8.08699V15.0508Z",
          fill: "#496E2D"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M8.08699 8.03593H7.63042V7.57937H6.82816L6.26088 7.01208L5.69364 7.57937H4.89138V8.03593H4.43486V8.94896H4.89138V9.40548H5.69359L6.26092 9.97285L6.82816 9.40548H7.63042V8.94896H8.08699V8.03593Z",
          fill: "#496E2D"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs211("g", { children: [
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M5.73907 11.4782H6.78256V12.5217H5.73907V11.4782Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26079 9.01402C6.54893 9.01402 6.78251 8.78044 6.78251 8.4923C6.78251 8.20416 6.54893 7.97058 6.26079 7.97058C5.97266 7.97058 5.73907 8.20416 5.73907 8.4923C5.73907 8.78044 5.97266 9.01402 6.26079 9.01402Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx213(
        "path",
        {
          d: "M6.26079 16.0293C6.54893 16.0293 6.78251 15.7957 6.78251 15.5076C6.78251 15.2194 6.54893 14.9858 6.26079 14.9858C5.97266 14.9858 5.73907 15.2194 5.73907 15.5076C5.73907 15.7957 5.97266 16.0293 6.26079 16.0293Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx213("defs", { children: /* @__PURE__ */ jsx213("clipPath", { id: "cfTM_clip0_0_1", children: /* @__PURE__ */ jsx213("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TN.tsx
import { jsx as jsx214, jsxs as jsxs212 } from "react/jsx-runtime";
var FlagTN = (props) => /* @__PURE__ */ jsxs212(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs212("g", { clipPath: "url(#cfTN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx214(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx214(
      "path",
      {
        d: "M12 17.7391C15.1697 17.7391 17.7392 15.1696 17.7392 12C17.7392 8.83037 15.1697 6.26086 12 6.26086C8.83038 6.26086 6.26088 8.83037 6.26088 12C6.26088 15.1696 8.83038 17.7391 12 17.7391Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs212("g", { children: [
      /* @__PURE__ */ jsx214(
        "path",
        {
          d: "M12.707 9.8056L13.6916 11.1624L15.2861 10.6454L14.3 12.001L15.2845 13.3577L13.6905 12.8387L12.7043 14.1944L12.7054 12.5181L11.1114 11.9991L12.706 11.482L12.707 9.8056Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx214(
        "path",
        {
          d: "M13.3043 15.3913C11.4314 15.3913 9.91303 13.8729 9.91303 12C9.91303 10.127 11.4314 8.60867 13.3043 8.60867C13.8883 8.60867 14.4378 8.75633 14.9175 9.0163C14.165 8.28032 13.1358 7.82605 12 7.82605C9.69487 7.82605 7.82611 9.69477 7.82611 11.9999C7.82611 14.3051 9.69487 16.1738 12 16.1738C13.1358 16.1738 14.165 15.7196 14.9175 14.9836C14.4378 15.2436 13.8883 15.3913 13.3043 15.3913Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx214("defs", { children: /* @__PURE__ */ jsx214("clipPath", { id: "cfTN_clip0_0_1", children: /* @__PURE__ */ jsx214("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TO.tsx
import { jsx as jsx215, jsxs as jsxs213 } from "react/jsx-runtime";
var FlagTO = (props) => /* @__PURE__ */ jsxs213(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs213("g", { clipPath: "url(#cfTO_clip0_0_1)", children: [
    /* @__PURE__ */ jsx215(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs213("g", { children: [
      /* @__PURE__ */ jsx215(
        "path",
        {
          d: "M7.8261 6.26085V4.69565H6.26085V6.26085H4.69565V7.8261H6.26085V9.3913H7.8261V7.8261H9.3913V6.26085H7.8261Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx215(
        "path",
        {
          d: "M12 0V12C5.37262 12 2.73867 12 0 12C0 18.6274 5.37262 24 12 24C18.6274 24 24 18.6274 24 12C24 5.37262 18.6274 0 12 0Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx215("defs", { children: /* @__PURE__ */ jsx215("clipPath", { id: "cfTO_clip0_0_1", children: /* @__PURE__ */ jsx215("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TR.tsx
import { jsx as jsx216, jsxs as jsxs214 } from "react/jsx-runtime";
var FlagTR = (props) => /* @__PURE__ */ jsxs214(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs214("g", { clipPath: "url(#cfTR_clip0_0_1)", children: [
    /* @__PURE__ */ jsx216(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs214("g", { children: [
      /* @__PURE__ */ jsx216(
        "path",
        {
          d: "M11.5087 9.8056L12.4933 11.1624L14.0878 10.6454L13.1017 12.001L14.0861 13.3577L12.4922 12.8387L11.506 14.1944L11.5071 12.5181L9.91305 11.9991L11.5076 11.482L11.5087 9.8056Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx216(
        "path",
        {
          d: "M8.8216 15.3913C6.94866 15.3913 5.43028 13.8729 5.43028 12C5.43028 10.127 6.94866 8.60867 8.8216 8.60867C9.40556 8.60867 9.95503 8.75638 10.4348 9.0163C9.68217 8.28027 8.65294 7.82605 7.51725 7.82605C5.21203 7.82605 3.34336 9.69477 3.34336 11.9999C3.34336 14.3051 5.21208 16.1738 7.51725 16.1738C8.65303 16.1738 9.68222 15.7196 10.4348 14.9836C9.95503 15.2436 9.40556 15.3913 8.8216 15.3913Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx216("defs", { children: /* @__PURE__ */ jsx216("clipPath", { id: "cfTR_clip0_0_1", children: /* @__PURE__ */ jsx216("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TT.tsx
import { jsx as jsx217, jsxs as jsxs215 } from "react/jsx-runtime";
var FlagTT = (props) => /* @__PURE__ */ jsxs215(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs215("g", { clipPath: "url(#cfTT_clip0_0_1)", children: [
    /* @__PURE__ */ jsx217(
      "path",
      {
        d: "M6.49766 1.33246C5.42338 1.88694 4.41496 2.61374 3.51439 3.5143C2.61378 4.41491 1.88699 5.42333 1.33255 6.49761L9.0485 14.9515L17.5024 22.6674C18.5767 22.1129 19.5851 21.3861 20.4857 20.4856C21.3863 19.585 22.113 18.5766 22.6675 17.5023L14.9516 9.04841L6.49766 1.33246Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx217(
      "path",
      {
        d: "M20.4856 20.4856C20.989 19.9822 21.438 19.4451 21.8332 18.8817L5.11831 2.16675C4.55492 2.56195 4.01778 3.01092 3.51439 3.51431C3.011 4.0177 2.56203 4.55484 2.16678 5.11828L18.8817 21.8332C19.445 21.4379 19.9822 20.989 20.4856 20.4856Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs215("g", { children: [
      /* @__PURE__ */ jsx217(
        "path",
        {
          d: "M3.51434 20.4856C7.30029 24.2716 12.9861 24.9983 17.5023 22.6674L1.33254 6.49762C-0.998317 11.0138 -0.271426 16.6997 3.51434 20.4856Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx217(
        "path",
        {
          d: "M20.4857 3.51434C16.6997 -0.271568 11.0139 -0.998318 6.49771 1.33245L22.6675 17.5024C24.9984 12.9861 24.2716 7.30024 20.4857 3.51434Z",
          fill: "#D80027"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx217("defs", { children: /* @__PURE__ */ jsx217("clipPath", { id: "cfTT_clip0_0_1", children: /* @__PURE__ */ jsx217("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TV.tsx
import { jsx as jsx218, jsxs as jsxs216 } from "react/jsx-runtime";
var FlagTV = (props) => /* @__PURE__ */ jsxs216(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs216("g", { clipPath: "url(#cfTV_clip0_0_1)", children: [
    /* @__PURE__ */ jsx218(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37263 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs216("g", { children: [
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M5.73912 12H9.7864L6.26084 7.82611L5.73912 9.39131V12Z",
          fill: "#0052B4"
        }
      ),
      /* @__PURE__ */ jsx218("path", { d: "M12 9.78646L7.82611 5.73914H12L12 9.78646Z", fill: "#0052B4" })
    ] }),
    /* @__PURE__ */ jsxs216("g", { children: [
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76581 12 0H11.998C5.3715 0.001125 0 5.37323 0 12H6.26091V8.47448L9.78642 12H11.968C11.9787 11.9893 11.9894 11.9786 12.0001 11.9679C12.0001 11.1594 12.0001 10.4381 12.0001 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs216("g", { children: [
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M6.071 1.56519C4.19417 2.63389 2.63389 4.19417 1.56519 6.071V12H4.69564V4.69573V4.69564H12C12 3.70831 12 2.76772 12 1.56519H6.071Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M12 10.5243L7.73652 6.26086H6.26089V6.26096L11.9999 12H12C12 12 12 10.9824 12 10.5243Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs216("g", { children: [
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M13.0972 19.8261L13.3562 20.6233H14.1944L13.5163 21.1159L13.7753 21.913L13.0972 21.4204L12.4191 21.913L12.6781 21.1159L12 20.6233H12.8381L13.0972 19.8261Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M19.7724 14.6087L20.0314 15.4059H20.8696L20.1915 15.8986L20.4506 16.6957L19.7724 16.2031L19.0944 16.6957L19.3534 15.8986L18.6752 15.4059H19.5134L19.7724 14.6087Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M21.445 6.78265L21.704 7.57981H22.5422L21.8641 8.07247L22.1231 8.86958L21.445 8.37697L20.7669 8.86958L21.026 8.07247L20.3478 7.57981H21.186L21.445 6.78265Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M21.8593 12.5217L22.1183 13.3189H22.9565L22.2785 13.8116L22.5375 14.6087L21.8593 14.1161L21.1813 14.6087L21.4403 13.8116L20.7621 13.3189H21.6003L21.8593 12.5217Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M14.2991 17.9513L15.046 18.3318L15.6386 17.7392L15.5076 18.567L16.2543 18.9475L15.4264 19.0787L15.2953 19.9065L14.9149 19.1597L14.087 19.2909L14.6796 18.6982L14.2991 17.9513Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M14.2991 14.8208L15.046 15.2013L15.6386 14.6087L15.5076 15.4365L16.2543 15.817L15.4264 15.9482L15.2953 16.776L14.9149 16.0292L14.087 16.1604L14.6796 15.5677L14.2991 14.8208Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M16.9077 16.8274L17.6547 17.208L18.2473 16.6154L18.1162 17.4431L18.863 17.8237L18.0351 17.9548L17.904 18.7827L17.5236 18.0358L16.6956 18.167L17.2883 17.5744L16.9077 16.8274Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M16.9077 10.1252L17.6547 10.5057L18.2473 9.91309L18.1163 10.7409L18.863 11.1214L18.0351 11.2525L17.904 12.0804L17.5236 11.3336L16.6957 11.4647L17.2883 10.8721L16.9077 10.1252Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx218(
        "path",
        {
          d: "M19.436 10.0449L20.183 10.4254L20.7756 9.83276L20.6445 10.6606L21.3913 11.0411L20.5634 11.1722L20.4323 12.0001L20.0519 11.2533L19.224 11.3844L19.8166 10.7918L19.436 10.0449Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx218("defs", { children: /* @__PURE__ */ jsx218("clipPath", { id: "cfTV_clip0_0_1", children: /* @__PURE__ */ jsx218("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TW.tsx
import { jsx as jsx219, jsxs as jsxs217 } from "react/jsx-runtime";
var FlagTW = (props) => /* @__PURE__ */ jsxs217(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs217("g", { clipPath: "url(#cfTW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx219(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C4.12172 7.87828 7.61737 4.38263 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx219(
      "path",
      {
        d: "M12 12C12 5.37262 12 4.59131 12 0C5.37262 0 0 5.37262 0 12H12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx219(
      "path",
      {
        d: "M10.4348 7.02287L8.96925 7.7123L9.74962 9.13163L8.15826 8.82713L7.95656 10.4348L6.84815 9.25243L5.73965 10.4348L5.53804 8.82713L3.94664 9.13154L4.72701 7.71221L3.26151 7.02287L4.72706 6.33357L3.94664 4.91419L5.538 5.21865L5.7397 3.61102L6.84815 4.7934L7.95661 3.61102L8.15826 5.21865L9.74967 4.91419L8.96925 6.33362L10.4348 7.02287Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx219(
      "path",
      {
        d: "M6.84836 9.25635C8.08202 9.25635 9.08209 8.25627 9.08209 7.02261C9.08209 5.78896 8.08202 4.78888 6.84836 4.78888C5.6147 4.78888 4.61462 5.78896 4.61462 7.02261C4.61462 8.25627 5.6147 9.25635 6.84836 9.25635Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx219(
      "path",
      {
        d: "M6.84816 8.21308C6.19186 8.21308 5.65796 7.67913 5.65796 7.02283C5.65796 6.36653 6.19191 5.83258 6.84816 5.83258C7.50446 5.83258 8.03841 6.36653 8.03841 7.02283C8.03836 7.67913 7.50436 8.21308 6.84816 8.21308Z",
        fill: "#F0F0F0"
      }
    )
  ] }),
  /* @__PURE__ */ jsx219("defs", { children: /* @__PURE__ */ jsx219("clipPath", { id: "cfTW_clip0_0_1", children: /* @__PURE__ */ jsx219("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/TZ.tsx
import { jsx as jsx220, jsxs as jsxs218 } from "react/jsx-runtime";
var FlagTZ = (props) => /* @__PURE__ */ jsxs218(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs218("g", { clipPath: "url(#cfTZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx220(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx220(
      "path",
      {
        d: "M3.51471 20.4853C4.0181 20.9887 4.55519 21.4376 5.11858 21.8329L21.8328 5.11863C21.4376 4.55524 20.9887 4.01814 20.4853 3.51475C19.9818 3.01136 19.4448 2.56249 18.8814 2.16724L2.16724 18.8815C2.56239 19.4448 3.01136 19.9819 3.51471 20.4853Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx220(
      "path",
      {
        d: "M3.51468 3.51465C-0.271037 7.30047 -0.997834 12.986 1.33293 17.5021L17.5021 1.33295C12.986 -0.997814 7.30045 -0.27097 3.51468 3.51465Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx220(
      "path",
      {
        d: "M20.4853 20.4853C24.271 16.6996 24.9977 11.014 22.6671 6.49789L6.49791 22.667C11.0139 24.9978 16.6995 24.271 20.4853 20.4853Z",
        fill: "#338AF3"
      }
    )
  ] }),
  /* @__PURE__ */ jsx220("defs", { children: /* @__PURE__ */ jsx220("clipPath", { id: "cfTZ_clip0_0_1", children: /* @__PURE__ */ jsx220("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/UA.tsx
import { jsx as jsx221, jsxs as jsxs219 } from "react/jsx-runtime";
var FlagUA = (props) => /* @__PURE__ */ jsxs219(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs219("g", { clipPath: "url(#cfUA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx221(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx221(
      "path",
      {
        d: "M0 12C0 5.37262 5.37262 0 12 0C18.6274 0 24 5.37262 24 12",
        fill: "#338AF3"
      }
    )
  ] }),
  /* @__PURE__ */ jsx221("defs", { children: /* @__PURE__ */ jsx221("clipPath", { id: "cfUA_clip0_0_1", children: /* @__PURE__ */ jsx221("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/UG.tsx
import { jsx as jsx222, jsxs as jsxs220 } from "react/jsx-runtime";
var FlagUG = (props) => /* @__PURE__ */ jsxs220(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs220("g", { clipPath: "url(#cfUG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M20.9434 19.9999C22.8439 17.8769 24 15.0737 24 12C24 8.92629 22.8438 6.12303 20.9434 3.99996L12 3.13043L3.05662 3.99996C1.15617 6.12303 0 8.92629 0 12C0 15.0737 1.15613 17.8769 3.05658 19.9999L12 20.8695L20.9434 19.9999Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M3.05661 3.99998H20.9434C18.7461 1.54528 15.5537 0 12 0C8.44625 0 5.25383 1.54528 3.05661 3.99998Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M0 12L12 13.0435L24 12C24 10.5973 23.7585 9.25113 23.3162 7.99994H0.683812C0.241453 9.25113 0 10.5973 0 12H0Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M0.683812 16H23.3163C23.7585 14.7488 24 13.4027 24 12H0C0 13.4027 0.241453 14.7488 0.683812 16Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M20.9434 19.9999H3.05658C5.2538 22.4547 8.44622 24 12 24C15.5538 24 18.7462 22.4547 20.9434 19.9999Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M16.0044 12C16.0044 14.2117 14.2117 16.0044 12 16.0044C9.78831 16.0044 7.99567 14.2117 7.99567 12C7.99567 9.78831 9.78831 7.99567 12 7.99567C14.2117 7.99567 16.0044 9.78831 16.0044 12Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx222(
      "path",
      {
        d: "M13.467 12.2042L12 11.5685C12 11.5685 12.3432 10.4293 12.3615 10.3514C12.3749 10.2944 12.382 10.235 12.382 10.174C12.382 9.95785 12.2944 9.76219 12.1527 9.62063L12.5216 9.25172C12.2856 9.01571 11.9595 8.86969 11.5993 8.86969C11.2391 8.86969 10.913 9.01566 10.677 9.25172L11.0459 9.62063C10.9042 9.76224 10.8167 9.95789 10.8167 10.174C10.8167 10.253 10.8286 10.3292 10.8504 10.4011L10.295 10.9565H11.3045C11.3045 10.9565 10.8871 11.5834 10.6593 12.0983C10.4314 12.613 10.6624 13.2415 11.189 13.4756L11.4935 13.6109L12 14.087V14.6087L11.4783 15.1304H12.5218V14.0869L12.9979 13.6108H13.9826C13.9874 13.601 13.9923 13.5915 13.9968 13.5814C14.2308 13.0549 13.9936 12.4382 13.467 12.2042Z",
        fill: "black"
      }
    )
  ] }),
  /* @__PURE__ */ jsx222("defs", { children: /* @__PURE__ */ jsx222("clipPath", { id: "cfUG_clip0_0_1", children: /* @__PURE__ */ jsx222("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/US.tsx
import { jsx as jsx223, jsxs as jsxs221 } from "react/jsx-runtime";
var FlagUS = (props) => /* @__PURE__ */ jsxs221(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs221("g", { clipPath: "url(#cfUS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx223(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs221("g", { children: [
      /* @__PURE__ */ jsx223(
        "path",
        {
          d: "M11.4783 12H24C24 10.9169 23.8556 9.86768 23.5866 8.86957H11.4783V12Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx223(
        "path",
        {
          d: "M11.4783 5.73916H22.2387C21.5041 4.54047 20.5649 3.48095 19.4698 2.6087H11.4783V5.73916Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx223(
        "path",
        {
          d: "M12 24C14.8242 24 17.42 23.0239 19.4698 21.3914H4.53021C6.58006 23.0239 9.17585 24 12 24Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx223(
        "path",
        {
          d: "M1.76128 18.2609H22.2387C22.8285 17.2986 23.2858 16.2467 23.5866 15.1304H0.413391C0.714188 16.2467 1.17155 17.2986 1.76128 18.2609Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx223(
      "path",
      {
        d: "M5.55863 1.87397H6.65217L5.63498 2.61295L6.02353 3.80869L5.00639 3.0697L3.98925 3.80869L4.32487 2.7757C3.42928 3.52172 2.64431 4.39575 1.99744 5.36963H2.34783L1.70034 5.84002C1.59947 6.0083 1.50272 6.17925 1.41 6.35273L1.71919 7.30434L1.14234 6.88523C0.998953 7.18903 0.867797 7.49967 0.749906 7.81678L1.09055 8.86528H2.34783L1.33064 9.60427L1.71919 10.8L0.702047 10.061L0.0927656 10.5037C0.0317812 10.9939 0 11.4932 0 12H12C12 5.37262 12 4.59131 12 0C9.62944 0 7.41961 0.687656 5.55863 1.87397ZM6.02353 10.8L5.00639 10.061L3.98925 10.8L4.3778 9.60427L3.36061 8.86528H4.61789L5.00639 7.66955L5.39489 8.86528H6.65217L5.63498 9.60427L6.02353 10.8ZM5.63498 6.10861L6.02353 7.30434L5.00639 6.56536L3.98925 7.30434L4.3778 6.10861L3.36061 5.36963H4.61789L5.00639 4.17389L5.39489 5.36963H6.65217L5.63498 6.10861ZM10.3279 10.8L9.31073 10.061L8.29359 10.8L8.68214 9.60427L7.66495 8.86528H8.92223L9.31073 7.66955L9.69923 8.86528H10.9565L9.93933 9.60427L10.3279 10.8ZM9.93933 6.10861L10.3279 7.30434L9.31073 6.56536L8.29359 7.30434L8.68214 6.10861L7.66495 5.36963H8.92223L9.31073 4.17389L9.69923 5.36963H10.9565L9.93933 6.10861ZM9.93933 2.61295L10.3279 3.80869L9.31073 3.0697L8.29359 3.80869L8.68214 2.61295L7.66495 1.87397H8.92223L9.31073 0.678234L9.69923 1.87397H10.9565L9.93933 2.61295Z",
        fill: "#0052B4"
      }
    )
  ] }),
  /* @__PURE__ */ jsx223("defs", { children: /* @__PURE__ */ jsx223("clipPath", { id: "cfUS_clip0_0_1", children: /* @__PURE__ */ jsx223("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/UY.tsx
import { jsx as jsx224, jsxs as jsxs222 } from "react/jsx-runtime";
var FlagUY = (props) => /* @__PURE__ */ jsxs222(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs222("g", { clipPath: "url(#cfUY_clip0_0_1)", children: [
    /* @__PURE__ */ jsx224(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx224(
      "path",
      {
        d: "M12 8.86953H23.5866C23.2857 7.75325 22.8284 6.70132 22.2387 5.73907H12V8.86953Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs222("g", { children: [
      /* @__PURE__ */ jsx224(
        "path",
        {
          d: "M4.53014 21.3913H19.4698C20.565 20.5191 21.5041 19.4596 22.2387 18.2609H1.76128C2.49586 19.4596 3.43505 20.5191 4.53014 21.3913Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx224(
        "path",
        {
          d: "M12 0V2.60869H19.4699C17.42 0.976125 14.8242 0 12 0Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsx224("g", { children: /* @__PURE__ */ jsx224(
      "path",
      {
        d: "M12 8.86953H23.5866C23.2857 7.75325 22.8284 6.70132 22.2387 5.73907H12V8.86953Z",
        fill: "#338AF3"
      }
    ) }),
    /* @__PURE__ */ jsx224(
      "path",
      {
        d: "M0 12C0 13.0831 0.144375 14.1323 0.413391 15.1305H23.5867C23.8556 14.1323 24 13.0831 24 12H0Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsx224(
      "path",
      {
        d: "M10.4348 7.02287L8.9692 7.71226L9.74963 9.13163L8.15822 8.82723L7.95656 10.4348L6.84816 9.25243L5.73966 10.4348L5.53805 8.82723L3.94664 9.13154L4.72702 7.71221L3.26152 7.02287L4.72706 6.33357L3.94664 4.91419L5.538 5.2186L5.7397 3.61102L6.84816 4.7934L7.95661 3.61102L8.15822 5.2186L9.74967 4.91419L8.96925 6.33362L10.4348 7.02287Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx224("defs", { children: /* @__PURE__ */ jsx224("clipPath", { id: "cfUY_clip0_0_1", children: /* @__PURE__ */ jsx224("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/UZ.tsx
import { jsx as jsx225, jsxs as jsxs223 } from "react/jsx-runtime";
var FlagUZ = (props) => /* @__PURE__ */ jsxs223(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs223("g", { clipPath: "url(#cfUZ_clip0_0_1)", children: [
    /* @__PURE__ */ jsx225(
      "path",
      {
        d: "M0.566625 8.34788C0.199031 9.4995 -4.68667e-05 10.7264 8.276e-09 11.9999C-4.68667e-05 13.2735 0.199078 14.5005 0.566672 15.6521L12 16.1739L23.4333 15.6522C23.8009 14.5005 24 13.2735 24 12C24 10.7264 23.8009 9.4995 23.4333 8.34783L12 7.82611L0.566625 8.34788Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs223("g", { children: [
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M23.4333 8.34789L12 7.82617L0.566576 8.34789C0.457639 8.68933 0.363842 9.03747 0.285233 9.39138H23.7145C23.636 9.03742 23.5422 8.68923 23.4333 8.34789Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M0.28537 14.6086C0.363885 14.9625 0.457682 15.3106 0.56662 15.6521L0.567745 15.6521L11.9999 16.1738L23.4332 15.6521C23.5422 15.3107 23.636 14.9625 23.7145 14.6086H0.28537Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx225(
      "path",
      {
        d: "M12 23.9999C17.3538 23.9999 21.888 20.4935 23.4333 15.6521H0.566673C2.11191 20.4935 6.64622 23.9999 12 23.9999Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx225(
      "path",
      {
        d: "M12 6.10356e-05C6.64617 0.000107911 2.11196 3.5065 0.566628 8.34789L23.4332 8.34794C21.8881 3.50645 17.3538 1.41606e-05 12 6.10356e-05Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs223("g", { children: [
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M5.4837 4.95661C5.4837 3.83268 6.27359 2.89378 7.32842 2.66344C7.16632 2.628 6.99809 2.60883 6.82531 2.60883C5.52865 2.60883 4.47748 3.65995 4.47748 4.95665C4.47748 6.25336 5.52856 7.30448 6.82531 7.30448C6.99809 7.30448 7.16627 7.28526 7.32842 7.24987C6.27363 7.01943 5.4837 6.08053 5.4837 4.95661Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M8.71608 6.0238L8.87499 6.51294H9.3893L8.97324 6.81529L9.13215 7.30443L8.71608 7.00213L8.29993 7.30443L8.45888 6.81529L8.04277 6.51294H8.55708L8.71608 6.0238Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M10.3577 6.0238L10.5166 6.51294H11.0309L10.6148 6.81529L10.7738 7.30443L10.3577 7.00213L9.94156 7.30443L10.1005 6.81529L9.68445 6.51294H10.1987L10.3577 6.0238Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M11.9993 6.0238L12.1582 6.51294H12.6725L12.2564 6.81529L12.4153 7.30443L11.9993 7.00213L11.5831 7.30443L11.7421 6.81529L11.3259 6.51294H11.8403L11.9993 6.0238Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M13.6409 6.0238L13.7998 6.51294H14.3141L13.898 6.81529L14.057 7.30443L13.6409 7.00213L13.2247 7.30443L13.3837 6.81529L12.9676 6.51294H13.4819L13.6409 6.0238Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M15.2824 6.0238L15.4414 6.51294H15.9557L15.5396 6.81529L15.6986 7.30443L15.2824 7.00213L14.8663 7.30443L15.0253 6.81529L14.6092 6.51294H15.1235L15.2824 6.0238Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M10.3577 4.31628L10.5166 4.80538H11.0309L10.6148 5.10777L10.7738 5.59686L10.3577 5.29457L9.94156 5.59686L10.1005 5.10777L9.68445 4.80538H10.1987L10.3577 4.31628Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M11.9993 4.31628L12.1582 4.80538H12.6725L12.2564 5.10777L12.4153 5.59686L11.9993 5.29457L11.5831 5.59686L11.7421 5.10777L11.3259 4.80538H11.8403L11.9993 4.31628Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M13.6409 4.31628L13.7998 4.80538H14.3141L13.898 5.10777L14.057 5.59686L13.6409 5.29457L13.2247 5.59686L13.3837 5.10777L12.9676 4.80538H13.4819L13.6409 4.31628Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M15.2824 4.31628L15.4414 4.80538H15.9557L15.5396 5.10777L15.6986 5.59686L15.2824 5.29457L14.8663 5.59686L15.0253 5.10777L14.6092 4.80538H15.1235L15.2824 4.31628Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M11.9993 2.6087L12.1582 3.09789H12.6725L12.2564 3.40019L12.4153 3.88938L11.9993 3.58708L11.5831 3.88938L11.7421 3.40019L11.3259 3.09789H11.8403L11.9993 2.6087Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M13.6409 2.6087L13.7998 3.09789H14.3141L13.898 3.40019L14.057 3.88938L13.6409 3.58708L13.2247 3.88938L13.3837 3.40019L12.9676 3.09789H13.4819L13.6409 2.6087Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx225(
        "path",
        {
          d: "M15.2824 2.6087L15.4414 3.09789H15.9557L15.5396 3.40019L15.6986 3.88938L15.2824 3.58708L14.8663 3.88938L15.0253 3.40019L14.6092 3.09789H15.1235L15.2824 2.6087Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx225("defs", { children: /* @__PURE__ */ jsx225("clipPath", { id: "cfUZ_clip0_0_1", children: /* @__PURE__ */ jsx225("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VA.tsx
import { jsx as jsx226, jsxs as jsxs224 } from "react/jsx-runtime";
var FlagVA = (props) => /* @__PURE__ */ jsxs224(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs224("g", { clipPath: "url(#cfVA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx226(
      "path",
      {
        d: "M12 0C18.6274 0 24 5.37262 24 12C24 18.6274 18.6274 24 12 24C12 23.4783 10.4348 12 10.4348 12L12 0Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx226(
      "path",
      {
        d: "M12 24C5.37262 24 0 18.6274 0 12C0 5.37262 5.37262 0 12 0",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx226(
      "path",
      {
        d: "M16.5951 10.4421L18.8503 13.4255C18.495 13.9635 18.4969 14.6884 18.9079 15.232C19.4291 15.9216 20.4107 16.0581 21.1003 15.5368C21.7899 15.0155 21.9264 14.0339 21.4051 13.3444C20.9942 12.8007 20.2973 12.6012 19.6827 12.7963L16.168 8.14667L15.3356 8.77592L14.0869 9.7197L15.3454 11.3845L16.5951 10.4421ZM19.8418 13.8719C20.0717 13.6981 20.3989 13.7437 20.5727 13.9735C20.7464 14.2033 20.701 14.5306 20.4711 14.7044C20.2413 14.8781 19.9141 14.8327 19.7404 14.6028C19.5665 14.3729 19.612 14.0457 19.8418 13.8719Z",
        fill: "#ACABB1"
      }
    ),
    /* @__PURE__ */ jsx226(
      "path",
      {
        d: "M20.4633 11.3844L21.7218 9.71967L20.4731 8.7758L19.6407 8.14655L16.126 12.7962C15.5116 12.6011 14.8146 12.8007 14.4037 13.3442C13.8823 14.0339 14.0188 15.0154 14.7084 15.5367C15.398 16.0579 16.3796 15.9215 16.9008 15.2319C17.3117 14.6883 17.3136 13.9634 16.9584 13.4254L19.2136 10.442L20.4633 11.3844ZM16.0684 14.6027C15.8947 14.8327 15.5675 14.8781 15.3377 14.7044C15.1078 14.5305 15.0623 14.2033 15.2361 13.9734C15.4098 13.7437 15.737 13.6981 15.9669 13.8719C16.1968 14.0457 16.2422 14.3729 16.0684 14.6027Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx226("defs", { children: /* @__PURE__ */ jsx226("clipPath", { id: "cfVA_clip0_0_1", children: /* @__PURE__ */ jsx226("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VC.tsx
import { jsx as jsx227, jsxs as jsxs225 } from "react/jsx-runtime";
var FlagVC = (props) => /* @__PURE__ */ jsxs225(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs225("g", { clipPath: "url(#cfVC_clip0_0_1)", children: [
    /* @__PURE__ */ jsx227(
      "path",
      {
        d: "M17.7391 1.45908C16.0342 0.528891 14.0789 0 12 0C9.92106 0 7.96572 0.528891 6.26083 1.45908L5.21735 12L6.26083 22.5409C7.96572 23.4711 9.92106 24 12 24C14.0789 24 16.0342 23.4711 17.7391 22.5409L18.7826 12L17.7391 1.45908Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx227(
      "path",
      {
        d: "M6.26086 22.5408V1.45905C2.53073 3.49436 0 7.4515 0 12C0 16.5485 2.53073 20.5057 6.26086 22.5408Z",
        fill: "#338AF3"
      }
    ),
    /* @__PURE__ */ jsxs225("g", { children: [
      /* @__PURE__ */ jsx227(
        "path",
        {
          d: "M24 12C24 7.4515 21.4693 3.49436 17.7391 1.45905V22.5408C21.4693 20.5057 24 16.5485 24 12Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx227(
        "path",
        {
          d: "M9.39131 15.1305L7.30429 12L9.39126 8.86957L11.4783 12L9.39131 15.1305Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx227(
        "path",
        {
          d: "M14.6087 15.1305L12.5217 12L14.6087 8.86957L16.6956 12L14.6087 15.1305Z",
          fill: "#6DA544"
        }
      ),
      /* @__PURE__ */ jsx227(
        "path",
        {
          d: "M12 19.3043L9.91306 16.1739L12 13.0435L14.087 16.1739L12 19.3043Z",
          fill: "#6DA544"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx227("defs", { children: /* @__PURE__ */ jsx227("clipPath", { id: "cfVC_clip0_0_1", children: /* @__PURE__ */ jsx227("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VE.tsx
import { jsx as jsx228, jsxs as jsxs226 } from "react/jsx-runtime";
var FlagVE = (props) => /* @__PURE__ */ jsxs226(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs226("g", { clipPath: "url(#cfVE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx228(
      "path",
      {
        d: "M22.8093 17.2174C23.5722 15.6397 24 13.8699 24 12C24 10.1301 23.5722 8.36025 22.8093 6.78262L12 5.73914L1.19072 6.78262C0.427828 8.36025 0 10.1301 0 12C0 13.8699 0.427828 15.6397 1.19072 17.2174L12 18.2609L22.8093 17.2174Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx228(
      "path",
      {
        d: "M12 24C16.7575 24 20.8683 21.2313 22.8093 17.2173H1.19073C3.13173 21.2313 7.24248 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsxs226("g", { children: [
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M20.7828 14.3556L20.149 14.8509L20.4241 15.6065L19.7572 15.1569L19.1234 15.6521L19.345 14.8789L18.6782 14.429L19.4821 14.401L19.7039 13.6278L19.979 14.3836L20.7828 14.3556Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M19.1597 11.5444L18.7336 12.2266L19.2507 12.8426L18.4702 12.6483L18.0439 13.3303L17.9877 12.5279L17.2073 12.3333L17.9531 12.0319L17.8971 11.2295L18.4141 11.8458L19.1597 11.5444Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M16.6732 9.45795L16.506 10.2447L17.2026 10.6469L16.4026 10.7313L16.2354 11.5178L15.9082 10.7831L15.1082 10.8671L15.706 10.3288L15.3789 9.59412L16.0754 9.99621L16.6732 9.45795Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M13.6229 8.34784L13.735 9.14439L14.527 9.28398L13.8041 9.63676L13.9161 10.4333L13.3574 9.85464L12.6343 10.2072L13.012 9.49693L12.4532 8.9184L13.2454 9.05804L13.6229 8.34784Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M10.3771 8.34784L10.7547 9.05804L11.5467 8.91831L10.9881 9.49707L11.3656 10.2073L10.6427 9.85464L10.0839 10.4331L10.1959 9.63676L9.47298 9.28417L10.2651 9.14439L10.3771 8.34784Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M7.3268 9.45795L7.92464 9.99621L8.62106 9.59412L8.29401 10.3291L8.89172 10.8672L8.0918 10.7831L7.76452 11.5178L7.5975 10.7311L6.79753 10.6471L7.49409 10.2448L7.3268 9.45795Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M4.84025 11.5445L5.58617 11.8458L6.10301 11.2297L6.04709 12.0322L6.79278 12.3336L6.01235 12.5281L5.95615 13.3303L5.53006 12.6483L4.74954 12.8428L5.26657 12.2267L4.84025 11.5445Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx228(
        "path",
        {
          d: "M3.21722 14.3556L4.02122 14.3836L4.29619 13.6279L4.51815 14.4011L5.32191 14.4292L4.65507 14.879L4.87669 15.6522L4.24294 15.157L3.57605 15.6066L3.85116 14.8508L3.21722 14.3556Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx228(
      "path",
      {
        d: "M22.8093 6.78263C20.8683 2.76867 16.7575 0 12 0C7.24248 0 3.13173 2.76867 1.19073 6.78263H22.8093Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx228("defs", { children: /* @__PURE__ */ jsx228("clipPath", { id: "cfVE_clip0_0_1", children: /* @__PURE__ */ jsx228("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VG.tsx
import { jsx as jsx229, jsxs as jsxs227 } from "react/jsx-runtime";
var FlagVG = (props) => /* @__PURE__ */ jsxs227(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs227("g", { clipPath: "url(#cfVG_clip0_0_1)", children: [
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs227("g", { children: [
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M6.07101 1.5652C4.19419 2.6339 2.63386 4.19419 1.5652 6.07101V12H4.69565V4.69575V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M17.2174 12L13.974 15.2434C14.5818 16.4158 15.8056 17.2174 17.2174 17.2174C18.6291 17.2174 19.8529 16.4158 20.4608 15.2434L17.2174 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M13.5652 6.26086V12V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V12V6.26086H13.5652Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx229("path", { d: "M16.4348 8.86955H18V13.0434H16.4348V8.86955Z", fill: "#F0F0F0" }),
    /* @__PURE__ */ jsx229(
      "path",
      {
        d: "M17.2174 9.65216C17.6496 9.65216 18 9.30177 18 8.86954C18 8.43731 17.6496 8.08691 17.2174 8.08691C16.7851 8.08691 16.4348 8.43731 16.4348 8.86954C16.4348 9.30177 16.7851 9.65216 17.2174 9.65216Z",
        fill: "#A2001D"
      }
    ),
    /* @__PURE__ */ jsxs227("g", { children: [
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M14.6087 7.30435H15.6522V8.34784H14.6087V7.30435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M14.6087 9.65218H15.6522V10.6957H14.6087V9.65218Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx229("path", { d: "M14.6087 12H15.6522V13.0435H14.6087V12Z", fill: "#FFDA44" }),
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M18.7826 7.30435H19.8261V8.34784H18.7826V7.30435Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx229(
        "path",
        {
          d: "M18.7826 9.65218H19.8261V10.6957H18.7826V9.65218Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx229("path", { d: "M18.7826 12H19.8261V13.0435H18.7826V12Z", fill: "#FFDA44" })
    ] })
  ] }),
  /* @__PURE__ */ jsx229("defs", { children: /* @__PURE__ */ jsx229("clipPath", { id: "cfVG_clip0_0_1", children: /* @__PURE__ */ jsx229("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VI.tsx
import { jsx as jsx230, jsxs as jsxs228 } from "react/jsx-runtime";
var FlagVI = (props) => /* @__PURE__ */ jsxs228(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs228("g", { clipPath: "url(#cfVI_clip0_0_1)", children: [
    /* @__PURE__ */ jsx230(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#FCFCFC"
      }
    ),
    /* @__PURE__ */ jsxs228("g", { children: [
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M14.038 8.38343C14.038 7.25787 13.1256 6.3454 12 6.3454C10.8744 6.3454 9.96188 7.25787 9.96188 8.38343H5.47824C5.47824 9.4916 6.44358 10.3901 7.55176 10.3901H7.48501C7.48501 11.4983 8.38332 12.3968 9.49168 12.3968C9.49168 13.3781 10.1964 14.1934 11.1271 14.3677L11.1219 14.3794H12.8781L12.8729 14.3677C13.8036 14.1933 14.5083 13.378 14.5083 12.3968C15.6166 12.3968 16.515 11.4983 16.515 10.3901H16.4481C17.5563 10.3901 18.5217 9.4916 18.5217 8.38343H14.038Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M11.104 14.1714L9.84036 17.0244C10.5071 17.2954 11.2359 17.4461 12 17.4461C12.7641 17.4461 13.4929 17.2955 14.1596 17.0244L12.896 14.1714H11.104Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx230(
      "path",
      {
        d: "M9.3913 9.38562V12.8961C9.3913 14.8929 12 15.5048 12 15.5048C12 15.5048 14.6087 14.8929 14.6087 12.8961V9.38562H9.3913Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs228("g", { children: [
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M2.90994 12.0845L4.32083 16.0306L5.71705 12.0845H6.78261L4.74701 17.3018H3.89459L1.84442 12.0845H2.90994Z",
          fill: "#338AF3"
        }
      ),
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M19.3044 17.3018V12.0845H20.3184V17.3018H19.3044Z",
          fill: "#338AF3"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs228("g", { children: [
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M10.4348 10.5193V14.7734C10.7935 15.0373 11.1793 15.2189 11.4783 15.3351V10.5193H10.4348Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx230(
        "path",
        {
          d: "M13.5652 10.5193V14.7734C13.2066 15.0373 12.8207 15.2189 12.5218 15.3351V10.5193H13.5652Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx230("path", { d: "M9.3913 9.3855H14.6087V11.0409H9.3913V9.3855Z", fill: "#0052B4" })
  ] }),
  /* @__PURE__ */ jsx230("defs", { children: /* @__PURE__ */ jsx230("clipPath", { id: "cfVI_clip0_0_1", children: /* @__PURE__ */ jsx230("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VN.tsx
import { jsx as jsx231, jsxs as jsxs229 } from "react/jsx-runtime";
var FlagVN = (props) => /* @__PURE__ */ jsxs229(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs229("g", { clipPath: "url(#cfVN_clip0_0_1)", children: [
    /* @__PURE__ */ jsx231(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx231(
      "path",
      {
        d: "M12 6.26086L13.295 10.2466H17.4859L14.0954 12.7099L15.3904 16.6957L12 14.2323L8.60951 16.6957L9.90457 12.7099L6.5141 10.2466H10.7049L12 6.26086Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx231("defs", { children: /* @__PURE__ */ jsx231("clipPath", { id: "cfVN_clip0_0_1", children: /* @__PURE__ */ jsx231("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/VU.tsx
import { jsx as jsx232, jsxs as jsxs230 } from "react/jsx-runtime";
var FlagVU = (props) => /* @__PURE__ */ jsxs230(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs230("g", { clipPath: "url(#cfVU_clip0_0_1)", children: [
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9999 12.0001C23.9999 11.4696 23.9651 10.9472 23.8984 10.4349H10.4346C7.98301 7.9834 3.51447 3.51515 3.51466 3.51477C-1.17161 8.20104 -1.17161 15.799 3.51466 20.4854L10.4348 13.5653H23.8984C23.9651 13.0529 23.9999 12.5306 23.9999 12.0001Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs230("g", { children: [
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9966 12.2729C23.996 12.296 23.9955 12.3193 23.9949 12.3425C23.9954 12.3192 23.996 12.296 23.9966 12.2729Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.985 12.6008C23.984 12.6238 23.9824 12.6466 23.9811 12.6695C23.9824 12.6466 23.9839 12.6238 23.985 12.6008Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9689 12.8624C23.9665 12.8961 23.9641 12.9298 23.9614 12.9634C23.9641 12.9298 23.9664 12.8961 23.9689 12.8624Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9288 10.6921C23.9332 10.7322 23.9376 10.7723 23.9416 10.8125C23.9376 10.7722 23.9331 10.7322 23.9288 10.6921Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9811 11.3304C23.9823 11.3533 23.9839 11.3761 23.985 11.3991C23.9838 11.3761 23.9823 11.3533 23.9811 11.3304Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9948 11.6575C23.9955 11.6807 23.996 11.7039 23.9965 11.7271C23.996 11.7038 23.9954 11.6807 23.9948 11.6575Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9614 11.0365C23.9641 11.0701 23.9665 11.1038 23.9689 11.1375C23.9664 11.1038 23.9641 11.0701 23.9614 11.0365Z",
          fill: "#F0F0F0"
        }
      )
    ] }),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.8984 10.4348C23.9037 10.4759 23.9085 10.5172 23.9134 10.5584C23.2012 4.61086 18.1394 6.10352e-05 12 6.10352e-05C8.68618 6.10352e-05 5.68614 1.34326 3.51456 3.51493L10.4344 10.4348H23.8984V10.4348Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.8984 13.5652H10.4344L3.51456 20.4852C5.68614 22.6568 8.68618 24 12 24C18.1394 24 23.2013 19.3892 23.9134 13.4416C23.9085 13.4828 23.9037 13.5241 23.8984 13.5652Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9415 13.1874C23.9375 13.2277 23.9331 13.2677 23.9287 13.3079C23.9331 13.2677 23.9376 13.2277 23.9415 13.1874Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9688 11.1376C23.9734 11.2017 23.9775 11.2661 23.981 11.3305C23.9775 11.2661 23.9734 11.2017 23.9688 11.1376Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9135 10.5584C23.9189 10.6029 23.924 10.6475 23.9288 10.6921C23.9239 10.6475 23.9188 10.6029 23.9135 10.5584Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9415 10.8126C23.9488 10.887 23.9554 10.9617 23.9614 11.0367C23.9555 10.9617 23.9489 10.887 23.9415 10.8126Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9999 12C23.9999 12.0913 23.9985 12.1822 23.9965 12.2729C23.9985 12.1821 23.9999 12.0912 23.9999 12Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9811 12.6695C23.9775 12.7339 23.9734 12.7983 23.9689 12.8624C23.9734 12.7983 23.9774 12.7339 23.9811 12.6695Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9614 12.9634C23.9554 13.0383 23.9488 13.113 23.9415 13.1875C23.9489 13.113 23.9555 13.0383 23.9614 12.9634Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9288 13.3079C23.924 13.3525 23.9189 13.3971 23.9135 13.4416C23.9188 13.3971 23.9239 13.3525 23.9288 13.3079Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9965 11.7271C23.9985 11.8179 23.9999 11.9088 23.9999 12C23.9999 11.9088 23.9985 11.8179 23.9965 11.7271Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.985 11.3991C23.9892 11.485 23.9924 11.5712 23.9948 11.6575C23.9924 11.5711 23.9893 11.485 23.985 11.3991Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx232(
      "path",
      {
        d: "M23.9948 12.3425C23.9923 12.4288 23.9892 12.515 23.9849 12.6009C23.9893 12.515 23.9924 12.4288 23.9948 12.3425Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsxs230("g", { children: [
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M23.9885 11.474H9.99376L2.80516 4.28644C2.57281 4.5315 2.32095 4.87369 2.16518 5.11739L9.03981 11.9956L2.15942 18.8762C2.29563 19.1185 2.5924 19.4772 2.80076 19.7106L9.9939 12.5174H23.9902C23.9976 12.3444 24 12.1706 24 11.9957C23.9999 11.7614 23.9935 11.5887 23.9885 11.474Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx232(
        "path",
        {
          d: "M2.91499 14.5581V13.553C3.91802 13.553 4.73397 12.7369 4.73397 11.734C4.73397 11.0243 4.15662 10.447 3.44702 10.447C2.9636 10.447 2.57028 10.8403 2.57028 11.3238C2.57028 11.633 2.82176 11.8845 3.13095 11.8845C3.3057 11.8845 3.44787 11.7422 3.44787 11.5675H4.45296C4.45296 12.2965 3.8599 12.8896 3.13099 12.8896C2.2676 12.8896 1.56528 12.1872 1.56528 11.3239C1.56528 10.2862 2.40949 9.44208 3.44707 9.44208C4.71091 9.44208 5.73916 10.4703 5.73916 11.7341C5.73911 13.2912 4.47222 14.5581 2.91499 14.5581Z",
          fill: "#FFDA44"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx232("defs", { children: /* @__PURE__ */ jsx232("clipPath", { id: "cfVU_clip0_0_1", children: /* @__PURE__ */ jsx232("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/WS.tsx
import { jsx as jsx233, jsxs as jsxs231 } from "react/jsx-runtime";
var FlagWS = (props) => /* @__PURE__ */ jsxs231(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs231("g", { clipPath: "url(#cfWS_clip0_0_1)", children: [
    /* @__PURE__ */ jsx233(
      "path",
      {
        d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C4.12172 7.87828 7.61737 4.38263 12 0C18.6274 0 24 5.37262 24 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx233(
      "path",
      {
        d: "M12 12C12 5.37262 12 4.59131 12 0C5.37262 0 0 5.37262 0 12H12Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsxs231("g", { children: [
      /* @__PURE__ */ jsx233(
        "path",
        {
          d: "M9.61189 7.82611L9.80619 8.424H10.4348L9.92619 8.79342L10.1205 9.39131L9.61189 9.0218L9.1033 9.39131L9.2976 8.79342L8.789 8.424H9.4176L9.61189 7.82611Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx233(
        "path",
        {
          d: "M6.4546 3.13046L6.77836 4.12698H7.82611L6.97843 4.74268L7.30229 5.73915L6.4546 5.12326L5.60696 5.73915L5.93077 4.74268L5.08313 4.12698H6.13079L6.4546 3.13046Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx233(
        "path",
        {
          d: "M9.58509 4.17389L9.90886 5.17045H10.9566L10.1089 5.78615L10.4327 6.78262L9.58509 6.16673L8.73745 6.78262L9.06126 5.78615L8.21362 5.17045H9.26128L9.58509 4.17389Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx233(
        "path",
        {
          d: "M7.1106 8.34784L7.43442 9.34435H8.48207L7.63443 9.96006L7.95825 10.9565L7.1106 10.3407L6.26296 10.9565L6.58678 9.96006L5.73914 9.34435H6.78684L7.1106 8.34784Z",
          fill: "#F0F0F0"
        }
      ),
      /* @__PURE__ */ jsx233(
        "path",
        {
          d: "M4.36764 5.73914L4.69145 6.73565H5.73915L4.89147 7.35135L5.21528 8.34782L4.36764 7.73198L3.52004 8.34782L3.84381 7.35135L2.99622 6.73565H4.04387L4.36764 5.73914Z",
          fill: "#F0F0F0"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsx233("defs", { children: /* @__PURE__ */ jsx233("clipPath", { id: "cfWS_clip0_0_1", children: /* @__PURE__ */ jsx233("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/XAF.tsx
import { jsx as jsx234, jsxs as jsxs232 } from "react/jsx-runtime";
var FlagXAF = (props) => /* @__PURE__ */ jsxs232(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M22.5409 6.26086C20.9903 3.41888 18.3239 1.27345 15.1305 0.412735L14.6087 6.26086H22.5409Z",
      fill: "#0052B4"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M1.45908 6.26086H9.39126L8.86954 0.412735C5.67609 1.27345 3.00975 3.41892 1.45908 6.26086Z",
      fill: "#0052B4"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M14.6087 12V17.7391H22.5409C23.4711 16.0342 24 14.0789 24 12H14.6087Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M9.39131 12H0C0 14.0789 0.528891 16.0342 1.45908 17.7391H9.39127V12H9.39131Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M1.45908 17.7391C3.00975 20.5811 5.67609 22.7265 8.86954 23.5873L9.39126 18.4728L1.45908 17.7391Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M15.1305 23.5873C18.3239 22.7265 20.9903 20.5811 22.5409 17.7391L14.6087 18.4727L15.1305 23.5873Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M14.6087 18.4727L22.5409 17.7391H14.6087V18.4727Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M9.39131 17.7391H1.45908L9.39131 18.4727V17.7391Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M12 0C10.9169 0 9.86771 0.144375 8.86955 0.413391V23.5867C9.86771 23.8556 10.9169 24 12 24C13.0831 24 14.1323 23.8556 15.1305 23.5866V0.413391C14.1323 0.144375 13.0831 0 12 0V0Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx234(
    "path",
    {
      d: "M6.4546 2.60869L6.77832 3.60511H7.82611L6.97852 4.22095L7.30228 5.21738L6.4546 4.60158L5.60696 5.21738L5.93082 4.22095L5.08322 3.60511H6.13092L6.4546 2.60869Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx234("defs", { children: /* @__PURE__ */ jsx234("clipPath", { id: "cfXAF_clip0_2_7731", children: /* @__PURE__ */ jsx234("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/XCD.tsx
import { jsx as jsx235, jsxs as jsxs233 } from "react/jsx-runtime";
var FlagXCD = (props) => /* @__PURE__ */ jsxs233(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M24 12C24 18.6274 18.6274 24 12 24C5.37262 24 0 18.6274 0 12C0 12.0029 12 0.0013125 12 0C18.6274 0 24 5.37262 24 12Z",
      fill: "#0052B4"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M11.9679 12H12C12 11.9892 12 11.9787 12 11.9679C11.9893 11.9786 11.9786 11.9893 11.9679 12Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M12 6.26086C12 4.14937 12 2.76586 12 0H11.998C5.37145 0.001125 0 5.37323 0 12H6.26086V8.47448L9.78638 12H11.9679C11.9786 11.9893 11.9893 11.9786 12 11.9679C12 11.1594 12 10.4381 12 9.78647L8.47444 6.26086H12Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M6.07101 1.5652C4.19419 2.6339 2.63386 4.19419 1.5652 6.07101V12H4.69565V4.69575V4.69565H12C12 3.70833 12 2.76773 12 1.5652H6.07101Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M17.2174 12L13.974 15.2434C14.5818 16.4158 15.8056 17.2174 17.2174 17.2174C18.6291 17.2174 19.8529 16.4158 20.4608 15.2434L17.2174 12Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M12 10.5243L7.73649 6.26091H6.26086V6.261L11.9999 12H12C12 12 12 10.9825 12 10.5243Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M13.5652 6.26086V12V12C13.5652 14.7956 17.2174 15.6522 17.2174 15.6522C17.2174 15.6522 20.8695 14.7956 20.8695 12V12V6.26086H13.5652Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx235("path", { d: "M16.4348 8.86955H18V13.0434H16.4348V8.86955Z", fill: "#F0F0F0" }),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M17.2174 9.65216C17.6496 9.65216 18 9.30177 18 8.86954C18 8.43731 17.6496 8.08691 17.2174 8.08691C16.7851 8.08691 16.4348 8.43731 16.4348 8.86954C16.4348 9.30177 16.7851 9.65216 17.2174 9.65216Z",
      fill: "#A2001D"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M14.6087 7.30435H15.6522V8.34784H14.6087V7.30435Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M14.6087 9.65218H15.6522V10.6957H14.6087V9.65218Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx235("path", { d: "M14.6087 12H15.6522V13.0435H14.6087V12Z", fill: "#FFDA44" }),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M18.7826 7.30435H19.8261V8.34784H18.7826V7.30435Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx235(
    "path",
    {
      d: "M18.7826 9.65218H19.8261V10.6957H18.7826V9.65218Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx235("path", { d: "M18.7826 12H19.8261V13.0435H18.7826V12Z", fill: "#FFDA44" }),
  /* @__PURE__ */ jsx235("defs", { children: /* @__PURE__ */ jsx235("clipPath", { id: "cfXCD_clip0_2_7618", children: /* @__PURE__ */ jsx235("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/XOF.tsx
import { jsx as jsx236, jsxs as jsxs234 } from "react/jsx-runtime";
var FlagXOF = (props) => /* @__PURE__ */ jsxs234(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx236(
    "path",
    {
      d: "M12 24C13.4678 24 14.8739 23.736 16.1739 23.2536L16.6956 12L16.1739 0.746391C14.8739 0.264047 13.4678 0 12 0C10.5321 0 9.12602 0.264047 7.82609 0.746391L7.30432 12L7.82604 23.2536C9.12602 23.736 10.5321 24 12 24Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx236(
    "path",
    {
      d: "M0 12C0 17.1596 3.2565 21.5581 7.82611 23.2536V0.746399C3.2565 2.44191 0 6.84043 0 12Z",
      fill: "#FF9811"
    }
  ),
  /* @__PURE__ */ jsx236(
    "path",
    {
      d: "M16.1739 0.746399V23.2537C20.7435 21.5581 24 17.1596 24 12C24 6.84043 20.7435 2.44191 16.1739 0.746399Z",
      fill: "#6DA544"
    }
  ),
  /* @__PURE__ */ jsx236("defs", { children: /* @__PURE__ */ jsx236("clipPath", { id: "cfXOF_clip0_2_8263", children: /* @__PURE__ */ jsx236("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/XPF.tsx
import { jsx as jsx237, jsxs as jsxs235 } from "react/jsx-runtime";
var FlagXPF = (props) => /* @__PURE__ */ jsxs235(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
      fill: "#F0F0F0"
    }
  ),
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M1.45911 6.26086H22.5409C20.5057 2.53073 16.5485 0 12 0C7.45151 0 3.49437 2.53073 1.45911 6.26086Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M12 24C16.5485 24 20.5057 21.4693 22.5409 17.7391H1.45911C3.49437 21.4693 7.45151 24 12 24Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M16.1739 12C16.1739 14.3052 14.3052 15.6522 12 15.6522C9.69483 15.6522 7.82611 14.3052 7.82611 12C7.82611 9.69478 9.69483 7.82611 12 7.82611C14.3052 7.82611 16.1739 9.69478 16.1739 12Z",
      fill: "#FFDA44"
    }
  ),
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M16.1739 12C16.1739 14.3052 14.3052 16.1739 12 16.1739C9.69483 16.1739 7.82611 14.3052 7.82611 12",
      fill: "#0052B4"
    }
  ),
  /* @__PURE__ */ jsx237("path", { d: "M9.3913 10.9565H10.4348V13.0435H9.3913V10.9565Z", fill: "#D80027" }),
  /* @__PURE__ */ jsx237(
    "path",
    {
      d: "M13.5652 10.9565H14.6087V13.0435H13.5652V10.9565Z",
      fill: "#D80027"
    }
  ),
  /* @__PURE__ */ jsx237("path", { d: "M11.4783 9.3913H12.5218V13.0435H11.4783V9.3913Z", fill: "#D80027" }),
  /* @__PURE__ */ jsx237("defs", { children: /* @__PURE__ */ jsx237("clipPath", { id: "cfXPF_clip0_2_8053", children: /* @__PURE__ */ jsx237("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/YE.tsx
import { jsx as jsx238, jsxs as jsxs236 } from "react/jsx-runtime";
var FlagYE = (props) => /* @__PURE__ */ jsxs236(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs236("g", { clipPath: "url(#cfYE_clip0_0_1)", children: [
    /* @__PURE__ */ jsx238(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx238(
      "path",
      {
        d: "M12 24C17.1596 24 21.5581 20.7435 23.2536 16.1739H0.746429C2.44194 20.7435 6.84046 24 12 24Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx238(
      "path",
      {
        d: "M12 0C6.84046 0 2.44195 3.2565 0.746429 7.82611H23.2537C21.5581 3.2565 17.1596 0 12 0Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx238("defs", { children: /* @__PURE__ */ jsx238("clipPath", { id: "cfYE_clip0_0_1", children: /* @__PURE__ */ jsx238("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ZA.tsx
import { jsx as jsx239, jsxs as jsxs237 } from "react/jsx-runtime";
var FlagZA = (props) => /* @__PURE__ */ jsxs237(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs237("g", { clipPath: "url(#cfZA_clip0_0_1)", children: [
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M3.51465 20.4853C-1.17163 15.799 -1.17163 8.20104 3.51465 3.51471C3.51432 3.51523 10.4348 12 10.4348 12L3.51465 20.4853Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M10.4348 12L1.59153 6.02405C1.31736 6.50063 1.07558 6.99806 0.867828 7.51294L5.34481 12L0.867969 16.4874C1.07553 17.0019 1.31708 17.499 1.59106 17.9753L10.4348 12Z",
        fill: "#FFDA44"
      }
    ),
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M23.8984 10.4348H10.4345L3.51461 3.51489C2.76892 4.26058 2.12106 5.10391 1.59156 6.02411L7.55589 12L1.59109 17.9752C2.12059 18.8956 2.76874 19.7392 3.51461 20.4851L10.4345 13.5652H23.8984C23.9651 13.0529 24 12.5305 24 12C24 11.4695 23.9651 10.9472 23.8984 10.4348Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M4.69397 21.5192C6.71752 23.0746 9.2505 24 12 24C17.5443 24 22.2096 20.2396 23.5866 15.1305H11.0828L4.69397 21.5192Z",
        fill: "#0052B4"
      }
    ),
    /* @__PURE__ */ jsx239(
      "path",
      {
        d: "M23.5866 8.86955C22.2096 3.76036 17.5443 0 12 0C9.2505 0 6.71752 0.925359 4.69397 2.48077L11.0828 8.86955H23.5866Z",
        fill: "#D80027"
      }
    )
  ] }),
  /* @__PURE__ */ jsx239("defs", { children: /* @__PURE__ */ jsx239("clipPath", { id: "cfZA_clip0_0_1", children: /* @__PURE__ */ jsx239("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ZM.tsx
import { jsx as jsx240, jsxs as jsxs238 } from "react/jsx-runtime";
var FlagZM = (props) => /* @__PURE__ */ jsxs238(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs238("g", { clipPath: "url(#cfZM_clip0_0_1)", children: [
    /* @__PURE__ */ jsx240(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#496E2D"
      }
    ),
    /* @__PURE__ */ jsx240(
      "path",
      {
        d: "M16.6957 12H12V24C13.4027 24 14.7488 23.7585 16 23.3163L16.6957 12Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx240(
      "path",
      {
        d: "M19.9999 20.9434C22.4547 18.7462 24 15.5537 24 12H19.3043L19.9999 20.9434Z",
        fill: "#FF9811"
      }
    ),
    /* @__PURE__ */ jsx240(
      "path",
      {
        d: "M15.9999 12V23.3163C17.4904 22.7894 18.8458 21.9766 20 20.9435V12H15.9999Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx240(
      "path",
      {
        d: "M22.1739 7.82611H19.0434C19.0434 7.24983 18.5762 6.78262 17.9999 6.78262C17.4236 6.78262 16.9564 7.24983 16.9564 7.82611H13.826C13.826 8.40244 14.328 8.86959 14.9042 8.86959H14.8695C14.8695 9.44592 15.3366 9.91308 15.913 9.91308C15.913 10.4894 16.3801 10.9566 16.9564 10.9566H19.0434C19.6197 10.9566 20.0869 10.4894 20.0869 9.91308C20.6632 9.91308 21.1304 9.44592 21.1304 8.86959H21.0956C21.6719 8.86955 22.1739 8.40239 22.1739 7.82611Z",
        fill: "#FF9811"
      }
    )
  ] }),
  /* @__PURE__ */ jsx240("defs", { children: /* @__PURE__ */ jsx240("clipPath", { id: "cfZM_clip0_0_1", children: /* @__PURE__ */ jsx240("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/ZW.tsx
import { jsx as jsx241, jsxs as jsxs239 } from "react/jsx-runtime";
var FlagZW = (props) => /* @__PURE__ */ jsxs239(FlagIcon, { ...props, children: [
  /* @__PURE__ */ jsxs239("g", { clipPath: "url(#cfZW_clip0_0_1)", children: [
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24Z",
        fill: "#F0F0F0"
      }
    ),
    /* @__PURE__ */ jsxs239("g", { children: [
      /* @__PURE__ */ jsx241(
        "path",
        {
          d: "M22.8514 6.87202C22.2418 5.58417 21.4097 4.42247 20.4047 3.43599L12 2.60869L3.59531 3.43603C3.5685 3.46233 3.54234 3.48924 3.51581 3.51581L6.87206 6.87206L12 7.02867L22.8514 6.87202Z",
          fill: "#FFDA44"
        }
      ),
      /* @__PURE__ */ jsx241(
        "path",
        {
          d: "M3.51569 20.4843C3.55984 20.5285 3.6039 20.5727 3.64872 20.6162L12 21.3913L20.3512 20.6162C21.3666 19.6318 22.2088 18.47 22.8271 17.1802L7.30431 16.6957C5.9687 18.0313 4.4282 19.5717 3.51569 20.4843Z",
          fill: "#FFDA44"
        }
      )
    ] }),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M23.8807 10.308L9.91302 9.91302L12 12C11.3819 12.6181 10.4591 13.5409 9.91302 14.087L23.8733 13.7441C23.9563 13.1746 24 12.5924 24 12C24 11.4256 23.9587 10.8609 23.8807 10.308Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M12 0C8.72684 0 5.76031 1.31109 3.59534 3.43603H20.4047C18.2397 1.31109 15.2732 0 12 0Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsxs239("g", { children: [
      /* @__PURE__ */ jsx241(
        "path",
        {
          d: "M10.308 10.308H23.8806C23.7094 9.09496 23.3571 7.94024 22.8514 6.87201H6.87201L10.308 10.308Z",
          fill: "#D80027"
        }
      ),
      /* @__PURE__ */ jsx241(
        "path",
        {
          d: "M6.81982 17.1802H22.8271C23.3387 16.1127 23.6966 14.9579 23.8734 13.7441H10.2559C8.92521 15.0748 7.7975 16.2025 6.81982 17.1802Z",
          fill: "#D80027"
        }
      )
    ] }),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M12 24C15.2463 24 18.191 22.7104 20.3512 20.6161H3.6488C5.80899 22.7104 8.75368 24 12 24Z",
        fill: "#6DA544"
      }
    ),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M4.2851 2.8094C4.01833 3.03356 3.76094 3.26854 3.51471 3.51468L12 12L3.51471 20.4853C3.7609 20.7315 4.01833 20.9664 4.2851 21.1906L13.4757 12L4.2851 2.8094Z",
        fill: "black"
      }
    ),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M4.82461 8.86955L5.60156 11.2609H8.11617L6.08189 12.739L6.85889 15.1305L4.82461 13.6524L2.79028 15.1305L3.56738 12.739L1.53314 11.2609H4.04761L4.82461 8.86955Z",
        fill: "#D80027"
      }
    ),
    /* @__PURE__ */ jsx241(
      "path",
      {
        d: "M6.96184 12.1957L4.93694 11.4783C4.93694 11.4783 4.78731 10.0747 4.77827 10.0271C4.70955 9.66511 4.39141 9.39131 4.00937 9.39131C3.57719 9.39131 3.22675 9.7417 3.22675 10.1739C3.22675 10.2456 3.2372 10.3147 3.25525 10.3807L2.6875 10.9522H3.697C3.697 12 2.91512 12 2.91512 13.0435L3.34886 14.087H5.95755L6.39236 13.0435H6.39217C6.43572 12.9434 6.46216 12.8401 6.47308 12.7369C6.84789 12.5853 6.96184 12.1957 6.96184 12.1957Z",
        fill: "#FFDA44"
      }
    )
  ] }),
  /* @__PURE__ */ jsx241("defs", { children: /* @__PURE__ */ jsx241("clipPath", { id: "cfZW_clip0_0_1", children: /* @__PURE__ */ jsx241("rect", { width: "24", height: "24", fill: "white" }) }) })
] });

// src/components/ui/flags/assets/index.ts
var flagAssets = {
  AD: FlagAD,
  AE: FlagAE,
  AF: FlagAF,
  AG: FlagAG,
  AI: FlagAI,
  AL: FlagAL,
  AM: FlagAM,
  ANG: FlagANG,
  AO: FlagAO,
  AR: FlagAR,
  Artsakh: FlagArtsakh,
  AS: FlagAS,
  AT: FlagAT,
  AU: FlagAU,
  AW: FlagAW,
  AX: FlagAX,
  AZ: FlagAZ,
  BA: FlagBA,
  BB: FlagBB,
  BD: FlagBD,
  BE: FlagBE,
  BF: FlagBF,
  BG: FlagBG,
  BH: FlagBH,
  BI: FlagBI,
  BJ: FlagBJ,
  BL: FlagBL,
  BM: FlagBM,
  BN: FlagBN,
  BO: FlagBO,
  BR: FlagBR,
  BS: FlagBS,
  BT: FlagBT,
  BW: FlagBW,
  BY: FlagBY,
  BZ: FlagBZ,
  CA: FlagCA,
  CC: FlagCC,
  CD: FlagCD,
  CF: FlagCF,
  CG: FlagCG,
  CH: FlagCH,
  CI: FlagCI,
  CK: FlagCK,
  CL: FlagCL,
  CM: FlagCM,
  CN: FlagCN,
  CO: FlagCO,
  CR: FlagCR,
  CU: FlagCU,
  CV: FlagCV,
  CY: FlagCY,
  CZ: FlagCZ,
  DE: FlagDE,
  DJ: FlagDJ,
  DK: FlagDK,
  DM: FlagDM,
  DO: FlagDO,
  DZ: FlagDZ,
  EC: FlagEC,
  EE: FlagEE,
  EG: FlagEG,
  EHP: FlagEHP,
  ER: FlagER,
  ES: FlagES,
  ET: FlagET,
  EUR: FlagEUR,
  FI: FlagFI,
  FJ: FlagFJ,
  FK: FlagFK,
  FM: FlagFM,
  FO: FlagFO,
  FR: FlagFR,
  GA: FlagGA,
  GB: FlagGB,
  GD: FlagGD,
  GE: FlagGE,
  GG: FlagGG,
  GH: FlagGH,
  GI: FlagGI,
  GL: FlagGL,
  GM: FlagGM,
  GN: FlagGN,
  GQ: FlagGQ,
  GR: FlagGR,
  GT: FlagGT,
  GU: FlagGU,
  GW: FlagGW,
  GY: FlagGY,
  HK: FlagHK,
  HN: FlagHN,
  HR: FlagHR,
  HT: FlagHT,
  HU: FlagHU,
  ID: FlagID,
  IE: FlagIE,
  IL: FlagIL,
  IM: FlagIM,
  IN: FlagIN,
  IO: FlagIO,
  IQ: FlagIQ,
  IR: FlagIR,
  IS: FlagIS,
  IT: FlagIT,
  JE: FlagJE,
  JM: FlagJM,
  JO: FlagJO,
  JP: FlagJP,
  KE: FlagKE,
  KG: FlagKG,
  KH: FlagKH,
  KI: FlagKI,
  KM: FlagKM,
  KP: FlagKP,
  KR: FlagKR,
  KW: FlagKW,
  KY: FlagKY,
  KZ: FlagKZ,
  LA: FlagLA,
  LB: FlagLB,
  LC: FlagLC,
  LI: FlagLI,
  LK: FlagLK,
  LR: FlagLR,
  LS: FlagLS,
  LT: FlagLT,
  LU: FlagLU,
  LV: FlagLV,
  LY: FlagLY,
  MA: FlagMA,
  MC: FlagMC,
  MD: FlagMD,
  ME: FlagME,
  MG: FlagMG,
  MH: FlagMH,
  MK: FlagMK,
  ML: FlagML,
  MM: FlagMM,
  MN: FlagMN,
  MO: FlagMO,
  MP: FlagMP,
  MQ: FlagMQ,
  MR: FlagMR,
  MS: FlagMS,
  MT: FlagMT,
  MU: FlagMU,
  MV: FlagMV,
  MW: FlagMW,
  MX: FlagMX,
  MY: FlagMY,
  MZ: FlagMZ,
  NA: FlagNA,
  NE: FlagNE,
  NF: FlagNF,
  NG: FlagNG,
  NI: FlagNI,
  NL: FlagNL,
  NO: FlagNO,
  NP: FlagNP,
  NR: FlagNR,
  NU: FlagNU,
  NZ: FlagNZ,
  OM: FlagOM,
  PA: FlagPA,
  PE: FlagPE,
  PF: FlagPF,
  PG: FlagPG,
  PH: FlagPH,
  PK: FlagPK,
  PL: FlagPL,
  PN: FlagPN,
  PR: FlagPR,
  PRB: FlagPRB,
  PS: FlagPS,
  PT: FlagPT,
  PW: FlagPW,
  PY: FlagPY,
  QA: FlagQA,
  RO: FlagRO,
  RS: FlagRS,
  RU: FlagRU,
  RW: FlagRW,
  SA: FlagSA,
  SB: FlagSB,
  SC: FlagSC,
  SD: FlagSD,
  SE: FlagSE,
  SG: FlagSG,
  SHP: FlagSHP,
  SI: FlagSI,
  SK: FlagSK,
  SL: FlagSL,
  SLS: FlagSLS,
  SM: FlagSM,
  SN: FlagSN,
  SO: FlagSO,
  SR: FlagSR,
  SS: FlagSS,
  ST: FlagST,
  SV: FlagSV,
  SY: FlagSY,
  SZ: FlagSZ,
  TC: FlagTC,
  TD: FlagTD,
  TG: FlagTG,
  TH: FlagTH,
  TJ: FlagTJ,
  TK: FlagTK,
  TL: FlagTL,
  TM: FlagTM,
  TN: FlagTN,
  TO: FlagTO,
  TR: FlagTR,
  TT: FlagTT,
  TV: FlagTV,
  TW: FlagTW,
  TZ: FlagTZ,
  UA: FlagUA,
  UG: FlagUG,
  US: FlagUS,
  UY: FlagUY,
  UZ: FlagUZ,
  VA: FlagVA,
  VC: FlagVC,
  VE: FlagVE,
  VG: FlagVG,
  VI: FlagVI,
  VN: FlagVN,
  VU: FlagVU,
  WS: FlagWS,
  XAF: FlagXAF,
  XCD: FlagXCD,
  XOF: FlagXOF,
  XPF: FlagXPF,
  YE: FlagYE,
  ZA: FlagZA,
  ZM: FlagZM,
  ZW: FlagZW
};

// src/components/ui/flags/currency-map.ts
var currencyToFlag = {
  AED: "AE",
  AFN: "AF",
  ALL: "AL",
  AMD: "AM",
  ANG: "ANG",
  // Netherlands Antillean guilder — no current ISO country
  AOA: "AO",
  ARS: "AR",
  AUD: "AU",
  AWG: "AW",
  AZN: "AZ",
  Artsakh: "Artsakh",
  // Artsakh — NOT Argentina (AR)
  BAM: "BA",
  BBD: "BB",
  BDT: "BD",
  BGN: "BG",
  BHD: "BH",
  BIF: "BI",
  BMD: "BM",
  BND: "BN",
  BOB: "BO",
  BRL: "BR",
  BSD: "BS",
  BTN: "BT",
  BWP: "BW",
  BYN: "BY",
  BZD: "BZ",
  CAD: "CA",
  CDF: "CD",
  CHF: "CH",
  CKD: "CK",
  CLP: "CL",
  CNY: "CN",
  COP: "CO",
  CRC: "CR",
  CUC: "CU",
  CUP: "CU",
  CVE: "CV",
  CZK: "CZ",
  DJF: "DJ",
  DKK: "DK",
  DOP: "DO",
  DZD: "DZ",
  EGP: "EG",
  EHP: "EHP",
  // no country flag in set
  ERN: "ER",
  ETB: "ET",
  EUR: "EUR",
  // EU flag — no single country
  FJD: "FJ",
  FKP: "FK",
  FOK: "FO",
  GBP: "GB",
  GEL: "GE",
  GGP: "GG",
  GHS: "GH",
  GIP: "GI",
  GMD: "GM",
  GNF: "GN",
  GTQ: "GT",
  GYD: "GY",
  HKD: "HK",
  HNL: "HN",
  HRK: "HR",
  HTG: "HT",
  HUF: "HU",
  IDR: "ID",
  ILS: "IL",
  IMP: "IM",
  INR: "IN",
  IQD: "IQ",
  IRR: "IR",
  ISK: "IS",
  JEP: "JE",
  JMD: "JM",
  JOD: "JO",
  JPY: "JP",
  KES: "KE",
  KGS: "KG",
  KHR: "KH",
  KID: "KI",
  KMF: "KM",
  KPW: "KP",
  KRW: "KR",
  KWD: "KW",
  KYD: "KY",
  KZT: "KZ",
  LAK: "LA",
  LBP: "LB",
  LKR: "LK",
  LRD: "LR",
  LSL: "LS",
  LYD: "LY",
  MAD: "MA",
  MDL: "MD",
  MGA: "MG",
  MKD: "MK",
  MMK: "MM",
  MNT: "MN",
  MOP: "MO",
  MRU: "MR",
  MUR: "MU",
  MVR: "MV",
  MWK: "MW",
  MXN: "MX",
  MYR: "MY",
  MZN: "MZ",
  NAD: "NA",
  NGN: "NG",
  NIO: "NI",
  NOK: "NO",
  NPR: "NP",
  NZD: "NZ",
  OMR: "OM",
  PAB: "PA",
  PEN: "PE",
  PGK: "PG",
  PHP: "PH",
  PKR: "PK",
  PLN: "PL",
  PND: "PN",
  PRB: "PRB",
  // Transnistria — NOT Puerto Rico (PR)
  PYG: "PY",
  QAR: "QA",
  RON: "RO",
  RSD: "RS",
  RUB: "RU",
  RWF: "RW",
  SAR: "SA",
  SBD: "SB",
  SCR: "SC",
  SDG: "SD",
  SEK: "SE",
  SGD: "SG",
  SHP: "SHP",
  // Saint Helena pound — no country flag in set
  SLL: "SL",
  SLS: "SLS",
  // Somaliland — NOT Sierra Leone (SL)
  SOS: "SO",
  SRD: "SR",
  SSP: "SS",
  STN: "ST",
  SVC: "SV",
  SYP: "SY",
  SZL: "SZ",
  THB: "TH",
  TJS: "TJ",
  TMT: "TM",
  TND: "TN",
  TOP: "TO",
  TRY: "TR",
  TTD: "TT",
  TVD: "TV",
  TWD: "TW",
  TZS: "TZ",
  UAH: "UA",
  UGX: "UG",
  USD: "US",
  UYU: "UY",
  UZS: "UZ",
  VED: "VE",
  VES: "VE",
  VND: "VN",
  VUV: "VU",
  WST: "WS",
  XAF: "XAF",
  // Central African CFA franc — currency union
  XCD: "XCD",
  // East Caribbean dollar — currency union
  XOF: "XOF",
  // West African CFA franc — currency union
  XPF: "XPF",
  // CFP franc — French Pacific
  YER: "YE",
  ZAR: "ZA",
  ZMW: "ZM",
  ZWL: "ZW"
};
var currencyFlags = Object.fromEntries(
  Object.keys(currencyToFlag).map((code) => [code, flagAssets[currencyToFlag[code]]])
);

// src/components/ui/flags/currency/index.tsx
import { jsx as jsx242 } from "react/jsx-runtime";
function CurrencyFlag({ currency, ...props }) {
  const Flag = currency && currencyFlags[currency] || currencyFlags.USD;
  return /* @__PURE__ */ jsx242(Flag, { ...props });
}

// src/components/ui/flags/country/index.tsx
import { jsx as jsx243 } from "react/jsx-runtime";
var COUNTRY_CODES = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BR",
  "BS",
  "BT",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GQ",
  "GR",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SI",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WS",
  "YE",
  "ZA",
  "ZM",
  "ZW"
];
var countryFlags = Object.fromEntries(
  COUNTRY_CODES.map((code) => [code, flagAssets[code]])
);
function CountryFlag({ country, ...props }) {
  const Flag = country ? countryFlags[country.toUpperCase()] : void 0;
  return Flag ? /* @__PURE__ */ jsx243(Flag, { ...props }) : null;
}

// src/components/ui/accordion.tsx
import { ChevronDownIcon } from "lucide-react";

// src/components/animate-ui/primitives/radix/accordion.tsx
import * as React5 from "react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import { motion, AnimatePresence } from "motion/react";

// src/hooks/use-controlled-state.tsx
import * as React3 from "react";
function useControlledState(props) {
  const { value, defaultValue, onChange } = props;
  const [state, setInternalState] = React3.useState(
    value !== void 0 ? value : defaultValue
  );
  const isControlled = value !== void 0;
  const setState = React3.useCallback(
    (next, ...args) => {
      if (!isControlled) setInternalState(next);
      onChange?.(next, ...args);
    },
    [isControlled, onChange]
  );
  return [isControlled ? value : state, setState];
}

// src/lib/get-strict-context.tsx
import * as React4 from "react";
import { jsx as jsx244 } from "react/jsx-runtime";
function getStrictContext(name) {
  const Context = React4.createContext(void 0);
  const Provider = ({
    value,
    children
  }) => /* @__PURE__ */ jsx244(Context.Provider, { value, children });
  const useSafeContext = () => {
    const ctx = React4.useContext(Context);
    if (ctx === void 0) {
      throw new Error(`useContext must be used within ${name ?? "a Provider"}`);
    }
    return ctx;
  };
  return [Provider, useSafeContext];
}

// src/components/animate-ui/primitives/radix/accordion.tsx
import { jsx as jsx245 } from "react/jsx-runtime";
var [AccordionProvider, useAccordion] = getStrictContext("AccordionContext");
var [AccordionItemProvider, useAccordionItem] = getStrictContext("AccordionItemContext");
function Accordion(props) {
  const [value, setValue] = useControlledState({
    value: props?.value,
    defaultValue: props?.defaultValue,
    onChange: props?.onValueChange
  });
  return /* @__PURE__ */ jsx245(AccordionProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx245(
    AccordionPrimitive.Root,
    {
      "data-slot": "accordion",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function AccordionItem(props) {
  const { value } = useAccordion();
  const [isOpen, setIsOpen] = React5.useState(
    value?.includes(props?.value) ?? false
  );
  React5.useEffect(() => {
    setIsOpen(value?.includes(props?.value) ?? false);
  }, [value, props?.value]);
  return /* @__PURE__ */ jsx245(AccordionItemProvider, { value: { isOpen, setIsOpen, value: props.value }, children: /* @__PURE__ */ jsx245(AccordionPrimitive.Item, { "data-slot": "accordion-item", ...props }) });
}
function AccordionHeader(props) {
  return /* @__PURE__ */ jsx245(AccordionPrimitive.Header, { "data-slot": "accordion-header", ...props });
}
function AccordionTrigger(props) {
  return /* @__PURE__ */ jsx245(AccordionPrimitive.Trigger, { "data-slot": "accordion-trigger", ...props });
}
function AccordionContent({
  keepRendered = false,
  transition = { duration: 0.175, ease: "easeInOut" },
  ...props
}) {
  const { isOpen } = useAccordionItem();
  return /* @__PURE__ */ jsx245(AnimatePresence, { children: keepRendered ? /* @__PURE__ */ jsx245(AccordionPrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx245(
    motion.div,
    {
      "data-slot": "accordion-content",
      initial: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      animate: isOpen ? { height: "auto", opacity: 1, "--mask-stop": "100%", y: 0 } : { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      transition,
      style: {
        maskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        WebkitMaskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        overflow: "hidden"
      },
      ...props
    },
    "accordion-content"
  ) }) : isOpen && /* @__PURE__ */ jsx245(AccordionPrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx245(
    motion.div,
    {
      "data-slot": "accordion-content",
      initial: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      animate: {
        height: "auto",
        opacity: 1,
        "--mask-stop": "100%",
        y: 0
      },
      exit: { height: 0, opacity: 0, "--mask-stop": "0%", y: 20 },
      transition,
      style: {
        maskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        WebkitMaskImage: "linear-gradient(black var(--mask-stop), transparent var(--mask-stop))",
        overflow: "hidden"
      },
      ...props
    },
    "accordion-content"
  ) }) });
}

// src/components/ui/accordion.tsx
import { jsx as jsx246, jsxs as jsxs240 } from "react/jsx-runtime";
function Accordion2({
  ...props
}) {
  return /* @__PURE__ */ jsx246(Accordion, { ...props });
}
function AccordionItem2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx246(
    AccordionItem,
    {
      "data-slot": "accordion-item",
      className: cn("border-b last:border-b-0", className),
      ...props
    }
  );
}
function AccordionTrigger2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx246(AccordionHeader, { className: "flex", children: /* @__PURE__ */ jsxs240(
    AccordionTrigger,
    {
      "data-slot": "accordion-trigger",
      className: cn(
        "flex flex-1 cursor-pointer items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx246(ChevronDownIcon, { className: "pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
}
function AccordionContent2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx246(
    AccordionContent,
    {
      "data-slot": "accordion-content",
      className: "text-sm text-muted-foreground",
      ...props,
      children: /* @__PURE__ */ jsx246("div", { className: cn("pt-0 pb-4", className), children })
    }
  );
}

// src/components/ui/alert.tsx
import { cva } from "class-variance-authority";
import { jsx as jsx247 } from "react/jsx-runtime";
var alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive: "bg-card text-destructive *:data-[slot=alert-description]:text-destructive/75 [&>svg]:text-current"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Alert({
  className,
  variant,
  ...props
}) {
  return /* @__PURE__ */ jsx247(
    "div",
    {
      "data-slot": "alert",
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props
    }
  );
}
function AlertTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx247(
    "div",
    {
      "data-slot": "alert-title",
      className: cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      ),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx247(
    "div",
    {
      "data-slot": "alert-description",
      className: cn(
        "col-start-2 grid justify-items-start gap-1 text-sm font-normal text-muted-foreground [&_p]:leading-relaxed",
        className
      ),
      ...props
    }
  );
}

// src/components/animate-ui/primitives/radix/alert-dialog.tsx
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence2, motion as motion2 } from "motion/react";
import { jsx as jsx248 } from "react/jsx-runtime";
var [AlertDialogProvider, useAlertDialog] = getStrictContext("AlertDialogContext");
function AlertDialog(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx248(AlertDialogProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx248(
    AlertDialogPrimitive.Root,
    {
      "data-slot": "alert-dialog",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function AlertDialogTrigger(props) {
  return /* @__PURE__ */ jsx248(AlertDialogPrimitive.Trigger, { "data-slot": "alert-dialog-trigger", ...props });
}
function AlertDialogPortal(props) {
  const { isOpen } = useAlertDialog();
  return /* @__PURE__ */ jsx248(AnimatePresence2, { children: isOpen && /* @__PURE__ */ jsx248(
    AlertDialogPrimitive.Portal,
    {
      "data-slot": "alert-dialog-portal",
      forceMount: true,
      ...props
    }
  ) });
}
function AlertDialogOverlay({
  transition = { duration: 0.1, ease: "easeInOut" },
  ...props
}) {
  return /* @__PURE__ */ jsx248(
    AlertDialogPrimitive.Overlay,
    {
      "data-slot": "alert-dialog-overlay",
      asChild: true,
      forceMount: true,
      children: /* @__PURE__ */ jsx248(
        motion2.div,
        {
          initial: { opacity: 0, filter: "blur(4px)" },
          animate: { opacity: 1, filter: "blur(0px)" },
          exit: { opacity: 0, filter: "blur(4px)" },
          transition,
          ...props
        },
        "alert-dialog-overlay"
      )
    }
  );
}
function AlertDialogContent({
  from = "top",
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  transition = { type: "spring", stiffness: 300, damping: 30 },
  ...props
}) {
  const initialRotation = from === "bottom" || from === "left" ? "20deg" : "-20deg";
  const isVertical = from === "top" || from === "bottom";
  const rotateAxis = isVertical ? "rotateX" : "rotateY";
  return /* @__PURE__ */ jsx248(
    AlertDialogPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      children: /* @__PURE__ */ jsx248(
        motion2.div,
        {
          "data-slot": "alert-dialog-content",
          initial: {
            opacity: 0,
            filter: "blur(4px)",
            transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`
          },
          animate: {
            opacity: 1,
            filter: "blur(0px)",
            transform: `perspective(500px) ${rotateAxis}(0deg) scale(1)`
          },
          exit: {
            opacity: 0,
            filter: "blur(4px)",
            transform: `perspective(500px) ${rotateAxis}(${initialRotation}) scale(0.8)`
          },
          transition,
          ...props
        },
        "alert-dialog-content"
      )
    }
  );
}
function AlertDialogCancel(props) {
  return /* @__PURE__ */ jsx248(AlertDialogPrimitive.Cancel, { "data-slot": "alert-dialog-cancel", ...props });
}
function AlertDialogAction(props) {
  return /* @__PURE__ */ jsx248(AlertDialogPrimitive.Action, { "data-slot": "alert-dialog-action", ...props });
}
function AlertDialogTitle(props) {
  return /* @__PURE__ */ jsx248(AlertDialogPrimitive.Title, { "data-slot": "alert-dialog-title", ...props });
}
function AlertDialogDescription(props) {
  return /* @__PURE__ */ jsx248(
    AlertDialogPrimitive.Description,
    {
      "data-slot": "alert-dialog-description",
      ...props
    }
  );
}

// src/components/ui/button.tsx
import * as React6 from "react";
import { cva as cva2 } from "class-variance-authority";
import { Slot } from "radix-ui";
import { jsx as jsx249 } from "react/jsx-runtime";
var buttonVariants = cva2(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Variant names match the legacy ui-kit Button API.
        primary: "bg-primary text-white shadow-btn inset-shadow-btn hover:bg-primary-hover active:bg-primary-active dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
        danger: "bg-destructive text-white shadow-btn inset-shadow-btn hover:bg-destructive-hover active:bg-destructive-active focus-visible:ring-destructive/20 dark:bg-red-600 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-destructive-hover dark:active:bg-destructive-active dark:focus-visible:ring-destructive/40",
        secondary: "bg-white text-gray-600 shadow-btn-secondary hover:shadow-btn-secondary-hover dark:bg-input/30 dark:text-gray-100 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]",
        tertiary: "bg-white text-gray-600 shadow-btn-tertiary hover:shadow-btn-tertiary-hover dark:bg-gray-800 dark:text-gray-100",
        muted: "bg-transparent text-gray-600 shadow-btn-muted hover:shadow-btn-muted-hover hover:bg-[rgb(252_252_252)] hover:text-gray-700 dark:text-gray-100 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-white/5",
        "subtle-primary": "text-primary underline-offset-4 hover:underline dark:text-[oklch(0.725_0.16_259.7)]"
      },
      size: {
        // data-icon-side (set below) tightens padding on the side(s) holding an icon.
        default: "h-9 px-4 py-2 data-[icon-side=left]:pl-3 data-[icon-side=right]:pr-3 data-[icon-side=both]:px-3",
        xs: "h-6 gap-1 rounded-lg px-2 text-xs data-[icon-side=left]:pl-1.5 data-[icon-side=right]:pr-1.5 data-[icon-side=both]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-lg px-3 data-[icon-side=left]:pl-2.5 data-[icon-side=right]:pr-2.5 data-[icon-side=both]:px-2.5",
        lg: "h-10 rounded-lg px-6 data-[icon-side=left]:pl-4 data-[icon-side=right]:pr-4 data-[icon-side=both]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10"
      }
    },
    defaultVariants: {
      variant: "primary",
      size: "default"
    }
  }
);
function getIconSide(children) {
  const items = React6.Children.toArray(children);
  if (items.length === 0) return void 0;
  const isIcon = (node) => React6.isValidElement(node) && (typeof node.type !== "string" || node.type === "svg");
  const hasLabel = items.some((node) => !isIcon(node));
  if (!hasLabel) return "both";
  const left = isIcon(items[0]);
  const right = isIcon(items[items.length - 1]);
  if (left && right) return "both";
  if (left) return "left";
  if (right) return "right";
  return void 0;
}
var Button = React6.forwardRef(function Button2({ className, variant = "primary", size = "default", asChild = false, children, ...props }, ref) {
  const Comp = asChild ? Slot.Root : "button";
  const iconSide = asChild ? void 0 : getIconSide(children);
  return /* @__PURE__ */ jsx249(
    Comp,
    {
      ref,
      "data-slot": "button",
      "data-variant": variant,
      "data-size": size,
      "data-icon-side": iconSide,
      className: cn(buttonVariants({ variant, size, className })),
      ...props,
      children
    }
  );
});

// src/components/ui/alert-dialog.tsx
import { jsx as jsx250, jsxs as jsxs241 } from "react/jsx-runtime";
function AlertDialog2({
  ...props
}) {
  return /* @__PURE__ */ jsx250(AlertDialog, { ...props });
}
function AlertDialogTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx250(AlertDialogTrigger, { "data-slot": "alert-dialog-trigger", ...props });
}
function AlertDialogPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx250(AlertDialogPortal, { "data-slot": "alert-dialog-portal", ...props });
}
function AlertDialogOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    AlertDialogOverlay,
    {
      "data-slot": "alert-dialog-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function AlertDialogContent2({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsxs241(AlertDialogPortal2, { children: [
    /* @__PURE__ */ jsx250(AlertDialogOverlay2, {}),
    /* @__PURE__ */ jsx250(
      AlertDialogContent,
      {
        "data-slot": "alert-dialog-content",
        "data-size": size,
        className: cn(
          "group/alert-dialog-content fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg bg-background p-6 shadow-md ring-1 ring-black/5 duration-200 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[size=sm]:max-w-xs data-[size=default]:sm:max-w-lg",
          className
        ),
        ...props
      }
    )
  ] });
}
function AlertDialogHeader({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    "div",
    {
      "data-slot": "alert-dialog-header",
      className: cn(
        "grid grid-rows-[auto_1fr] place-items-center gap-1.5 text-center has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] has-data-[slot=alert-dialog-media]:gap-x-6 sm:group-data-[size=default]/alert-dialog-content:place-items-start sm:group-data-[size=default]/alert-dialog-content:text-left sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]",
        className
      ),
      ...props
    }
  );
}
function AlertDialogFooter({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    "div",
    {
      "data-slot": "alert-dialog-footer",
      className: cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end group-data-[size=sm]/alert-dialog-content:grid group-data-[size=sm]/alert-dialog-content:grid-cols-2",
        className
      ),
      ...props
    }
  );
}
function AlertDialogTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    AlertDialogTitle,
    {
      "data-slot": "alert-dialog-title",
      className: cn(
        "text-lg font-medium tracking-tight sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
        className
      ),
      ...props
    }
  );
}
function AlertDialogDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    AlertDialogDescription,
    {
      "data-slot": "alert-dialog-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function AlertDialogMedia({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx250(
    "div",
    {
      "data-slot": "alert-dialog-media",
      className: cn(
        "mb-2 inline-flex size-16 items-center justify-center rounded-md bg-muted sm:group-data-[size=default]/alert-dialog-content:row-span-2 *:[svg:not([class*='size-'])]:size-8",
        className
      ),
      ...props
    }
  );
}
function AlertDialogAction2({
  className,
  variant = "primary",
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx250(Button, { variant, size, asChild: true, children: /* @__PURE__ */ jsx250(
    AlertDialogAction,
    {
      "data-slot": "alert-dialog-action",
      className: cn(className),
      ...props
    }
  ) });
}
function AlertDialogCancel2({
  className,
  variant = "muted",
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx250(Button, { variant, size, asChild: true, children: /* @__PURE__ */ jsx250(
    AlertDialogCancel,
    {
      "data-slot": "alert-dialog-cancel",
      className: cn(className),
      ...props
    }
  ) });
}

// src/components/ui/aspect-ratio.tsx
import { AspectRatio as AspectRatioPrimitive } from "radix-ui";
import { jsx as jsx251 } from "react/jsx-runtime";
function AspectRatio({
  ...props
}) {
  return /* @__PURE__ */ jsx251(AspectRatioPrimitive.Root, { "data-slot": "aspect-ratio", ...props });
}

// src/components/ui/avatar.tsx
import { Avatar as AvatarPrimitive } from "radix-ui";
import { jsx as jsx252 } from "react/jsx-runtime";
function Avatar({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx252(
    AvatarPrimitive.Root,
    {
      "data-slot": "avatar",
      "data-size": size,
      className: cn(
        "group/avatar relative flex size-8 shrink-0 overflow-hidden rounded-full select-none data-[size=lg]:size-10 data-[size=sm]:size-6",
        className
      ),
      ...props
    }
  );
}
function AvatarImage({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx252(
    AvatarPrimitive.Image,
    {
      "data-slot": "avatar-image",
      className: cn("aspect-square size-full", className),
      ...props
    }
  );
}
function AvatarFallback({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx252(
    AvatarPrimitive.Fallback,
    {
      "data-slot": "avatar-fallback",
      className: cn(
        "flex size-full items-center justify-center rounded-full bg-muted text-sm text-muted-foreground group-data-[size=sm]/avatar:text-xs",
        className
      ),
      ...props
    }
  );
}
function AvatarBadge({ className, ...props }) {
  return /* @__PURE__ */ jsx252(
    "span",
    {
      "data-slot": "avatar-badge",
      className: cn(
        "absolute right-0 bottom-0 z-10 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background select-none",
        "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
        "group-data-[size=default]/avatar:size-2.5 group-data-[size=default]/avatar:[&>svg]:size-2",
        "group-data-[size=lg]/avatar:size-3 group-data-[size=lg]/avatar:[&>svg]:size-2",
        className
      ),
      ...props
    }
  );
}
function AvatarGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx252(
    "div",
    {
      "data-slot": "avatar-group",
      className: cn(
        "group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background",
        className
      ),
      ...props
    }
  );
}
function AvatarGroupCount({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx252(
    "div",
    {
      "data-slot": "avatar-group-count",
      className: cn(
        "relative flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground ring-2 ring-background group-has-data-[size=lg]/avatar-group:size-10 group-has-data-[size=sm]/avatar-group:size-6 [&>svg]:size-4 group-has-data-[size=lg]/avatar-group:[&>svg]:size-5 group-has-data-[size=sm]/avatar-group:[&>svg]:size-3",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/badge.tsx
import { cva as cva3 } from "class-variance-authority";
import { Slot as Slot2 } from "radix-ui";
import { jsx as jsx253 } from "react/jsx-runtime";
var badgeVariants = cva3(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary: "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive: "bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline: "border-border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        ghost: "[a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({
  className,
  variant = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot2.Root : "span";
  return /* @__PURE__ */ jsx253(
    Comp,
    {
      "data-slot": "badge",
      "data-variant": variant,
      className: cn(badgeVariants({ variant }), className),
      ...props
    }
  );
}

// src/components/ui/breadcrumb.tsx
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { Slot as Slot3 } from "radix-ui";
import { jsx as jsx254, jsxs as jsxs242 } from "react/jsx-runtime";
function Breadcrumb({ ...props }) {
  return /* @__PURE__ */ jsx254("nav", { "aria-label": "breadcrumb", "data-slot": "breadcrumb", ...props });
}
function BreadcrumbList({ className, ...props }) {
  return /* @__PURE__ */ jsx254(
    "ol",
    {
      "data-slot": "breadcrumb-list",
      className: cn(
        "flex flex-wrap items-center gap-1.5 text-sm font-medium break-words text-muted-foreground sm:gap-2.5",
        className
      ),
      ...props
    }
  );
}
function BreadcrumbItem({ className, ...props }) {
  return /* @__PURE__ */ jsx254(
    "li",
    {
      "data-slot": "breadcrumb-item",
      className: cn("inline-flex items-center gap-1.5", className),
      ...props
    }
  );
}
function BreadcrumbLink({
  asChild,
  className,
  ...props
}) {
  const Comp = asChild ? Slot3.Root : "a";
  return /* @__PURE__ */ jsx254(
    Comp,
    {
      "data-slot": "breadcrumb-link",
      className: cn("transition-colors hover:text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbPage({ className, ...props }) {
  return /* @__PURE__ */ jsx254(
    "span",
    {
      "data-slot": "breadcrumb-page",
      role: "link",
      "aria-disabled": "true",
      "aria-current": "page",
      className: cn("font-medium text-foreground", className),
      ...props
    }
  );
}
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx254(
    "li",
    {
      "data-slot": "breadcrumb-separator",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("[&>svg]:size-3.5", className),
      ...props,
      children: children ?? /* @__PURE__ */ jsx254(ChevronRight, {})
    }
  );
}
function BreadcrumbEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs242(
    "span",
    {
      "data-slot": "breadcrumb-ellipsis",
      role: "presentation",
      "aria-hidden": "true",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx254(MoreHorizontal, { className: "size-4" }),
        /* @__PURE__ */ jsx254("span", { className: "sr-only", children: "More" })
      ]
    }
  );
}

// src/components/ui/button-group.tsx
import { cva as cva4 } from "class-variance-authority";
import { Slot as Slot4 } from "radix-ui";

// src/components/ui/separator.tsx
import { Separator as SeparatorPrimitive } from "radix-ui";
import { jsx as jsx255 } from "react/jsx-runtime";
function Separator({
  className,
  orientation = "horizontal",
  decorative = true,
  ...props
}) {
  return /* @__PURE__ */ jsx255(
    SeparatorPrimitive.Root,
    {
      "data-slot": "separator",
      decorative,
      orientation,
      className: cn(
        "shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/button-group.tsx
import { jsx as jsx256 } from "react/jsx-runtime";
var buttonGroupVariants = cva4(
  [
    "flex w-fit items-stretch rounded-lg",
    // Match secondary button shadow: outer drop shadow + bottom inset for depth
    "shadow-[0_1px_2px_0_rgb(0_0_0/0.05)] inset-shadow-[0_-1px_1px_0_rgb(0_0_0/0.07)]",
    "dark:shadow-none dark:inset-shadow-none",
    // Strip child shadow layers — we replace them with CSS border
    // Use !important to override button dark: variants which have higher specificity
    "[&>*:not([data-slot=button-group-separator])]:shadow-none! [&>*:not([data-slot=button-group-separator])]:inset-shadow-none!",
    // Add CSS border to every direct child (except separators)
    "[&>*:not([data-slot=button-group-separator])]:border [&>*:not([data-slot=button-group-separator])]:border-[rgb(0_0_0/0.1)]",
    // Dark mode border — match secondary button dark inset-shadow opacity (0.08)
    "dark:[&>*:not([data-slot=button-group-separator])]:border-[rgb(255_255_255/0.08)]",
    // Dark mode: apply secondary button appearance to children (bg, gradient, text, hover, active)
    "dark:[&>*:not([data-slot=button-group-separator])]:bg-gray-800!",
    "dark:[&>*:not([data-slot=button-group-separator])]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))]!",
    "dark:[&>*:not([data-slot=button-group-separator])]:text-gray-100!",
    "dark:[&>*:not([data-slot=button-group-separator])]:hover:bg-secondary-hover!",
    "dark:[&>*:not([data-slot=button-group-separator])]:active:bg-secondary-active!",
    // Focus: raise above siblings so ring isn't clipped
    "[&>*]:focus-visible:relative [&>*]:focus-visible:z-10",
    // Nested button groups get a gap
    "has-[>[data-slot=button-group]]:gap-2",
    // Select / input tweaks
    "has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md",
    "[&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit",
    "[&>input]:flex-1"
  ].join(" "),
  {
    variants: {
      orientation: {
        horizontal: [
          // Round only the outer edges
          "[&>*]:rounded-none [&>:first-child:not([data-slot=button-group-separator])]:rounded-l-lg [&>:last-child:not([data-slot=button-group-separator])]:rounded-r-lg",
          // Remove left border on non-first children to prevent doubling (skip separators & items after separators)
          "[&>*:not(:first-child):not([data-slot=button-group-separator]):not([data-slot=button-group-separator]+*)]:border-l-0",
          // After a separator, reset rounding
          "[&>[data-slot=button-group-separator]+*]:rounded-l-none",
          // Before a separator, reset rounding (via has-selector on the child before it)
          "[&>*:has(+[data-slot=button-group-separator])]:rounded-r-none"
        ].join(" "),
        vertical: [
          // Round only the outer edges
          "flex-col [&>*]:rounded-none [&>:first-child:not([data-slot=button-group-separator])]:rounded-t-lg [&>:last-child:not([data-slot=button-group-separator])]:rounded-b-lg",
          // Remove top border on non-first children to prevent doubling (skip separators & items after separators)
          "[&>*:not(:first-child):not([data-slot=button-group-separator]):not([data-slot=button-group-separator]+*)]:border-t-0",
          // After a separator, reset rounding
          "[&>[data-slot=button-group-separator]+*]:rounded-t-none",
          // Before a separator, reset rounding
          "[&>*:has(+[data-slot=button-group-separator])]:rounded-b-none"
        ].join(" ")
      }
    },
    defaultVariants: {
      orientation: "horizontal"
    }
  }
);
function ButtonGroup({
  className,
  orientation,
  ...props
}) {
  return /* @__PURE__ */ jsx256(
    "div",
    {
      role: "group",
      "data-slot": "button-group",
      "data-orientation": orientation,
      className: cn(buttonGroupVariants({ orientation }), className),
      ...props
    }
  );
}
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot4.Root : "div";
  return /* @__PURE__ */ jsx256(
    Comp,
    {
      className: cn(
        "flex items-center gap-2 rounded-md bg-white px-4 text-sm font-medium text-secondary-foreground dark:bg-gray-800 dark:text-gray-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx256(
    Separator,
    {
      "data-slot": "button-group-separator",
      orientation,
      className: cn(
        "relative self-stretch !border-0",
        // Vertical separator (used in horizontal groups)
        "data-[orientation=vertical]:h-auto data-[orientation=vertical]:!w-1.5 data-[orientation=vertical]:my-px data-[orientation=vertical]:bg-gray-25 data-[orientation=vertical]:shadow-[0_1px_0_var(--tw-shadow-color,var(--color-gray-300)),0_-1px_0_var(--tw-shadow-color,var(--color-gray-200))] dark:data-[orientation=vertical]:bg-white/[0.03] dark:data-[orientation=vertical]:shadow-[0_1px_0_var(--color-gray-800),0_-1px_0_var(--color-gray-800)]",
        // Horizontal separator (used in vertical groups)
        "data-[orientation=horizontal]:!h-1.5 data-[orientation=horizontal]:w-auto data-[orientation=horizontal]:mx-px data-[orientation=horizontal]:bg-gray-25 data-[orientation=horizontal]:shadow-[1px_0_0_var(--tw-shadow-color,var(--color-gray-200)),-1px_0_0_var(--tw-shadow-color,var(--color-gray-200))] dark:data-[orientation=horizontal]:bg-white/[0.03] dark:data-[orientation=horizontal]:shadow-[1px_0_0_var(--color-gray-800),-1px_0_0_var(--color-gray-800)]",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/calendar.tsx
import * as React7 from "react";
import {
  ChevronDownIcon as ChevronDownIcon2,
  ChevronLeftIcon,
  ChevronRightIcon
} from "lucide-react";
import {
  DayPicker,
  getDefaultClassNames
} from "react-day-picker";
import { jsx as jsx257 } from "react/jsx-runtime";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "muted",
  formatters,
  components,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  return /* @__PURE__ */ jsx257(
    DayPicker,
    {
      showOutsideDays,
      className: cn(
        "group/calendar rounded-lg bg-background p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      ),
      captionLayout,
      formatters: {
        formatMonthDropdown: (date) => date.toLocaleString("default", { month: "short" }),
        ...formatters
      },
      classNames: {
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-4 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) rounded-lg p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-(--cell-size) rounded-lg p-0 select-none aria-disabled:opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative rounded-lg border border-input shadow-xs has-focus:border-ring has-focus:ring-[3px] has-focus:ring-ring/50 dark:shadow-none",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 bg-popover opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "font-medium select-none",
          captionLayout === "label" ? "text-sm" : "flex h-8 items-center gap-1 rounded-lg pr-1 pl-2 text-sm [&>svg]:size-3.5 [&>svg]:text-muted-foreground",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "flex-1 rounded-lg text-[0.8rem] font-normal text-muted-foreground select-none",
          defaultClassNames.weekday
        ),
        week: cn("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-[0.8rem] text-muted-foreground select-none",
          defaultClassNames.week_number
        ),
        day: cn(
          "group/day relative aspect-square h-full w-full p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-lg",
          props.showWeekNumber ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-lg" : "[&:first-child[data-selected=true]_button]:rounded-l-lg",
          defaultClassNames.day
        ),
        range_start: cn(
          defaultClassNames.range_start,
          "!rounded-l-lg bg-blue-100 dark:bg-blue-900/40"
        ),
        range_middle: cn(defaultClassNames.range_middle, "rounded-none"),
        range_end: cn(defaultClassNames.range_end, "!rounded-r-lg bg-blue-100 dark:bg-blue-900/40"),
        today: cn(
          defaultClassNames.today,
          "rounded-lg bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-200 data-[selected=true]:rounded-none data-[selected=true]:bg-transparent"
        ),
        outside: cn(
          "text-muted-foreground aria-selected:text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground opacity-50",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames
      },
      components: {
        Root: ({ className: className2, rootRef, ...props2 }) => {
          return /* @__PURE__ */ jsx257(
            "div",
            {
              "data-slot": "calendar",
              ref: rootRef,
              className: cn(className2),
              ...props2
            }
          );
        },
        Chevron: ({ className: className2, orientation, ...props2 }) => {
          if (orientation === "left") {
            return /* @__PURE__ */ jsx257(ChevronLeftIcon, { className: cn("size-4", className2), ...props2 });
          }
          if (orientation === "right") {
            return /* @__PURE__ */ jsx257(
              ChevronRightIcon,
              {
                className: cn("size-4", className2),
                ...props2
              }
            );
          }
          return /* @__PURE__ */ jsx257(ChevronDownIcon2, { className: cn("size-4", className2), ...props2 });
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props2 }) => {
          return /* @__PURE__ */ jsx257("td", { ...props2, children: /* @__PURE__ */ jsx257("div", { className: "flex size-(--cell-size) items-center justify-center text-center", children }) });
        },
        ...components
      },
      ...props
    }
  );
}
function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React7.useRef(null);
  React7.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);
  return /* @__PURE__ */ jsx257(
    Button,
    {
      ref,
      variant: "muted",
      size: "icon",
      "data-day": day.date.toLocaleDateString(),
      "data-selected-single": modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle,
      "data-range-start": modifiers.range_start,
      "data-range-end": modifiers.range_end,
      "data-range-middle": modifiers.range_middle,
      className: cn(
        "flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-medium group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-blue-100 data-[range-middle=true]:text-blue-900 dark:data-[range-middle=true]:bg-blue-900/40 dark:data-[range-middle=true]:text-blue-200 data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-accent-foreground [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      ),
      ...props
    }
  );
}

// src/components/ui/card.tsx
import { jsx as jsx258 } from "react/jsx-runtime";
function Card({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card",
      "data-size": size,
      className: cn(
        "group/card flex flex-col gap-6 bg-card text-card-foreground shadow-surface-raised dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]",
        size === "sm" ? "rounded-lg gap-4 py-4" : "rounded-xl py-6",
        className
      ),
      ...props
    }
  );
}
function CardHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-header",
      className: cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6 group-data-[size=sm]/card:px-4 group-data-[size=sm]/card:[.border-b]:pb-4",
        className
      ),
      ...props
    }
  );
}
function CardTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-title",
      className: cn("leading-none font-semibold", className),
      ...props
    }
  );
}
function CardDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function CardAction({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-action",
      className: cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      ),
      ...props
    }
  );
}
function CardContent({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-content",
      className: cn("px-6 text-sm group-data-[size=sm]/card:px-4", className),
      ...props
    }
  );
}
function CardFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx258(
    "div",
    {
      "data-slot": "card-footer",
      className: cn("flex items-center px-6 [.border-t]:pt-6 group-data-[size=sm]/card:px-4 group-data-[size=sm]/card:[.border-t]:pt-4", className),
      ...props
    }
  );
}

// src/components/ui/carousel.tsx
import * as React8 from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { jsx as jsx259, jsxs as jsxs243 } from "react/jsx-runtime";
var CarouselContext = React8.createContext(null);
function useCarousel() {
  const context = React8.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y"
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = React8.useState(false);
  const [canScrollNext, setCanScrollNext] = React8.useState(false);
  const onSelect = React8.useCallback((api2) => {
    if (!api2) return;
    setCanScrollPrev(api2.canScrollPrev());
    setCanScrollNext(api2.canScrollNext());
  }, []);
  const scrollPrev = React8.useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const scrollNext = React8.useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const handleKeyDown = React8.useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );
  React8.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);
  React8.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);
  return /* @__PURE__ */ jsx259(
    CarouselContext.Provider,
    {
      value: {
        carouselRef,
        api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      },
      children: /* @__PURE__ */ jsx259(
        "div",
        {
          onKeyDownCapture: handleKeyDown,
          className: cn("relative", className),
          role: "region",
          "aria-roledescription": "carousel",
          "data-slot": "carousel",
          ...props,
          children
        }
      )
    }
  );
}
function CarouselContent({ className, ...props }) {
  const { carouselRef, orientation } = useCarousel();
  return /* @__PURE__ */ jsx259(
    "div",
    {
      ref: carouselRef,
      className: "overflow-hidden",
      "data-slot": "carousel-content",
      children: /* @__PURE__ */ jsx259(
        "div",
        {
          className: cn(
            "flex",
            orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
            className
          ),
          ...props
        }
      )
    }
  );
}
function CarouselItem({ className, ...props }) {
  const { orientation } = useCarousel();
  return /* @__PURE__ */ jsx259(
    "div",
    {
      role: "group",
      "aria-roledescription": "slide",
      "data-slot": "carousel-item",
      className: cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className
      ),
      ...props
    }
  );
}
function CarouselPrevious({
  className,
  variant = "muted",
  size = "icon",
  ...props
}) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  return /* @__PURE__ */ jsxs243(
    Button,
    {
      "data-slot": "carousel-previous",
      variant,
      size,
      className: cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollPrev,
      onClick: scrollPrev,
      ...props,
      children: [
        /* @__PURE__ */ jsx259(ArrowLeft, {}),
        /* @__PURE__ */ jsx259("span", { className: "sr-only", children: "Previous slide" })
      ]
    }
  );
}
function CarouselNext({
  className,
  variant = "muted",
  size = "icon",
  ...props
}) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  return /* @__PURE__ */ jsxs243(
    Button,
    {
      "data-slot": "carousel-next",
      variant,
      size,
      className: cn(
        "absolute size-8 rounded-full",
        orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollNext,
      onClick: scrollNext,
      ...props,
      children: [
        /* @__PURE__ */ jsx259(ArrowRight, {}),
        /* @__PURE__ */ jsx259("span", { className: "sr-only", children: "Next slide" })
      ]
    }
  );
}

// src/components/ui/chart.tsx
import * as React9 from "react";
import * as RechartsPrimitive from "recharts";
import { Fragment, jsx as jsx260, jsxs as jsxs244 } from "react/jsx-runtime";
var THEMES = { light: "", dark: ".dark" };
var ChartContext = React9.createContext(null);
function useChart() {
  const context = React9.useContext(ChartContext);
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }
  return context;
}
function ChartContainer({
  id,
  className,
  children,
  config,
  ...props
}) {
  const uniqueId = React9.useId();
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`;
  return /* @__PURE__ */ jsx260(ChartContext.Provider, { value: { config }, children: /* @__PURE__ */ jsxs244(
    "div",
    {
      "data-slot": "chart",
      "data-chart": chartId,
      className: cn(
        "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx260(ChartStyle, { id: chartId, config }),
        /* @__PURE__ */ jsx260(RechartsPrimitive.ResponsiveContainer, { children })
      ]
    }
  ) });
}
var ChartStyle = ({ id, config }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config2]) => config2.theme || config2.color
  );
  if (!colorConfig.length) {
    return null;
  }
  return /* @__PURE__ */ jsx260(
    "style",
    {
      dangerouslySetInnerHTML: {
        __html: Object.entries(THEMES).map(
          ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig.map(([key, itemConfig]) => {
            const color = itemConfig.theme?.[theme] || itemConfig.color;
            return color ? `  --color-${key}: ${color};` : null;
          }).join("\n")}
}
`
        ).join("\n")
      }
    }
  );
};
var ChartTooltip = RechartsPrimitive.Tooltip;
function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey
}) {
  const { config } = useChart();
  const tooltipLabel = React9.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null;
    }
    const [item] = payload;
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`;
    const itemConfig = getPayloadConfigFromPayload(config, item, key);
    const value = !labelKey && typeof label === "string" ? config[label]?.label || label : itemConfig?.label;
    if (labelFormatter) {
      return /* @__PURE__ */ jsx260("div", { className: cn("font-normal", labelClassName), children: labelFormatter(value, payload) });
    }
    if (!value) {
      return null;
    }
    return /* @__PURE__ */ jsx260("div", { className: cn("font-normal", labelClassName), children: value });
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey
  ]);
  if (!active || !payload?.length) {
    return null;
  }
  const nestLabel = payload.length === 1 && indicator !== "dot";
  return /* @__PURE__ */ jsxs244(
    "div",
    {
      className: cn(
        "grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl",
        className
      ),
      children: [
        !nestLabel ? tooltipLabel : null,
        /* @__PURE__ */ jsx260("div", { className: "grid gap-1.5", children: payload.filter((item) => item.type !== "none").map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          const indicatorColor = color || item.payload.fill || item.color;
          return /* @__PURE__ */ jsx260(
            "div",
            {
              className: cn(
                "flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground",
                indicator === "dot" && "items-center"
              ),
              children: formatter && item?.value !== void 0 && item.name ? formatter(item.value, item.name, item, index, item.payload) : /* @__PURE__ */ jsxs244(Fragment, { children: [
                itemConfig?.icon ? /* @__PURE__ */ jsx260(itemConfig.icon, {}) : !hideIndicator && /* @__PURE__ */ jsx260(
                  "div",
                  {
                    className: cn(
                      "shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
                      {
                        "h-2.5 w-2.5": indicator === "dot",
                        "w-1": indicator === "line",
                        "w-0 border-[1.5px] border-dashed bg-transparent": indicator === "dashed",
                        "my-0.5": nestLabel && indicator === "dashed"
                      }
                    ),
                    style: {
                      "--color-bg": indicatorColor,
                      "--color-border": indicatorColor
                    }
                  }
                ),
                /* @__PURE__ */ jsxs244(
                  "div",
                  {
                    className: cn(
                      "flex flex-1 justify-between leading-none",
                      nestLabel ? "items-end" : "items-center"
                    ),
                    children: [
                      /* @__PURE__ */ jsxs244("div", { className: "grid gap-1.5", children: [
                        nestLabel ? tooltipLabel : null,
                        /* @__PURE__ */ jsx260("span", { className: "text-muted-foreground", children: itemConfig?.label || item.name })
                      ] }),
                      item.value && /* @__PURE__ */ jsx260("span", { className: "font-mono font-normal text-foreground tabular-nums", children: item.value.toLocaleString() })
                    ]
                  }
                )
              ] })
            },
            item.dataKey
          );
        }) })
      ]
    }
  );
}
var ChartLegend = RechartsPrimitive.Legend;
function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  nameKey
}) {
  const { config } = useChart();
  if (!payload?.length) {
    return null;
  }
  return /* @__PURE__ */ jsx260(
    "div",
    {
      className: cn(
        "flex items-center justify-center gap-4",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className
      ),
      children: payload.filter((item) => item.type !== "none").map((item) => {
        const key = `${nameKey || item.dataKey || "value"}`;
        const itemConfig = getPayloadConfigFromPayload(config, item, key);
        return /* @__PURE__ */ jsxs244(
          "div",
          {
            className: cn(
              "flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
            ),
            children: [
              itemConfig?.icon && !hideIcon ? /* @__PURE__ */ jsx260(itemConfig.icon, {}) : /* @__PURE__ */ jsx260(
                "div",
                {
                  className: "h-2 w-2 shrink-0 rounded-[2px]",
                  style: {
                    backgroundColor: item.color
                  }
                }
              ),
              itemConfig?.label
            ]
          },
          item.value
        );
      })
    }
  );
}
function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== "object" || payload === null) {
    return void 0;
  }
  const payloadPayload = "payload" in payload && typeof payload.payload === "object" && payload.payload !== null ? payload.payload : void 0;
  let configLabelKey = key;
  if (key in payload && typeof payload[key] === "string") {
    configLabelKey = payload[key];
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === "string") {
    configLabelKey = payloadPayload[key];
  }
  return configLabelKey in config ? config[configLabelKey] : config[key];
}

// src/components/animate-ui/primitives/radix/checkbox.tsx
import { Checkbox as CheckboxPrimitive } from "radix-ui";
import { motion as motion3 } from "motion/react";
import { jsx as jsx261 } from "react/jsx-runtime";
var [CheckboxProvider, useCheckbox] = getStrictContext("CheckboxContext");
function Checkbox({
  defaultChecked,
  checked,
  onCheckedChange,
  disabled,
  required,
  name,
  value,
  ...props
}) {
  const [isChecked, setIsChecked] = useControlledState({
    value: checked,
    defaultValue: defaultChecked,
    onChange: onCheckedChange
  });
  return /* @__PURE__ */ jsx261(CheckboxProvider, { value: { isChecked, setIsChecked }, children: /* @__PURE__ */ jsx261(
    CheckboxPrimitive.Root,
    {
      defaultChecked,
      checked,
      onCheckedChange: setIsChecked,
      disabled,
      required,
      name,
      value,
      asChild: true,
      children: /* @__PURE__ */ jsx261(
        motion3.button,
        {
          "data-slot": "checkbox",
          whileTap: { scale: 0.95 },
          whileHover: { scale: 1.05 },
          ...props
        }
      )
    }
  ) });
}
function CheckboxIndicator(props) {
  const { isChecked } = useCheckbox();
  return /* @__PURE__ */ jsx261(CheckboxPrimitive.Indicator, { forceMount: true, asChild: true, children: /* @__PURE__ */ jsx261(
    motion3.svg,
    {
      "data-slot": "checkbox-indicator",
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      strokeWidth: "3.5",
      stroke: "currentColor",
      initial: "unchecked",
      animate: isChecked ? "checked" : "unchecked",
      ...props,
      children: isChecked === "indeterminate" ? /* @__PURE__ */ jsx261(
        motion3.line,
        {
          x1: "5",
          y1: "12",
          x2: "19",
          y2: "12",
          strokeLinecap: "round",
          initial: { pathLength: 0, opacity: 0 },
          animate: {
            pathLength: 1,
            opacity: 1,
            transition: { duration: 0.2 }
          }
        }
      ) : /* @__PURE__ */ jsx261(
        motion3.path,
        {
          strokeLinecap: "round",
          strokeLinejoin: "round",
          d: "M4.5 12.75l6 6 9-13.5",
          variants: {
            checked: {
              pathLength: 1,
              opacity: 1,
              transition: {
                duration: 0.2
              }
            },
            unchecked: {
              pathLength: 0,
              opacity: 0,
              transition: {
                duration: 0.2
              }
            }
          }
        }
      )
    }
  ) });
}

// src/components/ui/checkbox.tsx
import { jsx as jsx262 } from "react/jsx-runtime";
function Checkbox2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx262(
    Checkbox,
    {
      "data-slot": "checkbox",
      className: cn(
        "peer grid size-4 shrink-0 cursor-pointer place-content-center rounded-[4px] border-none bg-white shadow-checkbox inset-shadow-checkbox transition-[background-color,box-shadow] duration-150 ease-out outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:bg-primary data-[state=checked]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.1),rgb(255_255_255/0))] data-[state=checked]:bg-blend-screen data-[state=checked]:shadow-checkbox-checked data-[state=checked]:inset-shadow-checkbox-checked data-[state=checked]:text-primary-foreground dark:bg-input/30 dark:shadow-none dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.25)] dark:aria-invalid:ring-destructive/40 dark:data-[state=checked]:bg-blue-600 dark:data-[state=checked]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.12),rgb(255_255_255/0))] dark:data-[state=checked]:text-white dark:data-[state=checked]:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:data-[state=checked]:shadow-none dark:hover:data-[state=checked]:bg-primary-hover dark:active:data-[state=checked]:bg-primary-active data-[state=indeterminate]:bg-primary data-[state=indeterminate]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.1),rgb(255_255_255/0))] data-[state=indeterminate]:bg-blend-screen data-[state=indeterminate]:shadow-checkbox-checked data-[state=indeterminate]:inset-shadow-checkbox-checked data-[state=indeterminate]:text-primary-foreground dark:data-[state=indeterminate]:bg-blue-600 dark:data-[state=indeterminate]:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.12),rgb(255_255_255/0))] dark:data-[state=indeterminate]:text-white dark:data-[state=indeterminate]:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:data-[state=indeterminate]:shadow-none dark:hover:data-[state=indeterminate]:bg-primary-hover dark:active:data-[state=indeterminate]:bg-primary-active",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx262(
        CheckboxIndicator,
        {
          "data-slot": "checkbox-indicator",
          className: "size-3.5 text-current"
        }
      )
    }
  );
}

// src/components/animate-ui/primitives/radix/collapsible.tsx
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence3, motion as motion4 } from "motion/react";
import { jsx as jsx263 } from "react/jsx-runtime";
var [CollapsibleProvider, useCollapsible] = getStrictContext("CollapsibleContext");
function Collapsible(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx263(CollapsibleProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx263(
    CollapsiblePrimitive.Root,
    {
      "data-slot": "collapsible",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function CollapsibleTrigger(props) {
  return /* @__PURE__ */ jsx263(CollapsiblePrimitive.Trigger, { "data-slot": "collapsible-trigger", ...props });
}
function CollapsibleContent({
  keepRendered = false,
  transition = { duration: 0.35, ease: "easeInOut" },
  ...props
}) {
  const { isOpen } = useCollapsible();
  return /* @__PURE__ */ jsx263(AnimatePresence3, { children: keepRendered ? /* @__PURE__ */ jsx263(CollapsiblePrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx263(
    motion4.div,
    {
      "data-slot": "collapsible-content",
      layout: true,
      initial: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      animate: isOpen ? { opacity: 1, height: "auto", overflow: "hidden", y: 0 } : { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      transition,
      ...props
    },
    "collapsible-content"
  ) }) : isOpen && /* @__PURE__ */ jsx263(CollapsiblePrimitive.Content, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx263(
    motion4.div,
    {
      "data-slot": "collapsible-content",
      layout: true,
      initial: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      animate: { opacity: 1, height: "auto", overflow: "hidden", y: 0 },
      exit: { opacity: 0, height: 0, overflow: "hidden", y: 6 },
      transition,
      ...props
    },
    "collapsible-content"
  ) }) });
}

// src/components/ui/collapsible.tsx
import { jsx as jsx264 } from "react/jsx-runtime";
function Collapsible2({
  ...props
}) {
  return /* @__PURE__ */ jsx264(Collapsible, { "data-slot": "collapsible", ...props });
}
function CollapsibleTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx264(
    CollapsibleTrigger,
    {
      "data-slot": "collapsible-trigger",
      ...props
    }
  );
}
function CollapsibleContent2({
  transition = {
    type: "spring",
    stiffness: 500,
    damping: 35,
    mass: 0.6,
    opacity: { duration: 0.12, ease: "easeOut" }
  },
  ...props
}) {
  return /* @__PURE__ */ jsx264(
    CollapsibleContent,
    {
      "data-slot": "collapsible-content",
      transition,
      ...props
    }
  );
}

// src/components/ui/combobox.tsx
import * as React13 from "react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { ChevronDownIcon as ChevronDownIcon3, XIcon } from "lucide-react";

// src/lib/item-highlight.ts
var itemHighlight = "relative isolate before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:rounded-[inherit] before:opacity-0 before:transition-opacity before:duration-150";

// src/components/ui/input-group.tsx
import * as React12 from "react";
import { cva as cva6 } from "class-variance-authority";

// src/components/ui/input.tsx
import * as React10 from "react";
import { cva as cva5 } from "class-variance-authority";
import { Check, Copy } from "lucide-react";
import { AnimatePresence as AnimatePresence4, motion as motion5 } from "motion/react";
import { Fragment as Fragment2, jsx as jsx265, jsxs as jsxs245 } from "react/jsx-runtime";
var inputVariants = cva5(
  "w-full min-w-0 rounded-lg text-sm text-gray-600 caret-blue-500 dark:text-gray-100 shadow-[inset_0_1px_1px_rgb(0_0_0/0.075),inset_0_2px_4px_rgb(0_0_0/0.025),0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color,background-color] duration-200 outline-none selection:bg-blue-100 selection:text-inherit file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-white file:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.7),rgb(255_255_255/0))] file:bg-blend-screen file:px-3 file:font-medium file:text-secondary-foreground file:shadow-btn-secondary file:inset-shadow-btn-secondary file:transition-colors file:duration-150 hover:file:bg-secondary-hover active:file:bg-secondary-active dark:file:bg-gray-800 dark:file:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))] dark:file:text-gray-100 dark:file:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:file:bg-secondary-hover dark:active:file:bg-secondary-active placeholder:text-[rgb(175_176_179)] hover:placeholder:text-[rgb(138_140_145)] focus-visible:placeholder:text-[rgb(138_140_145)] focus-visible:bg-white dark:focus-visible:bg-input/30 focus-visible:[--input-border-color:var(--color-blue-500)] focus-visible:[--input-ring-color:rgb(69_117_205/0.15)] focus-visible:[--input-ring-size:3px] dark:focus-visible:[--input-border-color:var(--color-blue-500)] dark:focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:[--input-border-color:var(--color-destructive)] aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] aria-invalid:[--input-ring-size:3px] dark:selection:bg-blue-800/50 dark:aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
  {
    variants: {
      inputSize: {
        default: "h-10 px-4 py-0 leading-[16px] file:h-9 file:text-sm",
        sm: "h-8 px-3 py-0 leading-[16px] file:h-7 file:text-sm"
      },
      variant: {
        default: "bg-[rgb(87_90_100/0.04)] inset-shadow-input hover:bg-[rgb(87_90_100/0.06)] hover:[--input-border-color:rgb(43_49_67/0.125)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        // Border only: drops the default variant's inner depth shadows but keeps the focus ring. Focus uses :focus, not
        // :focus-visible, because Chrome doesn't apply :focus-visible to a clicked read-only input.
        copy: "bg-white inset-shadow-input-copy shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] hover:[--input-border-color:rgb(43_49_67/0.125)] focus:bg-white focus:[--input-border-color:var(--color-blue-500)] focus:[--input-ring-color:rgb(69_117_205/0.15)] focus:[--input-ring-size:3px] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)] dark:focus:bg-input/30 dark:focus:[--input-border-color:var(--color-blue-500)] dark:focus:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)]"
      }
    },
    defaultVariants: {
      inputSize: "default",
      variant: "default"
    }
  }
);
function Input({
  className,
  type,
  inputSize = "default",
  variant = "default",
  htmlSize,
  onFocus,
  showCopyButton = false,
  ...props
}) {
  const inputRef = React10.useRef(null);
  const handleFocus = React10.useCallback(
    (e) => {
      if (variant === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [variant, onFocus]
  );
  const inputElement = /* @__PURE__ */ jsx265(
    "input",
    {
      ref: inputRef,
      type,
      "data-slot": "input",
      "data-size": inputSize,
      "data-variant": variant,
      size: htmlSize,
      className: cn(
        inputVariants({ inputSize, variant }),
        type === "file" && "h-auto bg-transparent py-1 text-gray-500 inset-shadow-none shadow-none hover:[--input-border-color:initial] dark:bg-transparent dark:inset-shadow-none",
        showCopyButton && (inputSize === "sm" ? "pr-8" : "pr-9"),
        showCopyButton && "group-hover/input-copy:not-focus:[--input-border-color:var(--color-gray-300)] dark:group-hover/input-copy:not-focus:[--input-border-color:rgb(255_255_255/0.2)]",
        !showCopyButton && className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
  if (!showCopyButton) {
    return inputElement;
  }
  return /* @__PURE__ */ jsx265(InputCopyWrapper, { inputRef, inputSize, className, children: inputElement });
}
function InputCopyWrapper({
  inputRef,
  inputSize,
  multiline = false,
  className,
  children
}) {
  const [copied, setCopied] = React10.useState(false);
  const [ghostText, setGhostText] = React10.useState("");
  const [ghostKey, setGhostKey] = React10.useState(0);
  const timeoutRef = React10.useRef(null);
  const [fade, setFade] = React10.useState({ start: false, end: false });
  React10.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  const updateFade = React10.useCallback(() => {
    const el = inputRef.current;
    if (!el || multiline) return;
    const start = el.scrollLeft > 0;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setFade((prev) => prev.start === start && prev.end === end ? prev : { start, end });
  }, [inputRef, multiline]);
  React10.useLayoutEffect(updateFade);
  React10.useEffect(() => {
    const el = inputRef.current;
    if (!el || multiline) return;
    el.addEventListener("scroll", updateFade);
    const observer = typeof ResizeObserver === "undefined" ? void 0 : new ResizeObserver(updateFade);
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", updateFade);
      observer?.disconnect();
    };
  }, [inputRef, multiline, updateFade]);
  const handleCopy = async () => {
    const value = inputRef.current?.value ?? "";
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setGhostText(value.split("\n")[0]);
      setGhostKey((k) => k + 1);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };
  return /* @__PURE__ */ jsxs245(
    "div",
    {
      "data-slot": "input-copy-wrapper",
      className: cn(
        "group/input-copy relative inline-flex w-full items-center",
        // Matches the copy variant's background; override on non-standard backgrounds.
        "[--input-copy-fade:var(--color-white)] dark:[--input-copy-fade:color-mix(in_oklab,var(--color-input)_30%,var(--color-card))]",
        className
      ),
      children: [
        children,
        !multiline && /* @__PURE__ */ jsxs245(Fragment2, { children: [
          /* @__PURE__ */ jsx265(
            "div",
            {
              "aria-hidden": true,
              className: cn(
                "pointer-events-none absolute inset-y-px w-4 bg-linear-to-l from-transparent to-(--input-copy-fade) transition-opacity duration-150",
                inputSize === "sm" ? "left-3" : "left-4",
                fade.start ? "opacity-100" : "opacity-0"
              )
            }
          ),
          /* @__PURE__ */ jsx265(
            "div",
            {
              "aria-hidden": true,
              className: cn(
                "pointer-events-none absolute inset-y-px w-6 bg-linear-to-r from-transparent to-(--input-copy-fade) transition-opacity duration-150",
                inputSize === "sm" ? "right-8" : "right-9",
                fade.end ? "opacity-100" : "opacity-0"
              )
            }
          )
        ] }),
        ghostText && /* @__PURE__ */ jsx265(
          "div",
          {
            "aria-hidden": true,
            className: cn(
              "pointer-events-none absolute flex overflow-visible",
              multiline ? "top-1.5 items-start" : "inset-y-0 items-center",
              inputSize === "sm" ? "left-2 text-xs" : "left-3 text-sm"
            ),
            children: /* @__PURE__ */ jsx265("span", { className: "whitespace-pre", children: Array.from(ghostText).map((char, i) => /* @__PURE__ */ jsx265(
              motion5.span,
              {
                initial: { y: 0, opacity: 0, filter: "blur(0px)", rotate: 0 },
                animate: {
                  y: -28,
                  opacity: [0, 0.85, 0],
                  filter: "blur(2px)",
                  rotate: -3
                },
                transition: {
                  duration: 0.4,
                  delay: i * 0.012,
                  ease: "easeOut",
                  opacity: {
                    duration: 0.4,
                    delay: i * 0.012,
                    times: [0, 0.4, 1],
                    ease: "linear"
                  }
                },
                style: { display: "inline-block", whiteSpace: "pre" },
                children: char
              },
              i
            )) })
          },
          ghostKey
        ),
        /* @__PURE__ */ jsxs245(
          "div",
          {
            className: cn(
              // flex, not block: a block wrapper puts the inline-flex button on a text line box, which nudges it off center.
              "absolute flex",
              multiline ? inputSize === "sm" ? "top-1" : "top-1.5" : "top-1/2 -translate-y-1/2",
              // Same inset as the button's top/bottom gap: (40px - 24px) / 2 = 8px; sm is (32px - 24px) / 2 = 4px.
              inputSize === "sm" ? "right-1" : "right-2"
            ),
            children: [
              /* @__PURE__ */ jsx265(
                Button,
                {
                  type: "button",
                  variant: "muted",
                  size: "icon-xs",
                  "aria-label": "Copy",
                  onMouseDown: (e) => e.preventDefault(),
                  onClick: handleCopy,
                  className: cn(
                    "group/copy-button overflow-hidden rounded-[4px] shadow-none hover:shadow-none",
                    copied ? "bg-seafoam-50 hover:bg-seafoam-50" : "hover:bg-gray-50"
                  ),
                  children: /* @__PURE__ */ jsx265(AnimatePresence4, { mode: "wait", initial: false, children: copied ? /* @__PURE__ */ jsx265(
                    motion5.span,
                    {
                      initial: { scale: 0.5, opacity: 0 },
                      animate: { scale: 1, opacity: 1 },
                      exit: { scale: 0.5, opacity: 0 },
                      transition: { duration: 0.08, ease: "easeOut" },
                      className: "flex items-center justify-center",
                      children: /* @__PURE__ */ jsx265(Check, { className: "size-3.5 text-seafoam-700" })
                    },
                    "check"
                  ) : /* @__PURE__ */ jsx265(
                    motion5.span,
                    {
                      initial: { scale: 0.5, opacity: 0 },
                      animate: { scale: 1, opacity: 1 },
                      exit: { scale: 0.5, opacity: 0 },
                      transition: { duration: 0.08, ease: "easeOut" },
                      className: "flex items-center justify-center",
                      children: /* @__PURE__ */ jsx265(Copy, { className: "size-3.5 text-gray-400 transition-colors group-hover/copy-button:text-gray-600 dark:group-hover/copy-button:text-gray-200" })
                    },
                    "copy"
                  ) })
                }
              ),
              /* @__PURE__ */ jsx265(AnimatePresence4, { children: copied && /* @__PURE__ */ jsx265(
                motion5.div,
                {
                  role: "status",
                  "aria-live": "polite",
                  initial: { opacity: 0, y: 4 },
                  animate: { opacity: 1, y: 0 },
                  exit: { opacity: 0, y: 4 },
                  transition: { duration: 0.15, ease: "easeOut" },
                  className: "pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap rounded-lg bg-popover px-2 py-1 text-xs font-medium text-seafoam-700 shadow-md ring-1 ring-black/5 dark:ring-white/15",
                  children: "Copied"
                },
                "copied-label"
              ) })
            ]
          }
        )
      ]
    }
  );
}

// src/components/ui/textarea.tsx
import * as React11 from "react";
import { jsx as jsx266 } from "react/jsx-runtime";
function Textarea({
  className,
  variant = "default",
  showCopyButton = false,
  onFocus,
  ...props
}) {
  const textareaRef = React11.useRef(null);
  const handleFocus = React11.useCallback(
    (e) => {
      if (variant === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [variant, onFocus]
  );
  const textarea = /* @__PURE__ */ jsx266(
    "textarea",
    {
      ref: textareaRef,
      "data-slot": "textarea",
      "data-variant": variant,
      className: cn(
        "flex field-sizing-content min-h-16 w-full rounded-lg px-4 py-3 text-sm text-gray-600 caret-blue-500 leading-5 dark:text-gray-100 shadow-[inset_0_1px_1px_rgb(0_0_0/0.075),inset_0_2px_4px_rgb(0_0_0/0.025),0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color,background-color] duration-200 outline-none selection:bg-blue-100 selection:text-inherit placeholder:text-[rgb(175_176_179)] hover:placeholder:text-[rgb(138_140_145)] focus-visible:placeholder:text-[rgb(138_140_145)] focus-visible:bg-white dark:focus-visible:bg-input/30 focus-visible:[--input-border-color:var(--color-blue-500)] focus-visible:[--input-ring-color:rgb(69_117_205/0.15)] focus-visible:[--input-ring-size:3px] dark:focus-visible:[--input-border-color:var(--color-blue-500)] dark:focus-visible:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:[--input-border-color:var(--color-destructive)] aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] aria-invalid:[--input-ring-size:3px] dark:selection:bg-blue-800/50 dark:aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        variant === "copy" ? "bg-white inset-shadow-input-copy hover:[--input-border-color:rgb(43_49_67/0.125)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]" : "bg-[rgb(87_90_100/0.04)] inset-shadow-input hover:bg-[rgb(87_90_100/0.06)] hover:[--input-border-color:rgb(43_49_67/0.125)] dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        showCopyButton && "pr-9 group-hover/input-copy:not-focus:[--input-border-color:var(--color-gray-300)] dark:group-hover/input-copy:not-focus:[--input-border-color:rgb(255_255_255/0.2)]",
        !showCopyButton && className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
  if (!showCopyButton) {
    return textarea;
  }
  return /* @__PURE__ */ jsx266(InputCopyWrapper, { inputRef: textareaRef, inputSize: "sm", multiline: true, className, children: textarea });
}

// src/components/ui/input-group.tsx
import { jsx as jsx267 } from "react/jsx-runtime";
function InputGroup({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx267(
    "div",
    {
      "data-slot": "input-group",
      "data-variant": variant,
      role: "group",
      className: cn(
        "group/input-group relative flex w-full items-center rounded-lg shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color,background-color] duration-150 outline-none dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)]",
        // The default variant takes a plain Input's resting and hover fills across the whole group.
        variant === "copy" ? "bg-white inset-shadow-input-copy hover:[--input-border-color:var(--color-gray-300)]" : "bg-[rgb(87_90_100/0.04)] inset-shadow-input hover:bg-[rgb(87_90_100/0.06)] hover:[--input-border-color:var(--color-gray-300)]",
        "h-9 min-w-0 has-[>textarea]:h-auto",
        // Variants based on alignment.
        "has-[>[data-align=inline-start]]:[&>input]:pl-2",
        "has-[>[data-align=inline-start]_[data-slot=input-group-text]]:[&>input]:pl-1",
        "has-[>[data-align=inline-end]]:[&>input]:pr-2",
        "has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>[data-align=block-start]]:[&>input]:pb-3",
        "has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-end]]:[&>input]:pt-3",
        // Focus state. The group takes the white fill a focused Input has.
        "has-[[data-slot=input-group-control]:focus-visible]:bg-white dark:has-[[data-slot=input-group-control]:focus-visible]:bg-input/30 has-[[data-slot=input-group-control]:focus-visible]:[--input-border-color:var(--color-blue-500)] has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-size:3px] dark:has-[[data-slot=input-group-control]:focus-visible]:[--input-border-color:var(--color-blue-500)] dark:has-[[data-slot=input-group-control]:focus-visible]:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)]",
        // Error state.
        "has-[[data-slot][aria-invalid=true]]:[--input-border-color:var(--color-destructive)] has-[[data-slot][aria-invalid=true]]:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] has-[[data-slot][aria-invalid=true]]:[--input-ring-size:3px] dark:has-[[data-slot][aria-invalid=true]]:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        className
      ),
      ...props
    }
  );
}
var inputGroupAddonVariants = cva6(
  "flex h-auto cursor-text items-center justify-center gap-2 py-1.5 text-sm font-normal text-gray-400 select-none group-data-[disabled=true]/input-group:opacity-50 [&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]",
        "inline-end": "order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]",
        "block-start": "order-first w-full justify-start px-3 pt-3 group-has-[>input]/input-group:pt-2.5 [.border-b]:pb-3",
        "block-end": "order-last w-full justify-start px-3 pb-3 group-has-[>input]/input-group:pb-2.5 [.border-t]:pt-3"
      }
    },
    defaultVariants: {
      align: "inline-start"
    }
  }
);
function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}) {
  return /* @__PURE__ */ jsx267(
    "div",
    {
      role: "group",
      "data-slot": "input-group-addon",
      "data-align": align,
      className: cn(inputGroupAddonVariants({ align }), className),
      onClick: (e) => {
        if (e.target.closest("button")) {
          return;
        }
        e.currentTarget.parentElement?.querySelector("input")?.focus();
      },
      ...props
    }
  );
}
var inputGroupButtonVariants = cva6(
  "flex items-center gap-2 text-sm shadow-none",
  {
    variants: {
      size: {
        xs: "h-6 gap-1 rounded-[calc(var(--radius-lg)-5px)] px-2 has-[>svg]:px-2 [&>svg:not([class*='size-'])]:size-3.5",
        sm: "h-8 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2.5",
        "icon-xs": "size-6 rounded-[calc(var(--radius-lg)-5px)] p-0 has-[>svg]:p-0",
        "icon-sm": "size-8 p-0 has-[>svg]:p-0"
      }
    },
    defaultVariants: {
      size: "xs"
    }
  }
);
function InputGroupButton({
  className,
  type = "button",
  variant = "muted",
  size = "xs",
  ...props
}) {
  return /* @__PURE__ */ jsx267(
    Button,
    {
      type,
      "data-size": size,
      variant,
      className: cn(inputGroupButtonVariants({ size }), className),
      ...props
    }
  );
}
function InputGroupText({ className, ...props }) {
  return /* @__PURE__ */ jsx267(
    "span",
    {
      "data-slot": "input-group-text",
      className: cn(
        "flex items-center gap-2 text-sm text-gray-400 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props
    }
  );
}
function InputGroupInput({
  className,
  onFocus,
  ...props
}) {
  const handleFocus = React12.useCallback(
    (e) => {
      const group = e.currentTarget.closest("[data-slot=input-group]");
      if (group?.getAttribute("data-variant") === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [onFocus]
  );
  return /* @__PURE__ */ jsx267(
    Input,
    {
      "data-slot": "input-group-control",
      className: cn(
        "flex-1 rounded-none border-0 bg-transparent shadow-none inset-shadow-none hover:bg-transparent hover:inset-shadow-none focus-visible:bg-transparent focus-visible:ring-0 focus-visible:shadow-none dark:bg-transparent dark:hover:bg-transparent dark:focus-visible:bg-transparent",
        className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
}
function InputGroupTextarea({
  className,
  onFocus,
  ...props
}) {
  const handleFocus = React12.useCallback(
    (e) => {
      const group = e.currentTarget.closest("[data-slot=input-group]");
      if (group?.getAttribute("data-variant") === "copy") {
        e.currentTarget.select();
      }
      onFocus?.(e);
    },
    [onFocus]
  );
  return /* @__PURE__ */ jsx267(
    Textarea,
    {
      "data-slot": "input-group-control",
      className: cn(
        "flex-1 resize-none rounded-none border-0 bg-transparent py-3 shadow-none inset-shadow-none hover:inset-shadow-none focus-visible:ring-0 focus-visible:shadow-none dark:bg-transparent",
        className
      ),
      onFocus: handleFocus,
      ...props
    }
  );
}

// src/components/ui/combobox.tsx
import { jsx as jsx268, jsxs as jsxs246 } from "react/jsx-runtime";
var ComboboxValueContext = React13.createContext(void 0);
function Combobox(props) {
  const {
    value: controlledValue,
    defaultValue,
    onValueChange,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = React13.useState(controlledValue ?? defaultValue);
  React13.useEffect(() => {
    if (controlledValue !== void 0) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);
  const handleValueChange = React13.useCallback(
    (newValue, eventDetails) => {
      setInternalValue(newValue);
      onValueChange?.(newValue, eventDetails);
    },
    [onValueChange]
  );
  return /* @__PURE__ */ jsx268(ComboboxValueContext.Provider, { value: internalValue, children: /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Root,
    {
      value: controlledValue,
      defaultValue,
      onValueChange: handleValueChange,
      ...rest
    }
  ) });
}
function ComboboxValue({ ...props }) {
  return /* @__PURE__ */ jsx268(ComboboxPrimitive.Value, { "data-slot": "combobox-value", ...props });
}
function ComboboxTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs246(
    ComboboxPrimitive.Trigger,
    {
      "data-slot": "combobox-trigger",
      className: cn("[&_svg:not([class*='size-'])]:size-4", className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx268(
          ChevronDownIcon3,
          {
            "data-slot": "combobox-trigger-icon",
            className: "pointer-events-none size-4 text-muted-foreground"
          }
        )
      ]
    }
  );
}
function ComboboxClear({ className, ...props }) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Clear,
    {
      "data-slot": "combobox-clear",
      render: /* @__PURE__ */ jsx268(InputGroupButton, { variant: "muted", size: "icon-xs" }),
      className: cn(className),
      ...props,
      children: /* @__PURE__ */ jsx268(XIcon, { className: "pointer-events-none" })
    }
  );
}
function ComboboxInput({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  ...props
}) {
  return /* @__PURE__ */ jsxs246(InputGroup, { className: cn("w-auto", className), children: [
    /* @__PURE__ */ jsx268(
      ComboboxPrimitive.Input,
      {
        render: /* @__PURE__ */ jsx268(InputGroupInput, { disabled }),
        ...props
      }
    ),
    /* @__PURE__ */ jsxs246(InputGroupAddon, { align: "inline-end", children: [
      showTrigger && /* @__PURE__ */ jsx268(
        InputGroupButton,
        {
          size: "icon-xs",
          variant: "muted",
          asChild: true,
          "data-slot": "input-group-button",
          className: "group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent",
          disabled,
          children: /* @__PURE__ */ jsx268(ComboboxTrigger, {})
        }
      ),
      showClear && /* @__PURE__ */ jsx268(ComboboxClear, { disabled })
    ] }),
    children
  ] });
}
function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}) {
  return /* @__PURE__ */ jsx268(ComboboxPrimitive.Portal, { children: /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Positioner,
    {
      side,
      sideOffset,
      align,
      alignOffset,
      anchor,
      className: "isolate z-50",
      children: /* @__PURE__ */ jsx268(
        ComboboxPrimitive.Popup,
        {
          "data-slot": "combobox-content",
          "data-chips": !!anchor,
          className: cn(
            "group/combobox-content relative max-h-96 w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 duration-100 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[chips=true]:min-w-(--anchor-width) data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:mb-1 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 *:data-[slot=input-group]:shadow-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          ),
          ...props
        }
      )
    }
  ) });
}
function ComboboxList({ className, ...props }) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.List,
    {
      "data-slot": "combobox-list",
      className: cn(
        "max-h-[min(calc(--spacing(96)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto data-empty:p-0",
        className
      ),
      ...props
    }
  );
}
function ComboboxItem({
  className,
  children,
  value,
  ...props
}) {
  const currentValue = React13.useContext(ComboboxValueContext);
  const isChecked = Array.isArray(currentValue) ? currentValue.includes(value) : currentValue === value;
  return /* @__PURE__ */ jsxs246(
    ComboboxPrimitive.Item,
    {
      "data-slot": "combobox-item",
      value,
      className: cn(
        itemHighlight,
        "flex w-full cursor-default items-center gap-2 rounded-md py-2 pr-8 pl-3 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent data-highlighted:before:opacity-100 data-highlighted:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx268(
          "span",
          {
            "data-slot": "combobox-item-indicator",
            className: "pointer-events-none absolute right-2 flex size-4 items-center justify-center",
            children: /* @__PURE__ */ jsx268(AnimatedCheckIcon, { checked: isChecked })
          }
        )
      ]
    }
  );
}
function ComboboxGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Group,
    {
      "data-slot": "combobox-group",
      className: cn(className),
      ...props
    }
  );
}
function ComboboxLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.GroupLabel,
    {
      "data-slot": "combobox-label",
      className: cn(
        "px-2 py-1.5 text-xs font-medium text-gray-400 pointer-coarse:px-3 pointer-coarse:py-2 pointer-coarse:text-sm",
        className
      ),
      ...props
    }
  );
}
function ComboboxCollection({ ...props }) {
  return /* @__PURE__ */ jsx268(ComboboxPrimitive.Collection, { "data-slot": "combobox-collection", ...props });
}
function ComboboxEmpty({ className, ...props }) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Empty,
    {
      "data-slot": "combobox-empty",
      className: cn(
        "hidden w-full justify-center py-2 text-center text-sm text-muted-foreground group-has-[[data-slot=combobox-list][data-empty]]/combobox-content:flex",
        className
      ),
      ...props
    }
  );
}
function ComboboxSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Separator,
    {
      "data-slot": "combobox-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function ComboboxChips({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Chips,
    {
      "data-slot": "combobox-chips",
      className: cn(
        "flex min-h-9 flex-wrap items-center gap-1.5 rounded-lg bg-gray-25 bg-clip-padding px-2.5 py-1.5 text-sm inset-shadow-input shadow-[0_0_0_var(--input-ring-size)_var(--input-ring-color)] transition-[--input-border-color,--input-ring-color,--input-ring-size,color] duration-150 hover:[--input-border-color:var(--color-gray-300)] focus-within:[--input-border-color:var(--color-blue-500)] focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.1)] focus-within:[--input-ring-size:3px] has-aria-invalid:[--input-border-color:var(--color-destructive)] has-aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.2)] has-aria-invalid:[--input-ring-size:3px] has-data-[slot=combobox-chip]:px-1.5 dark:bg-input/30 dark:[--input-border-color:rgb(255_255_255/0.1)] dark:inset-shadow-[0_0_0_1px_var(--input-border-color)] dark:hover:[--input-border-color:rgb(255_255_255/0.2)] dark:focus-within:[--input-border-color:var(--color-blue-500)] dark:focus-within:[--input-ring-color:oklch(0.541_0.167_262.9/0.25)] dark:has-aria-invalid:[--input-ring-color:oklch(0.469_0.173_10.9/0.4)]",
        className
      ),
      ...props
    }
  );
}
function ComboboxChip({
  className,
  children,
  showRemove = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs246(
    ComboboxPrimitive.Chip,
    {
      "data-slot": "combobox-chip",
      className: cn(
        "flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm bg-muted px-1.5 text-xs font-normal whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0",
        className
      ),
      ...props,
      children: [
        children,
        showRemove && /* @__PURE__ */ jsx268(
          ComboboxPrimitive.ChipRemove,
          {
            render: /* @__PURE__ */ jsx268(Button, { variant: "muted", size: "icon-xs" }),
            className: "-ml-1 opacity-50 hover:opacity-100",
            "data-slot": "combobox-chip-remove",
            children: /* @__PURE__ */ jsx268(XIcon, { className: "pointer-events-none" })
          }
        )
      ]
    }
  );
}
function ComboboxChipsInput({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx268(
    ComboboxPrimitive.Input,
    {
      "data-slot": "combobox-chip-input",
      className: cn("min-w-16 flex-1 outline-none", className),
      ...props
    }
  );
}
function useComboboxAnchor() {
  return React13.useRef(null);
}

// src/components/ui/command.tsx
import { Command as CommandPrimitive } from "cmdk";
import { SearchIcon } from "lucide-react";

// src/components/ui/dialog.tsx
import { XIcon as XIcon2 } from "lucide-react";

// src/components/animate-ui/primitives/radix/dialog.tsx
import { Dialog as DialogPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence5, motion as motion6 } from "motion/react";
import { jsx as jsx269 } from "react/jsx-runtime";
var [DialogProvider, useDialog] = getStrictContext("DialogContext");
function Dialog(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx269(DialogProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx269(
    DialogPrimitive.Root,
    {
      "data-slot": "dialog",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function DialogTrigger(props) {
  return /* @__PURE__ */ jsx269(DialogPrimitive.Trigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal(props) {
  return /* @__PURE__ */ jsx269(DialogPrimitive.Portal, { "data-slot": "dialog-portal", forceMount: true, ...props });
}
function DialogOverlay({
  transition = { duration: 0.15 },
  ...props
}) {
  const { isOpen } = useDialog();
  return /* @__PURE__ */ jsx269(AnimatePresence5, { children: isOpen && /* @__PURE__ */ jsx269(DialogPrimitive.Overlay, { "data-slot": "dialog-overlay", asChild: true, forceMount: true, children: /* @__PURE__ */ jsx269(
    motion6.div,
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition,
      ...props
    },
    "dialog-overlay"
  ) }) });
}
function DialogContent({
  from: _from,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onInteractOutside,
  transition = { duration: 0.1 },
  style,
  ...props
}) {
  const { isOpen } = useDialog();
  return /* @__PURE__ */ jsx269(AnimatePresence5, { children: isOpen && /* @__PURE__ */ jsx269(
    DialogPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      onOpenAutoFocus,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
      children: /* @__PURE__ */ jsx269(
        motion6.div,
        {
          "data-slot": "dialog-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dialog-content"
      )
    }
  ) });
}
function DialogClose(props) {
  return /* @__PURE__ */ jsx269(DialogPrimitive.Close, { "data-slot": "dialog-close", ...props });
}
function DialogTitle(props) {
  return /* @__PURE__ */ jsx269(DialogPrimitive.Title, { "data-slot": "dialog-title", ...props });
}
function DialogDescription(props) {
  return /* @__PURE__ */ jsx269(DialogPrimitive.Description, { "data-slot": "dialog-description", ...props });
}

// src/components/ui/dialog.tsx
import { jsx as jsx270, jsxs as jsxs247 } from "react/jsx-runtime";
function Dialog2({
  ...props
}) {
  return /* @__PURE__ */ jsx270(Dialog, { ...props });
}
function DialogTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx270(DialogTrigger, { "data-slot": "dialog-trigger", ...props });
}
function DialogPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx270(DialogPortal, { "data-slot": "dialog-portal", ...props });
}
function DialogClose2({
  ...props
}) {
  return /* @__PURE__ */ jsx270(DialogClose, { "data-slot": "dialog-close", ...props });
}
function DialogOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx270(
    DialogOverlay,
    {
      "data-slot": "dialog-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
var dialogCardClassName = "z-50 grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg bg-background p-6 shadow-lg ring-1 ring-black/5 duration-200 outline-none dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] sm:max-w-lg";
function DialogContent2({
  className,
  children,
  showCloseButton = true,
  overlayScroll = false,
  ...props
}) {
  const closeButton = showCloseButton && /* @__PURE__ */ jsxs247(
    DialogClose,
    {
      "data-slot": "dialog-close",
      className: "absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      children: [
        /* @__PURE__ */ jsx270(XIcon2, {}),
        /* @__PURE__ */ jsx270("span", { className: "sr-only", children: "Close" })
      ]
    }
  );
  if (overlayScroll) {
    return /* @__PURE__ */ jsx270(OverlayScrollDialogContent, { className, closeButton, ...props, children });
  }
  return /* @__PURE__ */ jsxs247(DialogPortal2, { "data-slot": "dialog-portal", children: [
    /* @__PURE__ */ jsx270(DialogOverlay2, {}),
    /* @__PURE__ */ jsxs247(
      DialogContent,
      {
        "data-slot": "dialog-content",
        className: cn(
          "fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]",
          dialogCardClassName,
          className
        ),
        ...props,
        children: [
          children,
          closeButton
        ]
      }
    )
  ] });
}
function OverlayScrollDialogContent({
  className,
  children,
  closeButton,
  ...props
}) {
  return /* @__PURE__ */ jsx270(DialogPortal2, { "data-slot": "dialog-portal", children: /* @__PURE__ */ jsx270(DialogOverlay2, { className: "overflow-y-auto", children: /* @__PURE__ */ jsx270("div", { className: "flex min-h-full items-center justify-center p-4 sm:p-6", children: /* @__PURE__ */ jsxs247(
    DialogContent,
    {
      "data-slot": "dialog-content",
      className: cn("relative", dialogCardClassName, className),
      ...props,
      children: [
        children,
        closeButton
      ]
    }
  ) }) }) });
}
function DialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx270(
    "div",
    {
      "data-slot": "dialog-header",
      className: cn("flex flex-col gap-2 text-center sm:text-left", className),
      ...props
    }
  );
}
function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs247(
    "div",
    {
      "data-slot": "dialog-footer",
      className: cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      ),
      ...props,
      children: [
        children,
        showCloseButton && /* @__PURE__ */ jsx270(DialogClose, { asChild: true, children: /* @__PURE__ */ jsx270(Button, { variant: "muted", children: "Close" }) })
      ]
    }
  );
}
function DialogTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx270(
    DialogTitle,
    {
      "data-slot": "dialog-title",
      className: cn("text-base leading-5 font-medium", className),
      ...props
    }
  );
}
function DialogDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx270(
    DialogDescription,
    {
      "data-slot": "dialog-description",
      className: cn("text-sm leading-5 text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/command.tsx
import { jsx as jsx271, jsxs as jsxs248 } from "react/jsx-runtime";
function Command({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive,
    {
      "data-slot": "command",
      className: cn(
        "flex h-full w-full flex-col overflow-hidden rounded-lg bg-popover text-secondary-foreground",
        className
      ),
      ...props
    }
  );
}
function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  className,
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs248(Dialog2, { ...props, children: [
    /* @__PURE__ */ jsxs248(DialogHeader, { className: "sr-only", children: [
      /* @__PURE__ */ jsx271(DialogTitle2, { children: title }),
      /* @__PURE__ */ jsx271(DialogDescription2, { children: description })
    ] }),
    /* @__PURE__ */ jsx271(
      DialogContent2,
      {
        className: cn("overflow-hidden p-0", className),
        showCloseButton,
        children: /* @__PURE__ */ jsx271(Command, { className: "**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-gray-400 [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-3 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5", children })
      }
    )
  ] });
}
function CommandInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs248(
    "div",
    {
      "data-slot": "command-input-wrapper",
      className: "flex h-9 items-center gap-2 border-b border-gray-100 px-3 dark:border-white/[0.08]",
      children: [
        /* @__PURE__ */ jsx271(SearchIcon, { className: "size-4 shrink-0 opacity-50" }),
        /* @__PURE__ */ jsx271(
          CommandPrimitive.Input,
          {
            "data-slot": "command-input",
            className: cn(
              "flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
              className
            ),
            ...props
          }
        )
      ]
    }
  );
}
function CommandList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive.List,
    {
      "data-slot": "command-list",
      className: cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className
      ),
      ...props
    }
  );
}
function CommandEmpty({
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive.Empty,
    {
      "data-slot": "command-empty",
      className: "py-6 text-center text-sm",
      ...props
    }
  );
}
function CommandGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive.Group,
    {
      "data-slot": "command-group",
      className: cn(
        "overflow-hidden p-1 text-secondary-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-gray-400",
        className
      ),
      ...props
    }
  );
}
function CommandSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive.Separator,
    {
      "data-slot": "command-separator",
      className: cn("-mx-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function CommandItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    CommandPrimitive.Item,
    {
      "data-slot": "command-item",
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:text-accent-foreground data-[selected=true]:before:opacity-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
function CommandShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx271(
    "span",
    {
      "data-slot": "command-shortcut",
      className: cn("ml-auto text-xs font-normal text-gray-400", className),
      ...props
    }
  );
}

// src/components/ui/context-menu.tsx
import { ChevronRightIcon as ChevronRightIcon2, CircleIcon } from "lucide-react";
import { ContextMenu as ContextMenuPrimitive } from "radix-ui";
import { jsx as jsx272, jsxs as jsxs249 } from "react/jsx-runtime";
function ContextMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Root, { "data-slot": "context-menu", ...props });
}
function ContextMenuTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Trigger, { "data-slot": "context-menu-trigger", ...props });
}
function ContextMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Group, { "data-slot": "context-menu-group", ...props });
}
function ContextMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Portal, { "data-slot": "context-menu-portal", ...props });
}
function ContextMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Sub, { "data-slot": "context-menu-sub", ...props });
}
function ContextMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.RadioGroup,
    {
      "data-slot": "context-menu-radio-group",
      ...props
    }
  );
}
function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs249(
    ContextMenuPrimitive.SubTrigger,
    {
      "data-slot": "context-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[inset]:pl-8 data-[state=open]:before:opacity-100 data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx272(ChevronRightIcon2, { className: "ml-auto" })
      ]
    }
  );
}
function ContextMenuSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.SubContent,
    {
      "data-slot": "context-menu-sub-content",
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}
function ContextMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx272(ContextMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.Content,
    {
      "data-slot": "context-menu-content",
      className: cn(
        "z-50 max-h-(--radix-context-menu-content-available-height) min-w-[8rem] origin-(--radix-context-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function ContextMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.Item,
    {
      "data-slot": "context-menu-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:before:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:before:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=context-menu-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=context-menu-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs249(
    ContextMenuPrimitive.CheckboxItem,
    {
      "data-slot": "context-menu-checkbox-item",
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx272("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx272(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function ContextMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs249(
    ContextMenuPrimitive.RadioItem,
    {
      "data-slot": "context-menu-radio-item",
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx272("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx272(ContextMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx272(CircleIcon, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function ContextMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.Label,
    {
      "data-slot": "context-menu-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function ContextMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    ContextMenuPrimitive.Separator,
    {
      "data-slot": "context-menu-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function ContextMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx272(
    "span",
    {
      "data-slot": "context-menu-shortcut",
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/data-table/cells/amount-cell.tsx
import * as React14 from "react";
import { NumericFormat } from "react-number-format";

// src/components/ui/data-table/cells/cell.tsx
import { jsx as jsx273 } from "react/jsx-runtime";
var inlineOutlineClassName = "before:pointer-events-none before:absolute before:inset-0 before:rounded-(--table-cell-border-radius) before:shadow-[inset_1px_0_0_var(--data-table-edge-left,transparent),inset_-1px_0_0_var(--data-table-edge-right,transparent),inset_0_-1px_0_var(--data-table-edge-bottom,transparent)] after:pointer-events-none after:absolute after:top-[var(--data-table-outline-top,-1px)] after:right-[var(--data-table-outline-right,-1px)] after:bottom-[var(--data-table-outline-bottom,-1px)] after:left-[var(--data-table-outline-left,-1px)] after:rounded-(--table-cell-border-radius) after:opacity-[var(--input-inline-outline,0)] after:shadow-[inset_0_0_0_1px_var(--color-primary)] after:transition-opacity after:duration-200";
var inlineInputWrapperClassName = cn(
  "relative flex w-full cursor-text items-center rounded-(--table-cell-border-radius) hover:[--input-inline-outline:1] focus-within:[--input-inline-outline:1] has-disabled:cursor-not-allowed has-disabled:opacity-75",
  inlineOutlineClassName
);
var inlineInputClassName = "h-10 rounded-(--table-cell-border-radius) bg-transparent px-4 py-3 font-normal leading-4 text-foreground caret-primary shadow-none inset-shadow-none placeholder:text-muted-foreground hover:bg-transparent hover:placeholder:text-muted-foreground focus-visible:bg-transparent focus-visible:placeholder:text-muted-foreground disabled:opacity-100 dark:bg-transparent dark:text-foreground dark:inset-shadow-none dark:focus-visible:bg-transparent";
function DataTableCell({ className, ...props }) {
  return /* @__PURE__ */ jsx273(
    "div",
    {
      "data-slot": "data-table-cell-content",
      className: cn(
        "relative flex h-10 w-full items-center gap-2 truncate px-4 py-2 text-sm leading-6",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/data-table/cells/amount-cell.tsx
import { jsx as jsx274, jsxs as jsxs250 } from "react/jsx-runtime";
function DataTableAmountInput({
  value,
  fractionDigits,
  symbol,
  onValueChange,
  placeholder,
  disabled = false,
  isBold = false,
  className
}) {
  const [draft, setDraft] = React14.useState(
    () => toAmountDraft(value, fractionDigits)
  );
  const [synced, setSynced] = React14.useState({ value, fractionDigits });
  if (!Object.is(synced.value, value) || synced.fractionDigits !== fractionDigits) {
    setSynced({ value, fractionDigits });
    if (synced.fractionDigits !== fractionDigits || parseAmountDraft(draft, fractionDigits) !== (value ?? void 0)) {
      setDraft(toAmountDraft(value, fractionDigits));
    }
  }
  return /* @__PURE__ */ jsx274(
    "label",
    {
      "data-table-input": "",
      className: cn(inlineInputWrapperClassName, className),
      children: /* @__PURE__ */ jsx274(
        NumericFormat,
        {
          "data-slot": "data-table-amount-input",
          value: draft,
          valueIsNumericString: true,
          onValueChange: (values, sourceInfo) => {
            setDraft(values.value);
            if (sourceInfo.source === "event") {
              onValueChange?.(parseAmountDraft(values.value, fractionDigits));
            }
          },
          prefix: symbol,
          thousandSeparator: ",",
          decimalScale: fractionDigits,
          fixedDecimalScale: true,
          inputMode: "decimal",
          placeholder: placeholder ?? `${symbol}0${fractionDigits > 0 ? `.${"0".repeat(fractionDigits)}` : ""}`,
          disabled,
          className: cn(
            inputVariants({ inputSize: "sm" }),
            inlineInputClassName,
            "tabular-nums",
            isBold && "font-medium"
          )
        }
      )
    }
  );
}
function DataTableAmountCell({
  value,
  currency,
  isEditable = false,
  onValueChange,
  isColorized = false,
  isBold = false,
  note,
  placeholder,
  disabled,
  className
}) {
  const { formatter, fractionDigits, symbol } = getAmountFormat(currency);
  if (isEditable) {
    return /* @__PURE__ */ jsx274(
      DataTableAmountInput,
      {
        value,
        fractionDigits,
        symbol,
        onValueChange,
        placeholder,
        disabled,
        isBold,
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs250(
    DataTableCell,
    {
      className: cn(
        "tabular-nums",
        isColorized && value != null && value > 0 ? "text-success" : "text-foreground",
        isBold && "font-medium",
        className
      ),
      children: [
        value != null && formatter.format(value / 10 ** fractionDigits),
        note != null && /* @__PURE__ */ jsx274("span", { className: "truncate text-xs text-muted-foreground", children: note })
      ]
    }
  );
}

// src/components/ui/data-table/header/column-selector.tsx
import { EllipsisIcon } from "lucide-react";

// src/components/ui/data-table/data-table.tsx
import * as React18 from "react";
import { ChevronLeftIcon as ChevronLeftIcon4, ChevronRightIcon as ChevronRightIcon5 } from "lucide-react";

// src/components/ui/data-table/footer/pagination.tsx
import * as React16 from "react";
import { ChevronLeftIcon as ChevronLeftIcon3, ChevronRightIcon as ChevronRightIcon4 } from "lucide-react";

// src/components/ui/pagination.tsx
import {
  ChevronLeftIcon as ChevronLeftIcon2,
  ChevronRightIcon as ChevronRightIcon3,
  MoreHorizontalIcon
} from "lucide-react";
import { jsx as jsx275, jsxs as jsxs251 } from "react/jsx-runtime";
function Pagination({ className, ...props }) {
  return /* @__PURE__ */ jsx275(
    "nav",
    {
      role: "navigation",
      "aria-label": "pagination",
      "data-slot": "pagination",
      className: cn("mx-auto flex w-full justify-center", className),
      ...props
    }
  );
}
function PaginationContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx275(
    "ul",
    {
      "data-slot": "pagination-content",
      className: cn("flex flex-row items-center gap-1", className),
      ...props
    }
  );
}
function PaginationItem({ ...props }) {
  return /* @__PURE__ */ jsx275("li", { "data-slot": "pagination-item", ...props });
}
function PaginationLink({
  className,
  isActive,
  size = "icon",
  ...props
}) {
  return /* @__PURE__ */ jsx275(
    "a",
    {
      "aria-current": isActive ? "page" : void 0,
      "data-slot": "pagination-link",
      "data-active": isActive,
      className: cn(
        buttonVariants({
          variant: isActive ? "secondary" : "muted",
          size
        }),
        className
      ),
      ...props
    }
  );
}
function PaginationPrevious({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs251(
    PaginationLink,
    {
      "aria-label": "Go to previous page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pl-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx275(ChevronLeftIcon2, {}),
        /* @__PURE__ */ jsx275("span", { className: "hidden sm:block", children: "Previous" })
      ]
    }
  );
}
function PaginationNext({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs251(
    PaginationLink,
    {
      "aria-label": "Go to next page",
      size: "default",
      className: cn("gap-1 px-2.5 sm:pr-2.5", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx275("span", { className: "hidden sm:block", children: "Next" }),
        /* @__PURE__ */ jsx275(ChevronRightIcon3, {})
      ]
    }
  );
}
function PaginationEllipsis({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs251(
    "span",
    {
      "aria-hidden": true,
      "data-slot": "pagination-ellipsis",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx275(MoreHorizontalIcon, { className: "size-4" }),
        /* @__PURE__ */ jsx275("span", { className: "sr-only", children: "More pages" })
      ]
    }
  );
}

// src/components/ui/select.tsx
import * as React15 from "react";
import { cva as cva7 } from "class-variance-authority";
import { ChevronDownIcon as ChevronDownIcon4, ChevronUpIcon } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import { jsx as jsx276, jsxs as jsxs252 } from "react/jsx-runtime";
var selectTriggerVariants = cva7(
  "flex w-fit cursor-pointer items-center justify-between gap-2 rounded-lg border-none px-3 py-2 text-sm font-medium text-gray-600 whitespace-nowrap transition-[color,box-shadow] duration-150 outline-none focus-visible:ring-[3px] focus-visible:ring-blue-500/15 focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:ring-destructive/20 data-[placeholder]:text-gray-400 data-[size=default]:h-9 data-[size=sm]:h-8 data-[size=lg]:h-10 data-[size=lg]:px-4 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:focus-visible:shadow-[0_0_0_1px_var(--color-blue-500)] dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
  {
    variants: {
      variant: {
        default: "bg-white shadow-btn-secondary hover:shadow-btn-secondary-hover dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.25)]",
        outline: "bg-transparent shadow-[0_0_0_1px_rgb(0_0_0/0.08)] hover:shadow-[0_0_0_1px_rgb(0_0_0/0.16)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.2)]",
        // Flat secondary halo, no drop layer — reads like a card rather than a button.
        card: "bg-white shadow-btn-secondary-flat dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)]"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var SelectValueContext = React15.createContext(void 0);
function Select({
  value: controlledValue,
  defaultValue,
  onValueChange,
  ...props
}) {
  const [internalValue, setInternalValue] = React15.useState(
    controlledValue ?? defaultValue
  );
  React15.useEffect(() => {
    if (controlledValue !== void 0) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);
  const handleValueChange = React15.useCallback(
    (newValue) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange]
  );
  return /* @__PURE__ */ jsx276(SelectValueContext.Provider, { value: internalValue, children: /* @__PURE__ */ jsx276(
    SelectPrimitive.Root,
    {
      "data-slot": "select",
      value: controlledValue,
      defaultValue,
      onValueChange: handleValueChange,
      ...props
    }
  ) });
}
function SelectGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx276(SelectPrimitive.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({
  ...props
}) {
  return /* @__PURE__ */ jsx276(SelectPrimitive.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs252(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      "data-size": size,
      "data-variant": variant,
      className: cn(selectTriggerVariants({ variant }), className),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx276(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx276(ChevronDownIcon4, { className: "size-4 text-muted-foreground" }) })
      ]
    }
  );
}
function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  ...props
}) {
  return /* @__PURE__ */ jsx276(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs252(
    SelectPrimitive.Content,
    {
      "data-slot": "select-content",
      className: cn(
        "relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      align,
      ...props,
      children: [
        /* @__PURE__ */ jsx276(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx276(
          SelectPrimitive.Viewport,
          {
            className: cn(
              "p-1",
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx276(SelectScrollDownButton, {})
      ]
    }
  ) });
}
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx276(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn("px-2 py-1.5 text-xs font-medium text-gray-400", className),
      ...props
    }
  );
}
function SelectItem({
  className,
  children,
  value,
  ...props
}) {
  const currentValue = React15.useContext(SelectValueContext);
  const isChecked = currentValue === value;
  return /* @__PURE__ */ jsxs252(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      value,
      className: cn(
        itemHighlight,
        "flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 pr-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx276(
          "span",
          {
            "data-slot": "select-item-indicator",
            className: "absolute right-2 flex size-3.5 items-center justify-center",
            children: /* @__PURE__ */ jsx276(AnimatedCheckIcon, { checked: isChecked })
          }
        ),
        /* @__PURE__ */ jsx276(SelectPrimitive.ItemText, { children })
      ]
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx276(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn("pointer-events-none -mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx276(
    SelectPrimitive.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx276(ChevronUpIcon, { className: "size-4" })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx276(
    SelectPrimitive.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn(
        "flex cursor-default items-center justify-center py-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx276(ChevronDownIcon4, { className: "size-4" })
    }
  );
}

// src/components/ui/data-table/footer/pagination.tsx
import { jsx as jsx277, jsxs as jsxs253 } from "react/jsx-runtime";
var DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50];
var navButtonClassName = "size-7 rounded-[5px] bg-muted p-1.5 text-foreground enabled:hover:bg-border/60 disabled:bg-transparent disabled:text-muted-foreground disabled:opacity-100 [&_svg:not([class*='size-'])]:size-3";
var pageButtonClassName = "size-7 rounded-[5px] bg-transparent p-0 text-[11px] font-bold text-muted-foreground hover:bg-border/60 hover:text-foreground";
function DataTablePagination({
  pageIndex,
  pageSize,
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  className,
  ...props
}) {
  const sizes = React16.useMemo(
    () => Array.from(/* @__PURE__ */ new Set([...pageSizeOptions, pageSize])).sort((a, b) => a - b),
    [pageSizeOptions, pageSize]
  );
  return /* @__PURE__ */ jsxs253(
    "div",
    {
      "data-slot": "data-table-pagination",
      className: cn("flex justify-between", className),
      ...props,
      children: [
        onPageSizeChange && /* @__PURE__ */ jsxs253(
          Select,
          {
            value: String(pageSize),
            onValueChange: (value) => onPageSizeChange(Number(value)),
            children: [
              /* @__PURE__ */ jsx277(
                SelectTrigger,
                {
                  "aria-label": "Rows per page",
                  className: "gap-2 bg-transparent p-0 text-xs text-foreground shadow-none hover:shadow-none focus-visible:shadow-none data-[size=default]:h-auto dark:bg-transparent dark:shadow-none dark:hover:shadow-none [&_svg]:-ml-1 [&_svg]:size-4 [&_svg]:text-current [&_svg]:transition-transform data-[state=open]:[&_svg]:rotate-180",
                  children: /* @__PURE__ */ jsx277("span", { className: "min-w-3 rounded-lg bg-muted p-1.5 text-center leading-3", children: /* @__PURE__ */ jsx277(SelectValue, {}) })
                }
              ),
              /* @__PURE__ */ jsx277(SelectContent, { position: "popper", align: "start", children: /* @__PURE__ */ jsx277(SelectGroup, { children: sizes.map((size) => /* @__PURE__ */ jsx277(SelectItem, { value: String(size), children: size }, size)) }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx277(Pagination, { className: "mx-0 w-auto", children: /* @__PURE__ */ jsxs253(PaginationContent, { className: "gap-3", children: [
          /* @__PURE__ */ jsx277(PaginationItem, { children: /* @__PURE__ */ jsx277(
            Button,
            {
              type: "button",
              variant: null,
              size: null,
              "aria-label": "Go to previous page",
              disabled: !hasPrevious,
              onClick: onPrevious,
              className: navButtonClassName,
              children: /* @__PURE__ */ jsx277(ChevronLeftIcon3, {})
            }
          ) }),
          hasPrevious && /* @__PURE__ */ jsx277(PaginationItem, { children: /* @__PURE__ */ jsx277(
            Button,
            {
              type: "button",
              variant: null,
              size: null,
              onClick: onPrevious,
              className: pageButtonClassName,
              children: pageIndex
            }
          ) }),
          /* @__PURE__ */ jsx277(PaginationItem, { children: /* @__PURE__ */ jsx277(
            "span",
            {
              "aria-current": "page",
              className: "flex size-7 items-center justify-center rounded-[5px] bg-primary text-[11px] font-bold text-primary-foreground",
              children: pageIndex + 1
            }
          ) }),
          hasNext && /* @__PURE__ */ jsx277(PaginationItem, { children: /* @__PURE__ */ jsx277(
            Button,
            {
              type: "button",
              variant: null,
              size: null,
              onClick: onNext,
              className: pageButtonClassName,
              children: pageIndex + 2
            }
          ) }),
          /* @__PURE__ */ jsx277(PaginationItem, { children: /* @__PURE__ */ jsx277(
            Button,
            {
              type: "button",
              variant: null,
              size: null,
              "aria-label": "Go to next page",
              disabled: !hasNext,
              onClick: onNext,
              className: navButtonClassName,
              children: /* @__PURE__ */ jsx277(ChevronRightIcon4, {})
            }
          ) })
        ] }) })
      ]
    }
  );
}

// src/components/ui/empty.tsx
import { cva as cva8 } from "class-variance-authority";
import { jsx as jsx278 } from "react/jsx-runtime";
function Empty({ className, ...props }) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty",
      className: cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12",
        className
      ),
      ...props
    }
  );
}
function EmptyHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty-header",
      className: cn(
        "flex max-w-sm flex-col items-center gap-2 text-center",
        className
      ),
      ...props
    }
  );
}
var emptyMediaVariants = cva8(
  "mb-2 flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground [&_svg:not([class*='size-'])]:size-6"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function EmptyMedia({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty-icon",
      "data-variant": variant,
      className: cn(emptyMediaVariants({ variant, className })),
      ...props
    }
  );
}
function EmptyTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty-title",
      className: cn("text-lg font-medium tracking-tight", className),
      ...props
    }
  );
}
function EmptyDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty-description",
      className: cn(
        "text-sm/relaxed text-muted-foreground [&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function EmptyContent({ className, ...props }) {
  return /* @__PURE__ */ jsx278(
    "div",
    {
      "data-slot": "empty-content",
      className: cn(
        "flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/spinner.tsx
import { Loader2Icon } from "lucide-react";
import { jsx as jsx279 } from "react/jsx-runtime";
function Spinner({ className, ...props }) {
  return /* @__PURE__ */ jsx279(
    Loader2Icon,
    {
      role: "status",
      "aria-label": "Loading",
      className: cn("size-4 animate-spin", className),
      ...props
    }
  );
}

// src/hooks/use-horizontal-fade.tsx
import * as React17 from "react";
function useHorizontalFade() {
  const [showLeft, setShowLeft] = React17.useState(false);
  const [showRight, setShowRight] = React17.useState(false);
  const cleanupRef = React17.useRef(null);
  const ref = React17.useCallback((node) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!node) {
      setShowLeft(false);
      setShowRight(false);
      return;
    }
    let rafId = 0;
    let lastLeft = false;
    let lastRight = false;
    const commit = () => {
      rafId = 0;
      const { scrollLeft, scrollWidth, clientWidth } = node;
      const left = scrollLeft > 2;
      const right = scrollLeft + clientWidth < scrollWidth - 2;
      if (left !== lastLeft) {
        lastLeft = left;
        setShowLeft(left);
      }
      if (right !== lastRight) {
        lastRight = right;
        setShowRight(right);
      }
    };
    const schedule = () => {
      if (rafId === 0) rafId = requestAnimationFrame(commit);
    };
    commit();
    node.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(node);
    cleanupRef.current = () => {
      node.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, []);
  const style = {
    "--sa-left-fade": showLeft ? 1 : 0,
    "--sa-right-fade": showRight ? 1 : 0
  };
  return { ref, style, showLeft, showRight };
}

// src/components/ui/data-table/data-table.tsx
import { Fragment as Fragment3, jsx as jsx280, jsxs as jsxs254 } from "react/jsx-runtime";
function useAnimatedHeight() {
  const containerRef = React18.useRef(null);
  const contentRef = React18.useRef(null);
  const [height, setHeight] = React18.useState(void 0);
  const heightRef = React18.useRef(void 0);
  const measureHeight = React18.useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) {
      return;
    }
    const computed = getComputedStyle(container);
    const borderHeight = (Number.parseFloat(computed.borderTopWidth || "0") || 0) + (Number.parseFloat(computed.borderBottomWidth || "0") || 0);
    const contentHeight = Math.ceil(
      content.getBoundingClientRect().height + borderHeight
    );
    const minHeight = Math.ceil(
      Number.parseFloat(computed.minHeight || "0") || 0
    );
    const nextHeight = Math.max(contentHeight, minHeight);
    if (heightRef.current !== nextHeight) {
      heightRef.current = nextHeight;
      setHeight(nextHeight);
    }
  }, []);
  React18.useLayoutEffect(() => {
    measureHeight();
  });
  React18.useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const observer = new ResizeObserver(() => measureHeight());
    observer.observe(content);
    return () => observer.disconnect();
  }, [measureHeight]);
  return { containerRef, contentRef, height };
}
function useStickyLayout(sides, columnsKey) {
  const cellRefs = React18.useRef([]);
  const [layout, setLayout] = React18.useState({
    offsets: [],
    leftWidth: 0,
    rightWidth: 0
  });
  const layoutRef = React18.useRef(layout);
  const sidesKey = sides.map((side) => side ?? "").join(",");
  React18.useLayoutEffect(() => {
    const commit = (next) => {
      const current = layoutRef.current;
      if (current.leftWidth === next.leftWidth && current.rightWidth === next.rightWidth && current.offsets.length === next.offsets.length && current.offsets.every((offset, index) => offset === next.offsets[index])) {
        return;
      }
      layoutRef.current = next;
      setLayout(next);
    };
    const keys = sidesKey.split(",");
    if (!keys.some(Boolean)) {
      commit({ offsets: [], leftWidth: 0, rightWidth: 0 });
      return;
    }
    const cells = cellRefs.current.slice(0, keys.length);
    const measure = () => {
      const widths = cells.map(
        (cell) => cell?.getBoundingClientRect().width ?? 0
      );
      const offsets = keys.map(() => 0);
      let leftWidth = 0;
      keys.forEach((side, index) => {
        if (side === "left") {
          offsets[index] = leftWidth;
          leftWidth += widths[index];
        }
      });
      let rightWidth = 0;
      for (let index = keys.length - 1; index >= 0; index--) {
        if (keys[index] === "right") {
          offsets[index] = rightWidth;
          rightWidth += widths[index];
        }
      }
      commit({ offsets, leftWidth, rightWidth });
    };
    measure();
    const observer = new ResizeObserver(measure);
    cells.forEach((cell) => {
      if (cell) {
        observer.observe(cell);
      }
    });
    return () => observer.disconnect();
  }, [columnsKey, sidesKey]);
  const setCellRef = React18.useCallback(
    (index, node) => {
      cellRefs.current[index] = node;
    },
    []
  );
  return { setCellRef, layout };
}
var scrollHintClassName = "pointer-events-none fixed top-0 left-0 z-[4] flex size-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-xs transition-opacity duration-300 [&_svg]:size-3.5";
function DataTableScrollHints({
  containerRef,
  scrollRef,
  leftOffset,
  rightOffset,
  isDisabled,
  layoutKey
}) {
  const leftHintRef = React18.useRef(null);
  const rightHintRef = React18.useRef(null);
  const [isRevealed, setIsRevealed] = React18.useState(false);
  const {
    ref: observeEdges,
    showLeft,
    showRight
  } = useHorizontalFade();
  const isVisible = isRevealed && !isDisabled;
  React18.useEffect(() => {
    observeEdges(scrollRef.current);
    return () => {
      observeEdges(null);
    };
  }, [observeEdges, scrollRef]);
  React18.useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    let timeout;
    const reveal = () => {
      if (element.scrollWidth - element.clientWidth <= 2) {
        return;
      }
      setIsRevealed(true);
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => setIsRevealed(false), 1e3);
    };
    element.addEventListener("scroll", reveal, { passive: true });
    element.addEventListener("mouseenter", reveal);
    return () => {
      element.removeEventListener("scroll", reveal);
      element.removeEventListener("mouseenter", reveal);
      window.clearTimeout(timeout);
    };
  }, [scrollRef]);
  React18.useLayoutEffect(() => {
    const container = containerRef.current;
    const scrollElement = scrollRef.current;
    if (!isVisible || !container || !scrollElement) {
      return;
    }
    const rows = scrollElement.querySelectorAll(
      ':scope > [data-slot="data-table-row"]'
    );
    if (rows.length === 0) {
      return;
    }
    const offsetY = (rows[0].getBoundingClientRect().top + rows[rows.length - 1].getBoundingClientRect().bottom) / 2 - container.getBoundingClientRect().top;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = container.getBoundingClientRect();
      const y = rect.top + offsetY;
      const placements = [
        {
          hint: leftHintRef.current,
          x: rect.left + container.clientLeft + (leftOffset > 0 ? leftOffset : 0.5 - container.clientLeft)
        },
        {
          hint: rightHintRef.current,
          x: rect.right - container.clientLeft - (rightOffset > 0 ? rightOffset : 0.5 - container.clientLeft)
        }
      ].flatMap(({ hint, x }) => {
        if (!hint) {
          return [];
        }
        const hintRect = hint.getBoundingClientRect();
        const originX = hintRect.left + hintRect.width / 2 - (Number.parseFloat(hint.style.left) || 0);
        const originY = hintRect.top + hintRect.height / 2 - (Number.parseFloat(hint.style.top) || 0);
        return [{ hint, left: x - originX, top: y - originY }];
      });
      placements.forEach(({ hint, left, top }) => {
        hint.style.left = `${left}px`;
        hint.style.top = `${top}px`;
      });
    };
    const schedule = () => {
      if (frame === 0) {
        frame = requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
    };
  }, [containerRef, isVisible, layoutKey, leftOffset, rightOffset, scrollRef]);
  return /* @__PURE__ */ jsxs254(Fragment3, { children: [
    /* @__PURE__ */ jsx280(
      "span",
      {
        ref: leftHintRef,
        "data-slot": "data-table-scroll-hint",
        "aria-hidden": true,
        className: scrollHintClassName,
        style: { opacity: isVisible && showLeft ? 1 : 0 },
        children: /* @__PURE__ */ jsx280(ChevronLeftIcon4, {})
      }
    ),
    /* @__PURE__ */ jsx280(
      "span",
      {
        ref: rightHintRef,
        "data-slot": "data-table-scroll-hint",
        "aria-hidden": true,
        className: scrollHintClassName,
        style: { opacity: isVisible && showRight ? 1 : 0 },
        children: /* @__PURE__ */ jsx280(ChevronRightIcon5, {})
      }
    )
  ] });
}
var stickyGradientLeft = "after:pointer-events-none after:absolute after:inset-y-0 after:-right-3 after:w-3 after:bg-[linear-gradient(to_right,var(--data-table-sticky-bg)_0px,transparent_12px)]";
var stickyGradientRight = "before:pointer-events-none before:absolute before:inset-y-0 before:-left-3 before:w-3 before:bg-[linear-gradient(to_left,var(--data-table-sticky-bg)_0px,transparent_12px)]";
var actionMask = "before:pointer-events-none before:absolute before:inset-y-0 before:-left-3 before:w-3 before:bg-(--data-table-action-bg) before:[mask-image:linear-gradient(to_right,transparent_0px,black_16px,black_100%)] before:[mask-repeat:no-repeat] before:[mask-size:100%_100%]";
var cellContentReset = "[&>[data-slot=data-table-cell-content]]:h-auto [&>[data-slot=data-table-cell-content]]:p-0";
var gridCellClassName = "relative z-0 min-h-10 p-0 [transition:z-index_0s_200ms] has-[[data-table-input]:focus-within]:z-[4] has-[[data-table-input]:focus-within]:[transition-delay:0s] has-[[data-table-input]:hover]:z-[4] has-[[data-table-input]:hover]:[transition-delay:0s] has-[[data-table-input][data-state=open]]:z-[4] has-[[data-table-input][data-state=open]]:[transition-delay:0s]";
var gridSurfaceClassName = "[--data-table-head-bg:color-mix(in_oklab,var(--color-muted)_50%,var(--color-background))] [--data-table-foot-bg:color-mix(in_oklab,var(--color-muted)_50%,var(--color-background))]";
var headStickyBackground = "[--data-table-sticky-bg:var(--data-table-head-bg)]";
var footStickyBackground = "[--data-table-sticky-bg:var(--data-table-foot-bg)]";
var dataTableThemeClassName = "[--color-border:var(--color-gray-100)] dark:[--color-border:var(--color-gray-800)] [--data-table-row-hover-bg:var(--color-gray-25)] dark:[--data-table-row-hover-bg:var(--color-gray-850)]";
var rowClickIgnoreSelector = "a, button, input, select, textarea, [role='button'], [role='checkbox'], [role='combobox'], [data-table-input], [data-slot='data-table-action']";
function getColumnId(column) {
  return column.id ?? column.accessor ?? "";
}
function DataTable({
  columns,
  data,
  rowKey,
  onRowClick,
  headerClassName,
  rowClassName,
  isLoading = false,
  loadingContent,
  emptyContent,
  emptyMessage,
  variant = "default",
  columnVisibility,
  rowAction,
  headerAction,
  footer,
  pagination,
  className,
  style,
  ...containerProps
}) {
  const isGrid = variant === "grid";
  const visibleColumns = React18.useMemo(
    () => columns.map((column) => ({ column, id: getColumnId(column) })).filter(({ id }) => columnVisibility?.[id] !== false),
    [columns, columnVisibility]
  );
  const hasActions = rowAction != null || headerAction != null;
  const actionIndex = visibleColumns.length;
  const lastDataIndex = visibleColumns.length - 1;
  const gridTemplateColumns = [
    ...visibleColumns.map(({ column }) => column.width ?? "auto"),
    ...hasActions ? ["max-content"] : []
  ].join(" ");
  const stickySides = [
    ...visibleColumns.map(({ column }) => column.sticky),
    ...hasActions ? ["right"] : []
  ];
  const hasPinned = stickySides.some(Boolean);
  const hasScrollHints = hasPinned || isGrid;
  const { setCellRef: setHeaderCellRef, layout: stickyLayout } = useStickyLayout(
    stickySides,
    visibleColumns.map(({ id }) => id).join(",")
  );
  const subgridRow = {
    display: "grid",
    gridTemplateColumns: "subgrid",
    gridColumn: "1 / -1"
  };
  const scrollRef = React18.useRef(null);
  const { ref: fadeRef, style: fadeStyle } = useHorizontalFade();
  const setScrollElement = React18.useCallback(
    (node) => {
      scrollRef.current = node;
      if (!hasScrollHints) {
        fadeRef(node);
      }
    },
    [fadeRef, hasScrollHints]
  );
  const hasEmptyContent = !isLoading && data.length === 0 && emptyContent != null;
  const {
    containerRef,
    contentRef,
    height: containerHeight
  } = useAnimatedHeight();
  const [isInitialLoad, setIsInitialLoad] = React18.useState(
    isLoading && data.length === 0
  );
  if (isInitialLoad && !isLoading) {
    setIsInitialLoad(false);
  }
  const firstFooterIndex = visibleColumns.findIndex(
    ({ column }) => column.footer != null
  );
  const hasColumnFooters = firstFooterIndex !== -1;
  const showFooter = data.length > 0 && !isLoading && (footer != null || hasColumnFooters);
  const footerLabelSpan = hasColumnFooters ? firstFooterIndex : visibleColumns.length;
  const firstUnpinnedIndex = stickySides.slice(0, footerLabelSpan).findIndex((side) => side !== "left");
  const pinnedFooterSpan = firstUnpinnedIndex === -1 ? footerLabelSpan : firstUnpinnedIndex;
  const isFooterLabelPinned = pinnedFooterSpan > 0;
  const footerLabelCellSpan = isFooterLabelPinned ? pinnedFooterSpan : footerLabelSpan;
  const footerFillerSpan = footerLabelSpan - footerLabelCellSpan;
  const getCellProps = (index, kind) => {
    const side = stickySides[index];
    const column = visibleColumns[index]?.column;
    return {
      className: cn(
        !isGrid && index === 0 && "pl-4",
        !isGrid && !hasActions && index === lastDataIndex && "pr-4",
        isGrid && kind !== "foot" && index < lastDataIndex && !(kind === "head" && !column?.header) && !(kind === "body" && side === "right") && "border-r",
        side && "sticky z-[2] bg-(--data-table-sticky-bg)",
        side && (kind === "body" ? isGrid ? "[--data-table-sticky-bg:var(--color-background)]" : "[--data-table-sticky-bg:var(--data-table-row-bg)]" : isGrid ? kind === "head" ? headStickyBackground : footStickyBackground : "[--data-table-sticky-bg:var(--color-background)]"),
        side === "left" && !isGrid && kind !== "foot" && stickyGradientLeft,
        side === "right" && !isGrid && kind !== "foot" && stickyGradientRight,
        side === "right" && isGrid && kind !== "foot" && "border-l"
      ),
      style: side === "left" ? { left: stickyLayout.offsets[index] ?? 0 } : side === "right" ? { right: stickyLayout.offsets[index] ?? 0 } : void 0
    };
  };
  const rowSurfaceClassName = isGrid ? "[--data-table-row-bg:var(--color-background)]" : hasPinned ? "bg-[color-mix(in_oklab,var(--data-table-row-hover-bg)_calc(var(--data-table-row-hover)*100%),transparent)] [--data-table-row-bg:color-mix(in_oklab,var(--data-table-row-hover-bg)_calc(var(--data-table-row-hover)*100%),var(--color-background))] [transition-property:--data-table-row-hover] duration-150 ease-in-out hover:[--data-table-row-hover:1]" : "transition-colors hover:bg-(--data-table-row-hover-bg)";
  const bodyCells = visibleColumns.map(({ column }, colIndex) => {
    const cellProps = getCellProps(colIndex, "body");
    const getClassName = (isLastRow) => cn(
      "flex items-center p-2 whitespace-nowrap",
      isGrid ? gridCellClassName : cellContentReset,
      isLastRow && colIndex === 0 && "[--table-cell-border-radius:0_0_0_8px]",
      isLastRow && !hasActions && colIndex === lastDataIndex && "[--table-cell-border-radius:0_0_8px_0]",
      colIndex === 0 && "[--data-table-outline-left:0px] [--data-table-edge-left:var(--color-border)]",
      !hasActions && colIndex === lastDataIndex && "[--data-table-outline-right:0px] [--data-table-edge-right:var(--color-border)]",
      isLastRow && "[--data-table-outline-bottom:0px] [--data-table-edge-bottom:var(--color-border)]",
      cellProps.className,
      column.className
    );
    return {
      className: getClassName(false),
      lastRowClassName: getClassName(!showFooter),
      style: cellProps.style
    };
  });
  const actionCellClassName = cn(
    "sticky right-0 z-[3] flex items-center bg-(--data-table-action-bg)",
    isGrid ? "border-l px-2 py-1 [--data-table-action-bg:var(--color-background)]" : cn("px-3 [--data-table-action-bg:var(--data-table-row-bg)]", actionMask)
  );
  const table = /* @__PURE__ */ jsxs254(
    "div",
    {
      ref: containerRef,
      className: cn(
        "relative w-full overflow-hidden rounded-lg text-sm transition-[height] duration-150 ease-in-out",
        dataTableThemeClassName,
        isGrid ? cn(
          gridSurfaceClassName,
          "after:pointer-events-none after:absolute after:inset-0 after:z-[3] after:rounded-[inherit] after:border after:border-border"
        ) : "border",
        hasScrollHints && "isolate",
        (isLoading || hasEmptyContent) && "min-h-[120px]",
        emptyContent != null && data.length === 0 && "min-h-[341px]",
        className
      ),
      style: { ...style, height: containerHeight },
      ...containerProps,
      children: [
        /* @__PURE__ */ jsxs254(
          "div",
          {
            ref: contentRef,
            className: "relative",
            children: [
              /* @__PURE__ */ jsxs254(
                "div",
                {
                  ref: setScrollElement,
                  "data-slot": "data-table",
                  "data-variant": variant,
                  role: "table",
                  "data-fade-edges-x": hasScrollHints ? void 0 : "true",
                  className: "grid w-full overflow-x-auto",
                  style: {
                    ...fadeStyle,
                    gridTemplateColumns
                  },
                  children: [
                    /* @__PURE__ */ jsxs254(
                      "div",
                      {
                        "data-slot": "data-table-header",
                        role: "row",
                        className: cn("border-b", isGrid && "bg-(--data-table-head-bg)", headerClassName),
                        style: subgridRow,
                        children: [
                          visibleColumns.map(({ column: col, id }, colIndex) => {
                            const cellProps = getCellProps(colIndex, "head");
                            return /* @__PURE__ */ jsx280(
                              "div",
                              {
                                ref: (node) => setHeaderCellRef(colIndex, node),
                                "data-slot": "data-table-head",
                                role: "columnheader",
                                className: cn(
                                  "flex h-10 items-center px-2 text-left font-medium whitespace-nowrap text-gray-500",
                                  isGrid && "px-4",
                                  cellProps.className,
                                  col.className
                                ),
                                style: cellProps.style,
                                children: col.header
                              },
                              id
                            );
                          }),
                          hasActions && /* @__PURE__ */ jsx280(
                            "div",
                            {
                              ref: (node) => setHeaderCellRef(actionIndex, node),
                              "data-slot": "data-table-head",
                              role: "columnheader",
                              className: cn(
                                "sticky right-0 z-[3] flex h-full items-center bg-(--data-table-action-bg)",
                                isGrid ? cn(
                                  "border-l px-2 [--data-table-action-bg:var(--data-table-head-bg)]",
                                  headerAction == null && "border-l-transparent"
                                ) : cn("px-3 [--data-table-action-bg:var(--color-background)]", actionMask)
                              ),
                              children: headerAction
                            }
                          )
                        ]
                      }
                    ),
                    data.map((row, rowIndex) => {
                      const key = rowKey ? rowKey(row, rowIndex) : rowIndex;
                      const isLastRow = rowIndex === data.length - 1;
                      const resolvedRowClassName = typeof rowClassName === "function" ? rowClassName(row, rowIndex) : rowClassName;
                      return /* @__PURE__ */ jsxs254(
                        "div",
                        {
                          "data-slot": "data-table-row",
                          role: "row",
                          className: cn(
                            rowSurfaceClassName,
                            !isLastRow && "border-b",
                            onRowClick && "cursor-pointer",
                            resolvedRowClassName
                          ),
                          style: subgridRow,
                          onClick: onRowClick ? (event) => {
                            const target = event.target;
                            if (!event.currentTarget.contains(target) || target.closest(rowClickIgnoreSelector)) {
                              return;
                            }
                            onRowClick(row, rowIndex);
                          } : void 0,
                          children: [
                            visibleColumns.map(({ column: col, id }, colIndex) => {
                              const value = col.accessor ? row[col.accessor] : void 0;
                              const bodyCell = bodyCells[colIndex];
                              return /* @__PURE__ */ jsx280(
                                "div",
                                {
                                  "data-slot": "data-table-cell",
                                  role: "cell",
                                  className: isLastRow ? bodyCell.lastRowClassName : bodyCell.className,
                                  style: bodyCell.style,
                                  children: col.cell ? col.cell({
                                    value,
                                    row,
                                    rowIndex,
                                    isEditable: col.isEditable === true
                                  }) : String(value ?? "")
                                },
                                id
                              );
                            }),
                            hasActions && /* @__PURE__ */ jsx280(
                              "div",
                              {
                                "data-slot": "data-table-action",
                                role: "cell",
                                className: actionCellClassName,
                                children: rowAction?.(row, rowIndex)
                              }
                            )
                          ]
                        },
                        key
                      );
                    }),
                    showFooter && /* @__PURE__ */ jsxs254(
                      "div",
                      {
                        "data-slot": "data-table-footer",
                        role: "row",
                        className: cn("border-t", isGrid && "bg-(--data-table-foot-bg)"),
                        style: subgridRow,
                        children: [
                          footerLabelSpan > 0 && /* @__PURE__ */ jsx280(
                            "div",
                            {
                              "data-slot": "data-table-footer-cell",
                              role: "cell",
                              className: cn(
                                "flex h-10 items-center truncate px-2 pl-4 font-medium text-muted-foreground",
                                !hasActions && footerLabelCellSpan - 1 === lastDataIndex && "pr-4",
                                isGrid && "px-4",
                                cellContentReset,
                                isFooterLabelPinned && cn(
                                  "sticky left-0 z-[2] bg-(--data-table-sticky-bg)",
                                  isGrid ? footStickyBackground : "[--data-table-sticky-bg:var(--color-background)]"
                                )
                              ),
                              style: { gridColumn: `span ${footerLabelCellSpan}` },
                              children: footer
                            }
                          ),
                          footerFillerSpan > 0 && /* @__PURE__ */ jsx280(
                            "div",
                            {
                              "data-slot": "data-table-footer-cell",
                              role: "cell",
                              style: { gridColumn: `span ${footerFillerSpan}` }
                            }
                          ),
                          visibleColumns.slice(footerLabelSpan).map(({ column: col, id }, offset) => {
                            const cellProps = getCellProps(footerLabelSpan + offset, "foot");
                            return /* @__PURE__ */ jsx280(
                              "div",
                              {
                                "data-slot": "data-table-footer-cell",
                                role: "cell",
                                className: cn(
                                  "flex h-10 items-center truncate px-2 font-medium text-muted-foreground",
                                  isGrid && "px-4",
                                  cellContentReset,
                                  cellProps.className,
                                  col.className
                                ),
                                style: cellProps.style,
                                children: col.footer
                              },
                              id
                            );
                          }),
                          hasActions && /* @__PURE__ */ jsx280(
                            "div",
                            {
                              "data-slot": "data-table-footer-cell",
                              role: "cell",
                              className: cn(
                                "sticky right-0 z-[3] bg-(--data-table-action-bg)",
                                isGrid ? "[--data-table-action-bg:var(--data-table-foot-bg)]" : cn("[--data-table-action-bg:var(--color-background)]", actionMask)
                              )
                            }
                          )
                        ]
                      }
                    )
                  ]
                }
              ),
              !isLoading && data.length === 0 && emptyContent == null && emptyMessage != null && /* @__PURE__ */ jsx280(Empty, { className: "p-3 md:p-3", children: /* @__PURE__ */ jsx280(EmptyHeader, { children: /* @__PURE__ */ jsx280(EmptyDescription, { children: emptyMessage }) }) })
            ]
          }
        ),
        hasScrollHints && /* @__PURE__ */ jsx280(
          DataTableScrollHints,
          {
            containerRef,
            scrollRef,
            leftOffset: stickyLayout.leftWidth,
            rightOffset: stickyLayout.rightWidth,
            isDisabled: isLoading || data.length === 0,
            layoutKey: data
          }
        ),
        isLoading && /* @__PURE__ */ jsx280(
          "div",
          {
            "data-slot": "data-table-loading",
            className: "pointer-events-none absolute inset-x-px bottom-px top-[41px] z-10 grid min-h-20 place-items-center rounded-b-[calc(var(--radius-lg)-1px)] bg-background/80 *:relative! *:inset-auto! *:m-0!",
            children: loadingContent ?? /* @__PURE__ */ jsx280(Spinner, { className: "size-5 text-muted-foreground" })
          }
        ),
        hasEmptyContent && /* @__PURE__ */ jsx280(
          "div",
          {
            "data-slot": "data-table-empty",
            className: "pointer-events-none absolute inset-x-0 top-10 z-10 grid min-h-20 place-items-center [&>*]:pointer-events-auto",
            children: emptyContent
          }
        )
      ]
    }
  );
  if (!pagination) {
    return table;
  }
  return /* @__PURE__ */ jsxs254("div", { "data-slot": "data-table-container", className: "flex w-full flex-col gap-4", children: [
    table,
    !isInitialLoad && /* @__PURE__ */ jsx280("div", { className: "animate-in fade-in-0 slide-in-from-top-2 duration-200", children: /* @__PURE__ */ jsx280(DataTablePagination, { ...pagination }) })
  ] });
}

// src/components/ui/dropdown-menu.tsx
import { ChevronRightIcon as ChevronRightIcon6, CircleIcon as CircleIcon2 } from "lucide-react";

// src/components/animate-ui/primitives/radix/dropdown-menu.tsx
import * as React20 from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence6, motion as motion7 } from "motion/react";

// src/hooks/use-data-state.tsx
import * as React19 from "react";
function parseDatasetValue(value) {
  if (value === null) return null;
  if (value === "" || value === "true") return true;
  if (value === "false") return false;
  return value;
}
function useDataState(key, forwardedRef, onChange) {
  const localRef = React19.useRef(null);
  React19.useImperativeHandle(forwardedRef, () => localRef.current);
  const getSnapshot = () => {
    const el = localRef.current;
    return el ? parseDatasetValue(el.getAttribute(`data-${key}`)) : null;
  };
  const subscribe = (callback) => {
    const el = localRef.current;
    if (!el) return () => {
    };
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.attributeName === `data-${key}`) {
          callback();
          break;
        }
      }
    });
    observer.observe(el, {
      attributes: true,
      attributeFilter: [`data-${key}`]
    });
    return () => observer.disconnect();
  };
  const value = React19.useSyncExternalStore(subscribe, getSnapshot);
  React19.useEffect(() => {
    if (onChange) onChange(value);
  }, [value, onChange]);
  return [value, localRef];
}

// src/components/animate-ui/primitives/radix/dropdown-menu.tsx
import { jsx as jsx281 } from "react/jsx-runtime";
var [DropdownMenuProvider, useDropdownMenu] = getStrictContext("DropdownMenuContext");
var [DropdownMenuSubProvider, useDropdownMenuSub] = getStrictContext("DropdownMenuSubContext");
function DropdownMenu(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const [highlightedValue, setHighlightedValue] = React20.useState(
    null
  );
  return /* @__PURE__ */ jsx281(
    DropdownMenuProvider,
    {
      value: { isOpen, setIsOpen, highlightedValue, setHighlightedValue },
      children: /* @__PURE__ */ jsx281(
        DropdownMenuPrimitive.Root,
        {
          "data-slot": "dropdown-menu",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function DropdownMenuTrigger(props) {
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      ...props
    }
  );
}
function DropdownMenuPortal(props) {
  return /* @__PURE__ */ jsx281(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuGroup(props) {
  return /* @__PURE__ */ jsx281(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
function DropdownMenuSub(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx281(DropdownMenuSubProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.Sub,
    {
      "data-slot": "dropdown-menu-sub",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function DropdownMenuRadioGroup(props) {
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuSubTrigger({
  disabled,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.SubTrigger,
    {
      ref: highlightedRef,
      disabled,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-sub-trigger",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuSubContent({
  loop,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  sideOffset,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  transition = { duration: 0.1 },
  style,
  container,
  ...props
}) {
  const { isOpen } = useDropdownMenuSub();
  return /* @__PURE__ */ jsx281(AnimatePresence6, { children: isOpen && /* @__PURE__ */ jsx281(DropdownMenuPortal, { forceMount: true, container, children: /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.SubContent,
    {
      asChild: true,
      forceMount: true,
      loop,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      sideOffset,
      alignOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-sub-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dropdown-menu-sub-content"
      )
    }
  ) }) });
}
function DropdownMenuContent({
  loop,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onPointerDownOutside,
  onFocusOutside,
  onInteractOutside,
  side,
  sideOffset,
  align,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  transition = { duration: 0.1 },
  style,
  container,
  ...props
}) {
  const { isOpen } = useDropdownMenu();
  return /* @__PURE__ */ jsx281(AnimatePresence6, { children: isOpen && /* @__PURE__ */ jsx281(DropdownMenuPortal, { forceMount: true, container, children: /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.Content,
    {
      asChild: true,
      loop,
      onCloseAutoFocus,
      onEscapeKeyDown,
      onPointerDownOutside,
      onFocusOutside,
      onInteractOutside,
      side,
      sideOffset,
      align,
      alignOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: { willChange: "opacity, transform", ...style },
          ...props
        },
        "dropdown-menu-content"
      )
    }
  ) }) });
}
function DropdownMenuItem({
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.Item,
    {
      ref: highlightedRef,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuCheckboxItem({
  checked,
  onCheckedChange,
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value) => {
      if (value === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.CheckboxItem,
    {
      ref: highlightedRef,
      checked,
      onCheckedChange,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-checkbox-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuRadioItem({
  value,
  disabled,
  onSelect,
  textValue,
  ...props
}) {
  const { setHighlightedValue } = useDropdownMenu();
  const [, highlightedRef] = useDataState(
    "highlighted",
    void 0,
    (value2) => {
      if (value2 === true) {
        const el = highlightedRef.current;
        const v = el?.dataset.value || el?.id || null;
        if (v) setHighlightedValue(v);
      }
    }
  );
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.RadioItem,
    {
      ref: highlightedRef,
      value,
      disabled,
      onSelect,
      textValue,
      asChild: true,
      children: /* @__PURE__ */ jsx281(
        motion7.div,
        {
          "data-slot": "dropdown-menu-radio-item",
          "data-disabled": disabled,
          ...props
        }
      )
    }
  );
}
function DropdownMenuLabel(props) {
  return /* @__PURE__ */ jsx281(DropdownMenuPrimitive.Label, { "data-slot": "dropdown-menu-label", ...props });
}
function DropdownMenuSeparator(props) {
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      ...props
    }
  );
}
function DropdownMenuShortcut(props) {
  return /* @__PURE__ */ jsx281("span", { "data-slot": "dropdown-menu-shortcut", ...props });
}
function DropdownMenuItemIndicator(props) {
  return /* @__PURE__ */ jsx281(
    DropdownMenuPrimitive.ItemIndicator,
    {
      "data-slot": "dropdown-menu-item-indicator",
      asChild: true,
      children: /* @__PURE__ */ jsx281(motion7.div, { ...props })
    }
  );
}

// src/components/ui/dropdown-menu.tsx
import { jsx as jsx282, jsxs as jsxs255 } from "react/jsx-runtime";
function DropdownMenu2({
  ...props
}) {
  return /* @__PURE__ */ jsx282(DropdownMenu, { ...props });
}
function DropdownMenuPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx282(DropdownMenuPortal, { ...props });
}
function DropdownMenuTrigger2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuTrigger,
    {
      className: cn(
        "group [&_[data-slot=dropdown-chevron]]:text-gray-400 [&_[data-slot=dropdown-chevron]]:transition-colors [&_[data-slot=dropdown-chevron]]:duration-150 hover:[&_[data-slot=dropdown-chevron]]:text-gray-500",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuContent2({
  className,
  sideOffset = 2,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuContent,
    {
      sideOffset,
      className: cn(
        "z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuGroup2({
  ...props
}) {
  return /* @__PURE__ */ jsx282(DropdownMenuGroup, { ...props });
}
function DropdownMenuItem2({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuItem,
    {
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        itemHighlight,
        "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-gray-50 focus:before:opacity-100 focus:text-accent-foreground dark:before:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:before:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:before:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=dropdown-menu-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=dropdown-menu-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem2({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs255(
    DropdownMenuCheckboxItem,
    {
      className: cn(
        itemHighlight,
        "flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-gray-50 focus:before:opacity-100 focus:text-accent-foreground dark:before:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx282("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx282(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup2({
  ...props
}) {
  return /* @__PURE__ */ jsx282(DropdownMenuRadioGroup, { ...props });
}
function DropdownMenuRadioItem2({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs255(
    DropdownMenuRadioItem,
    {
      className: cn(
        itemHighlight,
        "flex cursor-pointer items-center gap-2 rounded-lg py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-gray-50 focus:before:opacity-100 focus:text-accent-foreground dark:before:bg-white/[0.06] data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx282("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx282(DropdownMenuItemIndicator, { children: /* @__PURE__ */ jsx282(CircleIcon2, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel2({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuLabel,
    {
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuSeparator,
    {
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function DropdownMenuShortcut2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuShortcut,
    {
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub2({
  ...props
}) {
  return /* @__PURE__ */ jsx282(DropdownMenuSub, { ...props });
}
function DropdownMenuSubTrigger2({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs255(
    DropdownMenuSubTrigger,
    {
      "data-inset": inset,
      className: cn(
        itemHighlight,
        "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-gray-50 focus:before:opacity-100 focus:text-accent-foreground dark:before:bg-white/[0.06] data-[inset]:pl-8 data-[state=open]:before:opacity-100 data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx282(ChevronRightIcon6, { className: "ml-auto size-4" })
      ]
    }
  );
}
function DropdownMenuSubContent2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx282(
    DropdownMenuSubContent,
    {
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/data-table/header/column-selector.tsx
import { jsx as jsx283, jsxs as jsxs256 } from "react/jsx-runtime";
function DataTableColumnSelector({
  columns,
  value,
  onValueChange,
  title = "Columns",
  ...props
}) {
  const options = columns.flatMap((column) => {
    const label = column.label ?? (typeof column.header === "string" ? column.header : "");
    return label ? [{ id: getColumnId(column), label }] : [];
  });
  return /* @__PURE__ */ jsxs256(DropdownMenu2, { children: [
    /* @__PURE__ */ jsx283(DropdownMenuTrigger2, { asChild: true, children: /* @__PURE__ */ jsx283(
      Button,
      {
        "data-slot": "data-table-column-selector",
        type: "button",
        variant: "muted",
        size: "icon-xs",
        "aria-label": title,
        ...props,
        children: /* @__PURE__ */ jsx283(EllipsisIcon, {})
      }
    ) }),
    /* @__PURE__ */ jsxs256(DropdownMenuContent2, { align: "end", children: [
      /* @__PURE__ */ jsx283(DropdownMenuLabel2, { children: title }),
      /* @__PURE__ */ jsx283(DropdownMenuSeparator2, {}),
      /* @__PURE__ */ jsx283(DropdownMenuGroup2, { children: options.map((option) => /* @__PURE__ */ jsx283(
        DropdownMenuCheckboxItem2,
        {
          checked: value[option.id] !== false,
          onCheckedChange: (checked) => onValueChange({ ...value, [option.id]: checked === true }),
          onSelect: (event) => event.preventDefault(),
          children: option.label
        },
        option.id
      )) })
    ] })
  ] });
}

// src/components/ui/data-table/cells/select-cell.tsx
import * as React22 from "react";
import { ChevronDownIcon as ChevronDownIcon5 } from "lucide-react";

// src/components/animate-ui/primitives/radix/popover.tsx
import * as React21 from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence7, motion as motion8 } from "motion/react";
import { jsx as jsx284 } from "react/jsx-runtime";
var [PopoverProvider, usePopover] = getStrictContext("PopoverContext");
function Popover(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  return /* @__PURE__ */ jsx284(PopoverProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx284(
    PopoverPrimitive.Root,
    {
      "data-slot": "popover",
      ...props,
      onOpenChange: setIsOpen
    }
  ) });
}
function PopoverTrigger(props) {
  return /* @__PURE__ */ jsx284(PopoverPrimitive.Trigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverPortal(props) {
  return /* @__PURE__ */ jsx284(PopoverPrimitive.Portal, { forceMount: true, "data-slot": "popover-portal", ...props });
}
var PopoverContent = React21.forwardRef(
  ({
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onFocusOutside,
    onInteractOutside,
    align,
    alignOffset,
    side,
    sideOffset,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    arrowPadding,
    sticky,
    hideWhenDetached,
    transition = { duration: 0.1 },
    style,
    ...props
  }, ref) => {
    const { isOpen } = usePopover();
    return /* @__PURE__ */ jsx284(AnimatePresence7, { children: isOpen && /* @__PURE__ */ jsx284(
      PopoverPrimitive.Content,
      {
        asChild: true,
        forceMount: true,
        align,
        alignOffset,
        side,
        sideOffset,
        avoidCollisions,
        collisionBoundary,
        collisionPadding,
        arrowPadding,
        sticky,
        hideWhenDetached,
        onOpenAutoFocus,
        onCloseAutoFocus,
        onEscapeKeyDown,
        onPointerDownOutside,
        onInteractOutside,
        onFocusOutside,
        children: /* @__PURE__ */ jsx284(
          motion8.div,
          {
            ref,
            "data-slot": "popover-content",
            initial: { opacity: 0, scale: 0.98 },
            animate: { opacity: 1, scale: 1 },
            exit: { opacity: 0, scale: 0.98 },
            transition,
            style: { willChange: "opacity, transform", ...style },
            ...props
          },
          "popover-content"
        )
      }
    ) });
  }
);
PopoverContent.displayName = "PopoverContent";
function PopoverAnchor({ ...props }) {
  return /* @__PURE__ */ jsx284(PopoverPrimitive.Anchor, { "data-slot": "popover-anchor", ...props });
}

// src/components/ui/popover.tsx
import { jsx as jsx285 } from "react/jsx-runtime";
function Popover2({
  ...props
}) {
  return /* @__PURE__ */ jsx285(Popover, { ...props });
}
function PopoverTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx285(PopoverTrigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverContent2({
  className,
  align = "center",
  sideOffset = 2,
  ...props
}) {
  return /* @__PURE__ */ jsx285(PopoverPortal, { children: /* @__PURE__ */ jsx285(
    PopoverContent,
    {
      "data-slot": "popover-content",
      align,
      sideOffset,
      className: cn(
        "z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-lg bg-popover p-4 text-secondary-foreground shadow-md ring-1 ring-black/5 outline-hidden dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  ) });
}
function PopoverAnchor2({
  ...props
}) {
  return /* @__PURE__ */ jsx285(PopoverAnchor, { "data-slot": "popover-anchor", ...props });
}
function PopoverHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx285(
    "div",
    {
      "data-slot": "popover-header",
      className: cn("flex flex-col gap-1 text-sm", className),
      ...props
    }
  );
}
function PopoverTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx285(
    "div",
    {
      "data-slot": "popover-title",
      className: cn("font-medium", className),
      ...props
    }
  );
}
function PopoverDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx285(
    "p",
    {
      "data-slot": "popover-description",
      className: cn("text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/data-table/cells/select-cell.tsx
import { jsx as jsx286, jsxs as jsxs257 } from "react/jsx-runtime";
var matchMarkClassName = "rounded-[2px] bg-blue-100 text-foreground dark:bg-blue-500/25";
var selectTriggerClassName = "relative w-full gap-1 rounded-(--table-cell-border-radius) bg-transparent px-4 py-2 font-normal leading-6 text-foreground shadow-none hover:shadow-none hover:[--input-inline-outline:1] focus-visible:shadow-none focus-visible:ring-0 focus-visible:[--input-inline-outline:1] data-[placeholder]:text-muted-foreground data-[size=default]:h-10 data-[state=open]:[--input-inline-outline:1] disabled:opacity-75 dark:bg-transparent dark:shadow-none dark:hover:shadow-none dark:focus-visible:shadow-none [&_svg]:size-3.5 [&_svg]:text-current [&_svg]:transition-transform data-[state=open]:[&_svg]:rotate-180";
var searchTriggerClassName = "relative flex h-10 w-full min-w-0 cursor-pointer items-center justify-between gap-1 rounded-(--table-cell-border-radius) bg-transparent px-4 py-2 text-left text-sm font-normal leading-6 text-foreground outline-none hover:[--input-inline-outline:1] focus-visible:[--input-inline-outline:1] data-[state=open]:[--input-inline-outline:1] disabled:cursor-not-allowed disabled:opacity-75 [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:transition-transform data-[state=open]:[&_svg]:rotate-180";
function highlightMatch(label, query) {
  const trimmed = query.trim();
  if (typeof label !== "string" || !trimmed) {
    return label;
  }
  const lowerLabel = label.toLowerCase();
  const lowerQuery = trimmed.toLowerCase();
  const parts = [];
  let cursor = 0;
  for (let index = lowerLabel.indexOf(lowerQuery); index !== -1; index = lowerLabel.indexOf(lowerQuery, cursor)) {
    if (index > cursor) {
      parts.push(label.slice(cursor, index));
    }
    parts.push(
      /* @__PURE__ */ jsx286("mark", { className: matchMarkClassName, children: label.slice(index, index + trimmed.length) }, index)
    );
    cursor = index + trimmed.length;
  }
  if (cursor === 0) {
    return label;
  }
  if (cursor < label.length) {
    parts.push(label.slice(cursor));
  }
  return parts;
}
function DataTableSearchSelect({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  onSearch,
  searchPlaceholder = "Search\u2026",
  emptyMessage = "No results found",
  isLoading = false,
  onScrollEnd,
  displayValue
}) {
  const [open, setOpen] = React22.useState(false);
  const [query, setQuery] = React22.useState("");
  const selectedLabel = displayValue ?? options.find((option) => option.value === value)?.label;
  const visibleOptions = React22.useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (onSearch || !trimmed) {
      return options;
    }
    return options.filter(
      (option) => typeof option.label === "string" && option.label.toLowerCase().includes(trimmed)
    );
  }, [onSearch, options, query]);
  const resetQuery = () => {
    if (query !== "") {
      setQuery("");
      onSearch?.("");
    }
  };
  return /* @__PURE__ */ jsxs257(
    Popover2,
    {
      open,
      onOpenChange: (next) => {
        setOpen(next);
        if (!next) {
          resetQuery();
        }
      },
      children: [
        /* @__PURE__ */ jsx286(PopoverTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs257(
          "button",
          {
            type: "button",
            "data-table-input": "",
            disabled,
            className: cn(
              searchTriggerClassName,
              inlineOutlineClassName,
              className
            ),
            children: [
              /* @__PURE__ */ jsx286(
                "span",
                {
                  className: cn(
                    "min-w-0 flex-1 truncate",
                    selectedLabel == null && "text-muted-foreground"
                  ),
                  children: selectedLabel ?? placeholder
                }
              ),
              /* @__PURE__ */ jsx286(ChevronDownIcon5, {})
            ]
          }
        ) }),
        /* @__PURE__ */ jsx286(
          PopoverContent2,
          {
            align: "start",
            sideOffset: 4,
            className: "w-max min-w-(--radix-popover-trigger-width) max-w-[min(32rem,calc(100vw-2rem))] p-0",
            children: /* @__PURE__ */ jsxs257(Command, { shouldFilter: false, className: "bg-transparent", children: [
              /* @__PURE__ */ jsx286(
                CommandInput,
                {
                  value: query,
                  onValueChange: (next) => {
                    setQuery(next);
                    onSearch?.(next);
                  },
                  placeholder: searchPlaceholder
                }
              ),
              /* @__PURE__ */ jsxs257(
                CommandList,
                {
                  className: "p-1",
                  onScroll: (event) => {
                    const element = event.currentTarget;
                    if (element.scrollHeight - element.scrollTop - element.clientHeight < 64) {
                      onScrollEnd?.();
                    }
                  },
                  children: [
                    visibleOptions.length === 0 && !isLoading && /* @__PURE__ */ jsx286("div", { className: "py-6 text-center text-sm text-muted-foreground", children: emptyMessage }),
                    visibleOptions.map(
                      ({
                        label,
                        value: optionValue,
                        disabled: optionDisabled,
                        description,
                        trailing,
                        trailingDescription,
                        ...itemProps
                      }) => /* @__PURE__ */ jsxs257(
                        CommandItem,
                        {
                          value: optionValue,
                          disabled: optionDisabled,
                          onSelect: () => {
                            if (optionDisabled) {
                              return;
                            }
                            onValueChange?.(optionValue);
                            setOpen(false);
                            resetQuery();
                          },
                          className: "flex items-start gap-3",
                          ...itemProps,
                          children: [
                            /* @__PURE__ */ jsxs257("div", { className: "flex min-w-0 flex-1 flex-col gap-0.5", children: [
                              /* @__PURE__ */ jsx286("span", { className: "truncate", children: highlightMatch(label, query) }),
                              description != null && /* @__PURE__ */ jsx286("span", { className: "truncate text-xs font-normal text-muted-foreground", children: description })
                            ] }),
                            (trailing != null || trailingDescription != null) && /* @__PURE__ */ jsxs257("div", { className: "flex shrink-0 flex-col items-end gap-0.5 pl-6 text-right", children: [
                              trailing != null && /* @__PURE__ */ jsx286("span", { className: "whitespace-nowrap", children: trailing }),
                              trailingDescription != null && /* @__PURE__ */ jsx286("span", { className: "text-xs font-normal whitespace-nowrap text-muted-foreground", children: trailingDescription })
                            ] })
                          ]
                        },
                        optionValue
                      )
                    ),
                    isLoading && /* @__PURE__ */ jsx286("div", { className: "flex justify-center py-3", children: /* @__PURE__ */ jsx286(Spinner, {}) })
                  ]
                }
              )
            ] })
          }
        )
      ]
    }
  );
}
function DataTableSelectCell({
  options,
  value,
  onValueChange,
  isEditable = false,
  placeholder = "Please select",
  disabled = false,
  className,
  isSearchable = false,
  displayValue,
  ...searchProps
}) {
  if (!isEditable) {
    return /* @__PURE__ */ jsx286(DataTableCell, { className, children: displayValue ?? options.find((option) => option.value === value)?.label });
  }
  if (isSearchable) {
    return /* @__PURE__ */ jsx286(
      DataTableSearchSelect,
      {
        options,
        value,
        onValueChange,
        placeholder,
        disabled,
        className,
        displayValue,
        ...searchProps
      }
    );
  }
  return /* @__PURE__ */ jsxs257(
    Select,
    {
      value: value ?? "",
      onValueChange,
      disabled,
      children: [
        /* @__PURE__ */ jsx286(
          SelectTrigger,
          {
            "data-table-input": "",
            className: cn(selectTriggerClassName, inlineOutlineClassName, className),
            children: /* @__PURE__ */ jsx286(SelectValue, { placeholder })
          }
        ),
        /* @__PURE__ */ jsx286(SelectContent, { position: "popper", align: "start", children: /* @__PURE__ */ jsx286(SelectGroup, { children: options.map((option) => /* @__PURE__ */ jsx286(
          SelectItem,
          {
            value: option.value,
            disabled: option.disabled,
            children: option.label
          },
          option.value
        )) }) })
      ]
    }
  );
}

// src/components/ui/data-table/cells/text-cell.tsx
import { jsx as jsx287, jsxs as jsxs258 } from "react/jsx-runtime";
function DataTableTextCell({
  value,
  onValueChange,
  isEditable = false,
  displayVariant = "normal",
  weight,
  className,
  children,
  ...props
}) {
  const textClassName = cn(
    "text-foreground",
    displayVariant === "monospaced" && "font-mono",
    weight === "normal" && "font-normal",
    weight === "medium" && "font-medium",
    weight === "semibold" && "font-semibold"
  );
  if (!isEditable) {
    return /* @__PURE__ */ jsxs258(DataTableCell, { className, children: [
      /* @__PURE__ */ jsx287("span", { className: cn("truncate", textClassName), children: value }),
      children
    ] });
  }
  return /* @__PURE__ */ jsx287(
    "label",
    {
      "data-table-input": "",
      className: cn(inlineInputWrapperClassName, className),
      children: /* @__PURE__ */ jsx287(
        Input,
        {
          inputSize: "sm",
          value: value ?? "",
          onChange: (event) => onValueChange?.(event.target.value),
          className: cn(inlineInputClassName, textClassName),
          ...props
        }
      )
    }
  );
}

// src/components/ui/data-table/index.ts
var DataTable2 = Object.assign(DataTable, {
  AmountCell: DataTableAmountCell,
  Cell: DataTableCell,
  ColumnSelector: DataTableColumnSelector,
  Pagination: DataTablePagination,
  SelectCell: DataTableSelectCell,
  TextCell: DataTableTextCell
});

// src/components/ui/date-input.tsx
import * as React23 from "react";
import { jsx as jsx288, jsxs as jsxs259 } from "react/jsx-runtime";
var SEGMENTS = [
  // Each box is just as wide as its digits (tabular figures, plus room for the caret), so typed digits
  // sit in place from the first keystroke; the wider placeholder letters overhang into the gaps.
  { key: "month", label: "Month", placeholder: "MM", length: 2, width: "w-[2.2ch]" },
  { key: "day", label: "Day", placeholder: "DD", length: 2, width: "w-[2.2ch]" },
  { key: "year", label: "Year", placeholder: "YYYY", length: 4, width: "w-[4.3ch]" }
];
var splitValue = (value) => {
  const [month = "", day = "", year = ""] = value.split("/");
  return [month, day, year];
};
var joinParts = (parts) => parts.every((part) => !part) ? "" : parts.join("/");
function parseDateInputValue(value) {
  const [month, day, year] = splitValue(value);
  if (month.length !== 2 || day.length !== 2 || year.length !== 4) return void 0;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day) ? date : void 0;
}
function formatDateInputValue(date) {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;
}
var parsePasted = (text) => {
  const trimmed = text.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) return [iso[2].padStart(2, "0"), iso[3].padStart(2, "0"), iso[1]];
  const parts = trimmed.split(/[^\d]+/).filter(Boolean);
  if (parts.length === 3 && parts[2].length === 4) return [parts[0].padStart(2, "0"), parts[1].padStart(2, "0"), parts[2]];
  if (/^\d{8}$/.test(trimmed)) return [trimmed.slice(0, 2), trimmed.slice(2, 4), trimmed.slice(4)];
  return void 0;
};
function DateInput({
  value,
  onValueChange,
  onBlur,
  id,
  disabled,
  inputSize = "default",
  className,
  "aria-invalid": ariaInvalid,
  trailing
}) {
  const refs = React23.useRef([]);
  const parts = splitValue(value);
  const setPart = (index, part) => {
    const next = [...parts];
    next[index] = part;
    onValueChange(joinParts(next));
  };
  const focusSegment = (index, caret = "all") => {
    const el = refs.current[index];
    if (!el) return;
    el.focus();
    if (caret === "all") el.select();
    else {
      const position = caret === "start" ? 0 : el.value.length;
      el.setSelectionRange(position, position);
    }
  };
  const handleChange = (index, input) => {
    const { length } = SEGMENTS[index];
    let digits = input.value.replace(/\D/g, "");
    if (digits.length > length) {
      const typed = input.value.charAt((input.selectionStart ?? input.value.length) - 1);
      digits = /\d/.test(typed) ? typed : digits.slice(0, length);
    }
    if (digits.length === 1 && (index === 0 && Number(digits) > 1 || index === 1 && Number(digits) > 3)) {
      digits = `0${digits}`;
    }
    setPart(index, digits);
    if (digits.length === length && index < SEGMENTS.length - 1) {
      focusSegment(index + 1);
    }
  };
  const handleKeyDown = (index, event) => {
    const el = event.currentTarget;
    const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
    const atEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
    if (event.key === "Backspace" && atStart && index > 0) {
      event.preventDefault();
      setPart(index - 1, parts[index - 1].slice(0, -1));
      focusSegment(index - 1, "end");
    } else if (event.key === "ArrowLeft" && atStart && index > 0) {
      event.preventDefault();
      focusSegment(index - 1, "end");
    } else if (event.key === "ArrowRight" && atEnd && index < SEGMENTS.length - 1) {
      event.preventDefault();
      focusSegment(index + 1, "start");
    } else if (["/", "-", ".", " "].includes(event.key)) {
      event.preventDefault();
      if (parts[index] && index < SEGMENTS.length - 1) {
        if (index < 2 && parts[index].length === 1) setPart(index, parts[index].padStart(2, "0"));
        focusSegment(index + 1);
      }
    }
  };
  const handlePaste = (event) => {
    const pasted = parsePasted(event.clipboardData.getData("text"));
    if (!pasted) return;
    event.preventDefault();
    onValueChange(joinParts(pasted));
    focusSegment(SEGMENTS.length - 1, "end");
  };
  return /* @__PURE__ */ jsxs259(
    "div",
    {
      "data-slot": "date-input",
      role: "group",
      "aria-invalid": ariaInvalid || void 0,
      "aria-disabled": disabled || void 0,
      className: cn(
        inputVariants({ inputSize }),
        "group/date-input relative flex cursor-text items-center tabular-nums",
        "focus-within:bg-white focus-within:[--input-border-color:var(--color-blue-500)] focus-within:[--input-ring-color:rgb(69_117_205/0.15)] focus-within:[--input-ring-size:3px] dark:focus-within:bg-input/30",
        "aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
        trailing ? inputSize === "sm" ? "pr-9" : "pr-11" : void 0,
        className
      ),
      onMouseDown: (event) => {
        const target = event.target;
        if (target instanceof HTMLInputElement || target.closest("button")) return;
        event.preventDefault();
        const index = parts.findIndex((part, i) => part.length < SEGMENTS[i].length);
        focusSegment(index === -1 ? SEGMENTS.length - 1 : index, "end");
      },
      onBlur: (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onBlur?.();
      },
      children: [
        SEGMENTS.map((segment, index) => /* @__PURE__ */ jsxs259(React23.Fragment, { children: [
          index > 0 && /* @__PURE__ */ jsx288("span", { "aria-hidden": true, className: "px-1.5 text-[rgb(175_176_179)] select-none", children: "/" }),
          /* @__PURE__ */ jsxs259("span", { className: cn("relative flex h-full shrink-0 items-center", segment.width), children: [
            !parts[index] && /* @__PURE__ */ jsx288(
              "span",
              {
                "aria-hidden": true,
                className: cn(
                  // Centered over its box, so the letters overhang both sides evenly.
                  "pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap",
                  "text-[rgb(175_176_179)] transition-colors group-hover/date-input:text-[rgb(138_140_145)] group-focus-within/date-input:text-[rgb(138_140_145)]"
                ),
                children: segment.placeholder
              }
            ),
            /* @__PURE__ */ jsx288(
              "input",
              {
                ref: (el) => {
                  refs.current[index] = el;
                },
                id: index === 0 ? id : void 0,
                "aria-label": segment.label,
                "aria-placeholder": segment.placeholder,
                value: parts[index],
                onChange: (event) => handleChange(index, event.currentTarget),
                onKeyDown: (event) => handleKeyDown(index, event),
                onPaste: handlePaste,
                onFocus: (event) => event.currentTarget.select(),
                inputMode: "numeric",
                autoComplete: "off",
                "data-1p-ignore": true,
                disabled,
                className: "relative h-full w-full bg-transparent p-0 outline-none"
              }
            )
          ] })
        ] }, segment.key)),
        trailing
      ]
    }
  );
}

// src/components/ui/date-picker.tsx
import * as React26 from "react";
import { CalendarIcon, ChevronDownIcon as ChevronDownIcon6, ChevronUpIcon as ChevronUpIcon2 } from "lucide-react";
import { AnimatePresence as AnimatePresence9, motion as motion10, useReducedMotion } from "motion/react";
import { dateMatchModifiers } from "react-day-picker";

// src/components/ui/tooltip.tsx
import * as React25 from "react";

// src/components/animate-ui/primitives/radix/tooltip.tsx
import * as React24 from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import {
  AnimatePresence as AnimatePresence8,
  motion as motion9,
  useMotionValue,
  useSpring
} from "motion/react";
import { jsx as jsx289 } from "react/jsx-runtime";
var [LocalTooltipProvider, useTooltip] = getStrictContext("TooltipContext");
function TooltipProvider(props) {
  return /* @__PURE__ */ jsx289(TooltipPrimitive.Provider, { "data-slot": "tooltip-provider", ...props });
}
function Tooltip2({
  followCursor = false,
  followCursorSpringOptions = { stiffness: 200, damping: 17 },
  ...props
}) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  return /* @__PURE__ */ jsx289(
    LocalTooltipProvider,
    {
      value: {
        isOpen,
        setIsOpen,
        x,
        y,
        followCursor,
        followCursorSpringOptions
      },
      children: /* @__PURE__ */ jsx289(
        TooltipPrimitive.Root,
        {
          "data-slot": "tooltip",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function TooltipTrigger({ onMouseMove, ...props }) {
  const { x, y, followCursor } = useTooltip();
  const handleMouseMove = (event) => {
    onMouseMove?.(event);
    const target = event.currentTarget.getBoundingClientRect();
    if (followCursor === "x" || followCursor === true) {
      const eventOffsetX = event.clientX - target.left;
      const offsetXFromCenter = (eventOffsetX - target.width / 2) / 2;
      x.set(offsetXFromCenter);
    }
    if (followCursor === "y" || followCursor === true) {
      const eventOffsetY = event.clientY - target.top;
      const offsetYFromCenter = (eventOffsetY - target.height / 2) / 2;
      y.set(offsetYFromCenter);
    }
  };
  return /* @__PURE__ */ jsx289(
    TooltipPrimitive.Trigger,
    {
      "data-slot": "tooltip-trigger",
      onMouseMove: handleMouseMove,
      ...props
    }
  );
}
function TooltipPortal(props) {
  const { isOpen } = useTooltip();
  return /* @__PURE__ */ jsx289(AnimatePresence8, { children: isOpen && /* @__PURE__ */ jsx289(
    TooltipPrimitive.Portal,
    {
      forceMount: true,
      "data-slot": "tooltip-portal",
      ...props
    }
  ) });
}
var TooltipContent = React24.forwardRef(({
  onEscapeKeyDown,
  onPointerDownOutside,
  side,
  sideOffset,
  align,
  alignOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  style,
  transition = { type: "spring", stiffness: 600, damping: 35 },
  ...props
}, ref) => {
  const { x, y, followCursor, followCursorSpringOptions } = useTooltip();
  const translateX = useSpring(x, followCursorSpringOptions);
  const translateY = useSpring(y, followCursorSpringOptions);
  return /* @__PURE__ */ jsx289(
    TooltipPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      align,
      alignOffset,
      side,
      sideOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      onEscapeKeyDown,
      onPointerDownOutside,
      children: /* @__PURE__ */ jsx289(
        motion9.div,
        {
          ref,
          "data-slot": "popover-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: {
            x: followCursor === "x" || followCursor === true ? translateX : void 0,
            y: followCursor === "y" || followCursor === true ? translateY : void 0,
            ...style
          },
          ...props
        },
        "popover-content"
      )
    }
  );
});
TooltipContent.displayName = "TooltipContent";

// src/components/ui/tooltip.tsx
import { jsx as jsx290 } from "react/jsx-runtime";
function TooltipProvider2({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx290(
    TooltipProvider,
    {
      delayDuration,
      ...props
    }
  );
}
function Tooltip3({
  ...props
}) {
  return /* @__PURE__ */ jsx290(Tooltip2, { ...props });
}
function TooltipTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx290(TooltipTrigger, { ...props });
}
var TooltipContent2 = React25.forwardRef(({
  className,
  sideOffset = 0,
  children,
  ...props
}, ref) => {
  return /* @__PURE__ */ jsx290(TooltipPortal, { children: /* @__PURE__ */ jsx290(
    TooltipContent,
    {
      ref,
      sideOffset,
      className: cn(
        "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg bg-popover px-2 py-1 text-xs font-medium text-pretty text-muted-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props,
      children
    }
  ) });
});
TooltipContent2.displayName = "TooltipContent";

// src/components/ui/date-picker.tsx
import { jsx as jsx291, jsxs as jsxs260 } from "react/jsx-runtime";
var defaultFormatDate = (date) => `${date.toLocaleDateString("en-US", { weekday: "long" })} ${date.toLocaleDateString("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric"
})}`;
var ROW_HEIGHT_PX = 28;
var ROW_GAP_PX = 8;
var ROW_PITCH_PX = ROW_HEIGHT_PX + ROW_GAP_PX;
var VIEWPORT_BLEED_PX = 4;
var VIEWPORT_FADE_PX = 12;
var fadeMask = (fade) => `linear-gradient(to bottom, transparent, black ${fade}, black calc(100% - ${fade}), transparent)`;
var SLIDE_EASE = [0.4, 0, 0.2, 1];
var JUMP_NUDGE_PX = 24;
var SLIDE_TRANSITION = "duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none";
var WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
var EPOCH_SUNDAY = new Date(2e3, 0, 2);
var DAY_MS = 24 * 60 * 60 * 1e3;
var addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
var startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
var addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);
var endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
var isSameDay = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
var isSameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
var dayIndexOf = (date) => Math.round(
  (new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() - EPOCH_SUNDAY.getTime()) / DAY_MS
);
var weekIndexOf = (date) => Math.floor(dayIndexOf(date) / 7);
var weekStart = (weekIndex) => addDays(EPOCH_SUNDAY, weekIndex * 7);
var monthWeeks = (month) => ({
  first: weekIndexOf(startOfMonth(month)),
  last: weekIndexOf(endOfMonth(month))
});
var ordinal = (day) => {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  return `${day}${{ 1: "st", 2: "nd", 3: "rd" }[day % 10] ?? "th"}`;
};
var formatShortDay = (date) => `${date.toLocaleDateString("en-US", { weekday: "short" })}, ${date.toLocaleDateString("en-US", { month: "short" })} ${ordinal(date.getDate())}`;
var formatMonth = (month) => month.toLocaleDateString("en-US", { month: "long", year: "numeric" });
var NAV_GROUP_CLASS = "inline-flex h-6 shrink-0 items-stretch overflow-hidden rounded-md shadow-btn-muted transition-[box-shadow,scale] duration-200 hover:shadow-btn-muted-hover has-[button:active:not(:disabled)]:scale-[0.975] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:shadow-[0_0_0_1px_rgb(255_255_255/0.16)]";
var TODAY_BUTTON_CLASS = "flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-[5px] bg-blue-50 text-[10px] leading-none font-semibold text-primary tabular-nums transition-[background-color,color,box-shadow,scale] duration-200 outline-none select-none hover:bg-blue-100 focus-visible:bg-blue-100 focus-visible:ring-2 focus-visible:ring-ring/50 active:scale-[0.95] disabled:pointer-events-none disabled:opacity-40 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 dark:focus-visible:bg-blue-900/60";
var NAV_BUTTON_CLASS = "flex w-6 cursor-pointer items-center justify-center text-gray-600 transition-colors duration-200 outline-none select-none hover:bg-gray-50 hover:text-gray-700 focus-visible:bg-gray-50 focus-visible:text-gray-700 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-40 dark:text-gray-100 dark:hover:bg-white/5 dark:focus-visible:bg-white/5 [&_svg]:size-3";
function Day({
  date,
  isSelected: selected,
  isToday: today,
  isOutside: outside,
  isDisabled: disabled,
  isFocusTarget,
  isHidden,
  onSelect,
  onFocusDate,
  buttonRef
}) {
  return /* @__PURE__ */ jsx291("div", { role: "gridcell", "aria-selected": selected || void 0, className: "h-7", children: /* @__PURE__ */ jsx291(
    "button",
    {
      ref: buttonRef,
      type: "button",
      tabIndex: isFocusTarget && !isHidden ? 0 : -1,
      "aria-disabled": disabled || void 0,
      "aria-label": date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric"
      }),
      "data-day": date.toLocaleDateString(),
      onClick: () => !disabled && onSelect(date),
      onFocus: () => onFocusDate(date),
      className: cn(
        "h-7 w-full cursor-pointer rounded-[5px] text-sm leading-7 font-medium text-foreground transition-[background-color,color,box-shadow] duration-200 outline-none",
        "focus-visible:ring-[3px] focus-visible:ring-ring/50",
        !selected && !disabled && "hover:bg-gray-100 dark:hover:bg-white/5",
        outside && "text-gray-400 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-500",
        today && !selected && "bg-blue-50 text-primary hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60",
        selected && "bg-primary text-white",
        disabled && "cursor-not-allowed text-gray-300 hover:text-gray-300 dark:text-gray-700 dark:hover:text-gray-700",
        disabled && today && !selected && "text-blue-450 hover:bg-blue-50 hover:text-blue-450 dark:text-blue-400/70 dark:hover:bg-blue-900/40 dark:hover:text-blue-400/70"
      ),
      children: date.getDate()
    }
  ) });
}
function DatePickerCalendar({
  selected,
  onSelect,
  onReset,
  resetDate: resetDateProp,
  disabledDays
}) {
  const reduceMotion = useReducedMotion();
  const today = React26.useMemo(() => /* @__PURE__ */ new Date(), []);
  const [month, setMonth] = React26.useState(
    () => startOfMonth(selected ?? today)
  );
  const [direction, setDirection] = React26.useState(1);
  const [focusDate, setFocusDate] = React26.useState(
    () => selected ?? today
  );
  const shouldFocus = React26.useRef(true);
  const dayRefs = React26.useRef(/* @__PURE__ */ new Map());
  const isDisabled = React26.useCallback(
    (date) => disabledDays ? dateMatchModifiers(date, disabledDays) : false,
    [disabledDays]
  );
  const isMonthFullyDisabled = React26.useCallback(
    (target) => {
      if (!disabledDays) return false;
      for (let d = startOfMonth(target); isSameMonth(d, target); d = addDays(d, 1)) {
        if (!isDisabled(d)) return false;
      }
      return true;
    },
    [disabledDays, isDisabled]
  );
  const [jumpKey, setJumpKey] = React26.useState(0);
  const goToMonth = (target) => {
    const next2 = startOfMonth(target);
    if (isSameMonth(next2, month)) return;
    const monthDelta = (next2.getFullYear() - month.getFullYear()) * 12 + next2.getMonth() - month.getMonth();
    setDirection(monthDelta > 0 ? 1 : -1);
    if (Math.abs(monthDelta) > 1) setJumpKey((key) => key + 1);
    setMonth(next2);
  };
  const [anchorWeek] = React26.useState(() => monthWeeks(month).first);
  const view = monthWeeks(month);
  const prev = monthWeeks(addMonths(month, -1));
  const next = monthWeeks(addMonths(month, 1));
  const weekIndexes = [];
  for (let w = prev.first; w <= next.last; w++) weekIndexes.push(w);
  const viewRows = view.last - view.first + 1;
  const viewportHeight = viewRows * ROW_PITCH_PX - ROW_GAP_PX;
  const stripOffset = (view.first - anchorWeek) * ROW_PITCH_PX;
  React26.useEffect(() => {
    if (!shouldFocus.current) return;
    shouldFocus.current = false;
    dayRefs.current.get(`${jumpKey}:${dayIndexOf(focusDate)}`)?.focus({ preventScroll: true });
  }, [focusDate, month, jumpKey]);
  const moveFocus = (target) => {
    shouldFocus.current = true;
    setFocusDate(target);
    goToMonth(target);
  };
  const handleKeyDown = (event) => {
    const moves = {
      ArrowLeft: () => addDays(focusDate, -1),
      ArrowRight: () => addDays(focusDate, 1),
      ArrowUp: () => addDays(focusDate, -7),
      ArrowDown: () => addDays(focusDate, 7),
      Home: () => addDays(focusDate, -focusDate.getDay()),
      End: () => addDays(focusDate, 6 - focusDate.getDay()),
      PageUp: () => new Date(focusDate.getFullYear(), focusDate.getMonth() - 1, Math.min(focusDate.getDate(), endOfMonth(addMonths(focusDate, -1)).getDate())),
      PageDown: () => new Date(focusDate.getFullYear(), focusDate.getMonth() + 1, Math.min(focusDate.getDate(), endOfMonth(addMonths(focusDate, 1)).getDate()))
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    moveFocus(move());
  };
  const navigate = (delta) => {
    const target = addMonths(month, delta);
    goToMonth(target);
    if (!isSameMonth(focusDate, target)) {
      setFocusDate(
        selected && isSameMonth(selected, target) ? selected : isSameMonth(today, target) ? today : target
      );
    }
  };
  const resetDate = resetDateProp ?? today;
  const reset = () => {
    goToMonth(resetDate);
    setFocusDate(resetDate);
    onReset(resetDate);
  };
  const label = formatMonth(month);
  return /* @__PURE__ */ jsxs260("div", { "data-slot": "date-picker-calendar", className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsxs260("div", { className: "flex h-7 items-center gap-2", children: [
      /* @__PURE__ */ jsx291(
        "div",
        {
          "aria-live": "polite",
          className: "relative flex h-7 min-w-0 flex-1 overflow-clip",
          style: { maskImage: fadeMask("7px"), WebkitMaskImage: fadeMask("7px") },
          children: /* @__PURE__ */ jsx291(AnimatePresence9, { initial: false, custom: direction, mode: "popLayout", children: /* @__PURE__ */ jsx291(
            motion10.span,
            {
              custom: direction,
              variants: {
                enter: (dir) => ({ y: dir * 14, opacity: 0 }),
                center: { y: 0, opacity: 1 },
                exit: (dir) => ({ y: dir * -14, opacity: 0 })
              },
              initial: "enter",
              animate: "center",
              exit: "exit",
              transition: reduceMotion ? { duration: 0 } : { duration: 0.3, ease: SLIDE_EASE },
              className: "text-sm leading-7 font-medium whitespace-nowrap text-foreground select-none",
              children: label
            },
            label
          ) })
        }
      ),
      /* @__PURE__ */ jsx291(TooltipProvider2, { children: /* @__PURE__ */ jsxs260(Tooltip3, { children: [
        /* @__PURE__ */ jsx291(TooltipTrigger2, { asChild: true, children: /* @__PURE__ */ jsx291(
          "button",
          {
            type: "button",
            "aria-label": `Return to ${formatShortDay(resetDate)}`,
            className: TODAY_BUTTON_CLASS,
            disabled: isDisabled(resetDate),
            onClick: reset,
            children: today.getDate()
          }
        ) }),
        /* @__PURE__ */ jsxs260(TooltipContent2, { side: "top", children: [
          "Return to ",
          formatShortDay(resetDate)
        ] })
      ] }) }),
      /* @__PURE__ */ jsxs260("div", { className: NAV_GROUP_CLASS, children: [
        /* @__PURE__ */ jsx291(
          "button",
          {
            type: "button",
            "aria-label": "Previous month",
            className: NAV_BUTTON_CLASS,
            disabled: isMonthFullyDisabled(addMonths(month, -1)),
            onClick: () => navigate(-1),
            children: /* @__PURE__ */ jsx291(ChevronUpIcon2, {})
          }
        ),
        /* @__PURE__ */ jsx291(
          "div",
          {
            "aria-hidden": true,
            className: "w-px bg-[rgb(43_49_67/0.075)] dark:bg-white/10"
          }
        ),
        /* @__PURE__ */ jsx291(
          "button",
          {
            type: "button",
            "aria-label": "Next month",
            className: NAV_BUTTON_CLASS,
            disabled: isMonthFullyDisabled(addMonths(month, 1)),
            onClick: () => navigate(1),
            children: /* @__PURE__ */ jsx291(ChevronDownIcon6, {})
          }
        )
      ] })
    ] }),
    /* @__PURE__ */ jsxs260("div", { role: "grid", "aria-label": label, onKeyDown: handleKeyDown, children: [
      /* @__PURE__ */ jsx291("div", { role: "row", className: "grid grid-cols-7 gap-2", children: WEEKDAYS.map((weekday) => /* @__PURE__ */ jsx291(
        "div",
        {
          role: "columnheader",
          className: "h-7 text-center text-[13px] leading-7 font-normal text-muted-foreground select-none",
          children: weekday
        },
        weekday
      )) }),
      /* @__PURE__ */ jsx291(
        "div",
        {
          className: cn(
            // `clip`, not `hidden`, so focusing a day can't scroll the strip out of place.
            "relative mt-2 overflow-clip transition-[height]",
            SLIDE_TRANSITION
          ),
          style: {
            height: viewportHeight + VIEWPORT_FADE_PX * 2,
            margin: `${ROW_GAP_PX - VIEWPORT_FADE_PX}px -${VIEWPORT_BLEED_PX}px -${VIEWPORT_FADE_PX}px`,
            maskImage: fadeMask(`${VIEWPORT_FADE_PX}px`),
            WebkitMaskImage: fadeMask(`${VIEWPORT_FADE_PX}px`)
          },
          children: /* @__PURE__ */ jsx291(AnimatePresence9, { initial: false, custom: direction, children: /* @__PURE__ */ jsx291(
            motion10.div,
            {
              custom: direction,
              variants: {
                enter: (dir) => ({ y: dir * JUMP_NUDGE_PX, opacity: 0 }),
                center: { y: 0, opacity: 1 },
                exit: (dir) => ({ y: dir * -JUMP_NUDGE_PX, opacity: 0 })
              },
              initial: "enter",
              animate: "center",
              exit: "exit",
              transition: reduceMotion ? { duration: 0 } : { duration: 0.3, ease: SLIDE_EASE },
              className: "absolute inset-0",
              children: /* @__PURE__ */ jsx291(
                "div",
                {
                  className: cn("absolute transition-transform", SLIDE_TRANSITION),
                  style: {
                    inset: `${VIEWPORT_FADE_PX}px ${VIEWPORT_BLEED_PX}px auto`,
                    transform: `translateY(${-stripOffset}px)`
                  },
                  children: weekIndexes.map((weekIndex) => {
                    const start = weekStart(weekIndex);
                    const isHidden = weekIndex < view.first || weekIndex > view.last;
                    return /* @__PURE__ */ jsx291(
                      "div",
                      {
                        role: "row",
                        "aria-hidden": isHidden || void 0,
                        className: cn(
                          "absolute inset-x-0 grid grid-cols-7 gap-2 transition-opacity",
                          SLIDE_TRANSITION,
                          isHidden && "pointer-events-none opacity-0"
                        ),
                        style: { top: (weekIndex - anchorWeek) * ROW_PITCH_PX },
                        children: Array.from({ length: 7 }, (_, dayOfWeek) => {
                          const date = addDays(start, dayOfWeek);
                          const dayIndex = dayIndexOf(date);
                          return /* @__PURE__ */ jsx291(
                            Day,
                            {
                              date,
                              isSelected: isSameDay(date, selected),
                              isToday: isSameDay(date, today),
                              isOutside: !isSameMonth(date, month),
                              isDisabled: isDisabled(date),
                              isFocusTarget: isSameDay(date, focusDate),
                              isHidden,
                              onSelect,
                              onFocusDate: setFocusDate,
                              buttonRef: (el) => {
                                const refKey = `${jumpKey}:${dayIndex}`;
                                if (el) dayRefs.current.set(refKey, el);
                                else dayRefs.current.delete(refKey);
                              }
                            },
                            dayIndex
                          );
                        })
                      },
                      weekIndex
                    );
                  })
                }
              )
            },
            jumpKey
          ) })
        }
      )
    ] })
  ] });
}
function DatePicker({
  value,
  onValueChange,
  disabledDays,
  defaultDate,
  placeholder = "Select a date",
  formatDate = defaultFormatDate,
  trailing,
  clearable = false,
  disabled,
  id,
  inputSize = "default",
  className,
  "aria-invalid": ariaInvalid,
  onOpenChange,
  editable = false,
  onBlur
}) {
  const [open, setOpen] = React26.useState(false);
  const content = /* @__PURE__ */ jsx291(
    PopoverContent2,
    {
      align: "end",
      sideOffset: 8,
      className: "w-[280px] rounded-xl p-4 [--date-picker-shadow:0_0_0_1px_rgb(78_81_90/0.075),0_4px_8px_rgb(87_90_100/0.1),0_8px_16px_rgb(87_90_100/0.05)] dark:[--date-picker-shadow:0_0_0_1px_rgb(255_255_255/0.1)]",
      style: { boxShadow: "var(--date-picker-shadow)" },
      onOpenAutoFocus: (event) => event.preventDefault(),
      children: /* @__PURE__ */ jsx291(
        DatePickerCalendar,
        {
          selected: value,
          disabledDays,
          resetDate: defaultDate,
          onReset: onValueChange,
          onSelect: (date) => {
            onValueChange(clearable && isSameDay(date, value) ? void 0 : date);
            setOpen(false);
            onOpenChange?.(false);
          }
        }
      )
    }
  );
  const handleOpenChange = (next) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  if (editable) {
    return /* @__PURE__ */ jsxs260(Popover2, { open, onOpenChange: handleOpenChange, children: [
      /* @__PURE__ */ jsx291(
        EditableDateField,
        {
          value,
          onValueChange,
          disabledDays,
          disabled,
          id,
          inputSize,
          className,
          "aria-invalid": ariaInvalid,
          onBlur
        }
      ),
      content
    ] });
  }
  return /* @__PURE__ */ jsxs260(Popover2, { open, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx291(PopoverTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs260(
      "button",
      {
        type: "button",
        id,
        "data-slot": "date-picker",
        disabled,
        "aria-invalid": ariaInvalid || void 0,
        className: cn(
          inputVariants({ inputSize }),
          "flex cursor-pointer items-center gap-2 text-left",
          "focus-visible:[--input-border-color:var(--color-blue-500)] focus-visible:[--input-ring-color:rgb(69_117_205/0.15)] focus-visible:[--input-ring-size:3px]",
          "data-[state=open]:bg-white data-[state=open]:[--input-border-color:var(--color-blue-500)] data-[state=open]:[--input-ring-color:rgb(69_117_205/0.15)] data-[state=open]:[--input-ring-size:3px] dark:data-[state=open]:bg-input/30",
          className
        ),
        onBlur,
        children: [
          /* @__PURE__ */ jsx291(
            "span",
            {
              className: cn(
                "min-w-0 flex-1 truncate",
                !value && "text-[rgb(175_176_179)]"
              ),
              children: value ? formatDate(value) : placeholder
            }
          ),
          trailing,
          /* @__PURE__ */ jsx291(CalendarIcon, { className: "size-4 shrink-0 text-muted-foreground" })
        ]
      }
    ) }),
    content
  ] });
}
function EditableDateField({
  value,
  onValueChange,
  disabledDays,
  disabled,
  id,
  inputSize,
  className,
  "aria-invalid": ariaInvalid,
  onBlur
}) {
  const [text, setText] = React26.useState(() => formatDateInputValue(value));
  const valueTime = value?.getTime();
  React26.useEffect(() => {
    setText(formatDateInputValue(value));
  }, [valueTime]);
  const parse = (next) => {
    const date = parseDateInputValue(next);
    return date && !(disabledDays && dateMatchModifiers(date, disabledDays)) ? date : void 0;
  };
  const isComplete = text.length === "MM/DD/YYYY".length;
  const isUnusable = isComplete && !parse(text);
  return /* @__PURE__ */ jsx291(PopoverAnchor2, { asChild: true, children: /* @__PURE__ */ jsx291("div", { "data-slot": "date-picker", className: cn("w-full", className), children: /* @__PURE__ */ jsx291(
    DateInput,
    {
      id,
      value: text,
      onValueChange: (next) => {
        setText(next);
        if (!next) {
          onValueChange(void 0);
        } else {
          const date = parse(next);
          if (date && !isSameDay(date, value)) onValueChange(date);
        }
      },
      onBlur: () => {
        if (!parse(text)) setText(formatDateInputValue(value));
        onBlur?.();
      },
      disabled,
      inputSize,
      "aria-invalid": ariaInvalid || isUnusable,
      trailing: /* @__PURE__ */ jsx291(PopoverTrigger2, { asChild: true, children: /* @__PURE__ */ jsx291(
        "button",
        {
          type: "button",
          "aria-label": "Choose date",
          disabled,
          className: cn(
            // Colored like the copy input's button: gray-400, darkening on hover and while open.
            "absolute top-1/2 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors outline-none",
            "hover:bg-gray-50 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none dark:hover:bg-white/10 dark:hover:text-gray-200",
            "data-[state=open]:bg-gray-50 data-[state=open]:text-gray-600 dark:data-[state=open]:bg-white/10 dark:data-[state=open]:text-gray-200",
            inputSize === "sm" ? "right-1" : "right-1.5"
          ),
          children: /* @__PURE__ */ jsx291(CalendarIcon, { className: "size-4" })
        }
      ) })
    }
  ) }) });
}

// src/components/ui/direction.tsx
import { Direction } from "radix-ui";
import { jsx as jsx292 } from "react/jsx-runtime";
function DirectionProvider({
  dir,
  direction,
  children
}) {
  return /* @__PURE__ */ jsx292(Direction.DirectionProvider, { dir: direction ?? dir, children });
}
var useDirection = Direction.useDirection;

// src/components/ui/drawer.tsx
import { Drawer as DrawerPrimitive } from "vaul";
import { jsx as jsx293, jsxs as jsxs261 } from "react/jsx-runtime";
function Drawer({
  ...props
}) {
  return /* @__PURE__ */ jsx293(DrawerPrimitive.Root, { "data-slot": "drawer", ...props });
}
function DrawerTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx293(DrawerPrimitive.Trigger, { "data-slot": "drawer-trigger", ...props });
}
function DrawerPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx293(DrawerPrimitive.Portal, { "data-slot": "drawer-portal", ...props });
}
function DrawerClose({
  ...props
}) {
  return /* @__PURE__ */ jsx293(DrawerPrimitive.Close, { "data-slot": "drawer-close", ...props });
}
function DrawerOverlay({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx293(
    DrawerPrimitive.Overlay,
    {
      "data-slot": "drawer-overlay",
      className: cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
}
function DrawerContent({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs261(DrawerPortal, { "data-slot": "drawer-portal", children: [
    /* @__PURE__ */ jsx293(DrawerOverlay, {}),
    /* @__PURE__ */ jsxs261(
      DrawerPrimitive.Content,
      {
        "data-slot": "drawer-content",
        className: cn(
          "group/drawer-content fixed z-50 flex h-auto flex-col bg-background dark:shadow-[0_0_30px_-4px_rgb(0_0_0/0.5)]",
          "data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b",
          "data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t",
          "data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:sm:max-w-sm",
          "data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r data-[vaul-drawer-direction=left]:sm:max-w-sm",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx293("div", { className: "mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full bg-muted group-data-[vaul-drawer-direction=bottom]/drawer-content:block" }),
          children
        ]
      }
    )
  ] });
}
function DrawerHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx293(
    "div",
    {
      "data-slot": "drawer-header",
      className: cn(
        "flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left",
        className
      ),
      ...props
    }
  );
}
function DrawerFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx293(
    "div",
    {
      "data-slot": "drawer-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function DrawerTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx293(
    DrawerPrimitive.Title,
    {
      "data-slot": "drawer-title",
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function DrawerDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx293(
    DrawerPrimitive.Description,
    {
      "data-slot": "drawer-description",
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/field.tsx
import { useMemo as useMemo6, useRef as useRef11 } from "react";
import { cva as cva9 } from "class-variance-authority";

// src/components/ui/label.tsx
import { Label as LabelPrimitive } from "radix-ui";
import { jsx as jsx294 } from "react/jsx-runtime";
function Label({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx294(
    LabelPrimitive.Root,
    {
      "data-slot": "label",
      className: cn(
        "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/field.tsx
import { jsx as jsx295, jsxs as jsxs262 } from "react/jsx-runtime";
function FieldSet({ className, ...props }) {
  return /* @__PURE__ */ jsx295(
    "fieldset",
    {
      "data-slot": "field-set",
      className: cn(
        "flex flex-col gap-6",
        "has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3",
        className
      ),
      ...props
    }
  );
}
function FieldLegend({
  className,
  variant = "legend",
  ...props
}) {
  return /* @__PURE__ */ jsx295(
    "legend",
    {
      "data-slot": "field-legend",
      "data-variant": variant,
      className: cn(
        "mb-3 font-medium",
        "data-[variant=legend]:text-base",
        "data-[variant=label]:text-sm",
        className
      ),
      ...props
    }
  );
}
var fieldGroupVariants = cva9(
  "group/field-group @container/field-group flex w-full",
  {
    variants: {
      variant: {
        default: "gap-7 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4",
        // Connected container that mirrors the ui-kit OptionGroup. The outer border
        // is a non-inset box-shadow halo (shadow-btn-secondary-flat) so it renders
        // outside the element and blends with the surrounding shadows/background,
        // rather than a solid border. The "-flat" token matches the OptionGroup's
        // shadow exactly — secondary halo + a 1px drop, without the larger soft
        // layer the plain secondary button shadow adds. Each direct Field child
        // becomes a padded cell; the dividers between cells are added by the
        // orientation compound variants below (a bottom border when stacked, a
        // right border when side-by-side).
        outline: [
          "gap-0 overflow-hidden rounded-lg bg-background shadow-btn-secondary-flat",
          // Dark mode mirrors the dropdown/select surface: an input-tinted background
          // with a hairline light edge (the light-mode shadow halo is invisible on a
          // dark background), so the group reads as the same kind of raised control.
          "dark:bg-input/30 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.15)]",
          // Explicit duration (matching the rest of the kit's transitions) so the
          // hover background eases in/out smoothly in both light and dark mode —
          // transition-colors alone leaves the duration at 0 and the color snaps.
          // Each option cell is a 40px-min row (matching input / form-field height) with the
          // content vertically centered; 16px horizontal padding, 12px vertical.
          "[&>[data-slot=field]]:min-h-10 [&>[data-slot=field]]:px-4 [&>[data-slot=field]]:py-3 [&>[data-slot=field]]:transition-colors [&>[data-slot=field]]:duration-150",
          // Light: a subtle muted tint on hover.
          "[&>[data-slot=field]:hover]:bg-muted/30",
          // Dark: the muted tint is nearly invisible against the input-tinted
          // surface, so lighten with a translucent white overlay (the kit's dark
          // hover convention). The resting colour is that SAME white at zero alpha
          // — not `transparent`, which is black-at-zero-alpha — so the background
          // stays white through the whole fade. Fading to plain `transparent`
          // instead interpolates toward transparent-black, which is invisible on
          // the dark surface and makes hovering out look like an instant snap.
          "dark:[&>[data-slot=field]]:bg-[rgb(255_255_255/0)]",
          "dark:[&>[data-slot=field]:hover]:bg-[rgb(255_255_255/0.04)]"
        ]
      },
      // Arrangement of the direct Field children. `responsive` stacks them
      // vertically on small viewports and lays them out side-by-side (equal width)
      // at the `md` breakpoint and up — the OptionGroup horizontal layout, but it
      // collapses to a vertical list on mobile. A viewport breakpoint (not a
      // container query) is used so the behavior is self-contained and doesn't
      // depend on an ancestor establishing a container context.
      orientation: {
        vertical: "flex-col",
        horizontal: "flex-row [&>[data-slot=field]]:flex-1",
        responsive: "flex-col md:flex-row md:[&>[data-slot=field]]:flex-1"
      },
      // Number of columns the options are laid out in. `1` (default) keeps the
      // flex behaviour driven by `orientation`. `2` switches to a CSS grid so the
      // options tile into two equal columns and wrap to new rows — the "2 column
      // layout" that reads better than a single stacked list for short options.
      // The grid collapses to a single stacked column below the `md` breakpoint so
      // the options never get squeezed into a cramped two-up grid on small screens.
      // `columns` is defined after `orientation` so its `grid` wins the display
      // conflict over the base `flex`/`orientation` classes once tailwind-merge runs.
      columns: {
        1: "",
        2: "grid grid-cols-1 md:grid-cols-2"
      }
    },
    compoundVariants: [
      // Outline dividers: a soft hairline (the border token at 60% so it reads
      // as a subtle separator rather than a hard line) between stacked rows…
      // These flex-based dividers only apply in single-column mode; the grid
      // layout below draws its own row/column dividers.
      {
        variant: "outline",
        orientation: "vertical",
        columns: 1,
        className: "[&>[data-slot=field]]:border-b [&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08] [&>[data-slot=field]:last-child]:border-b-0"
      },
      // …and between side-by-side columns.
      {
        variant: "outline",
        orientation: "horizontal",
        columns: 1,
        className: "[&>[data-slot=field]]:border-r [&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08] [&>[data-slot=field]:last-child]:border-r-0"
      },
      // Responsive flips the divider direction along with the flex direction:
      // bottom borders while stacked, right borders once side-by-side.
      {
        variant: "outline",
        orientation: "responsive",
        columns: 1,
        className: [
          "[&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08]",
          "[&>[data-slot=field]]:border-b [&>[data-slot=field]:last-child]:border-b-0",
          "md:[&>[data-slot=field]]:border-b-0 md:[&>[data-slot=field]]:border-r md:[&>[data-slot=field]:last-child]:border-r-0"
        ]
      },
      // Two-column grid dividers. Because the grid collapses to a single stacked
      // column below md (see the `columns` variant), the dividers are split by
      // breakpoint:
      //   • stacked (below md): a bottom border between every row, dropped on the
      //     last cell — a plain stacked list, no vertical dividers.
      //   • two-up (md+): a right border on the left column (odd cells) and a bottom
      //     border between rows, dropped for the final row (the last two cells) so the
      //     outer container's rounded edge stays clean.
      {
        variant: "outline",
        columns: 2,
        className: [
          "[&>[data-slot=field]]:border-border/60 dark:[&>[data-slot=field]]:border-white/[0.08]",
          "[&>[data-slot=field]]:border-b [&>[data-slot=field]:last-child]:border-b-0",
          "md:[&>[data-slot=field]:nth-child(odd)]:border-r",
          "md:[&>[data-slot=field]:nth-last-child(-n+2)]:border-b-0"
        ]
      }
    ],
    defaultVariants: {
      variant: "default",
      orientation: "vertical",
      columns: 1
    }
  }
);
function FieldGroup({
  className,
  variant = "default",
  orientation = "vertical",
  columns = 1,
  ...props
}) {
  return /* @__PURE__ */ jsx295(
    "div",
    {
      "data-slot": "field-group",
      "data-variant": variant,
      "data-orientation": orientation,
      "data-columns": columns,
      className: cn(fieldGroupVariants({ variant, orientation, columns }), className),
      ...props
    }
  );
}
var fieldVariants = cva9(
  "group/field flex w-full gap-2 data-[invalid=true]:text-destructive has-[[role=checkbox]]:cursor-pointer has-[[role=radio]]:cursor-pointer has-[[role=switch]]:cursor-pointer",
  {
    variants: {
      orientation: {
        vertical: ["flex-col [&>*]:w-full [&>.sr-only]:w-auto"],
        horizontal: [
          "flex-row items-center",
          "[&>[data-slot=field-label]]:flex-auto",
          "has-[>[data-slot=field-content]]:items-start has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
        ],
        responsive: [
          "flex-col @md/field-group:flex-row @md/field-group:items-center [&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto",
          "@md/field-group:[&>[data-slot=field-label]]:flex-auto",
          "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
        ]
      }
    },
    defaultVariants: {
      orientation: "vertical"
    }
  }
);
function Field({
  className,
  orientation = "vertical",
  onClick,
  ...props
}) {
  const fieldRef = useRef11(null);
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented) {
      return;
    }
    const target = event.target;
    if (target.closest(
      'label, button, a, input, select, textarea, [role="checkbox"], [role="radio"], [role="switch"]'
    )) {
      return;
    }
    fieldRef.current?.querySelector(
      '[role="checkbox"], [role="radio"], [role="switch"]'
    )?.click();
  };
  return /* @__PURE__ */ jsx295(
    "div",
    {
      ref: fieldRef,
      role: "group",
      "data-slot": "field",
      "data-orientation": orientation,
      className: cn(fieldVariants({ orientation }), className),
      onClick: handleClick,
      ...props
    }
  );
}
function FieldContent({ className, ...props }) {
  return /* @__PURE__ */ jsx295(
    "div",
    {
      "data-slot": "field-content",
      className: cn(
        "group/field-content flex flex-1 flex-col gap-0.5 leading-snug",
        className
      ),
      ...props
    }
  );
}
function FieldLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx295(
    Label,
    {
      "data-slot": "field-label",
      className: cn(
        "group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>*]:data-[slot=field]:p-4",
        "has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/5 dark:has-data-[state=checked]:bg-primary/10",
        className
      ),
      ...props
    }
  );
}
function FieldTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx295(
    "div",
    {
      "data-slot": "field-label",
      className: cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50",
        className
      ),
      ...props
    }
  );
}
function FieldDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx295(
    "p",
    {
      "data-slot": "field-description",
      className: cn(
        "text-sm leading-normal font-normal text-muted-foreground group-has-[[data-orientation=horizontal]]/field:text-balance",
        "last:mt-0 nth-last-2:-mt-1 [[data-variant=legend]+&]:-mt-1.5",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function FieldSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs262(
    "div",
    {
      "data-slot": "field-separator",
      "data-content": !!children,
      className: cn(
        "relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx295(Separator, { className: "absolute inset-0 top-1/2" }),
        children && /* @__PURE__ */ jsx295(
          "span",
          {
            className: "relative mx-auto block w-fit bg-background px-2 text-muted-foreground",
            "data-slot": "field-separator-content",
            children
          }
        )
      ]
    }
  );
}
function FieldError({
  className,
  children,
  errors,
  ...props
}) {
  const content = useMemo6(() => {
    if (children) {
      return children;
    }
    if (!errors?.length) {
      return null;
    }
    const uniqueErrors = [
      ...new Map(errors.map((error) => [error?.message, error])).values()
    ];
    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message;
    }
    return /* @__PURE__ */ jsx295("ul", { className: "ml-4 flex list-disc flex-col gap-1", children: uniqueErrors.map(
      (error, index) => error?.message && /* @__PURE__ */ jsx295("li", { children: error.message }, index)
    ) });
  }, [children, errors]);
  if (!content) {
    return null;
  }
  return /* @__PURE__ */ jsx295(
    "div",
    {
      role: "alert",
      "data-slot": "field-error",
      className: cn("text-sm font-normal text-destructive", className),
      ...props,
      children: content
    }
  );
}

// src/components/ui/form.tsx
import * as React27 from "react";
import { Slot as Slot5 } from "radix-ui";
import {
  Controller,
  FormProvider,
  useFormContext,
  useFormState
} from "react-hook-form";
import { jsx as jsx296 } from "react/jsx-runtime";
var Form = FormProvider;
var FormFieldContext = React27.createContext(
  {}
);
var FormField = ({
  ...props
}) => {
  return /* @__PURE__ */ jsx296(FormFieldContext.Provider, { value: { name: props.name }, children: /* @__PURE__ */ jsx296(Controller, { ...props }) });
};
var useFormField = () => {
  const fieldContext = React27.useContext(FormFieldContext);
  const itemContext = React27.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);
  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }
  const { id } = itemContext;
  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState
  };
};
var FormItemContext = React27.createContext(
  {}
);
function FormItem({ className, ...props }) {
  const id = React27.useId();
  return /* @__PURE__ */ jsx296(FormItemContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx296(
    "div",
    {
      "data-slot": "form-item",
      className: cn("grid gap-2", className),
      ...props
    }
  ) });
}
function FormLabel({
  className,
  ...props
}) {
  const { error, formItemId } = useFormField();
  return /* @__PURE__ */ jsx296(
    Label,
    {
      "data-slot": "form-label",
      "data-error": !!error,
      className: cn("data-[error=true]:text-destructive", className),
      htmlFor: formItemId,
      ...props
    }
  );
}
function FormControl({ ...props }) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return /* @__PURE__ */ jsx296(
    Slot5.Root,
    {
      "data-slot": "form-control",
      id: formItemId,
      "aria-describedby": !error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`,
      "aria-invalid": !!error,
      ...props
    }
  );
}
function FormDescription({ className, ...props }) {
  const { formDescriptionId } = useFormField();
  return /* @__PURE__ */ jsx296(
    "p",
    {
      "data-slot": "form-description",
      id: formDescriptionId,
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}
function FormMessage({ className, ...props }) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;
  if (!body) {
    return null;
  }
  return /* @__PURE__ */ jsx296(
    "p",
    {
      "data-slot": "form-message",
      id: formMessageId,
      className: cn("text-sm text-destructive", className),
      ...props,
      children: body
    }
  );
}

// src/components/animate-ui/primitives/radix/hover-card.tsx
import { HoverCard as HoverCardPrimitive } from "radix-ui";
import {
  AnimatePresence as AnimatePresence10,
  motion as motion11,
  useMotionValue as useMotionValue2,
  useSpring as useSpring2
} from "motion/react";
import { jsx as jsx297 } from "react/jsx-runtime";
var [HoverCardProvider, useHoverCard] = getStrictContext("HoverCardContext");
function HoverCard({
  followCursor = false,
  followCursorSpringOptions = { stiffness: 200, damping: 17 },
  ...props
}) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props?.open,
    defaultValue: props?.defaultOpen,
    onChange: props?.onOpenChange
  });
  const x = useMotionValue2(0);
  const y = useMotionValue2(0);
  return /* @__PURE__ */ jsx297(
    HoverCardProvider,
    {
      value: {
        isOpen,
        setIsOpen,
        x,
        y,
        followCursor,
        followCursorSpringOptions
      },
      children: /* @__PURE__ */ jsx297(
        HoverCardPrimitive.Root,
        {
          "data-slot": "hover-card",
          ...props,
          onOpenChange: setIsOpen
        }
      )
    }
  );
}
function HoverCardTrigger({ onMouseMove, ...props }) {
  const { x, y, followCursor } = useHoverCard();
  const handleMouseMove = (event) => {
    onMouseMove?.(event);
    const target = event.currentTarget.getBoundingClientRect();
    if (followCursor === "x" || followCursor === true) {
      const eventOffsetX = event.clientX - target.left;
      const offsetXFromCenter = (eventOffsetX - target.width / 2) / 2;
      x.set(offsetXFromCenter);
    }
    if (followCursor === "y" || followCursor === true) {
      const eventOffsetY = event.clientY - target.top;
      const offsetYFromCenter = (eventOffsetY - target.height / 2) / 2;
      y.set(offsetYFromCenter);
    }
  };
  return /* @__PURE__ */ jsx297(
    HoverCardPrimitive.Trigger,
    {
      "data-slot": "hover-card-trigger",
      onMouseMove: handleMouseMove,
      ...props
    }
  );
}
function HoverCardPortal(props) {
  const { isOpen } = useHoverCard();
  return /* @__PURE__ */ jsx297(AnimatePresence10, { children: isOpen && /* @__PURE__ */ jsx297(
    HoverCardPrimitive.Portal,
    {
      forceMount: true,
      "data-slot": "hover-card-portal",
      ...props
    }
  ) });
}
function HoverCardContent({
  align,
  alignOffset,
  side,
  sideOffset,
  avoidCollisions,
  collisionBoundary,
  collisionPadding,
  arrowPadding,
  sticky,
  hideWhenDetached,
  style,
  transition = { type: "spring", stiffness: 600, damping: 35 },
  ...props
}) {
  const { x, y, followCursor, followCursorSpringOptions } = useHoverCard();
  const translateX = useSpring2(x, followCursorSpringOptions);
  const translateY = useSpring2(y, followCursorSpringOptions);
  return /* @__PURE__ */ jsx297(
    HoverCardPrimitive.Content,
    {
      asChild: true,
      forceMount: true,
      align,
      alignOffset,
      side,
      sideOffset,
      avoidCollisions,
      collisionBoundary,
      collisionPadding,
      arrowPadding,
      sticky,
      hideWhenDetached,
      children: /* @__PURE__ */ jsx297(
        motion11.div,
        {
          "data-slot": "hover-card-content",
          initial: { opacity: 0, scale: 0.98 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.98 },
          transition,
          style: {
            x: followCursor === "x" || followCursor === true ? translateX : void 0,
            y: followCursor === "y" || followCursor === true ? translateY : void 0,
            ...style
          },
          ...props
        },
        "hover-card-content"
      )
    }
  );
}

// src/components/ui/hover-card.tsx
import { jsx as jsx298 } from "react/jsx-runtime";
function HoverCard2({
  ...props
}) {
  return /* @__PURE__ */ jsx298(HoverCard, { ...props });
}
function HoverCardTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx298(HoverCardTrigger, { "data-slot": "hover-card-trigger", ...props });
}
function HoverCardContent2({
  className,
  align = "center",
  sideOffset = 4,
  ...props
}) {
  return /* @__PURE__ */ jsx298(HoverCardPortal, { children: /* @__PURE__ */ jsx298(
    HoverCardContent,
    {
      "data-slot": "hover-card-content",
      align,
      sideOffset,
      className: cn(
        "z-50 w-64 origin-(--radix-hover-card-content-transform-origin) rounded-lg bg-popover p-4 text-popover-foreground shadow-md ring-1 ring-black/5 outline-hidden dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
        className
      ),
      ...props
    }
  ) });
}

// src/components/ui/amount-input.tsx
import { NumericFormat as NumericFormat2 } from "react-number-format";
import { jsx as jsx299, jsxs as jsxs263 } from "react/jsx-runtime";
function AmountInput({
  value,
  onValueChange,
  prefix,
  containerClassName,
  className,
  inputRef,
  ...props
}) {
  return /* @__PURE__ */ jsxs263("div", { className: cn("flex items-baseline", containerClassName), children: [
    prefix,
    /* @__PURE__ */ jsx299(
      NumericFormat2,
      {
        "data-slot": "amount-input",
        getInputRef: inputRef,
        value,
        onValueChange: (values) => onValueChange(values.value),
        thousandSeparator: ",",
        decimalScale: 2,
        fixedDecimalScale: true,
        allowNegative: false,
        inputMode: "decimal",
        className: cn(
          "w-full bg-transparent px-0 outline-none placeholder:text-gray-400 disabled:pointer-events-none disabled:opacity-50",
          className
        ),
        ...props
      }
    )
  ] });
}

// src/components/ui/input-otp.tsx
import * as React28 from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { MinusIcon } from "lucide-react";
import { jsx as jsx300, jsxs as jsxs264 } from "react/jsx-runtime";
function InputOTP({
  className,
  containerClassName,
  ...props
}) {
  return /* @__PURE__ */ jsx300(
    OTPInput,
    {
      "data-slot": "input-otp",
      containerClassName: cn(
        "flex items-center gap-2 has-disabled:opacity-50",
        containerClassName
      ),
      className: cn("disabled:cursor-not-allowed", className),
      ...props
    }
  );
}
function InputOTPGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx300(
    "div",
    {
      "data-slot": "input-otp-group",
      className: cn(
        "flex items-center rounded-lg bg-gray-25 inset-shadow-input transition-[color,box-shadow] duration-150 hover:inset-shadow-input-hover dark:bg-input/30 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.1)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.2)]",
        className
      ),
      ...props
    }
  );
}
function InputOTPSlot({
  index,
  className,
  ...props
}) {
  const inputOTPContext = React28.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};
  return /* @__PURE__ */ jsxs264(
    "div",
    {
      "data-slot": "input-otp-slot",
      "data-active": isActive,
      className: cn(
        "relative my-px flex h-9 w-9 items-center justify-center border-r border-gray-100 text-sm font-medium transition-all outline-none first:rounded-l-lg last:rounded-r-lg last:border-r-0 data-[active=true]:z-10 data-[active=true]:ring-2 data-[active=true]:ring-blue-500/10 data-[active=true]:shadow-[0_0_0_1px_var(--color-blue-500)] aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] data-[active=true]:aria-invalid:ring-destructive/20 dark:border-gray-700 dark:data-[active=true]:aria-invalid:ring-destructive/40",
        className
      ),
      ...props,
      children: [
        char,
        hasFakeCaret && /* @__PURE__ */ jsx300("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx300("div", { className: "h-4 w-px animate-caret-blink bg-foreground duration-1000" }) })
      ]
    }
  );
}
function InputOTPSeparator({ ...props }) {
  return /* @__PURE__ */ jsx300("div", { "data-slot": "input-otp-separator", role: "separator", ...props, children: /* @__PURE__ */ jsx300(MinusIcon, {}) });
}

// src/components/ui/item.tsx
import { cva as cva10 } from "class-variance-authority";
import { Slot as Slot6 } from "radix-ui";
import { jsx as jsx301 } from "react/jsx-runtime";
function ItemGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      role: "list",
      "data-slot": "item-group",
      className: cn("group/item-group flex flex-col", className),
      ...props
    }
  );
}
function ItemSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx301(
    Separator,
    {
      "data-slot": "item-separator",
      orientation: "horizontal",
      className: cn("my-0", className),
      ...props
    }
  );
}
var itemVariants = cva10(
  "group/item flex flex-wrap items-center rounded-lg border border-transparent text-sm transition-colors duration-100 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [a]:transition-colors [a]:hover:bg-accent/50",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border-border",
        muted: "bg-muted/50"
      },
      size: {
        default: "gap-4 p-4",
        sm: "gap-2.5 px-4 py-3"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Item({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot6.Root : "div";
  return /* @__PURE__ */ jsx301(
    Comp,
    {
      "data-slot": "item",
      "data-variant": variant,
      "data-size": size,
      className: cn(itemVariants({ variant, size, className })),
      ...props
    }
  );
}
var itemMediaVariants = cva10(
  "flex shrink-0 items-center justify-center gap-2 group-has-[[data-slot=item-description]]/item:translate-y-0.5 group-has-[[data-slot=item-description]]/item:self-start [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "size-8 rounded-sm border bg-muted [&_svg:not([class*='size-'])]:size-4",
        image: "size-10 overflow-hidden rounded-sm [&_img]:size-full [&_img]:object-cover"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function ItemMedia({
  className,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-media",
      "data-variant": variant,
      className: cn(itemMediaVariants({ variant, className })),
      ...props
    }
  );
}
function ItemContent({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-content",
      className: cn(
        "flex flex-1 flex-col gap-1 [&+[data-slot=item-content]]:flex-none",
        className
      ),
      ...props
    }
  );
}
function ItemTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-title",
      className: cn(
        "flex w-fit items-center gap-2 text-sm leading-snug font-medium",
        className
      ),
      ...props
    }
  );
}
function ItemDescription({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "p",
    {
      "data-slot": "item-description",
      className: cn(
        "line-clamp-2 text-sm leading-normal font-normal text-balance text-muted-foreground",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      ),
      ...props
    }
  );
}
function ItemActions({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-actions",
      className: cn("flex items-center gap-2", className),
      ...props
    }
  );
}
function ItemHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-header",
      className: cn(
        "flex basis-full items-center justify-between gap-2",
        className
      ),
      ...props
    }
  );
}
function ItemFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx301(
    "div",
    {
      "data-slot": "item-footer",
      className: cn(
        "flex basis-full items-center justify-between gap-2",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/kbd.tsx
import { jsx as jsx302 } from "react/jsx-runtime";
function Kbd({ className, ...props }) {
  return /* @__PURE__ */ jsx302(
    "kbd",
    {
      "data-slot": "kbd",
      className: cn(
        "pointer-events-none inline-flex h-5 w-fit min-w-5 items-center justify-center gap-1 rounded-sm bg-muted px-1 font-sans text-xs font-normal text-muted-foreground select-none",
        "[&_svg:not([class*='size-'])]:size-3",
        "[[data-slot=tooltip-content]_&]:bg-background/20 [[data-slot=tooltip-content]_&]:text-background dark:[[data-slot=tooltip-content]_&]:bg-background/10",
        className
      ),
      ...props
    }
  );
}
function KbdGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx302(
    "kbd",
    {
      "data-slot": "kbd-group",
      className: cn("inline-flex items-center gap-1", className),
      ...props
    }
  );
}

// src/components/ui/logo.tsx
import { jsx as jsx303, jsxs as jsxs265 } from "react/jsx-runtime";
function LogoWordmark({
  size = 16,
  color = "currentColor",
  className,
  ...props
}) {
  const width = size / 19 * 87;
  return /* @__PURE__ */ jsxs265(
    "svg",
    {
      width,
      height: size,
      viewBox: "0 0 87 19",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      className,
      ...props,
      children: [
        /* @__PURE__ */ jsx303("g", { clipPath: "url(#logo-wordmark-clip)", children: /* @__PURE__ */ jsx303(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M28.1811 2.16278V0H33.0581V18.654H29.814V3.24417C29.814 2.95736 29.7 2.68231 29.4972 2.47951C29.2944 2.27671 29.0194 2.16278 28.7326 2.16278H28.1811ZM8.77018 15.5284C9.29957 15.2419 9.74358 14.8203 10.057 14.3064L10.1219 14.155L12.7281 15.9826L12.6632 16.0691C12.0259 16.9766 11.1804 17.7182 10.1976 18.2319C9.19389 18.7455 8.08091 19.0089 6.95345 18.9997C5.7259 19.011 4.51718 18.6976 3.44975 18.0913C2.40616 17.4929 1.53747 16.6317 0.930117 15.5933C0.314039 14.5407 -0.00720686 13.3417 0.000122672 12.1221C-0.00561525 10.9027 0.31552 9.70399 0.930117 8.65078C1.52795 7.59667 2.39821 6.72268 3.44975 6.12034C4.51633 5.51074 5.72498 5.19365 6.95345 5.20115C8.00027 5.19459 9.03515 5.42373 9.98134 5.87161C10.8982 6.30469 11.7071 6.93635 12.3496 7.72079L12.4145 7.79649L10.003 9.81869L9.93808 9.73217C9.59507 9.28032 9.15541 8.91085 8.65123 8.65078C8.12907 8.37267 7.545 8.23129 6.95345 8.23985C6.28793 8.23221 5.63317 8.40806 5.06102 8.74811C4.50633 9.09381 4.05155 9.57841 3.74173 10.1539C3.41084 10.7568 3.2396 11.4343 3.24429 12.1221C3.2507 12.7994 3.42535 13.4646 3.75254 14.0577C4.05492 14.6449 4.51569 15.1356 5.08265 15.4743C5.64807 15.8116 6.29512 15.9874 6.95345 15.9826C7.5882 15.9915 8.21432 15.835 8.77018 15.5284ZM20.687 5.2123C21.926 5.20317 23.1455 5.52016 24.2232 6.13147C25.2819 6.72689 26.16 7.59749 26.7644 8.6511C27.3809 9.70747 27.7021 10.9101 27.6944 12.1332C27.7018 13.3528 27.3805 14.5518 26.7644 15.6044C26.1519 16.6462 25.2753 17.5079 24.2232 18.1024C23.1445 18.71 21.925 19.0232 20.687 19.0108C19.4595 19.0221 18.2508 18.7087 17.1833 18.1024C16.1367 17.5044 15.2644 16.6433 14.6529 15.6044C14.0368 14.5518 13.7156 13.3528 13.7229 12.1332C13.7152 10.9101 14.0364 9.70747 14.6529 8.6511C15.2565 7.60053 16.1302 6.73057 17.1833 6.13147C18.2499 5.52187 19.4586 5.20479 20.687 5.2123ZM22.5903 15.4314C23.1513 15.1044 23.6115 14.6293 23.9204 14.058C24.254 13.4523 24.4253 12.7706 24.4178 12.0791C24.4225 11.3914 24.2513 10.7139 23.9204 10.111C23.6106 9.53548 23.1558 9.05088 22.6011 8.70518C22.029 8.36513 21.3742 8.18928 20.7087 8.19692C20.0465 8.18903 19.3952 8.36497 18.827 8.70518C18.2668 9.04741 17.8077 9.53263 17.4969 10.111C17.1694 10.7147 17.0018 11.3923 17.0103 12.0791C16.9942 12.7689 17.1582 13.451 17.4861 14.058C17.7874 14.6313 18.2448 15.1074 18.8054 15.4314C19.3746 15.769 20.0252 15.9448 20.687 15.9397C21.3557 15.9449 22.0133 15.7693 22.5903 15.4314ZM45.0398 12.4575C45.0541 13.1281 44.9057 13.7923 44.6072 14.3931C44.3423 14.8879 43.9456 15.2996 43.461 15.5827C42.9837 15.8446 42.4482 15.9822 41.9038 15.9828C41.3906 15.9957 40.8832 15.8725 40.4331 15.6259C40.0208 15.3789 39.6842 15.0236 39.4598 14.5986C39.2129 14.1097 39.0903 13.5674 39.103 13.0198V5.52575H35.8588V13.6686C35.8422 14.6184 36.0693 15.5566 36.5185 16.3937C36.9441 17.1918 37.5846 17.8549 38.3676 18.3078C39.2054 18.7784 40.1536 19.0173 41.1144 18.9999C42.1583 19.0109 43.1846 18.73 44.0774 18.1888C44.4767 17.9355 44.8403 17.63 45.1588 17.2804V18.6971H48.2623V5.52575H45.0182L45.0398 12.4575ZM66.0079 5.21235C66.9256 5.20566 67.8299 5.43265 68.6357 5.87198C69.4093 6.31173 70.0514 6.95012 70.4957 7.72116C70.9585 8.4714 71.1985 9.33773 71.1878 10.2192V18.7513H67.9436V11.041C67.949 10.5533 67.8378 10.0713 67.6192 9.63522C67.4159 9.23107 67.1054 8.89063 66.7216 8.65116C66.331 8.4131 65.8814 8.28946 65.424 8.2943C64.9729 8.28634 64.5294 8.41038 64.1479 8.65116C63.7628 8.89567 63.4465 9.23437 63.2287 9.63522C62.9981 10.0677 62.8791 10.5509 62.8827 11.041V18.7513H59.6385V11.041C59.6406 10.5524 59.5257 10.0703 59.3033 9.63522C59.0894 9.23511 58.7767 8.89635 58.3949 8.65116C58.0042 8.38091 57.5399 8.23743 57.0648 8.24022C56.6178 8.2368 56.179 8.36057 55.7996 8.59708C55.412 8.83852 55.0949 9.17797 54.8804 9.58115C54.658 10.0163 54.5431 10.4983 54.5452 10.987V18.6973H51.301V5.52594H54.264V6.95337C54.569 6.55993 54.9339 6.21692 55.3454 5.93688C56.0822 5.45816 56.9432 5.20624 57.8218 5.21235C58.6427 5.20363 59.4491 5.42866 60.1468 5.86117C60.7499 6.22657 61.2605 6.72615 61.6391 7.32105C62.0829 6.74989 62.6385 6.27522 63.272 5.92605C64.1045 5.44969 65.0488 5.20337 66.0079 5.21235ZM85.8517 7.85075C85.4218 7.04573 84.7733 6.3785 83.9809 5.92588C83.1544 5.44443 82.2122 5.19767 81.2558 5.21218C80.1986 5.20035 79.1598 5.4891 78.2603 6.04484C77.8655 6.29163 77.5023 6.58578 77.1789 6.92076V5.52578H74.0429V18.6971H77.2871V11.7654C77.2695 11.1025 77.4143 10.4454 77.7088 9.85133C77.9904 9.33688 78.4198 8.91874 78.9416 8.65099C79.4194 8.37944 79.96 8.23776 80.5096 8.24006C81.017 8.22906 81.5179 8.35615 81.9587 8.60774C82.3864 8.86626 82.7315 9.24139 82.9536 9.68913C83.1987 10.1671 83.3213 10.6985 83.3104 11.2355V18.7403H86.5546V10.6191C86.5654 9.65087 86.3231 8.69656 85.8517 7.85075Z",
            fill: color
          }
        ) }),
        /* @__PURE__ */ jsx303("defs", { children: /* @__PURE__ */ jsx303("clipPath", { id: "logo-wordmark-clip", children: /* @__PURE__ */ jsx303("rect", { width: "86.5111", height: "19", fill: "white" }) }) })
      ]
    }
  );
}
function LogoIcon({
  size = 16,
  color = "currentColor",
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx303(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      className,
      ...props,
      children: /* @__PURE__ */ jsx303(
        "path",
        {
          d: "M6 6V10H8C9.06087 10 10.0783 10.4214 10.8284 11.1716C11.5786 11.9217 12 12.9391 12 14V16H4C2.93913 16 1.92172 15.5786 1.17157 14.8284C0.421427 14.0783 0 13.0609 0 12V4C0 2.93913 0.421427 1.92172 1.17157 1.17157C1.92172 0.421427 2.93913 0 4 0L12 0C13.0609 0 14.0783 0.421427 14.8284 1.17157C15.5786 1.92172 16 2.93913 16 4V6H6Z",
          fill: color
        }
      )
    }
  );
}
function Logo({ variant = "wordmark", ...props }) {
  if (variant === "icon") {
    return /* @__PURE__ */ jsx303(LogoIcon, { ...props });
  }
  return /* @__PURE__ */ jsx303(LogoWordmark, { ...props });
}

// src/components/ui/menubar.tsx
import { ChevronRightIcon as ChevronRightIcon7, CircleIcon as CircleIcon3 } from "lucide-react";
import { Menubar as MenubarPrimitive } from "radix-ui";
import { jsx as jsx304, jsxs as jsxs266 } from "react/jsx-runtime";
function Menubar({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.Root,
    {
      "data-slot": "menubar",
      className: cn(
        "flex h-9 items-center gap-1 rounded-lg bg-background p-1 ring-1 ring-black/5 shadow-xs dark:ring-white/15 dark:shadow-none",
        className
      ),
      ...props
    }
  );
}
function MenubarMenu({
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPrimitive.Menu, { "data-slot": "menubar-menu", ...props });
}
function MenubarGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPrimitive.Group, { "data-slot": "menubar-group", ...props });
}
function MenubarPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPrimitive.Portal, { "data-slot": "menubar-portal", ...props });
}
function MenubarRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPrimitive.RadioGroup, { "data-slot": "menubar-radio-group", ...props });
}
function MenubarTrigger({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.Trigger,
    {
      "data-slot": "menubar-trigger",
      className: cn(
        itemHighlight,
        "flex items-center rounded-md px-2 py-1 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[state=open]:before:opacity-100 data-[state=open]:text-accent-foreground",
        className
      ),
      ...props
    }
  );
}
function MenubarContent({
  className,
  align = "start",
  alignOffset = -4,
  sideOffset = 8,
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPortal, { children: /* @__PURE__ */ jsx304(
    MenubarPrimitive.Content,
    {
      "data-slot": "menubar-content",
      align,
      alignOffset,
      sideOffset,
      className: cn(
        "z-50 min-w-[12rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-md ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  ) });
}
function MenubarItem({
  className,
  inset,
  variant = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.Item,
    {
      "data-slot": "menubar-item",
      "data-inset": inset,
      "data-variant": variant,
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:before:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:text-red-400 dark:data-[variant=destructive]:before:bg-red-500/15 dark:data-[variant=destructive]:focus:text-red-400 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive! dark:data-[variant=destructive]:*:[svg]:text-red-400! data-[variant=destructive]:focus:[&_[data-slot=menubar-shortcut]]:text-destructive/50 dark:data-[variant=destructive]:focus:[&_[data-slot=menubar-shortcut]]:text-red-400/50",
        className
      ),
      ...props
    }
  );
}
function MenubarCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs266(
    MenubarPrimitive.CheckboxItem,
    {
      "data-slot": "menubar-checkbox-item",
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx304("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx304(AnimatedCheckIcon, { checked: checked === true }) }),
        children
      ]
    }
  );
}
function MenubarRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs266(
    MenubarPrimitive.RadioItem,
    {
      "data-slot": "menubar-radio-item",
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md py-2 pr-3 pl-8 text-sm font-medium outline-hidden transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx304("span", { className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx304(MenubarPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx304(CircleIcon3, { className: "size-2 fill-current text-primary" }) }) }),
        children
      ]
    }
  );
}
function MenubarLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.Label,
    {
      "data-slot": "menubar-label",
      "data-inset": inset,
      className: cn(
        "px-2 py-1.5 text-sm font-medium data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function MenubarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.Separator,
    {
      "data-slot": "menubar-separator",
      className: cn("-mx-1 my-1 h-px bg-gray-100 dark:bg-white/[0.08]", className),
      ...props
    }
  );
}
function MenubarShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    "span",
    {
      "data-slot": "menubar-shortcut",
      className: cn(
        "ml-auto text-xs font-normal text-gray-400",
        className
      ),
      ...props
    }
  );
}
function MenubarSub({
  ...props
}) {
  return /* @__PURE__ */ jsx304(MenubarPrimitive.Sub, { "data-slot": "menubar-sub", ...props });
}
function MenubarSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs266(
    MenubarPrimitive.SubTrigger,
    {
      "data-slot": "menubar-sub-trigger",
      "data-inset": inset,
      className: cn(
        itemHighlight,
        "flex cursor-default items-center gap-2 rounded-md px-3 py-2 text-sm font-medium outline-none transition-[color] duration-150 select-none before:bg-accent focus:before:opacity-100 focus:text-accent-foreground data-[inset]:pl-8 data-[state=open]:before:opacity-100 data-[state=open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx304(ChevronRightIcon7, { className: "ml-auto h-4 w-4" })
      ]
    }
  );
}
function MenubarSubContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx304(
    MenubarPrimitive.SubContent,
    {
      "data-slot": "menubar-sub-content",
      className: cn(
        "z-50 min-w-[8rem] origin-(--radix-menubar-content-transform-origin) overflow-hidden rounded-lg bg-popover p-1 text-secondary-foreground shadow-lg ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}

// src/components/ui/money-input.tsx
import * as React29 from "react";
import { NumericFormat as NumericFormat3 } from "react-number-format";
import { jsx as jsx305 } from "react/jsx-runtime";
function MoneyInput({
  value,
  currency,
  onValueChange,
  onBlur,
  placeholder,
  disabled,
  id,
  name,
  inputSize = "default",
  className,
  "aria-invalid": ariaInvalid,
  inputRef
}) {
  const { fractionDigits, symbol } = getAmountFormat(currency);
  const prefix = symbol ? `${symbol} ` : "";
  const [draft, setDraft] = React29.useState(
    () => toAmountDraft(value, fractionDigits)
  );
  const [synced, setSynced] = React29.useState({ value, fractionDigits });
  if (!Object.is(synced.value, value) || synced.fractionDigits !== fractionDigits) {
    setSynced({ value, fractionDigits });
    if (synced.fractionDigits !== fractionDigits || parseAmountDraft(draft, fractionDigits) !== (value ?? void 0)) {
      setDraft(toAmountDraft(value, fractionDigits));
    }
  }
  return /* @__PURE__ */ jsx305(
    NumericFormat3,
    {
      "data-slot": "money-input",
      getInputRef: inputRef,
      id,
      name,
      value: draft,
      valueIsNumericString: true,
      onValueChange: (values, sourceInfo) => {
        setDraft(values.value);
        if (sourceInfo.source === "event") {
          onValueChange?.(parseAmountDraft(values.value, fractionDigits));
        }
      },
      onBlur,
      prefix,
      thousandSeparator: ",",
      decimalScale: fractionDigits,
      fixedDecimalScale: true,
      allowNegative: false,
      inputMode: "decimal",
      placeholder: placeholder ?? `${prefix}0${fractionDigits > 0 ? `.${"0".repeat(fractionDigits)}` : ""}`,
      disabled,
      "aria-invalid": ariaInvalid || void 0,
      className: cn(
        inputVariants({ inputSize }),
        "text-sm leading-4 font-normal tracking-normal normal-nums",
        className
      )
    }
  );
}

// src/components/ui/native-select.tsx
import { ChevronDownIcon as ChevronDownIcon7 } from "lucide-react";
import { jsx as jsx306, jsxs as jsxs267 } from "react/jsx-runtime";
function NativeSelect({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsxs267(
    "div",
    {
      className: "group/native-select relative w-fit has-[select:disabled]:opacity-50",
      "data-slot": "native-select-wrapper",
      children: [
        /* @__PURE__ */ jsx306(
          "select",
          {
            "data-slot": "native-select",
            "data-size": size,
            className: cn(
              "h-9 w-full min-w-0 appearance-none rounded-lg border border-input bg-white px-3 pr-9 text-sm leading-none font-medium shadow-[0_1px_1px_0_rgb(0_0_0/0.03)] transition-[color,border-color,box-shadow] duration-150 outline-none placeholder:text-gray-400 hover:border-gray-200 active:shadow-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 data-[size=sm]:h-8 dark:border-white/10 dark:bg-input/30 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-input/50",
              "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
              "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
              className
            ),
            ...props
          }
        ),
        /* @__PURE__ */ jsx306(
          ChevronDownIcon7,
          {
            className: "pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground opacity-50 select-none",
            "aria-hidden": "true",
            "data-slot": "native-select-icon"
          }
        )
      ]
    }
  );
}
function NativeSelectOption({ ...props }) {
  return /* @__PURE__ */ jsx306("option", { "data-slot": "native-select-option", ...props });
}
function NativeSelectOptGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx306(
    "optgroup",
    {
      "data-slot": "native-select-optgroup",
      className: cn(className),
      ...props
    }
  );
}

// src/components/ui/navigation-menu.tsx
import { cva as cva11 } from "class-variance-authority";
import { ChevronDownIcon as ChevronDownIcon8 } from "lucide-react";
import { NavigationMenu as NavigationMenuPrimitive } from "radix-ui";
import { jsx as jsx307, jsxs as jsxs268 } from "react/jsx-runtime";
function NavigationMenu({
  className,
  children,
  viewport = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs268(
    NavigationMenuPrimitive.Root,
    {
      "data-slot": "navigation-menu",
      "data-viewport": viewport,
      className: cn(
        "group/navigation-menu relative flex max-w-max flex-1 items-center justify-center",
        className
      ),
      ...props,
      children: [
        children,
        viewport && /* @__PURE__ */ jsx307(NavigationMenuViewport, {})
      ]
    }
  );
}
function NavigationMenuList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    NavigationMenuPrimitive.List,
    {
      "data-slot": "navigation-menu-list",
      className: cn(
        "group flex flex-1 list-none items-center justify-center gap-1",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    NavigationMenuPrimitive.Item,
    {
      "data-slot": "navigation-menu-item",
      className: cn("relative", className),
      ...props
    }
  );
}
var navigationMenuTriggerStyle = cva11(
  "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-[color,box-shadow] outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50 data-[state=open]:text-accent-foreground data-[state=open]:hover:bg-accent data-[state=open]:focus:bg-accent"
);
function NavigationMenuTrigger({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs268(
    NavigationMenuPrimitive.Trigger,
    {
      "data-slot": "navigation-menu-trigger",
      className: cn(navigationMenuTriggerStyle(), "group", className),
      ...props,
      children: [
        children,
        " ",
        /* @__PURE__ */ jsx307(
          ChevronDownIcon8,
          {
            className: "relative top-[1px] ml-1 size-3 transition duration-300 group-data-[state=open]:rotate-180",
            "aria-hidden": "true"
          }
        )
      ]
    }
  );
}
function NavigationMenuContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    NavigationMenuPrimitive.Content,
    {
      "data-slot": "navigation-menu-content",
      className: cn(
        "top-0 left-0 w-full p-2 pr-2.5 data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 data-[motion^=from-]:animate-in data-[motion^=from-]:fade-in data-[motion^=to-]:animate-out data-[motion^=to-]:fade-out md:absolute md:w-auto",
        "group-data-[viewport=false]/navigation-menu:top-full group-data-[viewport=false]/navigation-menu:mt-1.5 group-data-[viewport=false]/navigation-menu:overflow-hidden group-data-[viewport=false]/navigation-menu:rounded-md group-data-[viewport=false]/navigation-menu:border group-data-[viewport=false]/navigation-menu:bg-popover group-data-[viewport=false]/navigation-menu:text-popover-foreground group-data-[viewport=false]/navigation-menu:shadow-md group-data-[viewport=false]/navigation-menu:ring-1 group-data-[viewport=false]/navigation-menu:ring-black/5 dark:group-data-[viewport=false]/navigation-menu:border-transparent dark:group-data-[viewport=false]/navigation-menu:ring-white/15 dark:group-data-[viewport=false]/navigation-menu:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] group-data-[viewport=false]/navigation-menu:duration-200 **:data-[slot=navigation-menu-link]:focus:ring-0 **:data-[slot=navigation-menu-link]:focus:outline-none group-data-[viewport=false]/navigation-menu:data-[state=closed]:animate-out group-data-[viewport=false]/navigation-menu:data-[state=closed]:fade-out-0 group-data-[viewport=false]/navigation-menu:data-[state=closed]:zoom-out-95 group-data-[viewport=false]/navigation-menu:data-[state=open]:animate-in group-data-[viewport=false]/navigation-menu:data-[state=open]:fade-in-0 group-data-[viewport=false]/navigation-menu:data-[state=open]:zoom-in-95",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuViewport({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    "div",
    {
      className: cn(
        "absolute top-full left-0 isolate z-50 flex justify-center"
      ),
      children: /* @__PURE__ */ jsx307(
        NavigationMenuPrimitive.Viewport,
        {
          "data-slot": "navigation-menu-viewport",
          className: cn(
            "origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md ring-1 ring-black/5 dark:border-transparent dark:ring-white/15 dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)] data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:zoom-in-90 md:w-[var(--radix-navigation-menu-viewport-width)]",
            className
          ),
          ...props
        }
      )
    }
  );
}
function NavigationMenuLink({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    NavigationMenuPrimitive.Link,
    {
      "data-slot": "navigation-menu-link",
      className: cn(
        "flex flex-col gap-1 rounded-sm p-2 text-sm transition-all outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 data-[active=true]:bg-accent/50 data-[active=true]:text-accent-foreground data-[active=true]:hover:bg-accent data-[active=true]:focus:bg-accent [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      ),
      ...props
    }
  );
}
function NavigationMenuIndicator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx307(
    NavigationMenuPrimitive.Indicator,
    {
      "data-slot": "navigation-menu-indicator",
      className: cn(
        "top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:animate-in data-[state=visible]:fade-in",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx307("div", { className: "relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" })
    }
  );
}

// src/components/ui/overflow.tsx
import * as React30 from "react";
import { createPortal } from "react-dom";
import { jsx as jsx308, jsxs as jsxs269 } from "react/jsx-runtime";
var VIEWPORT_MARGIN = 16;
var CONTAINER_MARGIN = 4;
var PILL_PADDING_X = 10;
var PILL_PADDING_Y = 6;
var PILL_RING = 1;
var roundToDevicePixel = (value, round = Math.round) => {
  const pixelRatio = window.devicePixelRatio || 1;
  return round(value * pixelRatio) / pixelRatio;
};
var clampEdge = (edge, width, min, max) => Math.max(roundToDevicePixel(min, Math.ceil), roundToDevicePixel(Math.min(edge, max - width), Math.floor));
function TextOverflow({
  align = "left",
  delay = 500,
  hoverContent,
  alwaysShow = false,
  nowrap = false,
  pillClassName,
  children,
  className,
  ...props
}) {
  const textRef = React30.useRef(null);
  const pillRef = React30.useRef(null);
  const containerRef = React30.useRef(null);
  const timerRef = React30.useRef(null);
  const [truncated, setTruncated] = React30.useState(false);
  const [visible, setVisible] = React30.useState(false);
  const [mounted, setMounted] = React30.useState(false);
  const [show, setShow] = React30.useState(false);
  const [position, setPosition] = React30.useState(null);
  const [textStyle, setTextStyle] = React30.useState({});
  const shouldShow = alwaysShow || truncated;
  const checkTruncation = React30.useCallback(() => {
    if (alwaysShow) return;
    const el = textRef.current;
    if (!el) return;
    setTruncated(el.scrollWidth > el.clientWidth);
  }, [alwaysShow]);
  React30.useEffect(() => {
    checkTruncation();
    const el = textRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, children]);
  React30.useEffect(() => {
    if (!visible || !textRef.current) {
      setShow(false);
      const unmountTimer = setTimeout(() => {
        setMounted(false);
        setPosition(null);
      }, 200);
      return () => clearTimeout(unmountTimer);
    }
    const rect = (containerRef.current ?? textRef.current).getBoundingClientRect();
    const computed = window.getComputedStyle(textRef.current);
    setTextStyle({
      fontSize: computed.fontSize,
      fontWeight: computed.fontWeight,
      fontFamily: computed.fontFamily,
      letterSpacing: computed.letterSpacing,
      lineHeight: computed.lineHeight
    });
    const viewportWidth = window.innerWidth;
    const top = roundToDevicePixel(rect.top - PILL_PADDING_Y);
    if (align === "right") {
      setPosition({
        top,
        right: roundToDevicePixel(viewportWidth - rect.right - PILL_PADDING_X),
        maxWidth: Math.floor(rect.right + PILL_PADDING_X - VIEWPORT_MARGIN)
      });
    } else {
      setPosition({
        top,
        left: roundToDevicePixel(rect.left - PILL_PADDING_X),
        maxWidth: Math.floor(viewportWidth - rect.left + PILL_PADDING_X - VIEWPORT_MARGIN)
      });
    }
    setMounted(true);
  }, [visible, align]);
  React30.useLayoutEffect(() => {
    if (!mounted || !position || !pillRef.current || !containerRef.current) return;
    const size = window.getComputedStyle(pillRef.current);
    const width = parseFloat(size.width);
    const viewportWidth = window.innerWidth;
    const bounds = containerRef.current.closest('[role="table"]')?.getBoundingClientRect();
    const viewportInset = VIEWPORT_MARGIN + PILL_RING;
    const containerInset = CONTAINER_MARGIN + PILL_RING;
    const minX = Math.max(viewportInset, bounds ? bounds.left + containerInset : 0);
    const maxX = Math.min(viewportWidth - viewportInset, bounds ? bounds.right - containerInset : viewportWidth);
    let { left, right, top } = position;
    if (left !== void 0) {
      left = clampEdge(left, width, minX, maxX);
    }
    if (right !== void 0) {
      right = clampEdge(right, width, viewportWidth - maxX, viewportWidth - minX);
    }
    if (nowrap) {
      const rect = containerRef.current.getBoundingClientRect();
      top = roundToDevicePixel(rect.top + rect.height / 2 - parseFloat(size.height) / 2);
    }
    if (left !== position.left || right !== position.right || top !== position.top) {
      setPosition({ ...position, left, right, top });
    }
  }, [mounted, position, nowrap]);
  React30.useEffect(() => {
    if (!mounted || !position || !visible) return;
    const raf = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(raf);
  }, [mounted, position, visible]);
  function handleMouseEnter() {
    if (!shouldShow) return;
    if (delay === 0) {
      setVisible(true);
    } else {
      timerRef.current = setTimeout(() => setVisible(true), delay);
    }
  }
  function handleMouseLeave() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }
  const pillContent = hoverContent ?? children;
  const pill = mounted && position && /* @__PURE__ */ jsx308(
    "span",
    {
      ref: pillRef,
      "data-slot": "overflow-reveal",
      className: cn(
        "pointer-events-none fixed z-50 flex transition-[opacity,transform] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-[opacity,transform]",
        align === "right" ? "origin-right" : "origin-left",
        show ? "scale-100 opacity-100" : "scale-95 opacity-0"
      ),
      style: {
        top: position.top,
        left: position.left,
        right: position.right
      },
      children: /* @__PURE__ */ jsx308(
        "span",
        {
          className: cn(
            "inline-block bg-popover px-2.5 py-1.5 text-secondary-foreground shadow-sm ring-1 ring-black/5 dark:ring-white/15 dark:shadow-[0_4px_12px_-2px_rgb(0_0_0/0.4)]",
            nowrap ? "whitespace-nowrap rounded-full" : "text-pretty rounded-xl",
            pillClassName
          ),
          style: {
            maxWidth: nowrap ? void 0 : position.maxWidth,
            ...textStyle
          },
          children: pillContent
        }
      )
    }
  );
  return /* @__PURE__ */ jsxs269(
    "span",
    {
      ref: containerRef,
      "data-slot": "overflow",
      className: cn(
        "relative inline-flex max-w-full",
        shouldShow && "cursor-pointer",
        className
      ),
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...props,
      children: [
        /* @__PURE__ */ jsx308(
          "span",
          {
            ref: textRef,
            "data-slot": "overflow-text",
            className: "invisible truncate",
            "aria-hidden": "true",
            children
          }
        ),
        /* @__PURE__ */ jsx308(
          "span",
          {
            "data-slot": "overflow-visible-text",
            className: cn(
              "absolute inset-0 truncate transition-opacity duration-100",
              visible ? "opacity-0" : "opacity-100"
            ),
            children
          }
        ),
        typeof document !== "undefined" && pill && createPortal(pill, document.body)
      ]
    }
  );
}

// src/components/ui/phone-input.tsx
import * as React31 from "react";
import { ChevronDown } from "lucide-react";
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  isValidPhoneNumber as isValidLibPhoneNumber,
  parsePhoneNumberFromString
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { jsx as jsx309, jsxs as jsxs270 } from "react/jsx-runtime";
var regionNames = typeof Intl !== "undefined" && "DisplayNames" in Intl ? new Intl.DisplayNames(["en"], { type: "region" }) : void 0;
var PHONE_COUNTRIES = getCountries().map((code) => ({ code, name: regionNames?.of(code) ?? code, dial: `+${getCountryCallingCode(code)}` })).sort((a, b) => a.name.localeCompare(b.name));
var toCountry = (code, fallback = "US") => {
  const upper = code?.toUpperCase();
  return upper && isSupportedCountry(upper) ? upper : fallback;
};
function formatPhoneValue(country, nationalNumber) {
  return nationalNumber ? `+${getCountryCallingCode(country)}-${nationalNumber}` : "";
}
function parsePhoneValue(value, fallback) {
  if (!value?.trim()) {
    return { country: fallback, nationalNumber: "" };
  }
  const parsed = parsePhoneNumberFromString(value, value.trim().startsWith("+") ? void 0 : "US");
  if (!parsed) {
    return { country: fallback, nationalNumber: value.replace(/\D/g, "") };
  }
  const country = parsed.country ?? (parsed.countryCallingCode === getCountryCallingCode(fallback) ? fallback : PHONE_COUNTRIES.find(({ dial }) => dial === `+${parsed.countryCallingCode}`)?.code) ?? fallback;
  return { country, nationalNumber: parsed.nationalNumber };
}
function isValidPhoneNumber(value) {
  return isValidLibPhoneNumber(value, value.trim().startsWith("+") ? void 0 : "US");
}
var formatNational = (country, nationalNumber) => {
  if (!nationalNumber) {
    return "";
  }
  const parsed = parsePhoneNumberFromString(`+${getCountryCallingCode(country)}${nationalNumber}`);
  return parsed?.formatNational() ?? new AsYouType(country).input(nationalNumber);
};
var matchCountry = (value, search) => {
  const query = search.trim().toLowerCase();
  const [name, dial, code] = value.split("|");
  if (/^\+?\d+$/.test(query)) {
    const digits = query.replace("+", "");
    return dial === `+${digits}` ? 1 : dial.startsWith(`+${digits}`) ? 0.5 : 0;
  }
  if (code === query) return 1;
  return name.startsWith(query) ? 0.9 : name.includes(query) ? 0.5 : 0;
};
var FlagSlot = ({ country }) => /* @__PURE__ */ jsx309("span", { className: "flex size-4 shrink-0 items-center justify-center", children: /* @__PURE__ */ jsx309(CountryFlag, { country, size: 16, className: "rounded-full" }) });
function PhoneCountrySelect({ value, onChange, disabled }) {
  const [open, setOpen] = React31.useState(false);
  const selected = PHONE_COUNTRIES.find(({ code }) => code === value);
  return /* @__PURE__ */ jsxs270(Popover2, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx309(PopoverTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs270(
      Button,
      {
        type: "button",
        variant: "muted",
        size: "sm",
        disabled,
        "aria-label": `Country code: ${selected?.name ?? value}`,
        className: "gap-1.5 rounded-md px-2 shadow-none tabular-nums",
        children: [
          /* @__PURE__ */ jsx309(FlagSlot, { country: value }),
          selected?.dial,
          /* @__PURE__ */ jsx309(ChevronDown, { className: "text-muted-foreground" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx309(PopoverContent2, { align: "start", sideOffset: 8, className: "w-72 p-0", children: /* @__PURE__ */ jsxs270(Command, { filter: matchCountry, children: [
      /* @__PURE__ */ jsx309(CommandInput, { placeholder: "Search country or code\u2026" }),
      /* @__PURE__ */ jsxs270(CommandList, { children: [
        /* @__PURE__ */ jsx309(CommandEmpty, { children: "No results found" }),
        /* @__PURE__ */ jsx309(CommandGroup, { children: PHONE_COUNTRIES.map((country) => /* @__PURE__ */ jsxs270(
          CommandItem,
          {
            value: `${country.name.toLowerCase()}|${country.dial}|${country.code.toLowerCase()}`,
            onSelect: () => {
              onChange(country.code);
              setOpen(false);
            },
            children: [
              /* @__PURE__ */ jsx309(FlagSlot, { country: country.code }),
              /* @__PURE__ */ jsx309("span", { className: "truncate", children: country.name }),
              /* @__PURE__ */ jsxs270("span", { className: "ml-auto flex shrink-0 items-center gap-1.5 text-muted-foreground tabular-nums", children: [
                country.code === value && /* @__PURE__ */ jsx309(AnimatedCheckIcon, { checked: true }),
                country.dial
              ] })
            ]
          },
          country.code
        )) })
      ] })
    ] }) })
  ] });
}
function PhoneInput({
  value,
  onChange,
  defaultCountry,
  disabled,
  placeholder,
  className,
  ...props
}) {
  const fallback = toCountry(defaultCountry);
  const [country, setCountry] = React31.useState(() => parsePhoneValue(value, fallback).country);
  const [text, setText] = React31.useState(() => {
    const parsed = parsePhoneValue(value, fallback);
    return formatNational(parsed.country, parsed.nationalNumber);
  });
  const emitted = React31.useRef(value);
  React31.useEffect(() => {
    if (value === emitted.current) {
      return;
    }
    emitted.current = value;
    const parsed = parsePhoneValue(value, fallback);
    setCountry(parsed.country);
    setText(formatNational(parsed.country, parsed.nationalNumber));
  }, [value]);
  React31.useEffect(() => {
    if (!text) {
      setCountry(fallback);
    }
  }, [fallback]);
  const emit = (nextCountry, nationalNumber) => {
    const next = formatPhoneValue(nextCountry, nationalNumber);
    emitted.current = next;
    onChange?.(next);
  };
  const handleTextChange = (raw) => {
    if (raw.trim().startsWith("+")) {
      const formatter2 = new AsYouType();
      formatter2.input(raw);
      const detected = formatter2.getCountry();
      if (detected) {
        const nationalNumber = formatter2.getNationalNumber();
        setCountry(detected);
        setText(formatNational(detected, nationalNumber));
        emit(detected, nationalNumber);
        return;
      }
    }
    const formatter = new AsYouType(country);
    const formatted = formatter.input(raw);
    setText(raw.length < text.length ? raw : formatted);
    emit(country, formatter.getNationalNumber());
  };
  const handleCountryChange = (nextCountry) => {
    const digits = text.replace(/\D/g, "");
    const formatter = new AsYouType(nextCountry);
    setCountry(nextCountry);
    setText(digits ? formatter.input(digits) : "");
    emit(nextCountry, digits ? formatter.getNationalNumber() : "");
  };
  return /* @__PURE__ */ jsxs270(InputGroup, { "data-disabled": disabled || void 0, className: cn("h-10", className), children: [
    /* @__PURE__ */ jsx309(InputGroupAddon, { children: /* @__PURE__ */ jsx309(PhoneCountrySelect, { value: country, onChange: handleCountryChange, disabled }) }),
    /* @__PURE__ */ jsx309(
      InputGroupInput,
      {
        type: "tel",
        inputMode: "tel",
        autoComplete: "tel-national",
        value: text,
        onChange: (event) => handleTextChange(event.target.value),
        disabled,
        placeholder: placeholder ?? getExampleNumber(country, examples)?.formatNational(),
        className: "tabular-nums",
        ...props
      }
    )
  ] });
}

// src/components/ui/platform-switcher.tsx
import * as React35 from "react";
import { AnimatePresence as AnimatePresence12, motion as motion14 } from "motion/react";
import { Check as Check2, ChevronDown as ChevronDown2, LoaderCircle } from "lucide-react";

// src/components/ui/sidebar.tsx
import * as React33 from "react";
import { cva as cva12 } from "class-variance-authority";
import { PanelLeftIcon } from "lucide-react";
import { Slot as Slot7 } from "radix-ui";

// src/components/ui/sheet.tsx
import { XIcon as XIcon3 } from "lucide-react";

// src/components/animate-ui/primitives/radix/sheet.tsx
import { Dialog as SheetPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence11, motion as motion12 } from "motion/react";
import { jsx as jsx310 } from "react/jsx-runtime";
var [SheetProvider, useSheet] = getStrictContext("SheetContext");
function Sheet(props) {
  const [isOpen, setIsOpen] = useControlledState({
    value: props.open,
    defaultValue: props.defaultOpen,
    onChange: props.onOpenChange
  });
  return /* @__PURE__ */ jsx310(SheetProvider, { value: { isOpen, setIsOpen }, children: /* @__PURE__ */ jsx310(SheetPrimitive.Root, { "data-slot": "sheet", ...props, onOpenChange: setIsOpen }) });
}
function SheetTrigger(props) {
  return /* @__PURE__ */ jsx310(SheetPrimitive.Trigger, { "data-slot": "sheet-trigger", ...props });
}
function SheetClose(props) {
  return /* @__PURE__ */ jsx310(SheetPrimitive.Close, { "data-slot": "sheet-close", ...props });
}
function SheetPortal(props) {
  const { isOpen } = useSheet();
  return /* @__PURE__ */ jsx310(AnimatePresence11, { children: isOpen && /* @__PURE__ */ jsx310(SheetPrimitive.Portal, { forceMount: true, "data-slot": "sheet-portal", ...props }) });
}
var OVERLAY_ENTER = { duration: 0.2, ease: [0.32, 0.72, 0, 1] };
var OVERLAY_EXIT = { duration: 0.15, ease: [0.4, 0, 1, 1] };
function SheetOverlay({ transition, ...props }) {
  return /* @__PURE__ */ jsx310(SheetPrimitive.Overlay, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsx310(
    motion12.div,
    {
      "data-slot": "sheet-overlay",
      initial: { opacity: 0, filter: "blur(4px)" },
      animate: { opacity: 1, filter: "blur(0px)", transition: transition ?? OVERLAY_ENTER },
      exit: { opacity: 0, filter: "blur(4px)", transition: transition ?? OVERLAY_EXIT },
      style: { willChange: "opacity, filter" },
      ...props
    },
    "sheet-overlay"
  ) });
}
var CONTENT_ENTER = { duration: 0.28, ease: [0.32, 0.72, 0, 1] };
var CONTENT_EXIT = { duration: 0.18, ease: [0.4, 0, 1, 1] };
function SheetContent({
  side = "right",
  transition,
  style,
  children,
  ...props
}) {
  const axis = side === "left" || side === "right" ? "x" : "y";
  const offscreen = {
    right: { x: "100%", opacity: 0 },
    left: { x: "-100%", opacity: 0 },
    top: { y: "-100%", opacity: 0 },
    bottom: { y: "100%", opacity: 0 }
  };
  const positionStyle = {
    right: { top: 0, bottom: 0, right: 0 },
    left: { top: 0, bottom: 0, left: 0 },
    top: { left: 0, right: 0, top: 0 },
    bottom: { left: 0, right: 0, bottom: 0 }
  };
  const animate = {
    [axis]: 0,
    opacity: 1,
    transition: transition ?? CONTENT_ENTER
  };
  const exit = {
    ...offscreen[side],
    transition: transition ?? CONTENT_EXIT
  };
  return /* @__PURE__ */ jsx310(SheetPrimitive.Content, { asChild: true, forceMount: true, ...props, children: /* @__PURE__ */ jsx310(
    motion12.div,
    {
      "data-slot": "sheet-content",
      "data-side": side,
      initial: offscreen[side],
      animate,
      exit,
      style: {
        position: "fixed",
        willChange: "transform, opacity",
        ...positionStyle[side],
        ...style
      },
      children
    },
    "sheet-content"
  ) });
}
function SheetTitle(props) {
  return /* @__PURE__ */ jsx310(SheetPrimitive.Title, { "data-slot": "sheet-title", ...props });
}
function SheetDescription(props) {
  return /* @__PURE__ */ jsx310(SheetPrimitive.Description, { "data-slot": "sheet-description", ...props });
}

// src/components/ui/sheet.tsx
import { jsx as jsx311, jsxs as jsxs271 } from "react/jsx-runtime";
function Sheet2({ ...props }) {
  return /* @__PURE__ */ jsx311(Sheet, { ...props });
}
function SheetTrigger2({
  ...props
}) {
  return /* @__PURE__ */ jsx311(SheetTrigger, { ...props });
}
function SheetClose2({
  ...props
}) {
  return /* @__PURE__ */ jsx311(SheetClose, { ...props });
}
function SheetPortal2({
  ...props
}) {
  return /* @__PURE__ */ jsx311(SheetPortal, { ...props });
}
function SheetOverlay2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx311(
    SheetOverlay,
    {
      className: cn(
        "fixed inset-0 z-50 bg-black/50",
        className
      ),
      ...props
    }
  );
}
function SheetContent2({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}) {
  return /* @__PURE__ */ jsxs271(SheetPortal2, { children: [
    /* @__PURE__ */ jsx311(SheetOverlay2, {}),
    /* @__PURE__ */ jsxs271(
      SheetContent,
      {
        side,
        className: cn(
          "z-50 flex flex-col gap-4 bg-background shadow-lg dark:shadow-[0_8px_20px_-4px_rgb(0_0_0/0.5),0_4px_8px_-2px_rgb(0_0_0/0.4)]",
          side === "right" && "fixed inset-y-0 right-0 w-3/4 border-l sm:max-w-sm after:absolute after:inset-y-0 after:-right-8 after:w-8 after:bg-background",
          side === "left" && "fixed inset-y-0 left-0 w-3/4 border-r sm:max-w-sm after:absolute after:inset-y-0 after:-left-8 after:w-8 after:bg-background",
          side === "top" && "h-auto border-b after:absolute after:inset-x-0 after:-top-8 after:h-8 after:bg-background",
          side === "bottom" && "h-auto border-t pb-4 after:absolute after:inset-x-0 after:-bottom-8 after:h-8 after:bg-background",
          className
        ),
        ...props,
        children: [
          children,
          showCloseButton && /* @__PURE__ */ jsxs271(SheetClose, { className: "absolute top-4 right-4 inline-flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground focus:outline-hidden dark:hover:bg-white/[0.08]", children: [
            /* @__PURE__ */ jsx311(XIcon3, { className: "size-3.5" }),
            /* @__PURE__ */ jsx311("span", { className: "sr-only", children: "Close" })
          ] })
        ]
      }
    )
  ] });
}
function SheetHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx311(
    "div",
    {
      "data-slot": "sheet-header",
      className: cn("flex flex-col gap-1.5 p-4", className),
      ...props
    }
  );
}
function SheetFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx311(
    "div",
    {
      "data-slot": "sheet-footer",
      className: cn("mt-auto flex flex-col gap-2 p-4", className),
      ...props
    }
  );
}
function SheetTitle2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx311(
    SheetTitle,
    {
      className: cn("font-semibold text-foreground", className),
      ...props
    }
  );
}
function SheetDescription2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx311(
    SheetDescription,
    {
      className: cn("text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/scroll-area.tsx
import * as React32 from "react";
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui";
import { jsx as jsx312, jsxs as jsxs272 } from "react/jsx-runtime";
var ScrollArea = React32.forwardRef(({ className, children, fadeEdges = false, ...props }, forwardedRef) => {
  const [showTopFade, setShowTopFade] = React32.useState(false);
  const [showBottomFade, setShowBottomFade] = React32.useState(false);
  const cleanupRef = React32.useRef(null);
  const rootCallbackRef = React32.useCallback(
    (root) => {
      if (typeof forwardedRef === "function") {
        forwardedRef(root);
      } else if (forwardedRef) {
        forwardedRef.current = root;
      }
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!fadeEdges || !root) {
        setShowTopFade(false);
        setShowBottomFade(false);
        return;
      }
      const viewport = root.querySelector(
        "[data-slot='scroll-area-viewport']"
      );
      if (!viewport) return;
      let rafId = 0;
      let lastTop = false;
      let lastBottom = false;
      const commit = () => {
        rafId = 0;
        const { scrollTop, scrollHeight, clientHeight } = viewport;
        const top = scrollTop > 2;
        const bottom = scrollTop + clientHeight < scrollHeight - 2;
        if (top !== lastTop) {
          lastTop = top;
          setShowTopFade(top);
        }
        if (bottom !== lastBottom) {
          lastBottom = bottom;
          setShowBottomFade(bottom);
        }
      };
      const schedule = () => {
        if (rafId === 0) rafId = requestAnimationFrame(commit);
      };
      commit();
      viewport.addEventListener("scroll", schedule, { passive: true });
      const observer = new ResizeObserver(schedule);
      observer.observe(viewport);
      cleanupRef.current = () => {
        viewport.removeEventListener("scroll", schedule);
        observer.disconnect();
        if (rafId !== 0) cancelAnimationFrame(rafId);
      };
    },
    [fadeEdges, forwardedRef]
  );
  const fadeStyle = fadeEdges ? {
    "--sa-top-fade": showTopFade ? 1 : 0,
    "--sa-bottom-fade": showBottomFade ? 1 : 0
  } : void 0;
  return /* @__PURE__ */ jsxs272(
    ScrollAreaPrimitive.Root,
    {
      ref: rootCallbackRef,
      "data-slot": "scroll-area",
      "data-fade-edges": fadeEdges ? "true" : void 0,
      className: cn("relative overflow-hidden rounded-[8px]", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx312(
          ScrollAreaPrimitive.Viewport,
          {
            "data-slot": "scroll-area-viewport",
            className: "h-full max-h-[inherit] w-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1",
            style: fadeStyle,
            children
          }
        ),
        /* @__PURE__ */ jsx312(ScrollBar, {}),
        /* @__PURE__ */ jsx312(ScrollAreaPrimitive.Corner, {})
      ]
    }
  );
});
ScrollArea.displayName = "ScrollArea";
function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx312(
    ScrollAreaPrimitive.ScrollAreaScrollbar,
    {
      "data-slot": "scroll-area-scrollbar",
      orientation,
      className: cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx312(
        ScrollAreaPrimitive.ScrollAreaThumb,
        {
          "data-slot": "scroll-area-thumb",
          className: "relative flex-1 rounded-full bg-border"
        }
      )
    }
  );
}

// src/components/ui/skeleton.tsx
import { jsx as jsx313 } from "react/jsx-runtime";
function Skeleton({ className, ...props }) {
  return /* @__PURE__ */ jsx313(
    "div",
    {
      "data-slot": "skeleton",
      className: cn("animate-pulse rounded-md bg-gray-200 dark:bg-gray-700", className),
      ...props
    }
  );
}

// src/components/ui/sidebar.tsx
import { Fragment as Fragment5, jsx as jsx314, jsxs as jsxs273 } from "react/jsx-runtime";
var SIDEBAR_COOKIE_NAME = "sidebar_state";
var SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
var SIDEBAR_WIDTH = "264px";
var SIDEBAR_WIDTH_MOBILE = "18rem";
var SIDEBAR_KEYBOARD_SHORTCUT = "b";
var SidebarContext = React33.createContext(null);
function useSidebar() {
  const context = React33.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}
function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React33.useState(false);
  const [_open, _setOpen] = React33.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React33.useCallback(
    (value) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );
  const toggleSidebar = React33.useCallback(() => {
    if (isMobile) {
      setOpenMobile((prev) => !prev);
    } else if (open) {
      setOpen(false);
    } else {
      setOpenMobile((prev) => !prev);
    }
  }, [isMobile, open, setOpen, setOpenMobile]);
  React33.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        if (isMobile) {
          setOpenMobile((prev) => !prev);
        } else {
          setOpen((prev) => !prev);
          setOpenMobile(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, setOpen, setOpenMobile]);
  const state = open ? "expanded" : "collapsed";
  const contextValue = React33.useMemo(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );
  return /* @__PURE__ */ jsx314(SidebarContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-wrapper",
      style: {
        "--sidebar-width": SIDEBAR_WIDTH,
        ...style
      },
      className: cn(
        "group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar",
        className
      ),
      ...props,
      children
    }
  ) });
}
function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { isMobile, open, state, openMobile, setOpenMobile } = useSidebar();
  if (collapsible === "none") {
    return /* @__PURE__ */ jsx314(
      "div",
      {
        "data-slot": "sidebar",
        className: cn(
          "flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground",
          className
        ),
        ...props,
        children
      }
    );
  }
  const sheet = /* @__PURE__ */ jsx314(Sheet2, { open: openMobile, onOpenChange: setOpenMobile, children: /* @__PURE__ */ jsxs273(
    SheetContent2,
    {
      "data-sidebar": "sidebar",
      "data-slot": "sidebar",
      "data-mobile": isMobile ? "true" : void 0,
      className: "w-(--sidebar-width) border-0 bg-sidebar p-0 text-sidebar-foreground [&>button]:hidden before:absolute before:inset-y-0 before:-left-16 before:w-16 before:bg-sidebar shadow-[0_-1px_0_rgb(0_0_0/0.15),0_1px_0_rgb(0_0_0/0.15),1px_0_0_rgb(0_0_0/0.15)] dark:shadow-[0_-1px_0_rgb(255_255_255/0.12),0_1px_0_rgb(255_255_255/0.12),1px_0_0_rgb(255_255_255/0.12)]",
      style: {
        "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
        top: "env(safe-area-inset-top)",
        bottom: "env(safe-area-inset-bottom)"
      },
      side,
      children: [
        /* @__PURE__ */ jsxs273(SheetHeader, { className: "sr-only", children: [
          /* @__PURE__ */ jsx314(SheetTitle2, { children: "Sidebar" }),
          /* @__PURE__ */ jsx314(SheetDescription2, { children: "Displays the sidebar." })
        ] }),
        /* @__PURE__ */ jsx314("div", { className: "flex h-full w-full flex-col", children })
      ]
    }
  ) });
  if (isMobile) {
    return sheet;
  }
  return /* @__PURE__ */ jsxs273(Fragment5, { children: [
    /* @__PURE__ */ jsx314(
      "div",
      {
        className: "group peer hidden text-sidebar-foreground lg:block",
        "data-state": state,
        "data-variant": variant,
        "data-side": side,
        "data-slot": "sidebar",
        ...props,
        children: open && /* @__PURE__ */ jsxs273(Fragment5, { children: [
          /* @__PURE__ */ jsx314(
            "div",
            {
              "data-slot": "sidebar-gap",
              className: cn(
                "relative w-(--sidebar-width) bg-transparent",
                "group-data-[side=right]:rotate-180"
              )
            }
          ),
          /* @__PURE__ */ jsx314(
            "div",
            {
              "data-slot": "sidebar-container",
              className: cn(
                "fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) lg:flex",
                side === "left" ? "left-0" : "right-0",
                // Adjust the padding for floating and inset variants.
                variant === "floating" || variant === "inset" ? "p-2" : "group-data-[side=left]:border-r group-data-[side=right]:border-l",
                className
              ),
              children: /* @__PURE__ */ jsx314(
                "div",
                {
                  "data-sidebar": "sidebar",
                  "data-slot": "sidebar-inner",
                  className: "flex h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm",
                  children
                }
              )
            }
          )
        ] })
      }
    ),
    !open && sheet
  ] });
}
function SidebarTrigger({
  className,
  onClick,
  ...props
}) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsxs273(
    Button,
    {
      type: "button",
      "data-sidebar": "trigger",
      "data-slot": "sidebar-trigger",
      variant: "muted",
      size: "icon",
      className: cn("size-7", className),
      onClick: (event) => {
        onClick?.(event);
        toggleSidebar();
      },
      ...props,
      children: [
        /* @__PURE__ */ jsx314(PanelLeftIcon, {}),
        /* @__PURE__ */ jsx314("span", { className: "sr-only", children: "Toggle Sidebar" })
      ]
    }
  );
}
function SidebarRail({ className, ...props }) {
  const { toggleSidebar } = useSidebar();
  return /* @__PURE__ */ jsx314(
    "button",
    {
      "data-sidebar": "rail",
      "data-slot": "sidebar-rail",
      "aria-label": "Toggle Sidebar",
      tabIndex: -1,
      onClick: toggleSidebar,
      title: "Toggle Sidebar",
      className: cn(
        "absolute inset-y-0 z-20 hidden w-4 translate-x-0 transition-all ease-linear group-data-[side=left]:-right-2 group-data-[side=right]:-left-2 after:absolute after:inset-y-0 after:left-full hover:bg-sidebar sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        className
      ),
      ...props
    }
  );
}
function SidebarInset({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx314(
    "main",
    {
      "data-slot": "sidebar-inset",
      className: cn(
        "relative flex w-full flex-1 flex-col bg-background",
        "lg:peer-data-[variant=inset]:m-2 lg:peer-data-[variant=inset]:ml-0 lg:peer-data-[variant=inset]:rounded-lg lg:peer-data-[variant=inset]:shadow-[0_0_0_1px_oklch(0.150_0.014_264.0/0.05),0_1px_2px_oklch(0.150_0.014_264.0/0.1)] lg:peer-data-[variant=inset]:overflow-hidden lg:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 dark:lg:peer-data-[variant=inset]:shadow-[0_0_0_1px_oklch(1_0_0/0.1),0_0_0_0.5px_oklch(1_0_0/0.05)]",
        className
      ),
      ...props,
      children
    }
  );
}
function SidebarInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx314(
    Input,
    {
      "data-slot": "sidebar-input",
      "data-sidebar": "input",
      className: cn("h-8 w-full bg-background shadow-none", className),
      ...props
    }
  );
}
function SidebarHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-header",
      "data-sidebar": "header",
      className: cn(
        "flex flex-col gap-2 p-2 [&>*:first-child]:pr-0",
        className
      ),
      ...props
    }
  );
}
function SidebarFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-footer",
      "data-sidebar": "footer",
      className: cn("flex flex-col gap-2", className),
      ...props
    }
  );
}
function SidebarSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx314("div", { className: "px-1", "data-slot": "sidebar-separator-wrapper", children: /* @__PURE__ */ jsx314(
    Separator,
    {
      "data-slot": "sidebar-separator",
      "data-sidebar": "separator",
      className: cn("bg-sidebar-border", className),
      ...props
    }
  ) });
}
function SidebarContent({ className, children }) {
  return /* @__PURE__ */ jsx314(
    ScrollArea,
    {
      fadeEdges: true,
      "data-slot": "sidebar-content",
      "data-sidebar": "content",
      className: cn(
        "min-h-0 flex-1 overflow-hidden rounded-none",
        className
      ),
      children: /* @__PURE__ */ jsx314("div", { className: "flex flex-col gap-2", children })
    }
  );
}
function SidebarGroup({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-group",
      "data-sidebar": "group",
      className: cn("relative flex w-full min-w-0 flex-col p-2", className),
      ...props
    }
  );
}
function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "div";
  return /* @__PURE__ */ jsx314(
    Comp,
    {
      "data-slot": "sidebar-group-label",
      "data-sidebar": "group-label",
      className: cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 ring-sidebar-ring outline-hidden focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "button";
  return /* @__PURE__ */ jsx314(
    Comp,
    {
      "data-slot": "sidebar-group-action",
      "data-sidebar": "group-action",
      className: cn(
        "absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-[transform,opacity] duration-200 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        className
      ),
      ...props
    }
  );
}
function SidebarGroupContent({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-group-content",
      "data-sidebar": "group-content",
      className: cn("w-full text-sm", className),
      ...props
    }
  );
}
function SidebarMenu({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "ul",
    {
      "data-slot": "sidebar-menu",
      "data-sidebar": "menu",
      className: cn("flex w-full min-w-0 flex-col gap-1", className),
      ...props
    }
  );
}
function SidebarMenuItem({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "li",
    {
      "data-slot": "sidebar-menu-item",
      "data-sidebar": "menu-item",
      className: cn("group/menu-item relative", className),
      ...props
    }
  );
}
var menuButtonLayers = "relative isolate before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:rounded-[inherit] before:bg-sidebar-accent before:opacity-0 before:transition-opacity before:duration-150 data-[active=true]:before:opacity-100 after:pointer-events-none after:absolute after:inset-0 after:-z-2 after:rounded-[inherit] after:bg-gray-100 after:opacity-0 after:transition-opacity after:duration-150 dark:after:bg-gray-800 hover:after:opacity-100 active:after:opacity-100";
var sidebarMenuButtonVariants = cva12(
  [
    menuButtonLayers,
    "peer/menu-button flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm font-medium ring-sidebar-ring outline-hidden transition-[color] duration-150 group-has-data-[sidebar=menu-action]/menu-item:pr-8 [&>span]:text-gray-600 dark:[&>span]:text-sidebar-foreground/80 [&_svg]:[--icon-color:var(--color-gray-500)]! dark:[&_svg]:[--icon-color:hsl(var(--sidebar-icon))] [&_svg]:[--icon-background:hsl(var(--sidebar))] hover:[&_svg]:[--icon-background:var(--color-gray-100)] dark:hover:[&_svg]:[--icon-background:var(--color-gray-800)] focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:[&>span]:text-sidebar-accent-foreground! data-[active=true]:[&_svg]:[--icon-color:var(--color-sidebar-accent-foreground)]! data-[active=true]:[&_svg]:[--icon-background:hsl(var(--sidebar-accent))] data-[active=true]:hover:[&_svg]:[--icon-background:hsl(var(--sidebar-accent))] data-[suppress-active=true]:before:hidden data-[active=true]:not-data-[suppress-active=true]:after:opacity-0! data-[active=true]:not-data-[suppress-active=true]:after:delay-150 data-[active-parent=true]:after:opacity-0! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&_[data-slot=dropdown-chevron]]:text-gray-400 [&_[data-slot=dropdown-chevron]]:transition-colors [&_[data-slot=dropdown-chevron]]:duration-150 hover:[&_[data-slot=dropdown-chevron]]:text-gray-500"
  ],
  {
    variants: {
      variant: {
        default: "",
        outline: "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:shadow-[0_0_0_1px_var(--color-gray-100)] dark:hover:shadow-[0_0_0_1px_var(--color-gray-800)]"
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var SidebarMenuButton = React33.forwardRef(
  ({
    asChild = false,
    isActive = false,
    variant = "default",
    size = "default",
    className,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot7.Root : "button";
    return /* @__PURE__ */ jsx314(
      Comp,
      {
        ref,
        "data-slot": "sidebar-menu-button",
        "data-sidebar": "menu-button",
        "data-size": size,
        "data-active": isActive,
        className: cn(sidebarMenuButtonVariants({ variant, size }), className),
        ...props
      }
    );
  }
);
SidebarMenuButton.displayName = "SidebarMenuButton";
function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "button";
  return /* @__PURE__ */ jsx314(
    Comp,
    {
      "data-slot": "sidebar-menu-action",
      "data-sidebar": "menu-action",
      className: cn(
        "absolute top-1.5 right-1 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-md p-0 text-sidebar-foreground ring-sidebar-ring outline-hidden transition-[transform,opacity] duration-200 peer-hover/menu-button:text-sidebar-accent-foreground hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-50 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-gray-400 [&>svg]:transition-colors [&>svg]:duration-150 hover:[&>svg]:text-gray-500",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 lg:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        showOnHover && "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground data-[state=open]:opacity-100 lg:opacity-0",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuBadge({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx314(
    "div",
    {
      "data-slot": "sidebar-menu-badge",
      "data-sidebar": "menu-badge",
      className: cn(
        "pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}) {
  const [width] = React33.useState(() => `${Math.floor(Math.random() * 40) + 50}%`);
  return /* @__PURE__ */ jsxs273(
    "div",
    {
      "data-slot": "sidebar-menu-skeleton",
      "data-sidebar": "menu-skeleton",
      className: cn("flex h-8 items-center gap-2 rounded-md px-2", className),
      ...props,
      children: [
        showIcon && /* @__PURE__ */ jsx314(
          Skeleton,
          {
            className: "size-4 rounded-md",
            "data-sidebar": "menu-skeleton-icon"
          }
        ),
        /* @__PURE__ */ jsx314(
          Skeleton,
          {
            className: "h-4 max-w-(--skeleton-width) flex-1",
            "data-sidebar": "menu-skeleton-text",
            style: {
              "--skeleton-width": width
            }
          }
        )
      ]
    }
  );
}
function SidebarMenuSub({ className, ...props }) {
  return /* @__PURE__ */ jsx314(
    "ul",
    {
      "data-slot": "sidebar-menu-sub",
      "data-sidebar": "menu-sub",
      className: cn(
        "ml-3.5 mr-0 flex min-w-0 translate-x-px flex-col gap-1 pl-3 pr-0.5 py-0.5",
        className
      ),
      ...props
    }
  );
}
function SidebarMenuSubItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx314(
    "li",
    {
      "data-slot": "sidebar-menu-sub-item",
      "data-sidebar": "menu-sub-item",
      className: cn("group/menu-sub-item relative", className),
      ...props
    }
  );
}
function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}) {
  const Comp = asChild ? Slot7.Root : "a";
  return /* @__PURE__ */ jsx314(
    Comp,
    {
      "data-slot": "sidebar-menu-sub-button",
      "data-sidebar": "menu-sub-button",
      "data-size": size,
      "data-active": isActive,
      className: cn(
        menuButtonLayers,
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 font-medium text-sidebar-foreground/60 ring-sidebar-ring outline-hidden transition-[color] duration-150 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        "data-[active=true]:after:opacity-0! data-[active=true]:after:delay-150 data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        className
      ),
      ...props
    }
  );
}

// src/components/animate-ui/primitives/radix/switch.tsx
import * as React34 from "react";
import { Switch as SwitchPrimitives } from "radix-ui";
import {
  motion as motion13
} from "motion/react";
import { jsx as jsx315 } from "react/jsx-runtime";
var [SwitchProvider, useSwitch] = getStrictContext("SwitchContext");
function Switch({
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  required,
  name,
  value,
  ...props
}) {
  const [isPressed, setIsPressed] = React34.useState(false);
  const [isChecked, setIsChecked] = useControlledState({
    value: checked,
    defaultValue: defaultChecked,
    onChange: onCheckedChange
  });
  return /* @__PURE__ */ jsx315(
    SwitchProvider,
    {
      value: { isChecked, setIsChecked, isPressed, setIsPressed },
      children: /* @__PURE__ */ jsx315(
        SwitchPrimitives.Root,
        {
          checked,
          defaultChecked,
          onCheckedChange: setIsChecked,
          disabled,
          required,
          name,
          value,
          asChild: true,
          children: /* @__PURE__ */ jsx315(
            motion13.button,
            {
              "data-slot": "switch",
              whileTap: "tap",
              initial: false,
              onTapStart: () => setIsPressed(true),
              onTapCancel: () => setIsPressed(false),
              onTap: () => setIsPressed(false),
              ...props
            }
          )
        }
      )
    }
  );
}
function SwitchThumb({
  pressedAnimation,
  transition = { type: "spring", stiffness: 300, damping: 25 },
  ...props
}) {
  const { isPressed } = useSwitch();
  return /* @__PURE__ */ jsx315(SwitchPrimitives.Thumb, { asChild: true, children: /* @__PURE__ */ jsx315(
    motion13.div,
    {
      "data-slot": "switch-thumb",
      whileTap: "tab",
      layout: true,
      transition,
      animate: isPressed ? pressedAnimation : void 0,
      ...props
    }
  ) });
}

// src/components/ui/switch.tsx
import { jsx as jsx316 } from "react/jsx-runtime";
function Switch2({
  className,
  size = "default",
  ...props
}) {
  return /* @__PURE__ */ jsx316(
    Switch,
    {
      "data-size": size,
      className: cn(
        "peer group/switch inline-flex shrink-0 items-center rounded-full border border-black/10 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-[1.15rem] data-[size=default]:w-8 data-[size=sm]:h-3.5 data-[size=sm]:w-6 data-[state=checked]:border-transparent data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80 dark:data-[state=unchecked]:border-white/15 data-[state=checked]:justify-end data-[state=unchecked]:justify-start px-px",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx316(
        SwitchThumb,
        {
          className: cn(
            "pointer-events-none block rounded-full bg-background shadow-[0_0_0_0.5px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.06)] group-data-[size=default]/switch:size-3.5 group-data-[size=sm]/switch:size-2.5 dark:shadow-[0_0_0_0.5px_rgba(255,255,255,0.1),0_1px_2px_rgba(0,0,0,0.2)] dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground"
          ),
          transition: { type: "spring", stiffness: 700, damping: 30 }
        }
      )
    }
  );
}

// src/components/ui/platform-switcher.tsx
import { Fragment as Fragment6, jsx as jsx317, jsxs as jsxs274 } from "react/jsx-runtime";
function getItemId(item) {
  return item.id ?? item.name;
}
function PlatformSwitcher({
  platforms,
  value,
  onValueChange,
  isLive,
  onLiveChange,
  liveLabels = { live: "Live", sandbox: "Sandbox" },
  renderIcon,
  groupLabel = "Platforms",
  className,
  onAcceptInvite,
  isLoading = false
}) {
  const { isMobile } = useSidebar();
  const [isOpen, setIsOpen] = React35.useState(false);
  const activePlatform = React35.useMemo(
    () => platforms.find((p) => getItemId(p) === value) ?? platforms[0],
    [platforms, value]
  );
  const hasToggle = isLive !== void 0 && onLiveChange !== void 0;
  return /* @__PURE__ */ jsx317(SidebarMenu, { children: /* @__PURE__ */ jsx317(SidebarMenuItem, { children: /* @__PURE__ */ jsxs274(DropdownMenu2, { open: isOpen, onOpenChange: setIsOpen, children: [
    /* @__PURE__ */ jsx317(DropdownMenuTrigger2, { asChild: true, children: /* @__PURE__ */ jsxs274(
      SidebarMenuButton,
      {
        className: cn(
          "h-auto gap-2 overflow-hidden rounded-lg border border-gray-200 shadow-none hover:bg-gray-50 hover:border-gray-300 data-[state=open]:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-white/[0.15] dark:data-[state=open]:bg-white/[0.06] focus-visible:ring-0 focus-visible:bg-gray-50 focus-visible:border-gray-300 dark:focus-visible:bg-white/[0.06] dark:focus-visible:border-white/[0.15]",
          className
        ),
        children: [
          /* @__PURE__ */ jsx317("div", { className: "flex size-[18px] shrink-0 items-center justify-center", children: isLoading ? /* @__PURE__ */ jsx317(LoaderCircle, { className: "size-4 animate-spin text-gray-500" }) : renderIcon?.(activePlatform, true) }),
          /* @__PURE__ */ jsx317("div", { className: "grid flex-1 gap-px text-left text-sm leading-tight", children: /* @__PURE__ */ jsx317("span", { className: "relative overflow-hidden truncate font-medium text-gray-600 dark:text-sidebar-foreground/80", children: /* @__PURE__ */ jsx317(AnimatePresence12, { initial: false, children: /* @__PURE__ */ jsx317(
            motion14.span,
            {
              initial: { opacity: 0, x: -8 },
              animate: { opacity: isLoading ? 0.4 : 1, x: 0 },
              exit: { opacity: 0, x: 8, position: "absolute" },
              transition: { duration: 0.12, ease: "easeOut" },
              className: "block truncate",
              children: activePlatform.name
            },
            activePlatform.name
          ) }) }) }),
          /* @__PURE__ */ jsx317(
            ChevronDown2,
            {
              "data-slot": "dropdown-chevron",
              className: cn(
                "size-4 shrink-0 transition-transform duration-200 ease-out",
                isOpen && "rotate-180"
              )
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs274(
      DropdownMenuContent2,
      {
        className: "min-w-72 max-w-md rounded-lg",
        side: "bottom",
        align: "start",
        sideOffset: 4,
        children: [
          hasToggle && /* @__PURE__ */ jsxs274(Fragment6, { children: [
            /* @__PURE__ */ jsxs274(
              "button",
              {
                type: "button",
                onClick: () => onLiveChange?.(!isLive),
                className: "flex w-full cursor-pointer items-center justify-between rounded-lg px-3 py-2 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.06]",
                children: [
                  /* @__PURE__ */ jsx317("span", { className: "relative overflow-hidden text-sm font-medium", children: /* @__PURE__ */ jsx317(AnimatePresence12, { initial: false, children: /* @__PURE__ */ jsx317(
                    motion14.span,
                    {
                      initial: { opacity: 0, x: -8 },
                      animate: { opacity: 1, x: 0 },
                      exit: { opacity: 0, x: 8, position: "absolute" },
                      transition: { duration: 0.12, ease: "easeOut" },
                      className: "block",
                      children: isLive ? liveLabels.live : liveLabels.sandbox
                    },
                    isLive ? "live" : "sandbox"
                  ) }) }),
                  /* @__PURE__ */ jsx317(
                    Switch2,
                    {
                      checked: isLive,
                      tabIndex: -1,
                      className: cn(
                        "pointer-events-none",
                        "data-[state=unchecked]:bg-[oklch(0.750_0.155_40.5)]",
                        "data-[state=checked]:bg-[oklch(0.695_0.119_165.5)]",
                        "data-[state=checked]:border-[oklch(0.595_0.119_165.5)]"
                      )
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx317(DropdownMenuSeparator2, {})
          ] }),
          platforms.some((p) => p.isInvite) && /* @__PURE__ */ jsxs274(Fragment6, { children: [
            /* @__PURE__ */ jsx317(DropdownMenuLabel2, { className: "text-xs text-muted-foreground", children: "Invitations" }),
            /* @__PURE__ */ jsx317("div", { className: "flex flex-col gap-1 mb-2", children: platforms.filter((p) => p.isInvite === true).map((platform) => {
              const itemId = getItemId(platform);
              return /* @__PURE__ */ jsx317(
                "div",
                {
                  className: "flex flex-col gap-2 rounded-md p-3 hover:bg-gray-50 dark:hover:bg-white/[0.06]",
                  children: /* @__PURE__ */ jsxs274("div", { className: "flex items-center gap-2", children: [
                    /* @__PURE__ */ jsxs274("div", { className: "grid flex-1 text-left text-sm leading-tight", children: [
                      /* @__PURE__ */ jsx317("span", { className: "font-medium", children: platform.name }),
                      platform.description && /* @__PURE__ */ jsx317("span", { className: "text-xs font-normal text-muted-foreground", children: platform.description })
                    ] }),
                    /* @__PURE__ */ jsx317(
                      "button",
                      {
                        type: "button",
                        onClick: (e) => {
                          e.stopPropagation();
                          onAcceptInvite?.(itemId);
                        },
                        className: "ml-auto inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md bg-white bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.7),rgb(255_255_255/0))] bg-blend-screen text-secondary-foreground shadow-btn-secondary inset-shadow-btn-secondary hover:bg-secondary-hover active:bg-secondary-active dark:bg-gray-800 dark:bg-[image:linear-gradient(to_bottom,rgb(255_255_255/0.06),rgb(255_255_255/0))] dark:text-gray-100 dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.08)] dark:hover:bg-secondary-hover dark:active:bg-secondary-active px-3 py-1.5 text-xs font-medium transition-all",
                        children: "Accept"
                      }
                    )
                  ] })
                },
                itemId
              );
            }) }),
            /* @__PURE__ */ jsx317(DropdownMenuSeparator2, {})
          ] }),
          /* @__PURE__ */ jsx317(DropdownMenuLabel2, { className: "text-xs text-muted-foreground", children: groupLabel }),
          /* @__PURE__ */ jsx317("div", { className: "flex flex-col gap-px", children: platforms.filter((p) => !p.isInvite).map((platform) => {
            const itemId = getItemId(platform);
            const isActive = itemId === getItemId(activePlatform);
            return /* @__PURE__ */ jsxs274(
              DropdownMenuItem2,
              {
                onClick: () => onValueChange(itemId),
                className: cn(
                  "gap-2 p-2",
                  isActive && "bg-gray-50 dark:bg-white/[0.06]"
                ),
                children: [
                  /* @__PURE__ */ jsxs274("div", { className: "grid flex-1 text-left text-sm leading-tight", children: [
                    /* @__PURE__ */ jsx317("span", { className: "font-medium", children: platform.name }),
                    platform.description && /* @__PURE__ */ jsx317("span", { className: "text-xs font-normal text-muted-foreground", children: platform.description })
                  ] }),
                  /* @__PURE__ */ jsx317(Check2, { className: cn("ml-auto size-4 shrink-0 box-content pl-4", !isActive && "invisible") })
                ]
              },
              itemId
            );
          }) })
        ]
      }
    )
  ] }) }) });
}

// src/components/ui/progress.tsx
import * as React36 from "react";

// src/components/animate-ui/primitives/radix/progress.tsx
import { Progress as ProgressPrimitive } from "radix-ui";
import { motion as motion15 } from "motion/react";
import { jsx as jsx318 } from "react/jsx-runtime";
var [ProgressProvider, useProgress] = getStrictContext("ProgressContext");
function Progress(props) {
  return /* @__PURE__ */ jsx318(ProgressProvider, { value: { value: props.value ?? 0 }, children: /* @__PURE__ */ jsx318(ProgressPrimitive.Root, { "data-slot": "progress", ...props }) });
}
var MotionProgressIndicator = motion15.create(ProgressPrimitive.Indicator);
function ProgressIndicator({
  transition = { type: "spring", stiffness: 100, damping: 30 },
  ...props
}) {
  const { value } = useProgress();
  return /* @__PURE__ */ jsx318(
    MotionProgressIndicator,
    {
      "data-slot": "progress-indicator",
      animate: { x: `-${100 - (value || 0)}%` },
      transition,
      ...props
    }
  );
}

// src/components/ui/progress.tsx
import { jsx as jsx319 } from "react/jsx-runtime";
var progressColors = {
  blue: {
    track: "oklch(0.573 0.146 261.4 / 0.2)",
    trackDark: "oklch(0.573 0.146 261.4 / 0.35)",
    indicator: "oklch(0.573 0.146 261.4)",
    indicatorDark: "oklch(0.725 0.107 259.7)",
    shadow: "inset 0 0 0 1px oklch(0.327 0.118 267.8 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.725 0.107 259.7 / 0.15)"
  },
  cyan: {
    track: "oklch(0.816 0.101 220.4 / 0.2)",
    trackDark: "oklch(0.816 0.101 220.4 / 0.3)",
    indicator: "oklch(0.816 0.101 220.4)",
    indicatorDark: "oklch(0.851 0.085 208.0)",
    shadow: "inset 0 0 0 1px oklch(0.495 0.106 238.3 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.851 0.085 208.0 / 0.15)"
  },
  seafoam: {
    track: "oklch(0.842 0.131 156.6 / 0.2)",
    trackDark: "oklch(0.842 0.131 156.6 / 0.3)",
    indicator: "oklch(0.842 0.131 156.6)",
    indicatorDark: "oklch(0.880 0.118 156.0)",
    shadow: "inset 0 0 0 1px oklch(0.533 0.093 177.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.880 0.118 156.0 / 0.15)"
  },
  green: {
    track: "oklch(0.818 0.168 131.2 / 0.2)",
    trackDark: "oklch(0.818 0.168 131.2 / 0.3)",
    indicator: "oklch(0.818 0.168 131.2)",
    indicatorDark: "oklch(0.861 0.139 126.8)",
    shadow: "inset 0 0 0 1px oklch(0.532 0.117 135.1 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.861 0.139 126.8 / 0.15)"
  },
  yellow: {
    track: "oklch(0.898 0.140 99.3 / 0.2)",
    trackDark: "oklch(0.898 0.140 99.3 / 0.3)",
    indicator: "oklch(0.898 0.140 99.3)",
    indicatorDark: "oklch(0.913 0.134 100.8)",
    shadow: "inset 0 0 0 1px oklch(0.618 0.131 71.3 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.913 0.134 100.8 / 0.15)"
  },
  orange: {
    track: "oklch(0.715 0.154 52.8 / 0.2)",
    trackDark: "oklch(0.715 0.154 52.8 / 0.35)",
    indicator: "oklch(0.715 0.154 52.8)",
    indicatorDark: "oklch(0.790 0.118 64.6)",
    shadow: "inset 0 0 0 1px oklch(0.455 0.141 39.0 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.790 0.118 64.6 / 0.15)"
  },
  red: {
    track: "oklch(0.599 0.184 12.9 / 0.2)",
    trackDark: "oklch(0.599 0.184 12.9 / 0.35)",
    indicator: "oklch(0.599 0.184 12.9)",
    indicatorDark: "oklch(0.697 0.159 10.5)",
    shadow: "inset 0 0 0 1px oklch(0.356 0.134 11.9 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.697 0.159 10.5 / 0.15)"
  },
  pink: {
    track: "oklch(0.575 0.178 325.3 / 0.2)",
    trackDark: "oklch(0.575 0.178 325.3 / 0.35)",
    indicator: "oklch(0.575 0.178 325.3)",
    indicatorDark: "oklch(0.690 0.155 323.8)",
    shadow: "inset 0 0 0 1px oklch(0.336 0.144 318.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.690 0.155 323.8 / 0.15)"
  },
  purple: {
    track: "oklch(0.583 0.183 294.0 / 0.2)",
    trackDark: "oklch(0.583 0.183 294.0 / 0.35)",
    indicator: "oklch(0.583 0.183 294.0)",
    indicatorDark: "oklch(0.666 0.169 296.2)",
    shadow: "inset 0 0 0 1px oklch(0.328 0.147 293.4 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.666 0.169 296.2 / 0.15)"
  },
  gray: {
    track: "oklch(0.597 0.014 271.2 / 0.2)",
    trackDark: "oklch(0.597 0.014 271.2 / 0.35)",
    indicator: "oklch(0.597 0.014 271.2)",
    indicatorDark: "oklch(0.745 0.015 272.6)",
    shadow: "inset 0 0 0 1px oklch(0.365 0.016 270.9 / 0.08)",
    shadowDark: "inset 0 0 0 1px oklch(0.745 0.015 272.6 / 0.15)"
  }
};
function useDarkMode() {
  const [isDark, setIsDark] = React36.useState(
    () => typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
  );
  React36.useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
function Progress2({
  className,
  value,
  color = "blue",
  ...props
}) {
  const isDark = useDarkMode();
  const colors = progressColors[color];
  return /* @__PURE__ */ jsx319(
    Progress,
    {
      "data-slot": "progress",
      "data-color": color,
      className: cn(
        "relative h-2 w-full overflow-hidden rounded-full",
        className
      ),
      style: {
        backgroundColor: isDark ? colors.trackDark : colors.track,
        boxShadow: isDark ? colors.shadowDark : colors.shadow
      },
      value,
      ...props,
      children: /* @__PURE__ */ jsx319(
        ProgressIndicator,
        {
          "data-slot": "progress-indicator",
          className: "h-full w-full flex-1",
          style: {
            backgroundColor: isDark ? colors.indicatorDark : colors.indicator
          }
        }
      )
    }
  );
}

// src/components/animate-ui/primitives/radix/radio-group.tsx
import * as React37 from "react";
import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence13, motion as motion16 } from "motion/react";
import { jsx as jsx320 } from "react/jsx-runtime";
var [RadioGroupProvider, useRadioGroup] = getStrictContext("RadioGroupContext");
var [RadioGroupItemProvider, useRadioGroupItem] = getStrictContext("RadioGroupItemContext");
function RadioGroup(props) {
  const [value, setValue] = useControlledState({
    value: props.value ?? void 0,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx320(RadioGroupProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx320(
    RadioGroupPrimitive.Root,
    {
      "data-slot": "radio-group",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function RadioGroupIndicator({
  transition = { type: "spring", stiffness: 500, damping: 30 },
  ...props
}) {
  const { isChecked } = useRadioGroupItem();
  return /* @__PURE__ */ jsx320(AnimatePresence13, { children: isChecked && /* @__PURE__ */ jsx320(
    RadioGroupPrimitive.Indicator,
    {
      "data-slot": "radio-group-indicator",
      asChild: true,
      forceMount: true,
      children: /* @__PURE__ */ jsx320(
        motion16.div,
        {
          "data-slot": "radio-group-indicator-circle",
          initial: { opacity: 0, scale: 0 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0 },
          transition,
          ...props
        },
        "radio-group-indicator-circle"
      )
    }
  ) });
}
function RadioGroupItem({
  value: valueProps,
  disabled,
  required,
  ...props
}) {
  const { value } = useRadioGroup();
  const [isChecked, setIsChecked] = React37.useState(value === valueProps);
  React37.useEffect(() => {
    setIsChecked(value === valueProps);
  }, [value, valueProps]);
  return /* @__PURE__ */ jsx320(RadioGroupItemProvider, { value: { isChecked, setIsChecked }, children: /* @__PURE__ */ jsx320(
    RadioGroupPrimitive.Item,
    {
      asChild: true,
      value: valueProps,
      disabled,
      required,
      children: /* @__PURE__ */ jsx320(
        motion16.button,
        {
          "data-slot": "radio-group-item",
          whileHover: { scale: 1.05 },
          whileTap: { scale: 0.95 },
          ...props
        }
      )
    }
  ) });
}

// src/components/ui/radio-group.tsx
import { jsx as jsx321 } from "react/jsx-runtime";
function RadioGroup2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx321(
    RadioGroup,
    {
      className: cn("grid gap-3", className),
      ...props
    }
  );
}
function RadioGroupItem2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx321(
    RadioGroupItem,
    {
      className: cn(
        "group/radio relative aspect-square size-4 shrink-0 rounded-full bg-white text-primary shadow-checkbox inset-shadow-checkbox transition-[color,box-shadow] duration-150 outline-none hover:inset-shadow-checkbox-hover focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.1)] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:inset-shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:shadow-[0_0_0_3px_oklch(0.469_0.173_10.9/0.2)] dark:bg-input/30 dark:shadow-none dark:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.15)] dark:hover:inset-shadow-[0_0_0_1px_rgb(255_255_255/0.25)] dark:focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] dark:focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.25)] dark:aria-invalid:ring-destructive/40",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx321(
        RadioGroupIndicator,
        {
          className: "absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary dark:bg-blue-500"
        }
      )
    }
  );
}

// src/components/ui/resizable.tsx
import { GripVerticalIcon } from "lucide-react";
import * as ResizablePrimitive from "react-resizable-panels";
import { jsx as jsx322 } from "react/jsx-runtime";
function ResizablePanelGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx322(
    ResizablePrimitive.Group,
    {
      "data-slot": "resizable-panel-group",
      className: cn(
        "flex h-full w-full aria-[orientation=vertical]:flex-col",
        className
      ),
      ...props
    }
  );
}
function ResizablePanel({ ...props }) {
  return /* @__PURE__ */ jsx322(ResizablePrimitive.Panel, { "data-slot": "resizable-panel", ...props });
}
function ResizableHandle({
  withHandle,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx322(
    ResizablePrimitive.Separator,
    {
      "data-slot": "resizable-handle",
      className: cn(
        "relative flex w-px items-center justify-center bg-border after:absolute after:inset-y-0 after:left-1/2 after:w-1 after:-translate-x-1/2 focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden aria-[orientation=horizontal]:h-px aria-[orientation=horizontal]:w-full aria-[orientation=horizontal]:after:left-0 aria-[orientation=horizontal]:after:h-1 aria-[orientation=horizontal]:after:w-full aria-[orientation=horizontal]:after:translate-x-0 aria-[orientation=horizontal]:after:-translate-y-1/2 [&[aria-orientation=horizontal]>div]:rotate-90",
        className
      ),
      ...props,
      children: withHandle && /* @__PURE__ */ jsx322("div", { className: "z-10 flex h-4 w-3 items-center justify-center rounded-xs border bg-border", children: /* @__PURE__ */ jsx322(GripVerticalIcon, { className: "size-2.5" }) })
    }
  );
}

// src/components/ui/slider.tsx
import * as React38 from "react";
import { Slider as SliderPrimitive } from "radix-ui";
import { motion as motion17, useSpring as useSpring3, useTransform } from "motion/react";
import { jsx as jsx323, jsxs as jsxs275 } from "react/jsx-runtime";
function useDarkMode2() {
  const [isDark, setIsDark] = React38.useState(
    () => typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
  );
  React38.useEffect(() => {
    const el = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(el.classList.contains("dark"));
    });
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}
var springConfig = { stiffness: 500, damping: 20, mass: 0.6 };
function SliderThumb({ isDark }) {
  const [isActive, setIsActive] = React38.useState(false);
  const scaleRaw = useSpring3(1, springConfig);
  const scale = useTransform(scaleRaw, (v) => v);
  React38.useEffect(() => {
    scaleRaw.set(isActive ? 1.25 : 1);
  }, [isActive, scaleRaw]);
  return /* @__PURE__ */ jsx323(SliderPrimitive.Thumb, { asChild: true, children: /* @__PURE__ */ jsx323(
    motion17.span,
    {
      "data-slot": "slider-thumb",
      className: cn(
        "block size-4 shrink-0 rounded-full bg-white outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        "focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.1)]",
        "dark:bg-gray-950 dark:focus-visible:inset-shadow-[0_0_0_1px_var(--color-blue-500)] dark:focus-visible:shadow-[0_0_0_3px_oklch(0.541_0.167_262.9/0.25)]"
      ),
      style: {
        scale,
        boxShadow: isDark ? "0 0 0 1px rgba(255,255,255,0.12), 0 1px 3px 0 rgba(0,0,0,0.4)" : "0 0 0 1px rgba(0,0,0,0.1), 0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)"
      },
      onPointerDown: () => setIsActive(true),
      onPointerUp: () => setIsActive(false),
      onPointerCancel: () => setIsActive(false),
      onPointerLeave: () => {
        if (isActive) setIsActive(false);
      }
    }
  ) });
}
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  color = "blue",
  ...props
}) {
  const _values = React38.useMemo(
    () => Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max],
    [value, defaultValue, min, max]
  );
  const isDark = useDarkMode2();
  const colors = progressColors[color];
  return /* @__PURE__ */ jsxs275(
    SliderPrimitive.Root,
    {
      "data-slot": "slider",
      defaultValue,
      value,
      min,
      max,
      className: cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx323(
          SliderPrimitive.Track,
          {
            "data-slot": "slider-track",
            className: "relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-2 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2",
            style: {
              backgroundColor: isDark ? colors.trackDark : colors.track,
              boxShadow: isDark ? colors.shadowDark : colors.shadow
            },
            children: /* @__PURE__ */ jsx323(
              SliderPrimitive.Range,
              {
                "data-slot": "slider-range",
                className: "absolute rounded-full data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
                style: {
                  backgroundColor: isDark ? colors.indicatorDark : colors.indicator
                }
              }
            )
          }
        ),
        Array.from({ length: _values.length }, (_, index) => /* @__PURE__ */ jsx323(SliderThumb, { isDark }, index))
      ]
    }
  );
}

// src/components/ui/snapshot.tsx
import * as React41 from "react";
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  PencilIcon
} from "lucide-react";

// src/components/ui/table.tsx
import * as React39 from "react";
import { jsx as jsx324 } from "react/jsx-runtime";
var TableContext = React39.createContext("default");
function useTableVariant() {
  return React39.useContext(TableContext);
}
function Table({ className, variant = "default", ...props }) {
  const { ref, style } = useHorizontalFade();
  return /* @__PURE__ */ jsx324(TableContext.Provider, { value: variant, children: /* @__PURE__ */ jsx324(
    "div",
    {
      "data-slot": "table-container",
      className: cn(
        "relative w-full",
        variant === "contained" && "overflow-hidden rounded-lg border"
      ),
      children: /* @__PURE__ */ jsx324(
        "div",
        {
          ref,
          style,
          "data-slot": "table-scroll",
          "data-fade-edges-x": "true",
          className: "w-full overflow-x-auto",
          children: /* @__PURE__ */ jsx324(
            "table",
            {
              "data-slot": "table",
              className: cn("w-full caption-bottom text-sm", className),
              ...props
            }
          )
        }
      )
    }
  ) });
}
function TableHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx324(
    "thead",
    {
      "data-slot": "table-header",
      className: cn("[&_tr]:border-b", className),
      ...props
    }
  );
}
function TableBody({ className, ...props }) {
  return /* @__PURE__ */ jsx324(
    "tbody",
    {
      "data-slot": "table-body",
      className: cn("[&_tr:last-child]:border-0", className),
      ...props
    }
  );
}
function TableFooter({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx324(
    "tfoot",
    {
      "data-slot": "table-footer",
      className: cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        variant === "contained" && "[&>tr:last-child>td:first-child]:rounded-bl-lg [&>tr:last-child>td:last-child]:rounded-br-lg",
        className
      ),
      ...props
    }
  );
}
function TableRow({ className, ...props }) {
  return /* @__PURE__ */ jsx324(
    "tr",
    {
      "data-slot": "table-row",
      className: cn(
        "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
        className
      ),
      ...props
    }
  );
}
function TableHead({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx324(
    "th",
    {
      "data-slot": "table-head",
      className: cn(
        "h-10 px-2 text-left align-middle font-medium whitespace-nowrap text-gray-500 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        variant === "contained" && "first:pl-4 last:pr-4",
        className
      ),
      ...props
    }
  );
}
function TableCell({ className, ...props }) {
  const variant = useTableVariant();
  return /* @__PURE__ */ jsx324(
    "td",
    {
      "data-slot": "table-cell",
      className: cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        variant === "contained" && "first:pl-4 last:pr-4",
        className
      ),
      ...props
    }
  );
}
function TableCaption({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx324(
    "caption",
    {
      "data-slot": "table-caption",
      className: cn("mt-4 text-sm text-muted-foreground", className),
      ...props
    }
  );
}

// src/components/ui/timestamp.tsx
import * as React40 from "react";
import { jsx as jsx325 } from "react/jsx-runtime";
function defaultFormatUTC(date) {
  return date.toLocaleString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short"
  });
}
function Timestamp({
  date,
  children,
  formatHover = defaultFormatUTC,
  align = "left",
  className,
  ...props
}) {
  const parsed = React40.useMemo(
    () => date instanceof Date ? date : new Date(date),
    [date]
  );
  const hoverText = React40.useMemo(
    () => formatHover(parsed),
    [parsed, formatHover]
  );
  return /* @__PURE__ */ jsx325(
    TextOverflow,
    {
      "data-slot": "timestamp",
      align,
      delay: 0,
      alwaysShow: true,
      nowrap: true,
      hoverContent: hoverText,
      className: cn("cursor-pointer", className),
      ...props,
      children
    }
  );
}

// src/components/ui/snapshot.tsx
import { jsx as jsx326, jsxs as jsxs276 } from "react/jsx-runtime";
var TRANSFER_TYPES = [
  "ACH Debit",
  "ACH Credit",
  "Book",
  "Check",
  "International wire",
  "Wire",
  "Realtime"
];
var TRANSFER_TYPE_LABELS = {
  "ACH Debit": "ACH Debit",
  "ACH Credit": "ACH Credit",
  "Book": "Book",
  "Check": "Check",
  "International wire": "Intl Wire",
  "Wire": "Wire",
  "Realtime": "Realtime"
};
var TRANSFER_STATUSES = [
  "issued",
  "submitted",
  "completed",
  "returned",
  "canceled"
];
var FADE_SIZE = 32;
var FADE_DURATION = 150;
function easeOut(t) {
  return 1 - (1 - t) ** 3;
}
function useAnimatedValue(target, duration) {
  const [value, setValue] = React41.useState(target ? 1 : 0);
  const rafRef = React41.useRef(0);
  const startRef = React41.useRef(null);
  React41.useEffect(() => {
    const to = target ? 1 : 0;
    const currentValue = value;
    startRef.current = { time: performance.now(), from: currentValue };
    function tick(now) {
      if (!startRef.current) return;
      const elapsed = now - startRef.current.time;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOut(progress);
      const next = startRef.current.from + (to - startRef.current.from) * eased;
      setValue(next);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return value;
}
function useHorizontalFade2() {
  const [showLeft, setShowLeft] = React41.useState(false);
  const [showRight, setShowRight] = React41.useState(false);
  const ref = React41.useRef(null);
  const leftFade = useAnimatedValue(showLeft, FADE_DURATION);
  const rightFade = useAnimatedValue(showRight, FADE_DURATION);
  React41.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function update() {
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeft(scrollLeft > 2);
      setShowRight(scrollLeft + clientWidth < scrollWidth - 2);
    }
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);
  const maskStyle = (() => {
    if (leftFade === 0 && rightFade === 0) return void 0;
    const leftSize = Math.round(leftFade * FADE_SIZE);
    const rightSize = Math.round(rightFade * FADE_SIZE);
    const gradient = `linear-gradient(to right, rgba(0,0,0,${1 - leftFade}) 0px, black ${leftSize}px, black calc(100% - ${rightSize}px), rgba(0,0,0,${1 - rightFade}) 100%)`;
    return {
      maskImage: gradient,
      WebkitMaskImage: gradient
    };
  })();
  return { ref, maskStyle };
}
function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD"
  }).format(value);
}
function formatRelativeTime(timestamp) {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1e3);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}
var statusStyles = {
  issued: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  submitted: "bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300",
  completed: "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300",
  returned: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
  canceled: "bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-400"
};
function SnapshotEditDialog({
  title,
  filter = {},
  onFilterChange,
  children
}) {
  const [direction, setDirection] = React41.useState(
    filter.direction ?? "all"
  );
  const [selectedTypes, setSelectedTypes] = React41.useState(
    new Set(filter.types ?? TRANSFER_TYPES)
  );
  const [selectedStatuses, setSelectedStatuses] = React41.useState(new Set(filter.statuses ?? TRANSFER_STATUSES));
  const [minAmount, setMinAmount] = React41.useState(
    filter.minAmount?.toString() ?? ""
  );
  const [maxAmount, setMaxAmount] = React41.useState(
    filter.maxAmount?.toString() ?? ""
  );
  function toggleType(type) {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }
  function toggleStatus(status) {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }
  function handleSave() {
    onFilterChange?.({
      direction,
      types: Array.from(selectedTypes),
      statuses: Array.from(selectedStatuses),
      minAmount: minAmount ? parseFloat(minAmount) : void 0,
      maxAmount: maxAmount ? parseFloat(maxAmount) : void 0
    });
  }
  return /* @__PURE__ */ jsxs276(Dialog2, { children: [
    /* @__PURE__ */ jsx326(DialogTrigger2, { asChild: true, children }),
    /* @__PURE__ */ jsxs276(DialogContent2, { showCloseButton: false, children: [
      /* @__PURE__ */ jsxs276(DialogHeader, { children: [
        /* @__PURE__ */ jsx326(DialogTitle2, { children: "Edit Snapshot" }),
        /* @__PURE__ */ jsxs276(DialogDescription2, { children: [
          "Configure the filters for \u201C",
          title,
          "\u201D"
        ] })
      ] }),
      /* @__PURE__ */ jsxs276("div", { "data-slot": "snapshot-edit-form", className: "grid gap-5", children: [
        /* @__PURE__ */ jsxs276("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx326(Label, { children: "Direction" }),
          /* @__PURE__ */ jsxs276(Select, { value: direction, onValueChange: setDirection, children: [
            /* @__PURE__ */ jsx326(SelectTrigger, { className: "w-full", children: /* @__PURE__ */ jsx326(SelectValue, {}) }),
            /* @__PURE__ */ jsxs276(SelectContent, { children: [
              /* @__PURE__ */ jsx326(SelectItem, { value: "all", children: "All" }),
              /* @__PURE__ */ jsx326(SelectItem, { value: "incoming", children: "Incoming" }),
              /* @__PURE__ */ jsx326(SelectItem, { value: "outgoing", children: "Outgoing" })
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs276("div", { className: "grid gap-2", children: [
          /* @__PURE__ */ jsx326(Label, { children: "Amount range" }),
          /* @__PURE__ */ jsxs276("div", { className: "grid grid-cols-2 gap-3", children: [
            /* @__PURE__ */ jsx326(
              Input,
              {
                type: "number",
                placeholder: "Min",
                value: minAmount,
                onChange: (e) => setMinAmount(e.target.value)
              }
            ),
            /* @__PURE__ */ jsx326(
              Input,
              {
                type: "number",
                placeholder: "Max",
                value: maxAmount,
                onChange: (e) => setMaxAmount(e.target.value)
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs276("div", { className: "grid gap-2.5", children: [
          /* @__PURE__ */ jsx326(Label, { children: "Transfer types" }),
          /* @__PURE__ */ jsx326("div", { className: "grid grid-cols-2 gap-x-4 gap-y-2.5", children: TRANSFER_TYPES.map((type) => /* @__PURE__ */ jsxs276(
            "label",
            {
              className: "flex items-center gap-2 text-sm cursor-pointer",
              children: [
                /* @__PURE__ */ jsx326(
                  Checkbox2,
                  {
                    checked: selectedTypes.has(type),
                    onCheckedChange: () => toggleType(type)
                  }
                ),
                type
              ]
            },
            type
          )) })
        ] }),
        /* @__PURE__ */ jsxs276("div", { className: "grid gap-2.5", children: [
          /* @__PURE__ */ jsx326(Label, { children: "Statuses" }),
          /* @__PURE__ */ jsx326("div", { className: "grid grid-cols-2 gap-x-4 gap-y-2.5", children: TRANSFER_STATUSES.map((status) => /* @__PURE__ */ jsxs276(
            "label",
            {
              className: "flex items-center gap-2 text-sm capitalize cursor-pointer",
              children: [
                /* @__PURE__ */ jsx326(
                  Checkbox2,
                  {
                    checked: selectedStatuses.has(status),
                    onCheckedChange: () => toggleStatus(status)
                  }
                ),
                status
              ]
            },
            status
          )) })
        ] })
      ] }),
      /* @__PURE__ */ jsxs276(DialogFooter, { children: [
        /* @__PURE__ */ jsx326(DialogClose2, { asChild: true, children: /* @__PURE__ */ jsx326(Button, { variant: "muted", children: "Cancel" }) }),
        /* @__PURE__ */ jsx326(DialogClose2, { asChild: true, children: /* @__PURE__ */ jsx326(Button, { onClick: handleSave, children: "Save filters" }) })
      ] })
    ] })
  ] });
}
function DirectionIcon({
  direction,
  className
}) {
  const isIncoming = direction === "incoming";
  const Icon = isIncoming ? ArrowDownLeftIcon : ArrowUpRightIcon;
  return /* @__PURE__ */ jsx326(
    "span",
    {
      "data-slot": "snapshot-direction-icon",
      className: cn(
        "inline-flex size-4 items-center justify-center text-gray-400 dark:text-gray-500",
        className
      ),
      children: /* @__PURE__ */ jsx326(Icon, { className: "size-3.5" })
    }
  );
}
function Snapshot({
  title,
  transfers,
  filter,
  onTransferClick,
  onFilterChange,
  size = "default",
  className,
  ...props
}) {
  const { ref: fadeRef, maskStyle } = useHorizontalFade2();
  return /* @__PURE__ */ jsxs276(
    Card,
    {
      "data-slot": "snapshot",
      size,
      className: cn("overflow-hidden rounded-lg gap-0 py-0 shadow-none", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs276(CardHeader, { className: "grid-rows-[auto] gap-0 items-center border-b px-4 pt-3 pb-2 [.border-b]:pb-2", children: [
          /* @__PURE__ */ jsx326(CardTitle, { className: "text-sm font-medium tracking-tight", children: title }),
          /* @__PURE__ */ jsx326(CardAction, { className: "self-center", children: /* @__PURE__ */ jsx326(
            SnapshotEditDialog,
            {
              title,
              filter,
              onFilterChange,
              children: /* @__PURE__ */ jsx326(
                Button,
                {
                  variant: "muted",
                  size: "icon-xs",
                  "aria-label": "Edit snapshot filters",
                  children: /* @__PURE__ */ jsx326(PencilIcon, {})
                }
              )
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx326(CardContent, { className: "px-0 py-0", children: /* @__PURE__ */ jsx326(
          "div",
          {
            ref: fadeRef,
            className: "overflow-x-auto [&_[data-slot=table-container]]:overflow-visible",
            style: maskStyle,
            children: /* @__PURE__ */ jsx326(Table, { children: /* @__PURE__ */ jsx326(TableBody, { children: transfers.map((transfer) => {
              const isClickable = !!(onTransferClick || transfer.href);
              const row = /* @__PURE__ */ jsxs276(
                TableRow,
                {
                  "data-slot": "snapshot-row",
                  className: cn(
                    isClickable && "cursor-pointer hover:bg-muted/50 active:bg-muted"
                  ),
                  onClick: onTransferClick ? () => onTransferClick(transfer) : void 0,
                  children: [
                    /* @__PURE__ */ jsx326(TableCell, { className: "pl-4 w-[16px] pr-0", children: /* @__PURE__ */ jsx326(DirectionIcon, { direction: transfer.direction }) }),
                    /* @__PURE__ */ jsx326(TableCell, { className: "w-full pl-1.5", children: /* @__PURE__ */ jsx326("span", { className: "font-medium tracking-tight", children: formatCurrency(transfer.amount) }) }),
                    /* @__PURE__ */ jsx326(TableCell, { className: "font-medium text-muted-foreground", children: TRANSFER_TYPE_LABELS[transfer.type] }),
                    /* @__PURE__ */ jsx326(TableCell, { children: /* @__PURE__ */ jsx326(
                      Badge,
                      {
                        variant: "outline",
                        className: cn(
                          "capitalize border-transparent",
                          statusStyles[transfer.status]
                        ),
                        children: transfer.status
                      }
                    ) }),
                    /* @__PURE__ */ jsx326(TableCell, { className: "pr-4 text-right text-xs text-muted-foreground", children: /* @__PURE__ */ jsx326(Timestamp, { date: transfer.timestamp, align: "right", children: formatRelativeTime(transfer.timestamp) }) })
                  ]
                },
                transfer.id
              );
              if (transfer.href && !onTransferClick) {
                return /* @__PURE__ */ jsx326(
                  "a",
                  {
                    href: transfer.href,
                    className: "contents",
                    children: row
                  },
                  transfer.id
                );
              }
              return row;
            }) }) })
          }
        ) })
      ]
    }
  );
}

// src/components/ui/sonner.tsx
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon as Loader2Icon2,
  OctagonXIcon,
  TriangleAlertIcon
} from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { jsx as jsx327 } from "react/jsx-runtime";
var Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme();
  return /* @__PURE__ */ jsx327(
    Sonner,
    {
      theme,
      className: "toaster group",
      icons: {
        success: /* @__PURE__ */ jsx327(CircleCheckIcon, { className: "size-4" }),
        info: /* @__PURE__ */ jsx327(InfoIcon, { className: "size-4" }),
        warning: /* @__PURE__ */ jsx327(TriangleAlertIcon, { className: "size-4" }),
        error: /* @__PURE__ */ jsx327(OctagonXIcon, { className: "size-4" }),
        loading: /* @__PURE__ */ jsx327(Loader2Icon2, { className: "size-4 animate-spin" })
      },
      toastOptions: {
        style: {
          backgroundColor: "var(--color-popover)",
          color: "var(--color-popover-foreground)",
          borderColor: "var(--color-border)",
          opacity: 1
        },
        ...props.toastOptions
      },
      style: {
        "--normal-bg": "var(--color-popover)",
        "--normal-text": "var(--color-popover-foreground)",
        "--normal-border": "var(--color-border)",
        "--border-radius": "var(--radius-lg)",
        "--success-bg": "var(--color-popover)",
        "--success-text": "var(--color-popover-foreground)",
        "--success-border": "var(--color-border)",
        "--error-bg": "var(--color-popover)",
        "--error-text": "var(--color-popover-foreground)",
        "--error-border": "var(--color-border)",
        "--info-bg": "var(--color-popover)",
        "--info-text": "var(--color-popover-foreground)",
        "--info-border": "var(--color-border)",
        "--warning-bg": "var(--color-popover)",
        "--warning-text": "var(--color-popover-foreground)",
        "--warning-border": "var(--color-border)"
      },
      ...props
    }
  );
};

// src/components/ui/status-indicator.tsx
import { cva as cva13 } from "class-variance-authority";
import { jsx as jsx328 } from "react/jsx-runtime";
var modeColors = {
  sandbox: {
    bg: "oklch(0.750 0.155 40.5)",
    border: "oklch(0.580 0.145 40.5)"
  },
  live: {
    bg: "oklch(0.695 0.119 165.5)",
    border: "oklch(0.529 0.100 165.5)"
  }
};
var statusIndicatorVariants = cva13(
  "inline-flex shrink-0 items-center justify-center",
  {
    variants: {
      size: {
        sm: "size-3",
        default: "size-4",
        lg: "size-5",
        xl: "size-6"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
);
function StatusIndicator({
  mode = "sandbox",
  size,
  className,
  ...props
}) {
  const colors = modeColors[mode];
  return /* @__PURE__ */ jsx328(
    "div",
    {
      "data-slot": "status-indicator",
      "data-mode": mode,
      className: cn(statusIndicatorVariants({ size }), className),
      style: { "--status-border": colors.border },
      ...props,
      children: /* @__PURE__ */ jsx328(
        "div",
        {
          className: "size-1/2 rounded-full shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--status-border)_50%,transparent)] dark:shadow-none",
          style: { backgroundColor: colors.bg }
        }
      )
    }
  );
}

// src/components/ui/tabs.tsx
import * as React42 from "react";
import { cva as cva14 } from "class-variance-authority";
import { motion as motion19 } from "motion/react";

// src/components/animate-ui/primitives/radix/tabs.tsx
import { Tabs as TabsPrimitive } from "radix-ui";
import {
  motion as motion18,
  AnimatePresence as AnimatePresence14
} from "motion/react";
import { jsx as jsx329 } from "react/jsx-runtime";
var [TabsProvider, useTabs] = getStrictContext("TabsContext");
function Tabs(props) {
  const [value, setValue] = useControlledState({
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx329(TabsProvider, { value: { value, setValue }, children: /* @__PURE__ */ jsx329(
    TabsPrimitive.Root,
    {
      "data-slot": "tabs",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function TabsList(props) {
  return /* @__PURE__ */ jsx329(TabsPrimitive.List, { "data-slot": "tabs-list", ...props });
}
function TabsTrigger(props) {
  return /* @__PURE__ */ jsx329(TabsPrimitive.Trigger, { "data-slot": "tabs-trigger", ...props });
}
function TabsContent({
  value,
  forceMount,
  transition = { duration: 0.5, ease: "easeInOut" },
  ...props
}) {
  return /* @__PURE__ */ jsx329(AnimatePresence14, { mode: "wait", children: /* @__PURE__ */ jsx329(TabsPrimitive.Content, { asChild: true, forceMount, value, children: /* @__PURE__ */ jsx329(
    motion18.div,
    {
      "data-slot": "tabs-content",
      layout: true,
      layoutDependency: value,
      initial: { opacity: 0, filter: "blur(4px)" },
      animate: { opacity: 1, filter: "blur(0px)" },
      exit: { opacity: 0, filter: "blur(4px)" },
      transition,
      ...props
    }
  ) }) });
}

// src/components/ui/tabs.tsx
import { jsx as jsx330, jsxs as jsxs277 } from "react/jsx-runtime";
function Tabs2({
  className,
  orientation = "horizontal",
  ...props
}) {
  return /* @__PURE__ */ jsx330(
    Tabs,
    {
      "data-orientation": orientation,
      orientation,
      className: cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      ),
      ...props
    }
  );
}
var tabsListVariants = cva14(
  "group/tabs-list inline-flex w-fit items-center justify-center rounded-lg p-[3px] text-muted-foreground group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-muted",
        line: "gap-1 bg-transparent",
        ghost: "bg-transparent"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var highlightSpring = { type: "spring", stiffness: 500, damping: 35 };
var TabsListContext = React42.createContext({
  variant: "default",
  layoutId: ""
});
function TabsList2({
  className,
  variant = "default",
  children,
  ...props
}) {
  const layoutId = React42.useId();
  return /* @__PURE__ */ jsx330(TabsListContext.Provider, { value: { variant: variant ?? "default", layoutId }, children: /* @__PURE__ */ jsx330(
    TabsList,
    {
      "data-variant": variant,
      className: cn(tabsListVariants({ variant }), className),
      ...props,
      children
    }
  ) });
}
function TabsTrigger2({
  className,
  ...props
}) {
  const { value: activeValue } = useTabs();
  const { variant, layoutId } = React42.useContext(TabsListContext);
  const isActive = activeValue === props.value;
  return /* @__PURE__ */ jsxs277(
    TabsTrigger,
    {
      className: cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-colors duration-150 group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "data-[state=active]:text-foreground dark:data-[state=active]:text-foreground",
        className
      ),
      ...props,
      children: [
        isActive && /* @__PURE__ */ jsx330(
          motion19.span,
          {
            layoutId: `tab-highlight-${layoutId}`,
            className: cn(
              "absolute inset-0 z-0 rounded-md",
              variant === "default" && "bg-background shadow-sm dark:border dark:border-input dark:bg-input/30",
              variant === "line" && "inset-auto bottom-[-4px] left-0 right-0 h-0.5 rounded-full bg-foreground",
              variant === "ghost" && "bg-muted dark:bg-white/[0.06]"
            ),
            transition: highlightSpring
          }
        ),
        /* @__PURE__ */ jsx330("span", { className: "relative z-10", children: props.children })
      ]
    }
  );
}
function TabsContent2({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx330(
    TabsContent,
    {
      className: cn("flex-1 outline-none", className),
      ...props
    }
  );
}

// src/components/ui/toggle.tsx
import { cva as cva15 } from "class-variance-authority";

// src/components/animate-ui/primitives/radix/toggle.tsx
import { Toggle as TogglePrimitive } from "radix-ui";
import { motion as motion20, AnimatePresence as AnimatePresence15 } from "motion/react";
import { jsx as jsx331 } from "react/jsx-runtime";
var [ToggleProvider, useToggle] = getStrictContext("ToggleContext");
function Toggle({
  pressed,
  defaultPressed,
  onPressedChange,
  disabled,
  ...props
}) {
  const [isPressed, setIsPressed] = useControlledState({
    value: pressed,
    defaultValue: defaultPressed,
    onChange: onPressedChange
  });
  return /* @__PURE__ */ jsx331(ToggleProvider, { value: { isPressed, setIsPressed, disabled }, children: /* @__PURE__ */ jsx331(
    TogglePrimitive.Root,
    {
      pressed,
      defaultPressed,
      onPressedChange: setIsPressed,
      disabled,
      asChild: true,
      children: /* @__PURE__ */ jsx331(
        motion20.button,
        {
          "data-slot": "toggle",
          whileTap: { scale: 0.95 },
          ...props
        }
      )
    }
  ) });
}

// src/components/ui/toggle.tsx
import { jsx as jsx332 } from "react/jsx-runtime";
var toggleVariants = cva15(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-10 min-w-10 px-2.5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Toggle2({
  className,
  variant,
  size,
  ...props
}) {
  return /* @__PURE__ */ jsx332(
    Toggle,
    {
      className: cn(toggleVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/ui/toggle-group.tsx
import * as React44 from "react";

// src/components/animate-ui/primitives/radix/toggle-group.tsx
import * as React43 from "react";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";
import { AnimatePresence as AnimatePresence16, motion as motion21 } from "motion/react";
import { Fragment as Fragment7, jsx as jsx333, jsxs as jsxs278 } from "react/jsx-runtime";
var [ToggleGroupProvider, useToggleGroup] = getStrictContext("ToggleGroupContext");
function ToggleGroup(props) {
  const [value, setValue] = useControlledState({
    value: props.value,
    defaultValue: props.defaultValue,
    onChange: props.onValueChange
  });
  return /* @__PURE__ */ jsx333(ToggleGroupProvider, { value: { value, setValue, type: props.type }, children: /* @__PURE__ */ jsx333(
    ToggleGroupPrimitive.Root,
    {
      "data-slot": "toggle-group",
      ...props,
      onValueChange: setValue
    }
  ) });
}
function ToggleGroupItem({ value, disabled, ...props }) {
  return /* @__PURE__ */ jsx333(ToggleGroupPrimitive.Item, { value, disabled, asChild: true, children: /* @__PURE__ */ jsx333(
    motion21.button,
    {
      "data-slot": "toggle-group-item",
      whileTap: { scale: 0.95 },
      ...props
    }
  ) });
}

// src/components/ui/toggle-group.tsx
import { jsx as jsx334 } from "react/jsx-runtime";
var ToggleGroupContext = React44.createContext({
  size: "default",
  variant: "default",
  spacing: 0
});
function ToggleGroup2({
  className,
  variant,
  size,
  spacing = 0,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx334(
    ToggleGroup,
    {
      "data-variant": variant,
      "data-size": size,
      "data-spacing": spacing,
      style: { "--gap": spacing },
      className: cn(
        "group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx334(ToggleGroupContext.Provider, { value: { variant, size, spacing }, children })
    }
  );
}
function ToggleGroupItem2({
  className,
  children,
  variant,
  size,
  ...props
}) {
  const context = React44.useContext(ToggleGroupContext);
  return /* @__PURE__ */ jsx334(
    ToggleGroupItem,
    {
      "data-variant": context.variant || variant,
      "data-size": context.size || size,
      "data-spacing": context.spacing,
      className: cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size
        }),
        "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10",
        "data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l",
        className
      ),
      ...props,
      children
    }
  );
}
export {
  Accordion2 as Accordion,
  AccordionContent2 as AccordionContent,
  AccordionItem2 as AccordionItem,
  AccordionTrigger2 as AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog2 as AlertDialog,
  AlertDialogAction2 as AlertDialogAction,
  AlertDialogCancel2 as AlertDialogCancel,
  AlertDialogContent2 as AlertDialogContent,
  AlertDialogDescription2 as AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay2 as AlertDialogOverlay,
  AlertDialogPortal2 as AlertDialogPortal,
  AlertDialogTitle2 as AlertDialogTitle,
  AlertDialogTrigger2 as AlertDialogTrigger,
  AlertTitle,
  AmountInput,
  AnimatedCheckIcon,
  AspectRatio,
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
  Calendar,
  CalendarDayButton,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  Checkbox2 as Checkbox,
  CircleQuestionmarkIcon,
  Collapsible2 as Collapsible,
  CollapsibleContent2 as CollapsibleContent,
  CollapsibleTrigger2 as CollapsibleTrigger,
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  CountryFlag,
  CurrencyFlag,
  DataTable2 as DataTable,
  DateInput,
  DatePicker,
  Dialog2 as Dialog,
  DialogClose2 as DialogClose,
  DialogContent2 as DialogContent,
  DialogDescription2 as DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay2 as DialogOverlay,
  DialogPortal2 as DialogPortal,
  DialogTitle2 as DialogTitle,
  DialogTrigger2 as DialogTrigger,
  DirectionIcon,
  DirectionProvider,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu2 as DropdownMenu,
  DropdownMenuCheckboxItem2 as DropdownMenuCheckboxItem,
  DropdownMenuContent2 as DropdownMenuContent,
  DropdownMenuGroup2 as DropdownMenuGroup,
  DropdownMenuItem2 as DropdownMenuItem,
  DropdownMenuLabel2 as DropdownMenuLabel,
  DropdownMenuPortal2 as DropdownMenuPortal,
  DropdownMenuRadioGroup2 as DropdownMenuRadioGroup,
  DropdownMenuRadioItem2 as DropdownMenuRadioItem,
  DropdownMenuSeparator2 as DropdownMenuSeparator,
  DropdownMenuShortcut2 as DropdownMenuShortcut,
  DropdownMenuSub2 as DropdownMenuSub,
  DropdownMenuSubContent2 as DropdownMenuSubContent,
  DropdownMenuSubTrigger2 as DropdownMenuSubTrigger,
  DropdownMenuTrigger2 as DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  FlagIcon,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  HoverCard2 as HoverCard,
  HoverCardContent2 as HoverCardContent,
  HoverCardTrigger2 as HoverCardTrigger,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
  Kbd,
  KbdGroup,
  Label,
  Logo,
  LogoIcon,
  LogoWordmark,
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
  MoneyInput,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PhoneInput,
  PlatformSwitcher,
  Popover2 as Popover,
  PopoverAnchor2 as PopoverAnchor,
  PopoverContent2 as PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger2 as PopoverTrigger,
  Progress2 as Progress,
  RadioGroup2 as RadioGroup,
  RadioGroupItem2 as RadioGroupItem,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  ScrollArea,
  ScrollBar,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet2 as Sheet,
  SheetClose2 as SheetClose,
  SheetContent2 as SheetContent,
  SheetDescription2 as SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle2 as SheetTitle,
  SheetTrigger2 as SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Skeleton,
  Slider,
  Snapshot,
  SnapshotEditDialog,
  Spinner,
  StatusIndicator,
  Switch2 as Switch,
  TRANSFER_STATUSES,
  TRANSFER_TYPES,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs2 as Tabs,
  TabsContent2 as TabsContent,
  TabsList2 as TabsList,
  TabsTrigger2 as TabsTrigger,
  TextOverflow,
  Textarea,
  Timestamp,
  Toaster,
  Toggle2 as Toggle,
  ToggleGroup2 as ToggleGroup,
  ToggleGroupItem2 as ToggleGroupItem,
  Tooltip3 as Tooltip,
  TooltipContent2 as TooltipContent,
  TooltipProvider2 as TooltipProvider,
  TooltipTrigger2 as TooltipTrigger,
  badgeVariants,
  buttonGroupVariants,
  buttonVariants,
  cn,
  countryFlags,
  currencyFlags,
  defaultFormatUTC,
  fieldGroupVariants,
  formatAmount,
  formatDateInputValue,
  formatPhoneValue,
  formatRelativeTime,
  getAmountFormat,
  inputVariants,
  isValidPhoneNumber,
  navigationMenuTriggerStyle,
  parseDateInputValue,
  parsePhoneValue,
  progressColors,
  modeColors as statusIndicatorColors,
  tabsListVariants,
  toggleVariants,
  useComboboxAnchor,
  useDirection,
  useFormField,
  useIsMobile,
  useSidebar
};
//# sourceMappingURL=index.js.map